import MockAdapter from 'axios-mock-adapter';
import { type AxiosInstance } from 'axios';
import { type LicenseGroupData, type Profile, PublicId } from 'app/store/types';
import {
	getFormsUrl,
	getNodeTestingAPI,
	getR2mUrl,
	getLogApiUrl,
	getMetadataUrl,
	getWfxUrl,
	ALL_USER_GROUP,
	mockPrintPreferences,
	type PrintPreferences,
	reportTypeOptions,
	defaultPermissions,
	getUnivPrintUrl,
} from 'app/utils/helpers';
import _ from '@lodash';
import qs from 'qs';
import { getPlanDomain, licenseGroupPlanDesignCheck } from 'app/utils/tenant-plan';
import { Buffer } from 'buffer';
import { UNGROUPED_APP_GROUP_ID } from 'app/utils/default-groups';
import { defaultUserPreferencesEnterprise } from 'app/store/reducers';
import { reportByType } from './reports.mocking';

// toggle on or off mock data
const urlParams = new URLSearchParams(window.location.search);

if (urlParams.has('noMock')) {
	localStorage.removeItem('mock');
	localStorage.removeItem('mockUser');
	localStorage.removeItem('mockExpired');
	localStorage.removeItem('mockPlan');
	localStorage.removeItem('mockOrderType');
	localStorage.removeItem('mockRegion');
	localStorage.removeItem('mockLogs');
	localStorage.removeItem('mockForms');
	localStorage.removeItem('mockWorkflows');
	localStorage.removeItem('mockDirectPrintAgents');
	localStorage.removeItem('mockApps');
	localStorage.removeItem('mockPrints');
	localStorage.removeItem('mockReports');
	localStorage.removeItem('mockNotifications');
	localStorage.removeItem('mockEntitlements');
	localStorage.removeItem('mockPricing');
	localStorage.removeItem('noComingSoonWrapper');
	localStorage.removeItem('enableCheckout');
	localStorage.removeItem('mockNoRegion');
	localStorage.removeItem('mockMfpAuth');
	localStorage.removeItem('allPermissions');
	localStorage.removeItem('mockT');
	localStorage.removeItem('allRegions');
	localStorage.removeItem('exportFormsNoEncrypt');
	localStorage.removeItem('mockUnivPrint');
}

if (urlParams.has('mock')) {
	localStorage.setItem('mock', 'true');
}
if (urlParams.has('mockUser')) {
	localStorage.setItem('mockUser', 'true');
}
if (urlParams.has('mockUnivPrint')) {
	localStorage.setItem('mockUnivPrint', 'true');
}
if (urlParams.has('mockExpired')) {
	localStorage.setItem('mockExpired', urlParams.get('mockExpired') || `${+new Date('2020-12-12T23:59:59.000Z')}`);
}
if (Object.values(PublicId).includes(urlParams.get('mockPlan') as PublicId)) {
	localStorage.setItem('mockPlan', urlParams.get('mockPlan') as PublicId);
}
if (typeof urlParams.get('mockOrderType') === 'string') {
	localStorage.setItem('mockOrderType', urlParams.get('mockOrderType') as LicenseGroupData['orderType']);
}
if (typeof urlParams.get('mockRegion') === 'string') {
	localStorage.setItem('mockRegion', urlParams.get('mockRegion')!);
}
if (urlParams.has('mockLogs')) {
	localStorage.setItem('mockLogs', 'true');
}
if (urlParams.has('mockForms')) {
	localStorage.setItem('mockForms', 'true');
}
if (urlParams.has('mockWorkflows')) {
	localStorage.setItem('mockWorkflows', 'true');
}
if (urlParams.has('mockDirectPrintAgents')) {
	localStorage.setItem('mockDirectPrintAgents', 'true');
}
if (urlParams.has('mockApps')) {
	localStorage.setItem('mockApps', 'true');
}
if (urlParams.has('mockPrints')) {
	localStorage.setItem('mockPrints', 'true');
}
if (urlParams.has('mockReports')) {
	localStorage.setItem('mockReports', 'true');
}
if (urlParams.has('mockNotifications')) {
	localStorage.setItem('mockNotifications', 'true');
}
if (urlParams.has('mockEntitlements')) {
	localStorage.setItem('mockEntitlements', 'true');
}
if (urlParams.has('mockPricing')) {
	localStorage.setItem('mockPricing', 'true');
}
if (urlParams.has('noComingSoonWrapper')) {
	localStorage.setItem('noComingSoonWrapper', 'true');
}
if (urlParams.has('enableCheckout')) {
	localStorage.setItem('enableCheckout', 'true');
}
if (urlParams.has('mockNoRegion')) {
	localStorage.setItem('mockNoRegion', 'true');
}
if (urlParams.has('mockMfpAuth')) {
	localStorage.setItem('mockMfpAuth', 'true');
}

if (urlParams.has('allPermissions')) {
	localStorage.setItem('allPermissions', 'true');
}

if (urlParams.has('mockT')) {
	localStorage.setItem('mockT', 'true');
}

if (urlParams.has('allRegions')) {
	localStorage.setItem('allRegions', 'true');
}

if (urlParams.has('exportFormsNoEncrypt')) {
	localStorage.setItem('exportFormsNoEncrypt', 'true');
}

// HACK-ish::add option for Product to edit site content easily
if (urlParams.has('designMode')) {
	document.designMode = 'on';
	document.body.setAttribute('spellcheck', 'false');
}

const mocking = (instance: AxiosInstance) => {
	const mock = new MockAdapter(instance, { delayResponse: 600, onNoMatch: 'passthrough' });
	const mockExpired = localStorage.getItem('mockExpired');
	const selectedLicenseGroup = {
		id: 'mock-selected-tenant-id', // DEV NOTE::do _not_ hardcode this value into any mock API calls - use `${selectedLicenseGroup.id}` (or `[^/]+` for regex)
		slug: 'mock-selected-tenant-slug',
		name: 'ABC Company',
		timezone: 'America/New_York',
		catalogPublicId: localStorage.getItem('mockPlan') ?? PublicId.TermOneYear,
		orderType: localStorage.getItem('mockOrderType') ?? 'PRODUCTION',
		expirationDate: mockExpired ? +mockExpired : +new Date('2050-12-12T23:59:59.000Z'),
		userExpirationDate: mockExpired ? +mockExpired : +new Date('2025-12-12T23:59:59.000Z'),
		r2mExpirationDate: mockExpired ? +mockExpired : +new Date('2025-12-12T23:59:59.000Z'),
		isCoolOffPeriod: false,
		language: 'en',
		orderNumber: '1000596878880001',
		purchaseCode: '267791324',
		// DEV NOTE::magically matches whichever URL you visit (change to explicit value for testing redirect)
		// change to stratus-starter, stratus-business, stratus-enterprise
		plan: (licenseGroupPlanDesignCheck('stratus') ? 'stratus-enterprise' : 'stc') as LicenseGroupData['plan'],
		adminData: [
			{ email: 'lsmith@kmbs.konicaminolta.us', status: 'ACCEPTED' },
			{ email: 'mmetnetsky@kmbs.konicaminolta.us', status: 'PENDING' },
		],
		region: localStorage.getItem('mockNoRegion') ? false : 'us-east-1',
		quantity: 7,
		capacity: 11,
		userQuantity: 8,
		userCapacity: 25,
		r2mQuantity: 5,
		r2mCapacity: 6,
		managerEmails: [],
		isAdmin: true,
		isManager: false,
		authMethod: 'mp',
		dateAdded: 1614320869622,
		r2mEnabled: true,
		bsnDashboardEnabled: true,
		mfpAuthEnabled: true,
		storageEnabled: true,
		storageExpiresAt: undefined,
		mfpDashboardEnabled: true,
		msUnivPrintEnabled: true,
		// licenseUsage: {
		// 	dateUpdated: 1675058102193,
		// 	fileCount: 51,
		// 	fileSizeTotal: 10000
		// }
	};
	const user = {
		id: 'mock-user-id',
		firstName: 'Russell',
		lastName: 'Fellows',
		username: 'konicaminoltamarketplace_mock-user-mpId-1',
		email: 'rfellows@kmbs.konicaminolta.us',
		mpId: 'mock-user-mpId-1',
		country: 'US',
		awsRegion: selectedLicenseGroup.region as Profile['awsRegion'],
		tenant: selectedLicenseGroup.id,
		tokens: {
			accessToken:
				'eyJraWQiOiJMc0ZzOG5Xbjd5dlh0RXhnWjVpMkEyS0I4YmtLY0V3b0poQXJmVTVYVWkwPSIsImFsZyI6IlJTMjU2In0.eyJzdWIiOiI2ZjQzNmU0ZS01NjRhLTQyMjctYTVhMS1mZWRhY2NjOGYwNzUiLCJjb2duaXRvOmdyb3VwcyI6WyJ1cy1lYXN0LTFfTEE0b1Bkbm1FX0tvbmljYU1pbm9sdGFNYXJrZXRQbGFjZSJdLCJpc3MiOiJodHRwczpcL1wvY29nbml0by1pZHAudXMtZWFzdC0xLmFtYXpvbmF3cy5jb21cL3VzLWVhc3QtMV9MQTRvUGRubUUiLCJ2ZXJzaW9uIjoyLCJjbGllbnRfaWQiOiI2Y3Bic3Y3bGs3NGlvbHNkZzRiamU5NG82dSIsIm9yaWdpbl9qdGkiOiI0MzQwMGI3ZS01OGJkLTRhYTItOTNjYi0zNDFiNzIyNDgzM2EiLCJ0b2tlbl91c2UiOiJhY2Nlc3MiLCJzY29wZSI6ImF3cy5jb2duaXRvLnNpZ25pbi51c2VyLmFkbWluIG9wZW5pZCBwcm9maWxlIGVtYWlsIiwiYXV0aF90aW1lIjoxNjk4OTM5NjA3LCJleHAiOjE2OTkyODY5MDksImlhdCI6MTY5OTI4MzMwOSwianRpIjoiNWMzNDQyYWYtMTg4My00YmFhLTg1MWItMzUwOTg4OWQ5OGU1IiwidXNlcm5hbWUiOiJLb25pY2FNaW5vbHRhTWFya2V0UGxhY2VfZjRjOWIxYzNkYjhmMDkzZmQ3MmY0ZjY3ZmViZjFmOTUxYzI1ZDBjMiJ9.OGDZg9KsWW7plCdgBMA6Kh-ccI5iumI3jpMaqiWy1-doIiOgOp6B2PLogI12bDJ-ohRTn2YmMsXUAbCRNyq5DCoHMSB6OhWO_AJFw_NG-tJNlyf2nuhba6PjViMqRM9RWKbCB0uTAnr21pin1_SNuoF0z5xBLrcsnGXILMDUvSs3I3O42wWFhCfrIvWSIP5aRWggmKWbULyV5r1Q5TTGvhdTYTZtvWfByorI-viwpeJYRdnNXRc7oYv__XaLyTlhqeoSY13lTpcjZjP2BttxG82DjtXK_QrHj0wpbXpXeXPZ7PXPUiBufE3xCwl-C_ssMpXv-hHjbNUvo7WFF2wJMw',
			refreshToken:
				'eyJjdHkiOiJKV1QiLCJlbmMiOiJBMjU2R0NNIiwiYWxnIjoiUlNBLU9BRVAifQ.H66tze9sIg7KyMNJ1o3S4jTitdGIigSmBe-J4jnvnf4P3mqUfJlaaV8yR238ZEf000QvSRcntLJ8zt8uEIMZHi_drDjo-X70CyUlBKbLUo_xd6ACKGggn9tZB_nFR8ODvUKK2ehEsgxH9ecf99kyfn9rvuuytpTKPxGEn7-XAR6HoTJSL3ztTOlnffymCyNoFHmHdgXv2OSV8IOXziymtblioLf7X3sdhdJzpvjUD_mLikbY7WpG0Tj4drJFovfpRhsXC3VxNJ-J-cKsEjIpwPtb5B2WjQcEPnskyOYhWCGTrqXhMh9hw8PMsOPbgHHZV8h6k9tDfoO-ngo_VT7_EQ.nwnvxvwaiZzaYdck.CH_FSKlopdwvyxKPbM4pqyN0uFdSk4aRA16s4RKmZzGwXifjKQXJu7ykkyt0SCSAfClJZJLim9aMf5NfNf1KANlSG1W-G9wqK44oLIYLffqu1GFgxA8gUEGg2yWXcDGj0HD0SMvEA-FhfybwSysFLEtfjR6k0sQnXdecpeGt3I75mrC-DsEJ_czSwO7kBGT44WK_zRxKOQiEXkUBBajIM3a8JPaetQ0Mn3QBU3OYWvr--7a361WlKNG3CITRyah5vD0v7rdNd-2tpqFpXOSDK21GHaip6F2nAhUmxNHiR5HQacXmYGXCbYfSZyIko3yWNLbuIaCEm3TfxqpZrVZUzNOEI9UZAfyvdIin8Z93riYvxCah8Td0X-rhi4ptcMZpzz2neFJNLQA4enaY5L0TP_F_SbaHLcAy8aX-A0PEuRhzkU0YxBG_oa9fw8pnElDRt92w6v_G7o7wNniTkZFYoTsbzTV3L7esnydPTRmVLNaTPEgNQQgblDipKPvt7zJsBPQ8uf11apIRmQUmAhNhL07Afqd42OH3Co6MkuPedAHu0VFlikvvoxOBXUvMiXoq79ebUEUuzwL4PZZtyomud23nQX5V7QxpJVu7dYsz6O79zRT2RXqXkOld68YCt1Mit2SLM0RklwBcTKPUqb7DW353vRPw_584zuRWrtI1MACk4ailj3NwZOHJqMEXlxdbI08ZI9UdAwi9ViBQQITp0MVptOftd4xF4QFi2grD3YtFkKPOdieTk_oy-np41va5BxN2HyR0JWdoIKPd1ukBVwCWSEaatEAj5vtooOFyDy1FndV4IynzDh0sGAEE7-VJa7a_q4ZRMXNpzRG4BKxck3DSUEy_cdOtr8tesSb-a6-Pazy98I5jEROE9wpvY2DlrnjQgD0ZtE774baW1dhQBfTx31YMXEwPSf0ILv0JlCickT-1fiuohnUo8IkHPMmAzOSQrQGN_4U_alCovo4oN6tmmL7REm1dX3mf5DB0Xp0_dBTcV08anV7fvA2EhrQqLc65RyiMgr_78Th7ScE1p1eEG1uI977hpq0w3SmfpjeisexWqrPIX5vDtCQfNCQKFDr119djv47wsHizb2O_zc9QLw52m1Irsi-rus1hxZHme691sQWlsYHs4ZXfHv5-di-eUv0rlXlRIL5k-jJ2tGnATmD9yXT0a1psmiUWmWJr3yqkM_mUbY8E1UW0zl59ApPaGCYG1gwcaVJQpuef-idoJEcQ_j6SghvwMGPsTBfCmQkLVXwgRhs7NW_Xltp_r3eeJiQchMMpHsP7To52eouVpx_VJ4nBQfoWZq43OcnuYy3T4lEuHSXgqQjH-bNXtPRexlUs7HuNZOk.Jje-zlGyNGle3eKmHkh9mw',
			idToken: 'mock-id-token',
			expireTime: (Date.now() + 1814400000) / 1000, // set far in the future (but do not exceed `2147483647` - see https://nodejs.org/api/timers.html#settimeoutcallback-delay-args)
		},
		licensed: true,
		ippPassword: 'mockPassword',
	};
	const userRecord = {
		id: user.id,
		userName: user.username,
		status: 'ACCEPTED',
		firstName: user.firstName,
		lastName: user.lastName,
		email: user.email,
		dateAdded: new Date('01/01/2023 10:00:00 am').getTime(),
		hidden: false,
		licensed: user.licensed,
		ippNumber: '',
		badgeNumber: '',
		pinNumber: '000001',
		roles: ['role-2'],
		groupList: ['userGroup-1', '00000000-2222-2222-2222-000000000000'],
		nonMFPLicenseCapacity: 0,
		nonMFPLicenseQuantity: 0,
	};
	const tenantLicenseGroups: any[] = [
		{
			id: '111',
			name: 'University',
			parentId: 'cccc',
			parentName: 'Next one',
			managerEmails: [],
		},
		{
			id: 'aaaa',
			name: 'Government Tenants',
			parentId: 'main',
			parentName: 'Main',
			managerEmails: [],
		},
		{
			id: 'avvv',
			name: 'Federal',
			parentId: 'aaaa',
			parentName: 'TEST GROUP',
			managerEmails: [],
		},
		{
			id: 'cccc',
			name: 'Education Tenants',
			parentId: 'main',
			parentName: 'Main',
			managerEmails: [],
		},
		{
			id: 'main',
			name: 'Main',
			managerEmails: [
				{
					firstName: 'z',
					lastName: 'A',
					email: 'a@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
				{
					firstName: 'm',
					lastName: 'G',
					email: 'jalbanese@kmbs.konicaminolta.us',
					status: 'PENDING',
				},
				{
					firstName: 'Russell',
					lastName: 'Fellows',
					email: 'rfellows@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
			],
			protected: true,
		},
	];
	const licenseGroups = [
		// admin
		{
			id: 'five',
			slug: 'five-slug',
			name: 'Gravitas Group',
			catalogPublicId: PublicId.EnterpriseMonthly,
			orderType: 'PRODUCTION',
			expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
			orderNumber: '1000596878880002',
			purchaseCode: '267791325',
			plan: 'stc',
			adminData: [{ email: 'jsmith@kmbs.konicaminolta.us', status: 'PENDING' }],
			region: 'us-east-1',
			quantity: 19,
			capacity: 50,
			userQuantity: 5,
			userCapacity: 20,
			isAdmin: true,
			isManager: false,
			authMethod: 'mp',
			dateAdded: 1614320869623,
		},
		// admin
		{
			id: 'six',
			slug: 'six-slug',
			name: 'Wallhaven Associates',
			catalogPublicId: PublicId.EnterpriseMonthly,
			orderType: 'PRODUCTION',
			expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
			orderNumber: '1000596878890002',
			purchaseCode: '267791425',
			plan: 'stc',
			adminData: [],
			region: 'us-east-1',
			quantity: 19,
			capacity: 50,
			userQuantity: 16,
			userCapacity: 120,
			isAdmin: true,
			isManager: false,
			authMethod: 'mp',
			dateAdded: 1614320869633,
		},
		{
			id: 'six-two',
			slug: 'six-two-slug',
			name: '',
			catalogPublicId: PublicId.EnterpriseMonthly,
			orderType: 'PRODUCTION',
			expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
			orderNumber: '1000596878890065',
			purchaseCode: '267791425',
			plan: 'stc',
			adminData: [
				{ email: 'ksmith@kmbs.konicaminolta.us', status: 'PENDING' },
				{ email: 'mmetnetsky@kmbs.konicaminolta.us', status: 'PENDING' },
			],
			region: '',
			quantity: 19,
			capacity: 50,
			userQuantity: 16,
			userCapacity: 120,
			isAdmin: true,
			isManager: false,
			authMethod: 'mp',
			dateAdded: 1614320869833,
		},
		// manager
		// {
		// 	id: 'seven',
		// 	slug: 'seven-slug',
		// 	name: 'Milstone Financial',
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: undefined,
		// 	orderNumber: '1000596878880003',
		// 	purchaseCode: '267791326',
		// 	adminData: [
		// 		{ email: 'lsmith@kmbs.konicaminolta.us', status: 'ACCEPTED' },
		// 		{ email: 'mmetnetsky@kmbs.konicaminolta.us', status: 'ACCEPTED' }
		// 	],
		// 	region: 'us-east-1',
		// 	status: 'CONFIGURED',
		// 	quantity: 14,
		// 	capacity: 25,
		// 	userQuantity: 14,
		// 	userCapacity: 25,
		// 	isAdmin: false,
		// 	isManager: true,
		// 	authMethod: 'local',
		// 	dateAdded: 1614320839624
		// },
		// {
		// 	id: 'eight',
		// 	slug: 'eight-slug',
		// 	name: 'Horizon Digital',
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: undefined,
		// 	orderNumber: '1000596878880007',
		// 	purchaseCode: '267791497',
		// 	adminData: [
		// 		{ email: 'msmith@kmbs.konicaminolta.us', status: 'ACCEPTED' },
		// 		{ email: 'mmetnetsky@kmbs.konicaminolta.us', status: 'ACCEPTED' }
		// 	],
		// 	region: 'us-east-1',
		// 	status: 'UNCONFIGURED',
		// 	quantity: 6,
		// 	capacity: 11,
		// 	userQuantity: 6,
		// 	userCapacity: 11,
		// 	isAdmin: false,
		// 	isManager: true,
		// 	authMethod: 'mp',
		// 	dateAdded: 1475320829626
		// },
		// {
		// 	id: 'twelve',
		// 	slug: 'twelve-slug',
		// 	name: 'Sabre Consulting Group',
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: undefined,
		// 	orderNumber: '1000596878880008',
		// 	purchaseCode: '267791497',
		// 	adminData: [
		// 		{ email: 'nsmith@kmbs.konicaminolta.us', status: 'PENDING' },
		// 		{ email: 'mmetnetsky@kmbs.konicaminolta.us', status: 'PENDING' },
		// 		{ email: 'msmith@kmbs.konicaminolta.us', status: 'PENDING' }
		// 	],
		// 	region: 'us-east-1',
		// 	status: 'CONFIGURED',
		// 	quantity: 6,
		// 	capacity: 11,
		// 	userQuantity: 6,
		// 	userCapacity: 11,
		// 	isAdmin: false,
		// 	isManager: true,
		// 	authMethod: 'mp',
		// 	dateAdded: 1614320869628
		// },
		{
			id: '11111111-2222-2222-1111-111111111111',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00012904',
			status: 'UNCONFIGURED',
			purchaseCode: '3718-1b76-e138-bf1e',
			plan: 'stc',
			adminData: [
				{
					email: 'aabbott@kmbs.konicaminolta.us',
					status: 'PENDING',
				},
			],
			managerEmails: [],
			isAdmin: false, // at least one mock tenant needs this property, else crash
			dateAdded: 1671139415108,
			isManager: true,
		},
		{
			id: '4505281a-2444-547d-a25a-31c618e570fd',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013026',
			status: 'UNCONFIGURED',
			purchaseCode: '34fe-4157-2c6b-be0d',
			plan: 'stratus-enterprise',
			managerEmails: [],
			adminData: [],
			dateAdded: 1675438138272,
			isManager: true,
			isAdmin: false,
		},
		{
			id: '6745859e-8f21-525e-8c00-40b5473edc78',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013061',
			status: 'UNCONFIGURED',
			purchaseCode: '6528-3c49-aba6-bae9',
			plan: 'stc',
			managerEmails: [],
			adminData: [],
			dateAdded: 1675691857902,
			isManager: true,
			isAdmin: false,
		},
		{
			id: '7aea885e-5d71-5a55-b2b1-4ea0712640c3',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013062',
			status: 'PENDING',
			purchaseCode: 'e0af-53d6-b194-8099',
			plan: 'stc',
			managerEmails: [],
			adminData: [],
			dateAdded: 1675691848640,
			isManager: true,
			isAdmin: false,
		},
		{
			id: '813e7464-6d39-56b9-852e-0d7bc7c70f3c',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013067',
			status: 'UNCONFIGURED',
			purchaseCode: '125d-81bf-ee43-d80b',
			plan: 'stc',
			managerEmails: [],
			adminData: [],
			dateAdded: 1675713684969,
			isManager: true,
			isAdmin: false,
		},
		{
			id: '88d695c6-8896-599c-bfbd-f06764c61777',
			slug: 'test',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013065',
			status: 'UNCONFIGURED',
			purchaseCode: 'e9ad-09b9-b471-3f3b',
			plan: 'stratus-enterprise',
			region: 'us-east-1',
			managerEmails: [],
			adminData: [
				{
					email: 'vdogames@gmail.com',
					status: 'PENDING',
				},
			],
			dateAdded: 1675691821770,
			isManager: true,
			isAdmin: false,
		},
		{
			id: '8ddc5e14-644e-5903-bd79-be31ac1b6bc5',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013012',
			status: 'UNCONFIGURED',
			purchaseCode: '287e-1c5b-c9c2-94d7',
			plan: 'stc',
			managerEmails: [],
			adminData: [
				{
					email: 'pat.white@kmbs.konicaminolta.com',
					status: 'PENDING',
				},
			],
			dateAdded: 1675265652769,
			isManager: true,
			isAdmin: false,
		},

		{
			id: 'e8680b51-fd2b-5ab0-ba86-f5fcaf8b3ea7',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013066',
			status: 'UNCONFIGURED',
			purchaseCode: '8356-5e16-e9b4-217c',
			plan: 'stc',
			managerEmails: [],
			adminData: [
				{
					email: 'pat.white@kmbs.konicaminolta.us',
					status: 'PENDING',
				},
			],
			dateAdded: 1675713692022,
			isManager: true,
			isAdmin: false,
		},
		{
			id: 'f5922df9-cbc1-5383-a86a-90ae4a0fc92f',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013064',
			status: 'UNCONFIGURED',
			purchaseCode: '13a1-efc6-a28e-f90f',
			plan: 'stc',
			managerEmails: [],
			adminData: [],
			dateAdded: 1675691829486,
			isManager: true,
			isAdmin: false,
		},
		{
			id: '81111112-1112-1112-1112-111111111112',
			slug: 'jack',
			name: 'Bergen Community College',
			quantity: 5,
			capacity: 6,
			userQuantity: 5,
			userCapacity: 6,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			parent: 'cccc',
			orderType: 'PRODUCTION',
			orderNumber: 'SomeOrder200',
			status: 'CONFIGURED',
			purchaseCode: 'SomePurchase200',
			plan: 'stc',
			expirationDate: 1780876799000,
			region: 'us-east-1',
			managerEmails: [{ email: 'qa@kmbs.konicaminolta.com', status: 'ACCEPTED' }],
			adminData: [
				{
					email: 'qa@kmbs.konicaminolta.com',
					status: 'ACCEPTED',
				},
				{
					email: 'frank@ape-escape.net',
					status: 'PENDING',
				},
			],
			dateAdded: 1647107307031,
			isManager: true,
			isAdmin: false,
		},
		{
			id: '11111111-1111-1111-1111-1212121212',
			slug: 'jack',
			name: 'Rutgers',
			quantity: 5,
			capacity: 6,
			userQuantity: 5,
			userCapacity: 6,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			parent: '111',
			orderType: 'PRODUCTION',
			orderNumber: 'SomeOrder200',
			status: 'CONFIGURED',
			purchaseCode: 'SomePurchase200',
			plan: 'stc',
			expirationDate: 1780876799000,
			region: 'us-east-1',
			managerEmails: [{ email: 'qa@kmbs.konicaminolta.com', status: 'ACCEPTED' }],
			adminData: [
				{
					email: 'qa@kmbs.konicaminolta.com',
					status: 'ACCEPTED',
				},
				{
					email: 'frank@ape-escape.net',
					status: 'PENDING',
				},
			],
			dateAdded: 1647107307031,
			isManager: true,
			isAdmin: false,
		},
		{
			id: 'd254bae9-d5eb-5719-b86a-c8e72a807402',
			slug: 'vivivivi',
			name: 'Ridgewood Public Schools',
			parent: 'cccc',
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			orderType: 'PRODUCTION',
			orderNumber: 'MP00013063',
			status: 'CONFIGURED',
			expirationDate: 1780876799000,
			purchaseCode: 'f22e-b506-0f0f-db72',
			plan: 'stc',
			region: 'us-east-1',
			managerEmails: [{ email: 'qa@kmbs.konicaminolta.com', status: 'ACCEPTED' }],
			managerEvents: {
				'30days': true,
			},
			adminData: [
				{
					email: 'vdogames@gmail.com',
					status: 'ACCEPTED',
				},
			],
			dateAdded: 1686172692397,
			isManager: true,
			isAdmin: false,
			suspensions: { viewOnly: true },
		},
		{
			id: '1ea16018-ee08-5eb3-ba96-a2baa04eeb8d',
			owner: 'f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
			name: 'William Paterson',
			region: 'us-east-1',
			status: 'CONFIGURED',
			dateAdded: 1680285802159,
			slug: 'sktenant11',
			parent: '111',
			purchaseCode: 'f5e5-c055-0878-f86f',
			plan: 'stc',
			orderNumber: 'MP00013303',
			materialNumber: 'DPSAAA01',
			catalogUid: 'dps-stc-subm',
			catalogPublicId: '3348c5fd-7123-4332-abd0-4d5c644ce3a7',
			licenseName: 'Dispatcher ScanTrip Cloud',
			licenseUsage: {},
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			orderType: 'PRODUCTION',
			isExpired: false,
			managerEmails: [
				{
					email: 'qa@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
			],
			isManager: true,
			adminData: [
				{
					email: 'qa@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
			],
		},
		{
			id: '7def2712-dcc1-5ad8-be99-28e56af8eae3',
			owner: 'f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
			name: 'Montclair State',
			parent: '111',
			region: 'us-east-1',
			status: 'CONFIGURED',
			dateAdded: 1680790426016,
			slug: 'domainalreadytakenlg',
			purchaseCode: '7bba-96f5-7085-5880',
			plan: 'stc',
			orderNumber: 'MP00013320',
			materialNumber: 'DPSAAA01',
			catalogUid: 'dps-stc-subm',
			catalogPublicId: '3348c5fd-7123-4332-abd0-4d5c644ce3a7',
			licenseName: 'Dispatcher ScanTrip Cloud',
			licenseUsage: {},
			quantity: 0,
			capacity: 10,
			userQuantity: 0,
			userCapacity: 10,
			orderType: 'PRODUCTION',
			isExpired: false,
			isManager: true,
			managerEmails: [
				{
					email: 'qa@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
			],
			adminData: [
				{
					email: 'qa@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
			],
		},
		{
			id: '9f0303a4-ede5-5060-914e-6f5523da60cd',
			owner: 'f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
			name: 'Princeton',
			region: 'us-east-1',
			parent: '111',
			status: 'CONFIGURED',
			dateAdded: 1681849276370,
			slug: 'sgalvisadmin',
			purchaseCode: 'e8bb-72c0-97e8-0174',
			plan: 'stc',
			orderNumber: 'MP00013372',
			materialNumber: 'DPSAAA01',
			catalogUid: 'dps-stc-subm',
			catalogPublicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
			licenseName: 'Enterprise',
			licenseUsage: {},
			quantity: 0,
			capacity: 11,
			userQuantity: 0,
			userCapacity: 11,
			orderType: 'PRODUCTION',
			isExpired: false,
			isManager: true,
			managerEmails: [
				{
					email: 'qa@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
			],
			adminData: [
				{
					email: 'sgalvis@kmbs.konicaminolta.us',
					status: 'ACCEPTED',
				},
			],
		},
		// ===================================================================

		// {
		// 	id: 'forty-one',
		// 	slug: 'forty-one-slug',
		// 	name: 'Cosgrove Medical',
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: undefined,
		// 	orderNumber: '1000596878880008',
		// 	purchaseCode: '267791497',
		// 	adminData: [],
		// 	region: 'eu-central-1',
		// 	quantity: 6,
		// 	capacity: 11,
		// 	userQuantity: 6,
		// 	userCapacity: 11,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614110769628
		// },

		// unconfigured tenant (i.e. no `region` [also no `name` - though that's not a rule in the logic])
		// {
		// 	id: 'nine',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880004',
		// 	purchaseCode: '267791327',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 119,
		// 	capacity: 25,
		// 	userQuantity: 119,
		// 	userCapacity: 25,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614020869623
		// },
		// {
		// 	id: 'ten',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880005',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614320859627
		// },
		// {
		// 	id: 'thirteen',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880013',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1398320849627
		// },
		// {
		// 	id: 'fourteen',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880014',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1586320869627
		// },

		// {
		// 	id: 'fifteen',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880015',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614020839627
		// },
		// {
		// 	id: 'sixteen',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880016',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1514200869627
		// },
		// {
		// 	id: 'seventeen',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880017',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1601320869627
		// },
		// {
		// 	id: 'eightteen',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880018',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614320869627
		// },
		// {
		// 	id: 'nineteen',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880019',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614320869627
		// },
		// {
		// 	id: 'twenty',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880020',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614320869627
		// },
		// {
		// 	id: 'twenty-one',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880021',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614320869627
		// },
		// {
		// 	id: 'twenty-two',
		// 	slug: undefined,
		// 	name: undefined,
		// 	catalogPublicId: PublicId.EnterpriseMonthly,
		// 	orderType: 'PRODUCTION',
		// 	expirationDate: +new Date('2050-12-12T23:59:59.000Z'),
		// 	orderNumber: '1000596878880022',
		// 	purchaseCode: '267791329',
		//  plan: 'stc',
		// 	adminData: [],
		// 	region: undefined,
		// 	quantity: 11,
		// 	capacity: 24,
		// 	userQuantity: 11,
		// 	userCapacity: 24,
		// 	isAdmin: false,
		// 	isManager: true,
		// authMethod: 'mp',
		// 	dateAdded: 1614320869627
		// },
		// ========================================================
		// ========================================================
		selectedLicenseGroup,
	];
	// DEV NOTE::admined and managed licenses are returned from two different lists - and the `isAdmin` and `isManager` property don't actually exist
	const managedLicenseGroups = licenseGroups
		.filter(({ isManager }) => isManager)
		.map((licenseGroup) => _.omit(licenseGroup, ['isAdmin', 'isManager']));
	// const adminedLicenseGroups = licenseGroups
	// 	.filter(({ isAdmin }) => isAdmin)
	// 	.map(licenseGroup => _.omit(licenseGroup, ['isAdmin', 'isManager']));

	const adminedLicenseGroups = [
		{
			id: selectedLicenseGroup.id,
			name: selectedLicenseGroup.name,
			domain: `https://${selectedLicenseGroup.slug}.tenant.${getPlanDomain(selectedLicenseGroup.plan as any)}`,
		},
		{
			id: licenseGroups[1].id,
			name: licenseGroups[1].name,
			domain: `https://${licenseGroups[1].slug}.tenant.${getPlanDomain(licenseGroups[1].plan as any)}`,
		},
	];

	const users = [
		userRecord,
		{
			id: 'mock-user-id-3',
			userName: 'mock-user-id-3',
			firstName: 'John',
			lastName: 'Smith',
			email: 'jsmith@example.com',
			mpId: 'mock-user-mpId-2',
			dateAdded: 1616202447928,
			// permissions: {
			// 	device_management: true,
			// 	user_management: true,
			// 	license_management: true
			// }
			roles: ['role-1'],
			status: 'ACCEPTED',
			groupList: ['userGroup-1'],
			licensed: true,
		},
		{
			id: 'mock-user-id-5',
			userName: 'mock-user-id-5',
			firstName: 'Katherine',
			lastName: 'Pryde',
			email: 'kpryde@example.com',
			mpId: 'mock-user-mpId-3',
			dateAdded: 1616202447928,
			// permissions: {
			// 	device_management: true
			// }
			roles: ['role-2'],
			status: 'ACCEPTED',
			groupList: ['userGroup-1', '00000000-1111-1111-1111-000000000000'],
			licensed: true,
		},
		{
			id: 'mock-user-id-11',
			userName: 'mock-user-id-11',
			firstName: 'Hank',
			lastName: 'McCoy',
			email: 'hmccoy@example.com',
			mpId: 'mock-user-mpId-4',
			dateAdded: 1616202447928,
			// permissions: {
			// 	user_management: true
			// }
			roles: ['role-3', 'role-2'],
			status: 'ACCEPTED',
			groupList: ['userGroup-1', '00000000-2222-2222-2222-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-user-id-13',
			userName: 'mock-user-id-13',
			firstName: undefined,
			lastName: undefined,
			username: 'ebrooks',
			email: 'ebrooks@example.com',
			mpId: 'mock-user-mpId-5',
			dateAdded: 1616202447928,
			// permissions: {
			// 	license_management: true
			// }
			roles: ['role-2'],
			status: 'ACCEPTED',
			groupList: ['userGroup-2', '00000000-2222-2222-2222-000000000000'],
			licensed: true,
		},
		{
			id: 'mock-user-id-19',
			userName: 'mock-user-id-19',
			firstName: 'Reed',
			lastName: 'Richards',
			email: 'rrichards@example.com',
			mpId: 'mock-user-mpId-6',
			dateAdded: 1616202447928,
			roles: ['role-2'],
			blocked: true,
			status: 'ACCEPTED',
			groupList: ['userGroup-2', '00000000-1111-1111-1111-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-user-id-21',
			userName: 'mock-user-id-21',
			firstName: 'Sean',
			lastName: 'Sullivan',
			email: 'ssullivan@example.com',
			mpId: 'mock-user-mpId-7',
			dateAdded: 1616202447928,
			roles: ['role-2'],
			status: 'ACCEPTED',
			groupList: ['userGroup-2', '00000000-1111-1111-1111-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-user-id-23',
			userName: 'mock-user-id-23',
			firstName: 'Tabrej',
			lastName: 'Chowdhury',
			email: 'tchowdhury@example.com',
			badgeNumber: '123456879',
			mpId: 'mock-user-mpId-8',
			dateAdded: 1616202447928,
			roles: ['role-2'],
			status: 'ACCEPTED',
			groupList: ['00000000-1111-1111-1111-000000000000', '00000000-3333-3333-3333-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-user-id-25',
			userName: 'mock-user-id-25',
			firstName: 'Pavani',
			lastName: 'Ennamaneni',
			email: 'pennamaneni@example.com',
			mpId: 'mock-user-mpId-9',
			dateAdded: 1615202447928,
			roles: ['role-2'],
			status: 'BLOCKED',
			groupList: ['00000000-1111-1111-1111-000000000000', '00000000-3333-3333-3333-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-user-id-27',
			userName: 'mock-user-id-27',
			firstName: 'William',
			lastName: 'Smith',
			email: 'wsmith@example.com',
			mpId: 'mock-user-mpId-10',
			dateAdded: 1615202447928,
			roles: ['role-2'],
			status: 'ACCEPTED',
			groupList: ['00000000-1111-1111-1111-000000000000', '00000000-3333-3333-3333-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-user-id-29',
			userName: 'mock-user-id-29',
			firstName: 'Sue',
			lastName: 'Johnson',
			email: 'sjohnson@example.com',
			mpId: 'mock-user-mpId-11',
			dateAdded: 1615002447928,
			roles: ['role-2'],
			status: 'ACCEPTED',
			groupList: ['00000000-1111-1111-1111-000000000000', '00000000-3333-3333-3333-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-user-id-30',
			userName: 'mock-user-id-30',
			firstName: 'Rob',
			lastName: 'Guest',
			email: 'rguest@example.com',
			mpId: 'mock-user-mpId-12',
			dateAdded: new Date('05/28/2025 7:38:30 AM').getTime(),
			roles: [],
			status: 'ACCEPTED',
			groupList: [],
			licensed: false,
			hidden: true,
			expireDate: Math.floor((new Date().getTime() + 2.4 * (24 * 60 * 60 * 1000)) / 1000),
		},
	];
	const pendingUsers = [
		{
			id: 'mock-pending-user-1',
			email: 'jfrost@example.com',
			dateAdded: 1611202447928,
			token: 'f024244021',
			roles: ['role-1'],
			status: 'PENDING',
			userGroupId: ['userGroup-1', '00000000-1111-1111-1111-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-pending-user-3',
			email: 'zjohnson@example.com',
			dateAdded: 1616202447528,
			token: 'f024244027',
			roles: ['role-2'],
			status: 'PENDING',
			userGroupId: ['00000000-1111-1111-1111-000000000000', '00000000-3333-3333-3333-000000000000'],
			licensed: false,
		},
		{
			id: 'mock-pending-user-4',
			email: 'jguest@example.com',
			dateAdded: new Date('05/27/2025 11:22:30 AM').getTime(),
			token: 'f024244028',
			roles: [],
			status: 'PENDING',
			userGroupId: [],
			licensed: false,
			hidden: true,
			expireDate: Math.floor((new Date().getTime() + 4.8 * (24 * 60 * 60 * 1000)) / 1000),
		},
	];
	const userGroups = [
		// uncomment user group 0 for a lengthy group title that falls to the next line
		// {
		// 	id: 'userGroup-0',
		// 	name: 'My name is Ozymandias, king of kings!  Look upon my works, ye mighty, and despair!',
		// 	updatedAt: 1619483850584
		// },
		// default ungrouped, user, and admin groups
		{
			id: '00000000-3333-3333-3333-000000000000',
			name: 'Default Group',
			updatedAt: 1619483850584,
		},
		{
			id: '00000000-1111-1111-1111-000000000000',
			name: 'Users Group',
			updatedAt: 1619483850584,
		},
		{
			id: '00000000-2222-2222-2222-000000000000',
			name: 'Administrators Group',
			updatedAt: 1619483850584,
		},
		{
			id: 'userGroup-1',
			name: 'Exec Group',
			updatedAt: 1619483870584,
			roles: ['role-1'],
			defaultUser: 'mock-user-id',
		},
		{
			id: 'userGroup-2',
			name: 'Marketing Group',
			updatedAt: 1619483850584,
			roles: [],
		},
		{
			id: 'userGroup-3',
			name: 'Admin Group',
			updatedAt: 1619483850584,
			roles: [],
		},
	];
	// uncomment the call below to mimic hundreds of users
	// addHundredsOfUsers();
	const bsn = [
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000011',
			status: 'unknown',
			// attributes: [],
			// updatedAt: new Date('April 8, 2025').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000009',
			status: 'unsecured',
			// attributes: [],
			updatedAt: new Date('June 6, 1944').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
		{
			serial: 'DA61F011000003',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('August 14, 1980').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000005',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('April 19, 1775').getTime(),
			// policyId: '',
		},
		{
			serial: 'A61F011000007',
			status: 'secure',
			// attributes: [],
			updatedAt: new Date('May 5, 1863').getTime(),
			// policyId: '',
		},
	];
	const devices = [
		{
			id: 'DA61F011000003',
			name: 'First Floor - Lobby',
			model: '287',
			location: 'First Floor - Lobby',
			stcApp: true,
			ip: '888.888.888.888',
			serial: 'A61F011000003',
			description: 'First Floor - Lobby: Description',
			licensed: true,
			r2mRegistered: true,
			groupList: ['00000000-4444-4444-4444-000000000000'],
		},
		{
			id: 'A61F011000005',
			name: 'Second Floor - Lobby',
			model: '364e',
			location: 'Second Floor - Lobby',
			stcApp: true,
			ip: '12.34.56.80',
			serial: 'A61F011000005',
			description: 'Second Floor - Lobby Description',
			licensed: true,
			mfpAuthStatus: {
				online: true,
				isRegistered: false,
				isPending: false,
				isFailed: false,
				isSameTenant: false,
			},
			r2mRegistered: true,
			groupList: ['00000000-4444-4444-4444-000000000000'],
		},
		{
			id: 'A61F011000007',
			name: 'First Floor - Lab',
			model: '364e',
			location: 'First Floor - Lab',
			stcApp: true,
			ip: '12.34.56.81',
			serial: 'A61F011000007',
			description: 'First Floor - Lab Description',
			licensed: true,
			mfpAuthStatus: {
				online: true,
				isRegistered: true,
				isPending: true,
				isFailed: true,
				isSameTenant: true,
			},
			r2mRegistered: false,
			groupList: ['deviceGroup-1'],
		},
		{
			id: 'A61F011000009',
			name: 'Second Floor - Lab',
			model: '4052',
			location: 'Second Floor - Lab',
			stcApp: true,
			ip: '12.34.56.82',
			serialNumber: 'A61F011000009',
			description: 'Second Floor - Lab Description Test Extra Long Description',
			licensed: false,
			mfpAuthStatus: {
				online: true,
				isRegistered: false,
				isPending: false,
				isFailed: false,
				isSameTenant: false,
			},
			r2mRegistered: false,
			groupList: ['00000000-4444-4444-4444-000000000000'],
		},
		{
			id: 'A61F011000011',
			name: 'Third Floor - Lab',
			model: '4750',
			location: 'Third Floor - Lab',
			stcApp: true,
			ip: '12.34.56.83',
			serial: 'A61F011000011',
			description: 'Third Floor - Lab Description',
			mfpAuthStatus: {
				online: true,
				isRegistered: false,
				isPending: false,
				isFailed: true,
				isSameTenant: false,
			},
			licensed: true,
			r2mRegistered: true,
			groupList: ['00000000-4444-4444-4444-000000000000'],
		},
		{
			id: 'A61F011000013',
			name: 'Warehouse',
			model: 'C287',
			location: 'Warehouse',
			stcApp: true,
			ip: '12.34.56.84',
			serial: 'A61F011000013',
			description: 'Warehouse Description',
			mfpAuthStatus: {
				online: true,
				isRegistered: false,
				isPending: true,
				isFailed: false,
				isSameTenant: false,
			},
			licensed: true,
			r2mRegistered: true,
			groupList: ['00000000-4444-4444-4444-000000000000'],
		},
		{
			id: 'A61F011000015',
			name: 'Third Floor - Lobby',
			model: 'C287',
			location: 'Third Floor - Lobby',
			stcApp: true,
			ip: '12.34.56.85',
			serial: 'A61F011000015',
			description: 'Third Floor - Lobby Description',
			mfpAuthStatus: {
				online: true,
				isRegistered: false,
				isPending: true,
				isFailed: false,
				isSameTenant: false,
			},
			licensed: false,
			r2mRegistered: false,
			groupList: ['deviceGroup-1'],
		},
		{
			id: '519FF9AC-1AAA-41D4-B2BF-CD3AEFF0A2EA',
			name: 'Fourth Floor - Lobby',
			model: 'C300i',
			location: 'Fourth Floor - Lobby',
			stcApp: false,
			ip: '12.34.56.86',
			serial: '519FF9AC-1AAA-41D4-B2BF-CD3AEFF0A2EA',
			description: 'Fourth Floor - Lobby Description',
			licensed: true,
			r2mRegistered: false,
			groupList: ['deviceGroup-1', 'deviceGroup-2'],
			variant: 'external-mfp',
			directPrintAgentId: 'DPA-agent-001',
			printProtocol: 'IPP',
			lprPort: 515,
			lprQueueName: 'mock-raw',
			lprHostName: 'mock-client-host',
			lprUserName: 'mock-r2meDirect',
			rawPort: 9100,
			supportsPjlHeaders: false,
		},
		{
			id: 'A61F011000234',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000235',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000236',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000237',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000238',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000239',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000240',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000242',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000243',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000244',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000245',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000246',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000247',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000241',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000248',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000249',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000250',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000251',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011000619',
			name: 'Fourth Floor - Lab',
			model: 'C666',
			location: 'Fourth Floor - Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description:
				'0987h1234f0987h1234f0987h1234f987h1234f09871h234f0987h1234f0987h1234f0987h1234f0987h1234f-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			groupList: ['deviceGroup-2', 'deviceGroup-3'],
			directPrintAgentId: 'DPA-agent-002',
			printProtocol: 'LPR',
			lprPort: 515,
			lprQueueName: 'mock-print-queue',
			lprHostName: 'mock-printer-host',
			lprUserName: 'mock-printuser',
			rawPort: 9100,
			supportsPjlHeaders: true,
		},
		{
			id: 'A61F011001234',
			name: 'Lab',
			model: 'C666',
			location: 'Lab',
			stcApp: true,
			ip: '12.34.56.87',
			serial: 'A61F011000619',
			description: '1234-098j1234fj',
			licensed: true,
			r2mRegistered: true,
			mfpAuthStatus: {
				online: true,
				isRegistered: false,
				isPending: false,
				isFailed: false,
				isSameTenant: false,
				metrics: {
					input_trays: [
						{
							id: 'Manual',
							kind: 'MultiManual',
							name: 'MultiBypassTray',
							capacity: '150',
							level: 'Ready',
							paper: {
								auto: false,
								name: 'A4',
								code: 'A4',
								dim: {
									x: '297.0000',
									y: '210.0000',
								},
								dir: 'LEF',
								media: {
									kind: 'PlainPaper',
									auto_detect: false,
									pending_status: true,
								},
								wide: false,
								eco: false,
								backside: false,
							},
						},
						{
							id: 'Tray1',
							kind: 'MultiPurposeTray',
							name: 'Tray1(MPC)',
							capacity: '500',
							level: 'Ready',
							paper: {
								auto: false,
								name: '8_1/2x11',
								code: 'In8_1_2x11',
								dim: {
									x: '215.9000',
									y: '279.4000',
								},
								dir: 'SEF',
								media: {
									kind: 'PlainPaper',
									auto_detect: false,
									pending_status: true,
								},
								wide: false,
								eco: false,
								backside: false,
							},
						},
						{
							id: 'Tray2',
							kind: 'MultiPurposeTray',
							name: 'Tray2',
							capacity: '500',
							level: 'Ready',
							paper: {
								auto: false,
								name: '8_1/2x14',
								code: 'In8_1_2x14',
								dim: {
									x: '215.9000',
									y: '355.6000',
								},
								dir: 'SEF',
								media: {
									kind: 'PlainPaper',
									auto_detect: false,
									pending_status: true,
								},
								wide: false,
								eco: false,
								backside: false,
							},
						},
					],
					exit_trays: [
						{
							id: 'MainTray',
							kind: 'StandardBin',
							status: 'Ready',
							capacity: '250',
						},
					],
					supplies: [
						{
							name: 'Toner (Yellow)',
							level: {
								state: 'Ready',
								percent: '91',
							},
							kind: 'Toner',
							color: 'Yellow',
						},
						{
							name: 'Toner (Magenta)',
							level: {
								state: 'Ready',
								percent: '93',
							},
							kind: 'Toner',
							color: 'Magenta',
						},
						{
							name: 'Toner (Cyan)',
							level: {
								state: 'Ready',
								percent: '92',
							},
							kind: 'Toner',
							color: 'Cyan',
						},
						{
							name: 'Toner (Black)',
							level: {
								state: 'Enough',
								percent: '68',
							},
							kind: 'Toner',
							color: 'Black',
						},
					],
					counters: {
						'copy::BwTotal': 1150000,
						'copy::FullColorTotal': 44,
						'print::BwTotal': 265,
						'print::FullColorTotal': 936,
						'scan_fax::ScanPrintBwTotal': 0,
						'scan_fax::ScanSendBWTotal': 763,
						'scan_fax::ScanPrintColorTotal': 0,
						'scan_fax::ScanSendColorTotal': 699,
					},
					lastActivity: 1765916606,
				},
			},
			groupList: ['deviceGroup-2'],
		},
		{
			id: 'A61F011000621',
			name: 'Fifth Floor - Lab',
			model: 'C666',
			location: 'Fifth Floor - Lab',
			stcApp: false,
			BSNApp: true,
			ip: '12.34.56.88',
			serial: 'A61F011000621',
			description: 'Fifth Floor - Lab Description',
			licensed: true,
			r2mRegistered: false,
			mfpAuthStatus: {
				online: true,
				isRegistered: false,
				isPending: false,
				isFailed: false,
				isSameTenant: false,
				metrics: {
					input_trays: [
						{
							id: 'Manual',
							kind: 'MultiManual',
							name: 'MultiBypassTray',
							capacity: '150',
							level: 'Ready',
							paper: {
								auto: false,
								name: 'A4',
								code: 'A4',
								dim: {
									x: '297.0000',
									y: '210.0000',
								},
								dir: 'LEF',
								media: {
									kind: 'PlainPaper',
									auto_detect: false,
									pending_status: true,
								},
								wide: false,
								eco: false,
								backside: false,
							},
						},
						{
							id: 'Tray1',
							kind: 'MultiPurposeTray',
							name: 'Tray1(MPC)',
							capacity: '500',
							level: 'Ready',
							paper: {
								auto: false,
								name: '8_1/2x11',
								code: 'In8_1_2x11',
								dim: {
									x: '215.9000',
									y: '279.4000',
								},
								dir: 'SEF',
								media: {
									kind: 'PlainPaper',
									auto_detect: false,
									pending_status: true,
								},
								wide: false,
								eco: false,
								backside: false,
							},
						},
						{
							id: 'Tray2',
							kind: 'MultiPurposeTray',
							name: 'Tray2',
							capacity: '500',
							level: 'Ready',
							paper: {
								auto: false,
								name: '8_1/2x14',
								code: 'In8_1_2x14',
								dim: {
									x: '215.9000',
									y: '355.6000',
								},
								dir: 'SEF',
								media: {
									kind: 'PlainPaper',
									auto_detect: false,
									pending_status: true,
								},
								wide: false,
								eco: false,
								backside: false,
							},
						},
					],
					exit_trays: [
						{
							id: 'MainTray',
							kind: 'StandardBin',
							status: 'Ready',
							capacity: '250',
						},
					],
					supplies: [
						{
							name: 'Toner (Yellow)',
							level: {
								state: 'Ready',
								percent: '91',
							},
							kind: 'Toner',
							color: 'Yellow',
						},
						{
							name: 'Toner (Magenta)',
							level: {
								state: 'Ready',
								percent: '93',
							},
							kind: 'Toner',
							color: 'Magenta',
						},
						{
							name: 'Toner (Cyan)',
							level: {
								state: 'Ready',
								percent: '92',
							},
							kind: 'Toner',
							color: 'Cyan',
						},
						{
							name: 'Toner (Black)',
							level: {
								state: 'Enough',
								percent: '68',
							},
							kind: 'Toner',
							color: 'Black',
						},
					],
					counters: {
						'copy::BwTotal': 115000,
						'copy::FullColorTotal': 44,
						'print::BwTotal': 265,
						'print::FullColorTotal': 936,
						'scan_fax::ScanPrintBwTotal': 0,
						'scan_fax::ScanSendBWTotal': 763,
						'scan_fax::ScanPrintColorTotal': 0,
						'scan_fax::ScanSendColorTotal': 699,
					},
					lastActivity: 1765916606,
				},
			},
			groupList: [
				'deviceGroup-1',
				'deviceGroup-2',
				'deviceGroup-3',
				'deviceGroup-4',
				'deviceGroup-5',
				'deviceGroup-6',
				'deviceGroup-7',
				'deviceGroup-8',
				'deviceGroup-9',
			],
			universalPrintRegistrationStatus: 'PENDING',
		},
	];
	const deviceGroups = [
		{
			id: '00000000-4444-4444-4444-000000000000',
			name: '00000000-4444-4444-4444-000000000000',
			dateUpdated: 1619483870584,
		},
		{
			id: 'deviceGroup-1',
			name: 'HQ Building 101',
		},
		{
			id: 'deviceGroup-2',
			name: 'HQ Building     201',
			dateUpdated: 1619483850584,
		},
		{
			id: 'deviceGroup-3',
			name: 'HQ Building 301',
			dateUpdated: 1619483860584,
		},
		{
			id: 'deviceGroup-4',
			name: 'HQ Building 401',
			dateUpdated: 1619483860584,
		},
		{
			id: 'deviceGroup-5',
			name: 'HQ Building 501',
			dateUpdated: 1619483860584,
		},
		{
			id: 'deviceGroup-6',
			name: 'HQ Building 601',
			dateUpdated: 1619483860584,
		},
		{
			id: 'deviceGroup-7',
			name: 'HQ Building 701',
			dateUpdated: 1619483860584,
		},
		{
			id: 'deviceGroup-8',
			name: 'HQ Building 801',
			dateUpdated: 1619483860587,
		},
		{
			id: 'deviceGroup-9',
			name: 'HQ Building 901',
			dateUpdated: 1619483860584,
		},
		{
			id: 'deviceGroup-10',
			name: 'HQ Building 1001',
			dateUpdated: 1619483860584,
		},
		{
			id: 'deviceGroup-11',
			name: 'Super Long Name that should wrap. aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa',
			dateUpdated: 1619483860584,
		},
	];
	// const notificationGroups = [
	// 	{
	// 		id: UNGROUPED_NOTIFICATION_GROUP_ID,
	// 		name: ''
	// 	},
	// 	{
	// 		id: 'notificationGroup-1',
	// 		name: 'Notification HQ Building 101'
	// 	},
	// 	{
	// 		id: 'notificationGroup-2',
	// 		name: 'Notification HQ Building     201'
	// 	},
	// 	{
	// 		id: 'notificationGroup-3',
	// 		name: 'Notification HQ Building 301'
	// 	},
	// 	{
	// 		id: 'notificationGroup-4',
	// 		name: 'Notification HQ Building 401'
	// 	},
	// 	{
	// 		id: 'notificationGroup-5',
	// 		name:
	// 			'Notification Super Long Name that should wrap. aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa'
	// 	}
	// ];
	const notifications = [
		{
			id: 'mock-notification-id-1',
			title: 'Enrollment to BiBop is required',
			message: 'This is a reminder that enrollment to BiBop is required by the end of the calendar year.',
			userGroupIds: ['userGroup-1', 'userGroup-2'],
			startDate: new Date('10/02/2024 5:00:00 pm').getTime(),
			endDate: new Date('11/20/2040 5:00:00 pm').getTime(),
			createdBy: user.id,
			updatedBy: user.id,
			color: 'pink',
			icon: 'celebration',
			dateCreated: new Date('10/01/2024 5:00:00 pm').getTime(),
			dateUpdated: new Date('10/01/2024 5:00:00 pm').getTime(),
		},
		{
			id: 'mock-notification-id-2',
			title: 'Training Reminder',
			message: 'This is a reminder that mandatory skills training must be completed by October 1, 2024.',
			userGroupIds: ['userGroup-1'],
			startDate: new Date('10/06/2024 9:00:00 am').getTime(),
			endDate: new Date('10/30/2040 9:00:00 am').getTime(),
			createdBy: user.id,
			updatedBy: 'mock-user-id-3',
			color: 'blue',
			icon: 'info',
			dateCreated: new Date('10/05/2024 9:00:00 am').getTime(),
			dateUpdated: new Date('10/05/2024 10:00:00 am').getTime(),
		},
		{
			id: 'mock-notification-id-3',
			title: 'Enrollment to Multi-Factor Authentication is required',
			message:
				'This is a reminder that enrollment to Multi-Factor Authentication is required by the end of the fiscal year.',
			userGroupIds: [ALL_USER_GROUP.id],
			startDate: new Date('10/10/2024 10:00:00 am').getTime(),
			endDate: new Date('12/30/2040 10:00:00 am').getTime(),
			createdBy: 'mock-user-id-3',
			updatedBy: 'mock-user-id-3',
			color: 'purple',
			icon: 'satisfied',
			dateCreated: new Date('10/09/2024 10:00:00 am').getTime(),
			dateUpdated: new Date('10/09/2024 11:00:00 am').getTime(),
		},
		{
			id: 'mock-notification-id-4',
			title: 'Enrollment to Snap is required',
			message: 'This is a reminder that enrollment to Snap is required by the end of the calendar year.',
			userGroupIds: ['userGroup-1', 'userGroup-2'],
			startDate: new Date('10/12/2035 4:00:00 pm').getTime(),
			endDate: new Date('10/30/2045 4:00:00 pm').getTime(),
			createdBy: 'mock-user-id-5',
			updatedBy: user.id,
			color: 'orange',
			icon: 'calendar',
			dateCreated: new Date('10/11/2035 4:00:00 pm').getTime(),
			dateUpdated: new Date('10/12/2035 4:00:00 pm').getTime(),
		},
		{
			id: 'mock-notification-id-5',
			title: 'Enrollment to BizBuz is required',
			message: 'Quick reminder that enrollment to BizBuz is required by the end of the calendar year.',
			userGroupIds: ['userGroup-2'],
			startDate: new Date('10/16/2024 11:00:00 am').getTime(),
			endDate: new Date('10/17/2024 11:00:00 am').getTime(),
			createdBy: 'mock-user-id-5',
			updatedBy: 'mock-user-id-5',
			color: 'red',
			icon: 'campaign',
			dateCreated: new Date('10/15/2024 11:00:00 am').getTime(),
			dateUpdated: new Date('10/15/2024 11:00:00 am').getTime(),
		},
	];
	const appGroups = [
		{
			id: UNGROUPED_APP_GROUP_ID,
			name: '',
		},
		{
			id: 'appGroup-1',
			name: 'App HQ Building 101',
		},
		{
			id: 'appGroup-2',
			name: 'App HQ Building     201',
		},
		{
			id: 'appGroup-3',
			name: 'App HQ Building 301',
		},
		{
			id: 'appGroup-4',
			name: 'App HQ Building 401',
		},
		{
			id: 'appGroup-5',
			name: 'App Super Long Name that should wrap. aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa',
		},
	];
	const apps = [
		{
			id: 'mock-app-id-1',
			name: 'Tickets (Internal) Form',
			description:
				'Create and manage tickets. Create and manage tickets. Create and manage tickets. Create and manage tickets. Lorem Ipsum is simply dummy text of the printing and typesetting industry. This app description is way too long.',
			icon: 'https://files.bizhub.sh/9d5ee41f343767d9211ec18c2c1a9a80961d1bb4',
			type: 'internal',
			workflowId: 'mock-workflow-id-1',
			nodeId: 'mock-node-id-1',
			licensed: true,
			groupList: ['appGroup-1'],
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [
					{ name: 'userGroup-1', role: 'edit' },
					{ name: 'userGroup-2', role: 'view' },
				],
			},
			aclRole: 'owner',
		},
		{
			id: 'mock-app-id-2',
			name: 'Service Form For Calculating Total Time Spent to Launch',
			description: 'Create and manage services.',
			url: 'https://example.com/temp',
			icon: 'https://files.bizhub.sh/f15037a4b7f1522071cda7f0ec10fecc9a847c6c',
			type: 'service',
			licensed: true,
			groupList: ['appGroup-1'],
			acl: {
				users: [
					{ name: 'mock-user-id-3', role: 'owner' },
					{ name: user.id, role: 'view' },
					{ name: 'mock-user-id-5', role: 'edit' },
					{ name: 'mock-user-id-11', role: 'view' },
				],
				groups: [],
			},
			aclRole: 'view',
		},
		{
			id: 'mock-app-id-3',
			name: 'Calendar (External) Form',
			description:
				'Create and manage calendars. Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
			icon: 'https://files.bizhub.sh/6d47ba9d245cce0d82f428e75f5c05e9ab9d8805',
			type: 'external',
			workflowId: 'mock-workflow-id-1',
			nodeId: 'mock-node-id-4',
			licensed: true,
			groupList: ['appGroup-1'],
			acl: {
				users: [
					{ name: 'mock-user-id-11', role: 'owner' },
					{ name: user.id, role: 'edit' },
					{ name: 'mock-user-id-5', role: 'edit' },
					{ name: 'mock-user-id-3', role: 'view' },
				],
				groups: [],
			},
			aclRole: 'edit',
		},
		{
			id: 'mock-app-id-4',
			name: 'Contacts (External [URL]) Form',
			description:
				'Create and manage contacts. Create and manage contacts. Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
			url: 'https://example.com/mockexternalformurl',
			icon: 'https://files.bizhub.sh/c121946853077d1faaebe90b08161f1c56a658e0',
			type: 'external',
			workflowId: 'mock-workflow-id-1',
			nodeId: 'mock-node-id-4',
			licensed: true,
			groupList: ['appGroup-1'],
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
			aclRole: 'owner',
		},
		{
			id: 'mock-app-id-5',
			name: 'Notes Form',
			description:
				'Create and manage notes. Create and manage notes. Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
			url: 'https://example.com/temp',
			icon: 'https://files.bizhub.sh/bc67f77fe921cee17c5e2907f15aa1a07ff60ea3',
			type: 'url',
			licensed: true,
			groupList: ['appGroup-2'],
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
			aclRole: 'owner',
		},
		{
			id: 'mock-app-id-6',
			name: 'Tasks Form',
			description:
				'Create and manage tasks. Create and manage tasks. Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
			url: 'https://example.com/temp',
			icon: 'https://files.bizhub.sh/e62bae1d966671d55d83fb8b0b20bbb0004588bb',
			type: 'url',
			licensed: true,
			groupList: ['appGroup-2'],
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
			aclRole: 'owner',
		},
		{
			id: 'mock-app-id-7',
			name: 'Email Form',
			description:
				'Create and manage emails. Create and manage emails. Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
			url: 'https://example.com/temp',
			icon: 'https://files.bizhub.sh/01fa6c06480fc0ed6ab7627711c18a25e5bd5fac',
			type: 'url',
			licensed: false,
			groupList: ['appGroup-2'],
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
			aclRole: 'owner',
		},
		{
			id: 'mock-app-id-8',
			name: 'Documents Form',
			description:
				'Create and manage documents. Create and manage documents. Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
			url: 'https://example.com/temp',
			icon: 'https://files.bizhub.sh/86a6c1724111d6b6230f485adc49f422f2d38ee6',
			type: 'url',
			licensed: true,
			groupList: undefined,
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
			aclRole: 'owner',
		},
		{
			id: 'mock-app-id-9',
			name: 'Photos Form',
			description:
				'Create and manage photos. Create and manage photos. Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
			url: 'https://example.com/temp',
			icon: 'https://files.bizhub.sh/9e7833cbc648be4fb14a6facfcf3c8fe845e7c11',
			type: 'url',
			licensed: false,
			groupList: undefined,
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
			aclRole: 'owner',
		},
		{
			id: 'mock-app-id-10',
			name: 'Paperclip (Internal) Form',
			description:
				'Create and manage paperclipss. This workflow may not be found. Create and manage paperclips. Create and manage paperclips. Create and manage paperclips. Lorem Ipsum is simply dummy text of the printing and typesetting industry. This app description is way too long.',
			icon: 'https://files.bizhub.sh/9d5ee41f343767d9211ec18c2c1a9a80961d1bb4',
			type: 'internal',
			workflowId: 'mock-workflow-id-unknown',
			nodeId: 'mock-node-id-1',
			licensed: true,
			groupList: undefined,
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [
					{ name: 'userGroup-1', role: 'edit' },
					{ name: 'userGroup-2', role: 'view' },
				],
			},
			aclRole: 'owner',
		},
	];

	const printPreferences: PrintPreferences = {
		...mockPrintPreferences,
		staple: 'ANY2POINT',
		binding_editable: false,
		headers: [
			{ name: 'mock name 1', value: 'mock value 1' },
			{ name: 'mock name 3', value: 'mock value 3' },
			{ name: 'mock name 4', value: 'mock value 4' },
		],
	};

	const userPrintPreferences: PrintPreferences = {
		...printPreferences,
		layout: 'DUPLEX',
		offset_editable: false,
		headers: [
			{ name: 'mock name 6', value: 'mock value 6' },
			{ name: 'mock name 7', value: 'mock value 7' },
			{ name: 'mock name 8', value: 'mock value 8' },
			{ name: 'mock name 9', value: 'mock value 9' },
		],
	};

	const tenantPrintPreferences: PrintPreferences = {
		...mockPrintPreferences,
		offset: 'False',
		collate_editable: false,
		headers: [
			{ name: 'mock-BINDING', value: 'mock-LONGEDGE' },
			{ name: 'mock-MEDIATYPE', value: 'mock-PLAIN' },
			{ name: 'mock-MEDIASOURCE', value: 'mock-TRAY4' },
		],
		systemPrintQueue: {
			useStratusDefault: true,
			blockedGuestEmails: ['blocked.guest@example.com'],
		},
	};

	const printGroups = [
		{
			id: 'mock-printGroup-ungrouped',
			slug: 'mock-pg-slug-ung',
			name: 'My Print Queue',
			type: 'personal',
			active: true,
			secure: false,
			usedCount: 99,
			dateCreated: new Date('05/20/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/20/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {
				...printPreferences,
			},
			userPreferences: {
				...userPrintPreferences,
			},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-printGroup-1',
			slug: 'mock-pg-slug-1',
			name: 'PrintGroup 1',
			type: 'shared',
			active: true,
			secure: false,
			usedCount: 99,
			dateCreated: new Date('05/21/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/21/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {
				...printPreferences,
			},
			userPreferences: {
				...userPrintPreferences,
			},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [
					{ name: 'userGroup-1', role: 'edit' },
					{ name: 'userGroup-2', role: 'view' },
				],
			},
		},
		{
			id: 'mock-printGroup-2',
			slug: 'mock-pg-slug-2',
			name: 'PrintGroup 2',
			type: 'shared',
			active: false,
			secure: false,
			usedCount: 99,
			dateCreated: new Date('05/22/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/22/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {
				...printPreferences,
			},
			userPreferences: {
				...userPrintPreferences,
			},
			acl: {
				users: [
					{ name: user.id, role: 'owner' },
					{ name: 'mock-user-id-5', role: 'edit' },
					{ name: 'mock-user-id-11', role: 'view' },
				],
				groups: [],
			},
		},
		{
			id: 'mock-printGroup-3',
			slug: 'mock-pg-slug-3',
			name: 'PrintGroup 3',
			type: 'guest',
			active: true,
			secure: true,
			usedCount: 99,
			dateCreated: new Date('05/23/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/23/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {
				...printPreferences,
				guestDuration: 5 * 24 * 60 * 60,
				printJobExpirationDuration: 5 * 24 * 60 * 60,
			},
			userPreferences: {
				...userPrintPreferences,
			},
			acl: {
				users: [
					{ name: user.id, role: 'owner' },
					{ name: 'mock-user-id-30', role: 'view' },
					{ name: 'mock-pending-user-4', role: 'view' },
				],
				groups: [],
			},
		},
		{
			id: 'mock-printGroup-4',
			slug: 'mock-pg-slug-4',
			name: 'PrintGroup 4',
			type: 'guest',
			active: true,
			secure: true,
			usedCount: 99,
			dateCreated: new Date('05/24/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/24/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {
				...printPreferences,
			},
			userPreferences: {
				...userPrintPreferences,
			},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-printGroup-5',
			slug: 'mock-pg-slug-5',
			name: 'PrintGroup 5',
			type: 'direct',
			deviceId: '519FF9AC-1AAA-41D4-B2BF-CD3AEFF0A2EA',
			active: true,
			secure: true,
			usedCount: 99,
			dateCreated: new Date('05/25/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/25/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {
				...printPreferences,
			},
			userPreferences: {
				...userPrintPreferences,
			},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-printGroup-universal',
			slug: 'mock-pg-slug-universal',
			name: 'MS Universal Print Queue',
			type: 'msUniversal',
			active: true,
			secure: false,
			usedCount: 2,
			dateCreated: new Date('05/26/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/26/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {},
			userPreferences: {},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-printGroup-system',
			slug: 'mock-pg-slug-system',
			name: 'System Print Queue',
			type: 'system',
			active: true,
			secure: false,
			usedCount: 2,
			dateCreated: new Date('05/26/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/26/2025 5:00:00 pm').getTime(),
			expireDate: undefined,
			preferences: {
				...printPreferences,
			},
			userPreferences: {
				...userPrintPreferences,
			},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
	];
	const prints = [
		{
			id: 'mock-print-id-1',
			name: 'Document Name 1.pdf',
			pageCount: 3,
			fileId: 'mock-print-file-id-1',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-ungrouped',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-2',
			name: 'Document Name 2.pdf',
			url: 'https://example.com/temp',
			pageCount: 3,
			fileId: 'mock-print-file-id-2',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-ungrouped',
			acl: {
				users: [
					{ name: user.id, role: 'owner' },
					{ name: 'mock-user-id-5', role: 'edit' },
					{ name: 'mock-user-id-11', role: 'view' },
				],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-3',
			name: 'Document Name 3.pdf',
			pageCount: 3,
			fileId: 'mock-print-file-id-3',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-1',
			acl: {
				users: [{ name: 'mock-user-id-11', role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-4',
			name: 'Document Name 4.pdf',
			url: 'https://example.com/mockexternalformurl',
			pageCount: 3,
			fileId: 'mock-print-file-id-4',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-1',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-5',
			name: 'Document Name 5.pdf',
			url: 'https://example.com/temp',
			pageCount: 3,
			fileId: 'mock-print-file-id-5',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-2',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-6',
			name: 'Document Name 6.pdf',
			url: 'https://example.com/temp',
			pageCount: 3,
			fileId: 'mock-print-file-id-6',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-2',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-7',
			name: 'Document Name 7.pdf',
			url: 'https://example.com/temp',
			pageCount: 3,
			fileId: 'mock-print-file-id-7',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-2',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-8',
			name: 'Document Name 8.pdf',
			url: 'https://example.com/temp',
			pageCount: 3,
			fileId: 'mock-print-file-id-8',
			fileSize: 8055,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-3',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-9',
			name: 'Document Name 9.pdf',
			url: 'https://example.com/temp',
			pageCount: 5,
			fileId: 'mock-print-file-id-9',
			fileSize: 9000005,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-3',
			acl: {
				users: [{ name: 'mock-user-id-11', role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-print-id-10',
			name: 'Document Name Super Extra long.doc',
			pageCount: 12,
			fileId: 'mock-print-file-id-10',
			fileSize: 12005,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-3',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [
					{ name: 'userGroup-1', role: 'edit' },
					{ name: 'userGroup-2', role: 'view' },
				],
			},
		},
		{
			id: 'mock-delegated-print-id-11',
			name: 'Del Document Name 11.doc',
			pageCount: 5,
			fileId: 'mock-delegated-print-file-id-11',
			fileSize: 12005,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-delegated-printGroup-11',
			acl: {
				users: [
					{ name: 'mock-user-id-5', role: 'owner' },
					{ name: user.id, role: 'edit' },
				],
				groups: [],
			},
		},
		{
			id: 'mock-delegated-print-id-12',
			name: 'Del Document Name 12.doc',
			pageCount: 12,
			fileId: 'mock-delegated-print-file-id-12',
			fileSize: 12005,
			dateCreated: new Date('04/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('04/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/20/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-delegated-printGroup-12',
			acl: {
				users: [
					{ name: 'mock-user-id-11', role: 'owner' },
					{ name: user.id, role: 'edit' },
					{ name: 'mock-user-id-5', role: 'edit' },
					{ name: 'mock-user-id-3', role: 'view' },
				],
				groups: [],
			},
		},
		{
			id: 'mock-direct-print-id-1',
			name: 'Direct Document Name 1.doc',
			pageCount: 12,
			fileId: 'mock-direct-print-file-id-1',
			fileSize: 12005,
			dateCreated: new Date('05/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/28/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-5',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-direct-print-id-2',
			name: 'Direct Document Name 2.doc',
			pageCount: 12,
			fileId: 'mock-direct-print-file-id-2',
			fileSize: 12005,
			dateCreated: new Date('05/08/2025 5:00:00 pm').getTime(),
			dateUpdated: new Date('05/09/2025 5:00:00 pm').getTime(),
			expireDate: new Date('12/28/2045 5:00:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-5',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-universal-print-id-1',
			name: 'Universal Print Job.pdf',
			pageCount: 1,
			fileId: 'mock-universal-print-file-id-1',
			fileSize: 57549,
			dateCreated: new Date('09/02/2025 11:22:00 am').getTime(),
			dateUpdated: new Date('09/02/2025 11:22:00 am').getTime(),
			expireDate: new Date('09/09/2025 11:22:00 am').getTime() / 1000,
			printQueueId: 'mock-printGroup-universal',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [],
			},
		},
		{
			id: 'mock-system-print-id-1',
			name: 'Guest Boarding Pass.pdf',
			pageCount: 1,
			fileId: 'mock-system-print-file-id-1',
			fileSize: 57549,
			dateCreated: new Date('09/02/2025 11:22:00 am').getTime(),
			dateUpdated: new Date('09/02/2025 11:22:00 am').getTime(),
			expireDate: new Date('09/09/2025 11:22:00 am').getTime() / 1000,
			printQueueId: 'mock-printGroup-system',
			senderEmail: 'guest.user@example.com',
			acl: {
				users: [],
				groups: [],
			},
		},
		{
			id: 'mock-system-print-id-2',
			name: 'Conference Ticket.pdf',
			pageCount: 2,
			fileId: 'mock-system-print-file-id-2',
			fileSize: 125840,
			dateCreated: new Date('09/01/2025 3:45:00 pm').getTime(),
			dateUpdated: new Date('09/01/2025 3:45:00 pm').getTime(),
			expireDate: new Date('09/08/2025 3:45:00 pm').getTime() / 1000,
			printQueueId: 'mock-printGroup-system',
			senderEmail: 'blocked.guest@example.com',
			acl: {
				users: [],
				groups: [],
			},
		},
	];
	const jobNames = [
		{
			id: 'A1',
			name: 'Primary Job',
			components: [],
			modified: 0,
			created: 0,
			editor: 'John Doe',
			creator: 'mock-user-id-5',
			groupList: ['01'],
			nodes: [
				{
					workflowId: 'mock-workflow-id-1',
					nodeId: 'mock-node-id',
				},
			],
		},
		{
			id: 'C3',
			name: 'Secondary Job',
			components: [
				{
					component: 'text',
					value: 'Sup',
					id: 1,
				},
			],
			modified: 999991000000,
			created: 99991000000,
			editor: 'Jane Doe',
			creator: 'mock-user-id-11',
			groupList: ['01'],
			nodes: [],
		},
		{
			id: 'B2',
			name: 'Auxiliary Job',
			components: [],
			modified: 0,
			created: 0,
			editor: 'John Doe',
			creator: 'mock-user-id-5',
			groupList: ['02'],
			nodes: [],
		},
		{
			id: 'D4',
			name: 'Backup Job',
			components: [],
			modified: 0,
			created: 0,
			editor: 'John Doe',
			creator: 'mock-user-id-11',
			groupList: ['01', '02'],
			nodes: [],
		},
		{
			id: 'E5',
			name: 'Unclaimed Job',
			components: [],
			modified: 0,
			created: 0,
			editor: 'Fulano de Tal',
			creator: 'mock-user-id-19',
			groupList: [],
			nodes: [],
		},
		{
			id: 'F6',
			name: 'Emergency Job',
			components: [],
			modified: 0,
			created: 0,
			editor: 'Fulano de Tal',
			creator: 'mock-user-id-19',
			groupList: ['00000000-1111-1111-1111-000000000000'],
			nodes: [],
		},
	];
	const jobNameGroups = [
		{
			id: '01',
			title: 'Dev',
		},
		{
			id: '02',
			title: 'QA',
		},
	];
	const inputNodes = [
		{
			id: 'inputNode-1',
			name: 'Dropbox In Node',
			licensed: true,
		},
		{
			id: 'inputNode-2',
			name: 'Dropbox In Node',
			licensed: false,
		},
	];
	const directPrintAgents = {
		things: [
			{
				thingName: 'stratus_direct_print_agent::agent1',
				thingTypeName: 'stratus_direct_print_agent',
				attributes: {
					AgentId: 'DPA-agent-001',
					AgentName: 'Office Printer Agent',
					TenantId: `DPA-${selectedLicenseGroup.id}`,
				},
				version: 1,
			},
			{
				thingName: 'stratus_direct_print_agent::agent2',
				thingTypeName: 'stratus_direct_print_agent',
				attributes: {
					AgentId: 'DPA-agent-002',
					AgentName: 'Warehouse Printer Agent',
					TenantId: `DPA-${selectedLicenseGroup.id}`,
				},
				version: 1,
			},
		],
	};
	const forms = [
		{
			id: 'form-1',
			title: 'Conversions and File Upload',
			creator: 'mock-user-id-27',
			formStatus: 'draft',
			active: false,
			formGroupId: undefined,
			created: +new Date('2022-04-18 15:55'),
			updated: +new Date('2022-04-18 15:55'),
		},
		{
			id: 'form-2',
			title: 'Email Sorting Automation',
			creator: 'mock-user-id-27',
			formStatus: 'draft',
			active: false,
			formGroupId: undefined,
			created: +new Date('2022-04-18 15:55'),
			updated: +new Date('2022-04-18 15:55'),
		},
		{
			id: 'form-3',
			title: 'Documentation Forms',
			creator: 'mock-user-id-27',
			formStatus: 'published',
			active: true,
			formGroupId: undefined,
			created: +new Date('2022-04-16 15:55'),
			updated: +new Date('2022-04-17 15:55'),
		},
		{
			id: 'form-5',
			title: 'File Storage',
			creator: 'mock-user-id-29',
			formStatus: 'published',
			active: true,
			formGroupId: 'formGroup-1',
			created: +new Date('2022-04-14 15:55'),
			updated: +new Date('2022-04-15 15:55'),
		},
		{
			id: 'form-7',
			title: 'Public Conversions with Upload',
			creator: 'mock-user-id-27',
			formStatus: 'draft',
			active: false,
			formGroupId: 'formGroup-1',
			created: +new Date('2022-04-12 15:55'),
			updated: +new Date('2022-04-13 15:55'),
		},
		{
			id: 'form-9',
			title: 'Upload Documents',
			creator: 'mock-user-id-27',
			formStatus: 'published',
			active: true,
			formGroupId: 'formGroup-1',
			created: +new Date('2022-04-12 15:55'),
			updated: +new Date('2022-04-13 15:55'),
		},
		{
			id: 'form-11',
			title: 'File Conversion',
			creator: 'mock-user-id-27',
			formStatus: 'draft',
			active: false,
			formGroupId: 'formGroup-1',
			created: +new Date('2022-04-10 15:55'),
			updated: +new Date('2022-04-11 15:55'),
		},
		{
			id: 'form-13',
			title: 'Cloud Storage',
			creator: 'mock-user-id-27',
			formStatus: 'published',
			active: true,
			formGroupId: 'formGroup-2',
			created: +new Date('2022-04-10 15:55'),
			updated: +new Date('2022-04-11 15:55'),
		},
	];
	const formGroups = [
		{
			id: 'formGroup-1',
			title: 'Dev Forms',
			updatedAt: 1619483870584,
		},
		{
			id: 'formGroup-2',
			title: 'Team Forms',
			updatedAt: 1619483850584,
		},
		{
			id: 'formGroup-3',
			title: 'Test Forms',
			updatedAt: 1619483850585,
		},
	];

	const roles = [
		{
			id: 'role-1',
			name: 'Tenant Super Admin',
			permissions: {
				devicesTab: {
					devicesSection: {
						view: true,
						add: true,
						edit: true,
						remove: true,
						activate: true,
						createGroup: true,
						editGroup: true,
						deleteGroup: true,
					},
				},
				formsTab: {
					formsSection: {
						view: true,
						add: true,
						edit: true,
						remove: true,
						import: true,
						activate: true,
						deactivate: true,
						createGroup: true,
						editGroup: true,
						deleteGroup: true,
						clone: true,
					},
				},
				usersTab: {
					usersSection: {
						view: true,
						invite: true,
						edit: true,
						remove: true,
						block: true,
						createGroup: true,
						editGroup: true,
						deleteGroup: true,
						transferOwnership: true,
						assignUser: true,
						assignGroup: true,
					},
				},
				workflowsTab: {
					workflowsSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						share: true,
						import: true,
						run: true,
						// schedule: true,
						changeOwner: true,
						// simulation: true,
						createGroup: true,
						shareGroup: true,
						editGroup: true,
						deleteGroup: true,
						clone: true,
						samples: true,
					},
				},
				dataTab: {
					reportsSection: {
						view: true,
					},
					logsSection: {
						view: true,
					},
				},
				jobsTab: {
					jobQueueSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						activate: true,
					},
					trackerSection: {
						view: true,
						track: true,
					},
					tasksSection: {
						view: true,
					},
					namingSection: {
						view: true,
					},
				},
				settingsTab: {
					settingsSection: {
						view: true,
						edit: true,
					},
					profileSection: {
						outOfOffice: true,
					},
					systemSection: {
						manageNotifications: true,
					},
				},
				rolesTab: {
					rolesSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						// clone: true
					},
				},
				r2mTab: {
					personalQueueSection: {
						deleteJobs: true,
						delegateJobs: true,
						use: true,
					},
					devicesSection: {
						manageDevices: true,
					},
					printSettingsSection: {
						setPreferences: true,
					},
					sharedQueueSection: {
						create: true,
						edit: true,
						delete: true,
						deleteJobs: true,
						manageUsers: true,
						use: true,
					},
					guestQueueSection: {
						create: true,
						edit: true,
						delete: true,
						deleteJobs: true,
						manageUsers: true,
					},
					directQueueSection: {
						create: true,
						edit: true,
						delete: true,
						deleteJobs: true,
						manageUsers: true,
						use: false,
					},
					msUniversalQueueSection: {
						create: true,
						edit: true,
						delete: true,
						deleteJobs: true,
						manageUsers: true,
						use: true,
					},
				},
				bsnTab: {
					dashboardSection: {
						view: true,
					},
					policiesSection: {
						create: true,
						edit: true,
						delete: true,
						sendEmail: true,
					},
					policyGroupsSection: {
						create: true,
						edit: true,
						delete: true,
						sendEmail: true,
					},
				},
				authTab: {
					systemSection: {
						view: true,
						addProvider: true,
						editProvider: true,
						removalTool: true,
					},
					devicesSection: {
						register: true,
					},
					userSection: {
						addCards: true,
						importCards: true,
					},
				},
				documentsTab: {
					archivedJobsSection: {
						view: true,
						downloadReport: true,
						downloadReportAndFiles: true,
						delete: true,
					},
					storageTrashSection: {
						view: true,
						restore: true,
						deletePermanently: true,
					},
				},
			},
			systemRole: 'tenant-super-admin',
		},
		{
			id: 'role-2',
			name: 'Tenant Admin',
			permissions: {
				devicesTab: {
					devicesSection: {
						view: true,
						add: true,
						edit: true,
						remove: true,
						activate: true,
						createGroup: true,
						editGroup: true,
						deleteGroup: true,
					},
				},
				formsTab: {
					formsSection: {
						view: true,
						add: true,
						edit: true,
						remove: true,
						import: true,
						activate: true,
						deactivate: true,
						createGroup: true,
						editGroup: true,
						deleteGroup: true,
						clone: true,
					},
				},
				usersTab: {
					usersSection: {
						view: true,
						invite: true,
						edit: true,
						remove: true,
						block: true,
						createGroup: true,
						editGroup: true,
						deleteGroup: true,
						transferOwnership: true,
						assignUser: true,
						assignGroup: true,
					},
				},
				workflowsTab: {
					workflowsSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						share: true,
						import: true,
						run: true,
						// schedule: true,
						changeOwner: true,
						// simulation: true,
						createGroup: true,
						shareGroup: true,
						editGroup: true,
						deleteGroup: true,
						clone: true,
						samples: true,
					},
				},
				dataTab: {
					reportsSection: {
						view: true,
					},
					logsSection: {
						view: true,
					},
				},
				jobsTab: {
					jobQueueSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						activate: true,
					},
					trackerSection: {
						view: true,
						track: true,
					},
					tasksSection: {
						view: true,
					},
					namingSection: {
						view: true,
					},
				},
				settingsTab: {
					settingsSection: {
						view: false,
						edit: false,
					},
					profileSection: {
						outOfOffice: true,
					},
					systemSection: {
						manageNotifications: true,
					},
				},
				rolesTab: {
					rolesSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						// clone: true
					},
				},
				r2mTab: {
					personalQueueSection: {
						deleteJobs: true,
						delegateJobs: true,
						use: true,
					},
					devicesSection: {
						manageDevices: true,
					},
					printSettingsSection: {
						setPreferences: true,
					},
					sharedQueueSection: {
						create: true,
						edit: true,
						delete: true,
						deleteJobs: true,
						manageUsers: true,
						use: true,
					},
					guestQueueSection: {
						create: true,
						edit: true,
						delete: true,
						deleteJobs: true,
						manageUsers: true,
					},
					directQueueSection: {
						create: true,
						edit: true,
						delete: true,
						deleteJobs: true,
						manageUsers: true,
						use: true,
					},
				},
				bsnTab: {
					dashboardSection: {
						view: true,
					},
					policiesSection: {
						create: true,
						edit: true,
						delete: true,
						sendEmail: true,
					},
					policyGroupsSection: {
						create: true,
						edit: true,
						delete: true,
						sendEmail: true,
					},
				},
				authTab: {
					systemSection: {
						view: true,
						addProvider: true,
						editProvider: true,
						removalTool: true,
					},
					devicesSection: {
						register: true,
					},
					userSection: {
						addCards: true,
						importCards: true,
					},
				},
				documentsTab: {
					archivedJobsSection: {
						view: true,
						downloadReport: true,
						downloadReportAndFiles: true,
						delete: true,
					},
					storageTrashSection: {
						view: true,
						restore: true,
						deletePermanently: true,
					},
				},
			},
		},
		{
			id: 'role-3',
			name: 'Tenant User',
			permissions: {
				devicesTab: {
					devicesSection: {
						view: false,
						add: false,
						edit: false,
						remove: false,
						activate: false,
						createGroup: false,
						editGroup: false,
						deleteGroup: false,
					},
				},
				formsTab: {
					formsSection: {
						view: false,
						add: false,
						edit: false,
						remove: false,
						import: false,
						activate: false,
						deactivate: false,
						createGroup: false,
						editGroup: false,
						deleteGroup: false,
						clone: false,
					},
				},
				usersTab: {
					usersSection: {
						view: false,
						invite: false,
						edit: false,
						remove: false,
						block: false,
						createGroup: false,
						editGroup: false,
						deleteGroup: false,
						transferOwnership: false,
						assignUser: false,
						assignGroup: false,
					},
				},
				workflowsTab: {
					workflowsSection: {
						view: true,
						create: false,
						edit: false,
						remove: false,
						share: false,
						import: false,
						run: false,
						// schedule: false,
						changeOwner: false,
						// simulation: false,
						createGroup: false,
						shareGroup: false,
						editGroup: false,
						deleteGroup: false,
						clone: false,
						samples: false,
					},
				},
				dataTab: {
					reportsSection: {
						view: false,
					},
					logsSection: {
						view: false,
					},
				},
				jobsTab: {
					jobQueueSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						activate: true,
					},
					trackerSection: {
						view: true,
						track: true,
					},
					tasksSection: {
						view: true,
					},
					namingSection: {
						view: true,
					},
				},
				settingsTab: {
					settingsSection: {
						view: false,
						edit: false,
					},
					profileSection: {
						outOfOffice: true,
					},
					systemSection: {
						manageNotifications: true,
					},
				},
				rolesTab: {
					rolesSection: {
						view: false,
						create: false,
						edit: false,
						remove: false,
					},
				},
				r2mTab: {
					personalQueueSection: {
						deleteJobs: false,
						delegateJobs: false,
						use: false,
					},
					devicesSection: {
						manageDevices: false,
					},
					printSettingsSection: {
						setPreferences: false,
					},
					sharedQueueSection: {
						create: false,
						edit: false,
						delete: false,
						deleteJobs: false,
						manageUsers: false,
						use: false,
					},
					guestQueueSection: {
						create: false,
						edit: false,
						delete: false,
						deleteJobs: false,
						manageUsers: false,
					},
					directQueueSection: {
						create: false,
						edit: false,
						delete: false,
						deleteJobs: false,
						manageUsers: false,
						use: false,
					},
				},
				bsnTab: {
					dashboardSection: {
						view: false,
					},
					policiesSection: {
						create: false,
						edit: false,
						delete: false,
						sendEmail: false,
					},
					policyGroupsSection: {
						create: false,
						edit: false,
						delete: false,
						sendEmail: false,
					},
				},
				authTab: {
					systemSection: {
						view: false,
						addProvider: false,
						editProvider: false,
						removalTool: false,
					},
					devicesSection: {
						register: false,
					},
					userSection: {
						addCards: false,
						importCards: false,
					},
				},
				documentsTab: {
					archivedJobsSection: {
						view: false,
						downloadReport: false,
						downloadReportAndFiles: false,
						delete: false,
					},
					storageTrashSection: {
						view: false,
						restore: false,
						deletePermanently: false,
					},
				},
			},
		},
		{
			id: 'role-4',
			name: 'Executive',
			permissions: {
				devicesTab: {
					devicesSection: {
						view: true,
						add: true,
						edit: true,
						remove: true,
						activate: true,
						createGroup: true,
						editGroup: true,
						deleteGroup: true,
					},
				},
				formsTab: {
					formsSection: {
						view: true,
						add: true,
						edit: false,
						remove: false,
						import: false,
						activate: false,
						deactivate: false,
						createGroup: false,
						editGroup: false,
						deleteGroup: false,
						clone: false,
					},
				},
				usersTab: {
					usersSection: {
						view: true,
						invite: true,
						edit: false,
						remove: false,
						block: false,
						createGroup: false,
						editGroup: false,
						deleteGroup: false,
						transferOwnership: false,
						assignUser: false,
						assignGroup: false,
					},
				},
				workflowsTab: {
					workflowsSection: {
						view: true,
						create: false,
						edit: false,
						remove: false,
						share: false,
						import: false,
						run: false,
						// schedule: false,
						changeOwner: false,
						// simulation: false,
						createGroup: false,
						shareGroup: false,
						editGroup: false,
						deleteGroup: false,
						clone: false,
						samples: false,
					},
				},
				dataTab: {
					reportsSection: {
						view: false,
					},
					logsSection: {
						view: false,
					},
				},
				jobsTab: {
					jobQueueSection: {
						view: true,
						create: true,
						edit: true,
						remove: true,
						activate: true,
					},
					trackerSection: {
						view: true,
						track: true,
					},
					tasksSection: {
						view: true,
					},
					namingSection: {
						view: true,
					},
				},
				settingsTab: {
					settingsSection: {
						view: false,
						edit: false,
					},
					profileSection: {
						outOfOffice: true,
					},
					systemSection: {
						manageNotifications: true,
					},
				},
				rolesTab: {
					rolesSection: {
						view: false,
						create: false,
						edit: false,
						remove: false,
					},
				},
				r2mTab: {
					personalQueueSection: {
						deleteJobs: false,
						delegateJobs: false,
						use: false,
					},
					devicesSection: {
						manageDevices: false,
					},
					printSettingsSection: {
						setPreferences: false,
					},
					sharedQueueSection: {
						create: false,
						edit: false,
						delete: false,
						deleteJobs: false,
						manageUsers: false,
						use: false,
					},
					guestQueueSection: {
						create: false,
						edit: false,
						delete: false,
						deleteJobs: false,
						manageUsers: false,
					},
					directQueueSection: {
						create: false,
						edit: false,
						delete: false,
						deleteJobs: false,
						manageUsers: false,
						use: false,
					},
				},
				bsnTab: {
					dashboardSection: {
						view: false,
					},
					policiesSection: {
						create: false,
						edit: false,
						delete: false,
						sendEmail: false,
					},
					policyGroupsSection: {
						create: false,
						edit: false,
						delete: false,
						sendEmail: false,
					},
				},
				authTab: {
					systemSection: {
						view: false,
						addProvider: false,
						editProvider: false,
						removalTool: false,
					},
					devicesSection: {
						register: false,
					},
					userSection: {
						addCards: false,
						importCards: false,
					},
				},
				documentsTab: {
					archivedJobsSection: {
						view: false,
						downloadReport: false,
						downloadReportAndFiles: false,
						delete: false,
					},
					storageTrashSection: {
						view: false,
						restore: false,
						deletePermanently: false,
					},
				},
			},
		},
	];

	const permissions: typeof defaultPermissions = {
		..._.cloneDeepWith(defaultPermissions, (value) => (value === false ? true : undefined)),
	};

	const stcPlanFeatures = {
		r2m: true,
		bsn: true,
		auth: true,
		ms_universal_print: true,
	};

	const stratusPlanFeatures = {
		advanced_forms: true,
		advanced_roles_and_permissions: true,
		annotate: true,
		assign_tenant_manager: true,
		azure_ad_integration: true,
		convert_to_pdf_w_tesseract: true,
		device_sharing: true,
		dropbox_in: true,
		email_in: true,
		forms_management: true,
		forms_selector: true,
		home_page: true,
		import_device: true,
		license_calculator: true,
		licensing_platform: true,
		linear_forms: true,
		metadata_scripting: true,
		'multi-tiered_tenant_management': true,
		nfr_portal: true,
		odbc: true,
		people_node: true,
		processing_node_logging: true,
		public_user_access: true,
		rendering_custom_forms_on_mfps: true,
		'reskin_portal&customization_option': true,
		samples_shared_with_all_admins: true,
		support_for_non_mfps: true,
		support_for_paragon_integration: true,
		tenant_settings_for_timezone: true,
		upland_fax_connector: true,
		r2m: true,
		bsn: true,
		auth: true,
		ipp_installer: true,
		ms_universal_print: true,
	};

	const sampleWorkflows = [
		{
			id: 'sample-workflow-1',
			name: 'Sample Scan to Google Drive',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:30:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-2',
			name: 'Sample Scan to Box',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:31:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-3',
			name: 'Sample Scan to OneDrive for Business',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:33:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-9',
			name: 'Sample Route to SharePoint Online',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:34:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-11',
			name: 'Sample Route to Marketing',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:35:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-13',
			name: 'Sample Route to R&D',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:36:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-15',
			name: 'Sample Convert and Route to Box',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:37:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-17',
			name: 'Sample Convert and Route to SharePoint',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:38:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-19',
			name: 'Sample Convert and Route to OneDrive',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:39:58.4033151+00:00',
		},
		{
			id: 'sample-workflow-21',
			name: 'Sample Route to R&D',
			status: 'draft',
			description: '',
			modified: '2022-08-15T14:40:58.4033151+00:00',
		},
	];

	const workflows = [
		{
			id: 'mock-workflow-id-1',
			name: 'Scan to Google Drive',
			status: 'running',
			modified: '2022-08-15T14:30:58.4033151+00:00',
			acl: undefined,
			options: {
				PreviewPageCount: 3,
				group: 'workflowGroup-1',
			},
			workflowGroupId: 'workflowGroup-1',
		},
		{
			id: 'mock-workflow-id-2',
			name: 'Scan to Box',
			status: 'running',
			modified: '2022-08-15T14:31:58.4033151+00:00',
			acl: undefined,
			options: {
				PreviewPageCount: 3,
				Group: 'workflowGroup-1',
			},
			workflowGroupId: 'workflowGroup-1',
		},
		{
			id: '4b501a9b9d2e493baaeee3c440c355ad',
			name: 'Scan to OneDrive for Business',
			status: 'stopped',
			modified: '2022-08-15T14:33:58.4033151+00:00',
			acl: {
				users: [{ name: user.id, role: 'owner' }],
				groups: [
					{ name: 'userGroup-1', role: 'edit' },
					{ name: 'userGroup-2', role: 'view' },
				],
			},
			options: {
				PreviewPageCount: 3,
				Group: 'workflowGroup-1',
			},
			workflowGroupId: 'workflowGroup-1',
		},
		{
			id: 'workflow-9',
			name: 'Route to SharePoint Online',
			status: 'running',
			modified: '2022-08-15T14:34:58.4033151+00:00',
			acl: {
				users: [
					{ name: user.id, role: 'owner' },
					{ name: 'mock-user-id-3', role: 'edit' },
					{ name: 'mock-user-id-5', role: 'edit' },
					{ name: 'mock-user-id-11', role: 'view' },
				],
				groups: [],
			},
			options: {
				PreviewPageCount: 3,
				Group: 'workflowGroup-2',
			},
			workflowGroupId: 'workflowGroup-2',
		},
		{
			id: 'workflow-11',
			name: 'Route to Marketing',
			status: 'stopped',
			modified: '2022-08-15T14:35:58.4033151+00:00',
			acl: {
				users: [
					{ name: user.id, role: 'owner' },
					{ name: 'mock-user-id-3', role: 'edit' },
					{ name: 'mock-user-id-5', role: 'edit' },
					{ name: 'mock-user-id-11', role: 'view' },
				],
				groups: [],
			},
			options: {
				PreviewPageCount: 3,
				Group: 'workflowGroup-2',
			},
			workflowGroupId: 'workflowGroup-2',
		},
		{
			id: 'workflow-13',
			name: 'Route to R&D',
			status: 'running',
			modified: '2022-08-15T14:36:58.4033151+00:00',
			acl: {
				users: [{ name: 'mock-user-id-13', role: 'owner' }],
				groups: [],
			},
			options: {
				PreviewPageCount: 3,
				Group: 'workflowGroup-2',
			},
			workflowGroupId: 'workflowGroup-2',
		},
		{
			id: 'workflow-15',
			name: 'Convert and Route to Box',
			status: 'paused',
			modified: '2022-08-15T14:37:58.4033151+00:00',
			acl: undefined,
			options: {
				PreviewPageCount: 3,
				Group: 'workflowGroup-2',
			},
			workflowGroupId: 'workflowGroup-2',
		},
		{
			id: 'workflow-17',
			name: 'Convert and Route to SharePoint',
			status: 'draft',
			modified: '2022-08-15T14:38:58.4033151+00:00',
			acl: {
				users: [
					{ name: 'mock-user-id-19', role: 'owner' },
					{ name: 'mock-user-id-21', role: 'edit' },
					{ name: 'mock-user-id-23', role: 'view' },
				],
				groups: [{ name: 'userGroup-2', role: 'view' }],
			},
			options: {
				PreviewPageCount: 3,
				Group: 'workflowGroup-2',
			},
			workflowGroupId: 'workflowGroup-2',
		},
		{
			id: 'workflow-19',
			name: 'Convert and Route to OneDrive',
			status: 'draft',
			modified: '2022-08-15T14:39:58.4033151+00:00',
			acl: undefined,
			options: {
				PreviewPageCount: 1,
				Group: 'workflowGroup-2',
			},
			workflowGroupId: 'workflowGroup-2',
		},
		{
			id: 'workflow-21',
			name: 'Route to R&D',
			status: 'stopped',
			modified: '2022-08-15T14:40:58.4033151+00:00',
			acl: undefined,
			options: {
				PreviewPageCount: 6,
				Group: 'workflowGroup-3',
			},
			workflowGroupId: 'workflowGroup-3',
		},
	];
	// uncomment below to mimic a workflow table that needs a scrollbar
	const tasks = [
		'Go to warp',
		'Open pod bay doors',
		'Use the Force',
		'Release the Kraken',
		'Test your might',
		'Do the time warp again',
		'Fastball Special',
		'Let the spice flow',
		'Kneel before Zod',
		'Save the cheerleader, save the world',
		'Hawkmen, dive',
		'Build it, they will come',
		'Make it so',
		'aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa',
	];
	for (let i = 0; i < tasks.length; i += 1) {
		workflows.push({
			id: `workflow-${i + 22}`,
			name: tasks[i],
			status: 'running',
			modified: '2022-08-15T14:30:58.4033151+00:00',
			acl: undefined,
			options: {
				PreviewPageCount: 0,
				Group: 'workflowGroup-2',
			},
			workflowGroupId: 'workflowGroup-2',
		});
	}
	const workflowGroups = [
		{
			id: 'workflowGroup-1',
			name: 'My Workflows',
			modified: 1619483870584,
		},
		{
			id: 'workflowGroup-2',
			name: 'Other Workflows',
			modified: 1617483870584,
		},
		{
			id: 'workflowGroup-3',
			name: 'Some Workflows',
			modified: 1615483870584,
		},
		// {
		// 	id: 'workflowGroup-4',
		// 	name: 'More Workflows',
		// 	updatedAt: 1615483870584
		// },
		// {
		// 	id: 'workflowGroup-5',
		// 	name: 'Further Workflows',
		// 	updatedAt: 1615483870584
		// },
		// {
		// 	id: 'workflowGroup-6',
		// 	name: 'Extra Workflows',
		// 	updatedAt: 1615483870584
		// },
		// {
		// 	id: 'workflowGroup-7',
		// 	name: 'Bonus Workflows',
		// 	updatedAt: 1615483870584
		// },
		// {
		// 	id: 'workflowGroup-8',
		// 	name: 'Added Workflows',
		// 	updatedAt: 1615483870584
		// },
		// {
		// 	id: 'workflowGroup-9',
		// 	name: 'New Workflows',
		// 	updatedAt: 1615483870584
		// }
	];
	const logs = [
		{
			SK: 'log-1',
			Type: 'workflow',
			severity: 'low',
			DateAdded: 1646110800000,
			MsgKey: 'Workflow Secure Files was checked in.',
			MessageTranslated: 'Workflow Secure Files was checked in.',
			Action: 'Check in',
			Info: {
				deviceId: 'A112233440001',
				deviceName: '558',
			},
		},
		{
			SK: 'log-2',
			Type: 'workflow',
			severity: 'low',
			DateAdded: 1646197200000,
			MsgKey: 'Workflow Scan Financial Records was checked out.',
			MessageTranslated: 'Workflow Scan Financial Records was checked out.',
			Action: 'Check out',
			Info: {
				deviceId: 'A112233440002',
				deviceName: '364e',
			},
		},
		{
			SK: 'log-3',
			Type: 'workflow',
			severity: 'low',
			DateAdded: 1646283600000,
			MsgKey: 'Workflow Verification was started and failed.',
			MessageTranslated: 'Workflow Verification was started and failed.',
			Action: 'Error',
			Info: {
				deviceId: 'A112233440003',
				deviceName: '364e',
				policyFails: {
					msg: "I can't do that, Dave",
					deviceSetting: 120,
					msgKey: 'encryptpdfFail',
					requiredSetting: '15',
					policyItemKey: 'encryptpdf',
				},
			},
		},
		{
			SK: 'log-4',
			Type: 'workflow',
			severity: 'high',
			DateAdded: 1646370000000,
			MsgKey: 'Workflow Secure Files was paused.',
			MessageTranslated: 'Workflow Secure Files was paused.',
			Action: 'Pause',
			Info: {
				deviceId: 'A112233440004',
				deviceName: '364e',
				policyFails: [
					{
						msg: "hhdencrypt is 'false' but should be 'true'",
						deviceSetting: false,
						msgKey: 'hhdencryptFail',
						requiredSetting: 'true',
						policyItemKey: 'hhdencrypt',
					},
					{
						msg: "encryptpdf is '120' but should be '5'",
						deviceSetting: 120,
						msgKey: 'encryptpdfFail',
						requiredSetting: '5',
						policyItemKey: 'encryptpdf',
					},
				],
			},
		},
		{
			SK: 'log-5',
			Type: 'workflow',
			severity: 'high',
			DateAdded: 1646456400000,
			MsgKey: 'Workflow Secure Files was scheduled.',
			MessageTranslated: 'Workflow Secure Files was scheduled.',
			Action: 'Scheduled',
			Info: {
				deviceId: 'A112233440005',
				deviceName: 'C300',
				policyFails: [
					{
						msg: "encryptpdf is '120' but should be '15'",
						deviceSetting: 120,
						msgKey: 'encryptpdfFail',
						requiredSetting: '15',
						policyItemKey: 'encryptpdf',
					},
				],
			},
		},
		{
			SK: 'log-6',
			Type: 'workflow',
			severity: 'high',
			DateAdded: 1646542800000,
			MsgKey: 'Workflow Scan Financial Records was started.',
			MessageTranslated: 'Workflow Scan Financial Records was started.',
			Action: 'Start',
			Info: {
				deviceId: 'A112233440006',
				deviceName: 'c558',
				policyFails: [
					{
						msg: "idandprint is '60`' but should be '30'",
						deviceSetting: 60,
						msgKey: 'idandprintFail',
						requiredSetting: '30',
						policyItemKey: 'idandprint',
					},
				],
			},
		},
		{
			SK: 'log-7',
			Type: 'workflow',
			severity: 'high',
			DateAdded: 1646629200000,
			MsgKey: 'Workflow Scan Financial Records was stopped.',
			MessageTranslated: 'Workflow Scan Financial Records was stopped.',
			Action: 'Stop',
			Info: {
				deviceId: 'A112233440007',
				deviceName: 'C300i',
				policyFails: [
					{
						msg: "publicauth is '2' but should be '1'",
						deviceSetting: 2,
						msgKey: 'publicauthFail',
						requiredSetting: '1',
						policyItemKey: 'publicauth',
					},
				],
			},
		},
		{
			SK: 'log-8',
			Type: 'device',
			severity: 'low',
			DateAdded: 1646715600000,
			MsgKey: 'Device bizhub 364e was added.',
			MessageTranslated: 'Device bizhub 364e was added.',
			Action: 'Added',
			Info: {
				deviceId: 'A112233440008',
				deviceName: '364e',
			},
		},
		{
			SK: 'log-10',
			Type: 'device',
			severity: 'low',
			DateAdded: 1646888400000,
			MsgKey: 'Device bizhub C300i was removed.',
			MessageTranslated: 'Device bizhub C300i was removed.',
			Action: 'Removed',
			Info: {
				deviceId: 'A1122334400010',
				deviceName: 'C300i',
			},
		},
		{
			SK: 'log-11',
			Type: 'device',
			severity: 'low',
			DateAdded: 1646974800000,
			MsgKey: 'Device bizhub 558 was registered.',
			MessageTranslated: 'Device bizhub 558 was registered.',
			Action: 'Registered',
			Info: {
				deviceId: 'A1122334400011',
				deviceName: '558',
			},
		},
		{
			SK: 'log-12',
			Type: 'device',
			severity: 'low',
			DateAdded: 1647061200000,
			MsgKey: 'Device bizhub C300i was unregistered.',
			MessageTranslated: 'Device bizhub C300i was unregistered.',
			Action: 'Unregistered',
			Info: {
				deviceId: 'A1122334400012',
				deviceName: 'C300i',
			},
		},
		{
			SK: 'log-13',
			Type: 'user',
			severity: 'low',
			DateAdded: 1647147600000,
			MsgKey: 'User rrichards@example.com was activated.',
			MessageTranslated: 'User rrichards@example.com was activated.',
			Action: 'Activated',
			Info: {
				deviceId: 'A1122334400013',
				deviceName: '558',
			},
		},
		{
			SK: 'log-14',
			Type: 'user',
			severity: 'low',
			DateAdded: 1647230400000,
			MsgKey: 'User kpryde@example.com was blocked.',
			MessageTranslated: 'User kpryde@example.com was blocked.',
			Action: 'Blocked',
			Info: {
				type: 'change-admin-password',
				id: '243000008-7g3uemBjwKNCVuzjes',
				status: 'completed',
				deviceId: 'A1122334400014',
				deviceName: 'C300',
			},
		},
		{
			SK: 'log-17',
			Type: 'user',
			severity: 'low',
			DateAdded: 1647403200000,
			MsgKey: 'User ebrooks@example.com was blocked and failed.',
			MessageTranslated: 'User ebrooks@example.com was blocked and failed.',
			Action: 'Error',
			Info: {
				msgParm1: 'autodoc',
				msgParm2: 'Day3',
				msgParm3: 'Day7',
				deviceId: 'A1122334400017',
				deviceName: '558',
				policyFails: {
					msg: "I can't do that, Dave",
					deviceSetting: 120,
					msgKey: 'encryptpdfFail',
					requiredSetting: '15',
					policyItemKey: 'encryptpdf',
				},
			},
		},
		{
			SK: 'log-19',
			Type: 'user',
			severity: 'low',
			DateAdded: 1647489600000,
			MsgKey: 'User hmccoy@example.com accepted user invite.',
			MessageTranslated: 'User hmccoy@example.com accepted user invite.',
			Action: 'Invited',
			Info: {
				msgParm1: 'ssltls',
				msgParm2: "{'ssl3': 'false', 'tls13': 'true', 'tls12': 'true', 'tls11': 'true', 'tls10': 'false'}",
				msgParm3: "{'ssl3': 'false', 'tls13': 'true', 'tls12': 'true', 'tls11': 'false', 'tls10': 'false'}",
				deviceId: 'A1122334400019',
				deviceName: 'C300',
			},
		},
		{
			SK: 'log-20',
			Type: 'device',
			severity: 'low',
			DateAdded: 1000,
			MsgKey: 'I cannot do that, Dave.',
			MessageTranslated: 'I cannot do that, Dave.',
			Action: 'Error',
			Info: {
				msgParm1: 'ssltls',
				msgParm2: "{'ssl3': 'false', 'tls13': 'true', 'tls12': 'true', 'tls11': 'true', 'tls10': 'false'}",
				msgParm3: "{'ssl3': 'false', 'tls13': 'true', 'tls12': 'true', 'tls11': 'false', 'tls10': 'false'}",
				deviceId: 'A1122334400019',
				deviceName: 'C300',
			},
		},
	];
	const scheduledReports = [
		{
			id: 'scheduledReports-1',
			name: 'Mock Scheduled Report 1',
			reportType: 'r2m-executive',
			format: 'csv',
			frequency: 'week',
			dateUpdated: +new Date('2025-06-04T00:00:00.000Z'),
			omitColumns: ['releaser_name'],
			excludeRows: {},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
			},
		},
		{
			id: 'scheduledReports-2',
			name: 'Mock Scheduled Report 2',
			reportType: 'r2m-devices',
			format: 'csv',
			frequency: 'month',
			dateUpdated: +new Date('2025-06-05T00:00:00.000Z'),
			omitColumns: [],
			excludeRows: {},
			acl: {
				users: [{ name: user.id, role: 'owner' }],
			},
		},
	];
	const mpDevices = [
		{
			id: 'mock-mp-device-guid-1',
			name: 'C558',
			family: 'ZeusSBK',
			generation: 'IT5',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-1',
			friendlyName: 'Second Floor - Dev',
		},
		{
			id: 'mock-mp-device-guid-2',
			name: 'C556',
			family: 'ZeusSBK',
			generation: 'IT5',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-2',
			friendlyName: 'Second Floor - QA',
		},
		{
			id: 'mock-mp-device-guid-3',
			name: 'C554',
			family: 'VenusMLK',
			generation: 'IT5',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-3',
			friendlyName: 'Second Floor - Lab',
		},
		{
			id: 'mock-mp-device-guid-4',
			name: 'C358',
			family: 'Sparrow',
			generation: 'IT6',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-4',
		},
		{
			id: 'mock-mp-device-guid-5',
			name: 'C356',
			family: 'Sparrow',
			generation: 'IT6',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-5',
		},
		{
			id: 'mock-mp-device-guid-6',
			name: 'C354',
			family: 'Sparrow',
			generation: 'IT6',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-6',
		},
		{
			id: 'mock-mp-device-guid-7',
			name: 'C666',
			family: 'Sparrow',
			generation: 'IT6',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-7',
		},
		{
			id: 'mock-mp-device-guid-8',
			name: 'C667',
			family: 'Sparrow',
			generation: 'IT6',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-8',
		},
		{
			id: 'mock-mp-device-guid-9',
			name: 'C668',
			family: 'Sparrow',
			generation: 'IT6',
			productLine: 'bizhub',
			serial: 'mock-mp-device-guid-9',
		},
	];

	if (localStorage.getItem('mock')) {
		mock.onGet('/api/tenants').reply(200, _.keyBy(managedLicenseGroups, 'id'));
		mock.onGet('/api/tenants/tenant-manager-groups').reply(200, _.keyBy(tenantLicenseGroups, 'id'));
		mock.onPost('/api/tenants/tenant-manager-groups').reply(async (config) => {
			const { name, parentId } = JSON.parse(config.data);
			const parentGroup = tenantLicenseGroups.filter((item) => item.id === parentId)[0];

			const rsp: any = tenantLicenseGroups;
			rsp.push({
				name,
				// eslint-disable-next-line prefer-template
				id: '' + Math.random() * 100,
				parentId,
				parentName: parentGroup ? parentGroup.name : undefined,
			});
			return [200, rsp];
		});
		mock.onGet(/\/api\/mp\/license\/order\/[^/]+$/).reply(async (config) => {
			const order = {
				catalogItems: [
					{
						expirationDate: 2554482820000,
						materialNumber: 'AEMPA04',
						name: 'Dispatcher Stratus 1 device license, 1 Year',
						publicId: PublicId.OneDeviceLicenseFiveYear,
						quantity: 10,
						uid: 'dps-stratus-device-term1',
					},
					{
						expirationDate: null,
						materialNumber: 'STRATUSUP4YR',
						name: 'Dispatcher Stratus 1 device upgrade license, 4 Year',
						publicId: '623001AF-C247-4882-AC71-F35728A1BE07',
						quantity: 11,
						uid: 'dps-stratus-device-upgrade-term4',
					},
				],
				// catalogPublicId: PublicId.OneDeviceLicenseFiveYear,
				// materialNumber: 'AEMPA00',
				country: 'US',
				creationDate: 1681912598915,
				// expirationDate: 2554482820000,
				// userExpirationDate: 2554482820000,
				// r2mExpirationDate: 2554482820000,
				number: 'STC0000001',
				// quantity: 1,
				rocs: ['BUS'],
				type: 'PRODUCTION',
			};
			return [200, { order }];
		});
		mock.onPut('/api/tenants/tenant-manager-groups').reply(async (config) => {
			const { name, parentId, id } = JSON.parse(config.data);
			const currentIndex = tenantLicenseGroups.findIndex((item) => item.id === id);

			const [parentName] = tenantLicenseGroups.filter((item) => item.id === parentId);
			const rsp = tenantLicenseGroups;
			tenantLicenseGroups.splice(currentIndex, 1);
			rsp.push({
				name,
				parentId: parentId ?? undefined,
				id,
				parentName: parentName?.name,
				managerEmails: [],
			});
			return [200, _.keyBy(rsp, 'id')];
		});
		mock.onPut(/\/api\/tenants\/edit-tenant-details\/[^/]+$/).reply(async (config) => {
			const id = config.url!.split('/').pop()!;
			const { name, parentId } = JSON.parse(config.data);
			const currentIndex = licenseGroups.findIndex((item) => item.id === id);

			const rsp: any = licenseGroups
				.filter(({ isManager }) => isManager)
				.map((licenseGroup) => _.omit(licenseGroup, ['isAdmin', 'isManager']));
			const currentTenant = licenseGroups.splice(currentIndex, 1);
			// @ts-ignore
			rsp.push({
				...currentTenant[0],
				name,
				parent: parentId,
				id,
			});
			return [200, _.keyBy(rsp, 'id')];
		});

		mock.onGet(/\/api\/tenants\/tenant-manager-group\/[^/]+$/).reply(async (config) => {
			return [200, users];
		});

		mock.onGet(/\/api\/inputNode\/licenses\/[^/]+$/).reply(async (config) => {
			const dat = {
				userId: 'mock-user-id',
				capacity: 10,
				quantity: 3,
				licenses: {
					licenses: {
						'Dropbox In': [
							{
								accountId: '',
								path: '',
								workflows: [
									{
										workflowId: 'workflow-22',
										refCount: 2,
									},
								],
							},
						],
						'GMail In': [
							{
								accountId: '',
								path: '',
								workflows: [
									{
										workflowId: 'workflow-23',
										refCount: 1,
									},
								],
							},
						],
					},
				},
			};
			return [200, dat];
		});

		mock.onGet(/\/api\/inputNode\/licenses/).reply(async (config) => {
			const dat = {
				'mock-user-id': {
					userId: 'mock-user-id',
					capacity: 10,
					quantity: 3,
					licenses: {
						licenses: {
							'Dropbox In': [
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-22',
											refCount: 2,
										},
									],
								},
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-22',
											refCount: 2,
										},
									],
								},
							],
							'GMail In': [
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-23',
											refCount: 1,
										},
										{
											workflowId: 'workflow-22',
											refCount: 1,
										},
									],
								},
							],
						},
					},
				},
				'mock-user-id-5': {
					userId: 'mock-user-id-5',
					capacity: 5,
					quantity: 2,
					licenses: {
						licenses: {
							'Dropbox In': [
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-24',
											refCount: 1,
										},
									],
								},
							],
							'EMail In': [
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-25',
											refCount: 1,
										},
									],
								},
							],
						},
					},
				},
				'mock-user-id-11': {
					userId: 'mock-user-id-11',
					capacity: 5,
					quantity: 3,
					licenses: {
						licenses: {
							'GMail In': [
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-26',
											refCount: 1,
										},
									],
								},
							],
							'Dropbox In': [
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-00',
											refCount: 2,
										},
									],
								},
							],
						},
					},
				},
				'mock-user-id-19': {
					userId: 'mock-user-id-19',
					capacity: 5,
					quantity: 1,
					licenses: {
						licenses: {
							'GMail In': [
								{
									accountId: '',
									path: '',
									workflows: [
										{
											workflowId: 'workflow-28',
											refCount: 1,
										},
									],
								},
							],
						},
					},
				},
				'mock-user-id-21': {
					userId: 'mock-user-id-21',
					capacity: 0,
					quantity: 0,
					licenses: {},
				},
			};
			return [200, dat];
		});

		mock.onPost(/\/api\/tenants\/[^/]+\/notifications/).reply(async (config) => {
			const { obj } = JSON.parse(config.data);
			const id = config.url?.split('/')[3];
			obj.groupId = id;
			return [200, obj];
		});

		mock.onPost(/\/api\/tenants\/[^/]+\/suspension/).reply(async (config) => {
			const { obj } = JSON.parse(config.data);
			const id = config.url?.split('/')[3];
			obj.tenantId = id;
			return [200, obj];
		});
		mock.onPatch(/\/api\/tenants\/tenant-group\/[^/]+\/invite-manager/).reply(async (config) => {
			const id = config.url?.split('/')[4];
			const { managerEmail } = JSON.parse(config.data);
			const update = tenantLicenseGroups.reduce((prev: any, item: any) => {
				if (item.id === id) {
					const copy = item;

					copy.managerEmails?.push({ email: managerEmail, status: 'PENDING' });
					return [...prev, copy];
				}
				return [...prev, item];
			}, []);

			return [200, update];
		});
		mock.onGet(/\/api\/tenants\/slug\/[^/]+$/).reply((config) => {
			// const slug = config.url!.split('/').pop()!;
			// const requestedLicenseGroup = licenseGroups.find(licenseGroup => licenseGroup.slug === slug);
			// return requestedLicenseGroup ? [200, { id: requestedLicenseGroup.id }] : [404];
			return [200, { id: selectedLicenseGroup.id }];
		});

		mock.onGet(`api/mfa/${selectedLicenseGroup.id}`).reply(200, { enabled: false, scheduled: false });

		mock.onPatch(`api/mfa/${selectedLicenseGroup.id}`).reply(200, { success: true });

		mock.onPost('api/mfa/reset-token').reply(200, []);

		mock.onPost('api/mfa/generate-codes').reply(200, () => [
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
			{ code: Math.round(Math.random() * 10000000000000).toString(16), free: true },
		]);

		mock.onPost('api/mfa/verify-set-up').reply(200, { success: true });

		mock.onPost('api/mfa/set-up').reply(200, { code: 'b45d5267-0643-4595-a732-6695b3c4d218' });

		mock.onPost('/api/tenants/reset-message').reply(200, { email: 'svivier@konicaminolta.us' });
		mock.onPost('/api/tenants/reset-password').reply(200, { email: 'svivier@konicaminolta.us' });

		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/details`).reply(200, selectedLicenseGroup);
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/users?hidden=true`).reply(200, _.keyBy(users, 'id'));
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/pendingUsers?hidden=true`).reply(
			200,
			_.keyBy(pendingUsers, 'id'),
		);
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/userGroups`).reply(200, _.keyBy(userGroups, 'id'));
		mock.onGet(/\/api\/tenants\/[^/]+\/devices(\?.*)?$/).reply(200, _.keyBy(devices, 'id'));
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/deviceGroups`).reply(200, _.keyBy(deviceGroups, 'id'));
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/inputNodes`).reply(200, _.keyBy(inputNodes, 'id'));
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/direct-print-agents`).reply(200, directPrintAgents);
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/forms`).reply(200, _.keyBy(forms, 'id'));
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/formGroups`).reply(200, _.keyBy(formGroups, 'id'));
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/roles`).reply(200, _.keyBy(roles, 'id'));
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/adminInfo`).reply(200, selectedLicenseGroup.adminData);
		mock.onGet(`/api/app/app-group`).reply(200, appGroups);
		// mock.onGet(`/api/notification/notification-group`).reply(200, notificationGroups);
		mock.onGet(`/api/portal/jnt/${selectedLicenseGroup.id}`).reply(200, _.keyBy(jobNames, 'id'));
		mock.onGet(`/api/portal/jntg/${selectedLicenseGroup.id}`).reply(200, _.keyBy(jobNameGroups, 'id'));
		mock.onPost(`/api/bsn/status/devices`).reply(200, bsn);
		mock.onGet(/\/api\/bsn\/status\/[^/]+$/).reply(200, {
			serial: 'ACVW015000103',
			status: 'unsecured',
			attributes: [
				{
					name: 'pw',
					nameDesc: 'Admin Password',
					statusDesc: 'Unchanged',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
				{
					name: 'pwr',
					nameDesc: 'Password Rules',
					statusDesc: 'Disabled',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
				{
					name: 'docdel',
					nameDesc: 'Auto Document Deletion',
					statusDesc: 'Enabled (1 Day)',
					pass: true,
					changed: false,
					requirementDesc: 'Required, 1 Day',
				},
				{
					name: 'encryptpdf',
					nameDesc: 'Encrypted PDF Function',
					statusDesc: 'Enabled (45 Minutes)',
					pass: false,
					changed: false,
					requirementDesc: 'Required, 1 Day',
				},
				{
					name: 'iddel',
					nameDesc: 'ID + Print Deletion',
					statusDesc: 'Disabled',
					pass: true,
					changed: false,
					requirementDesc: 'Required, 1 Day',
				},
				{
					name: 'secdel',
					nameDesc: 'Secure Document Deletion',
					statusDesc: 'Enabled (1 Day)',
					pass: true,
					changed: false,
					requirementDesc: 'Required, 1 Day',
				},
				{
					name: 'uauth',
					nameDesc: 'User Authentication',
					statusDesc: 'Disabled',
					pass: false,
					changed: false,
					requirementDesc: 'Required',
				},
				{
					name: 'pauth',
					nameDesc: 'Public User Authentication',
					statusDesc: 'Disabled (No Authentication)',
					pass: false,
					changed: false,
					requirementDesc: 'Required, On w/ Login',
				},
				{
					name: 'hdpw',
					nameDesc: 'Storage Lock Password',
					statusDesc: 'Disabled',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
				{
					name: 'enc',
					nameDesc: 'Storage Encryption',
					statusDesc: 'Enabled',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
				{
					name: 'temp',
					nameDesc: 'Temporary Data Overwrite',
					statusDesc: 'N/A',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
				{
					name: 'ssltls',
					nameDesc: 'TLS Security',
					statusDesc: 'TLSv1.0 -> TLSv1.3',
					pass: true,
					changed: false,
					requirementDesc: 'TLSv1.0 -> TLSv1.3',
				},
				{
					name: 'slp',
					nameDesc: 'SLP (Service Location Protocol)',
					statusDesc: 'Enabled',
					pass: true,
					changed: false,
					requirementDesc: 'Required',
				},
				{
					name: 'fttxenab',
					nameDesc: 'FTP TX',
					statusDesc: 'Enabled',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
				{
					name: 'ftsvenab',
					nameDesc: 'FTP Server',
					statusDesc: 'Enabled',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
				{
					name: 'mfpsharedfolder',
					nameDesc: 'MFP Shared Folder Deletion',
					statusDesc: 'Enabled (7 Days)',
					pass: true,
					changed: false,
					requirementDesc: 'Not Required',
				},
			],
		});

		mock.onGet(`/api/sso/${selectedLicenseGroup.id}/direct-print-data`).reply(200, {
			// identityPoolId: `${selectedLicenseGroup.region}:${selectedLicenseGroup.id}`,
			identityPoolId: 'mock-identity-pool-id',
		});

		mock.onPost('/api/tenants/toggle-mfp-auth').reply(200, []);
		mock.onPost('/api/auth/enable-provider').reply(200, []);
		mock.onPost('/api/auth/delete-provider').reply(200, []);
		mock.onPost('/api/auth/save-provider').reply(200, []);
		mock.onPost('/api/auth/save-settings').reply(200, []);

		mock.onPost('/api/bsn/monitor/devices').reply(200, []);
		mock.onPost('/api/bsn/policy/group').reply(200, []);
		mock.onPut('/api/bsn/policy/group').reply(200, []);
		mock.onDelete('/api/bsn/policy-group').reply(200, []);
		mock.onPut('/api/bsn/device/policy').reply(200, []);
		mock.onPost('/api/bsn/send-report').reply(200, []);
		mock.onPost('/api/bsn/assign-policy-group').reply(200, []);
		mock.onPost('/api/bsn/toggle-bsn-dashboard').reply(200, []);
		mock.onGet(`/api/bsn/device-options/${selectedLicenseGroup.id}`).reply(200, [
			{
				serial: 'AA2K011010707',
				stratusOptIn: true,
				tenantId: '7d94ba-69fa-5348-9a2d-220e02019d2c',
			},
			{
				serial: 'A61F011000621',
				stratusOptIn: true,
			},
		]);
		mock.onPost(`/api/tenants/sec/mfp-auth-providers`).reply(200, [
			{
				name: 'Konica Minolta MarketPlace',
				type: 'mp',
				enabled: true,
				isDefault: false,
				id: 'abcd123',
				registered: true,
				configuration: {
					metadataUrl:
						'https://login.microsoftonline.com/fbcdace5-31fa-4d37-b828-efc70cb83892/federationmetadata/2007-06/federationmetadata.xml?appid=401ede5e-2489-4473-91fc-a63f2714eb36',
				},
			},
			{
				name: 'COGNITO',
				type: 'native',
				enabled: true,
				isDefault: false,
				id: 'abcd123456',
				registered: true,
				configuration: {},
			},
			{
				name: 'OtherProviderName',
				type: 'saml',
				enabled: true,
				isDefault: false,
				id: '131313',
				registered: false,
				configuration: {
					metadataFile:
						'<?xml version=\"1.0\"?><md:EntityDescriptor xmlns:md=\"urn:oasis:names:tc:SAML:2.0:metadata\" cacheDuration=\"PT1645565990S\" entityID=\"https://konicaminoltamarketplace.com/idp/saml/\">  <md:IDPSSODescriptor WantAuthnRequestsSigned=\"false\" protocolSupportEnumeration=\"urn:oasis:names:tc:SAML:2.0:protocol\">    <md:KeyDescriptor use=\"signing\">      <ds:KeyInfo xmlns:ds=\"http://www.w3.org/2000/09/xmldsig#\">        <ds:X509Data>          <ds:X509Certificate>MIIEBjCCAu4CCQCVmuLFmm49zjANBgkqhkiG9w0BAQsFADCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwHhcNMTcwNDEzMTgwMTIwWhcNNDQwODI4MTgwMTIwWjCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwggEiMA0GCSqGSIb3DQEBAQUAA4IBDwAwggEKAoIBAQC1qJTSlmOyTTKgJJ4IOgRwAdMIvIXWchwVtqLIkBX67vSawZpCxQhrUjRxw7yLm8kaUmS0O9NLhCEm7uwvmAWIkokjK0murRhD8slr4iJVHTQaPpkS0VouJ6jSMCdoJkvduCYq3vSrqZxPZCkM0WqpTbZwgU4jPgeAP/Pv2fUY7j/X3lc3wIui/BiegZd4rBvilq/1eJrQlNOImGQ2Vwo8HGi4/udChdSHv8BgB09qPZQtPHfnyppM59790aH0sR3ifsJHWuONjJiy+Ejj0FMGuLY7zdBnSRqNU/hoDaJAd+PpeUQ+cFA0DwCfdwSE/rNoKRI9Mhv6e5QNSYh5jbbxAgMBAAEwDQYJKoZIhvcNAQELBQADggEBAEbm2DGS37YcdU56AtAoQGzNmID8p2x/dHJUK99zVIqH2nk4rIRBIDpq0g1Mp5slFGG3ZgpXRyL7t4OLpkUd9vaL4cvXiRwXKlOaqaLOLu7W4G5YPGH6Oz0+iDuvQ5G1AZdtbgv11mygxZpry9dbOiuycdq7y0fq8S/OaiY3ESIYwIqEklBcLR1cseO8G8c2BJjH20X3XtQPKy8PJ7UumKOk/Hn8yaBFxdEYpKyz7PDXeCibUfr90BcyedCz4XES5X6lxkOh4LRRXXP+GoNNU8O6kJte/nPiWrZXMYf6i3mZbOlBfSe2NanVNUX+b2ov5Jsq+smi3gC8/heR1JIfc1M=</ds:X509Certificate>        </ds:X509Data>      </ds:KeyInfo>    </md:KeyDescriptor>    <md:KeyDescriptor use=\"encryption\">      <ds:KeyInfo xmlns:ds=\"http://www.w3.org/2000/09/xmldsig#\">        <ds:X509Data>          <ds:X509Certificate>MIIEBjCCAu4CCQCVmuLFmm49zjANBgkqhkiG9w0BAQsFADCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwHhcNMTcwNDEzMTgwMTIwWhcNNDQwODI4MTgwMTIwWjCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwggEiMA0GCSqGSIb3DQEBAQUAA4IBDwAwggEKAoIBAQC1qJTSlmOyTTKgJJ4IOgRwAdMIvIXWchwVtqLIkBX67vSawZpCxQhrUjRxw7yLm8kaUmS0O9NLhCEm7uwvmAWIkokjK0murRhD8slr4iJVHTQaPpkS0VouJ6jSMCdoJkvduCYq3vSrqZxPZCkM0WqpTbZwgU4jPgeAP/Pv2fUY7j/X3lc3wIui/BiegZd4rBvilq/1eJrQlNOImGQ2Vwo8HGi4/udChdSHv8BgB09qPZQtPHfnyppM59790aH0sR3ifsJHWuONjJiy+Ejj0FMGuLY7zdBnSRqNU/hoDaJAd+PpeUQ+cFA0DwCfdwSE/rNoKRI9Mhv6e5QNSYh5jbbxAgMBAAEwDQYJKoZIhvcNAQELBQADggEBAEbm2DGS37YcdU56AtAoQGzNmID8p2x/dHJUK99zVIqH2nk4rIRBIDpq0g1Mp5slFGG3ZgpXRyL7t4OLpkUd9vaL4cvXiRwXKlOaqaLOLu7W4G5YPGH6Oz0+iDuvQ5G1AZdtbgv11mygxZpry9dbOiuycdq7y0fq8S/OaiY3ESIYwIqEklBcLR1cseO8G8c2BJjH20X3XtQPKy8PJ7UumKOk/Hn8yaBFxdEYpKyz7PDXeCibUfr90BcyedCz4XES5X6lxkOh4LRRXXP+GoNNU8O6kJte/nPiWrZXMYf6i3mZbOlBfSe2NanVNUX+b2ov5Jsq+smi3gC8/heR1JIfc1M=</ds:X509Certificate>        </ds:X509Data>      </ds:KeyInfo>    </md:KeyDescriptor>    <md:SingleLogoutService Binding=\"urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect\" Location=\"https://bizhub.singles/idp/saml/logout.php\"/>    <md:NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified</md:NameIDFormat>    <md:SingleSignOnService Binding=\"urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect\" Location=\"https://bizhub.singles/idp/saml/login.php\"/>  </md:IDPSSODescriptor></md:EntityDescriptor>',
				},
			},
		]);
		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}/auth-providers`).reply(200, [
			{
				name: 'Konica Minolta MarketPlace',
				type: 'mp',
				enabled: true,
				isDefault: false,
				id: 'abcd123',
				registered: true,
				configuration: {
					metadataUrl:
						'https://login.microsoftonline.com/fbcdace5-31fa-4d37-b828-efc70cb83892/federationmetadata/2007-06/federationmetadata.xml?appid=401ede5e-2489-4473-91fc-a63f2714eb36',
				},
			},
			{
				name: 'COGNITO',
				type: 'native',
				enabled: true,
				isDefault: false,
				id: 'abcd123456',
				registered: true,
				configuration: {},
			},
			{
				name: 'OtherProviderName',
				type: 'saml',
				enabled: true,
				isDefault: false,
				id: '131313',
				registered: false,
				configuration: {
					metadataFile:
						'<?xml version=\"1.0\"?><md:EntityDescriptor xmlns:md=\"urn:oasis:names:tc:SAML:2.0:metadata\" cacheDuration=\"PT1645565990S\" entityID=\"https://konicaminoltamarketplace.com/idp/saml/\">  <md:IDPSSODescriptor WantAuthnRequestsSigned=\"false\" protocolSupportEnumeration=\"urn:oasis:names:tc:SAML:2.0:protocol\">    <md:KeyDescriptor use=\"signing\">      <ds:KeyInfo xmlns:ds=\"http://www.w3.org/2000/09/xmldsig#\">        <ds:X509Data>          <ds:X509Certificate>MIIEBjCCAu4CCQCVmuLFmm49zjANBgkqhkiG9w0BAQsFADCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwHhcNMTcwNDEzMTgwMTIwWhcNNDQwODI4MTgwMTIwWjCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwggEiMA0GCSqGSIb3DQEBAQUAA4IBDwAwggEKAoIBAQC1qJTSlmOyTTKgJJ4IOgRwAdMIvIXWchwVtqLIkBX67vSawZpCxQhrUjRxw7yLm8kaUmS0O9NLhCEm7uwvmAWIkokjK0murRhD8slr4iJVHTQaPpkS0VouJ6jSMCdoJkvduCYq3vSrqZxPZCkM0WqpTbZwgU4jPgeAP/Pv2fUY7j/X3lc3wIui/BiegZd4rBvilq/1eJrQlNOImGQ2Vwo8HGi4/udChdSHv8BgB09qPZQtPHfnyppM59790aH0sR3ifsJHWuONjJiy+Ejj0FMGuLY7zdBnSRqNU/hoDaJAd+PpeUQ+cFA0DwCfdwSE/rNoKRI9Mhv6e5QNSYh5jbbxAgMBAAEwDQYJKoZIhvcNAQELBQADggEBAEbm2DGS37YcdU56AtAoQGzNmID8p2x/dHJUK99zVIqH2nk4rIRBIDpq0g1Mp5slFGG3ZgpXRyL7t4OLpkUd9vaL4cvXiRwXKlOaqaLOLu7W4G5YPGH6Oz0+iDuvQ5G1AZdtbgv11mygxZpry9dbOiuycdq7y0fq8S/OaiY3ESIYwIqEklBcLR1cseO8G8c2BJjH20X3XtQPKy8PJ7UumKOk/Hn8yaBFxdEYpKyz7PDXeCibUfr90BcyedCz4XES5X6lxkOh4LRRXXP+GoNNU8O6kJte/nPiWrZXMYf6i3mZbOlBfSe2NanVNUX+b2ov5Jsq+smi3gC8/heR1JIfc1M=</ds:X509Certificate>        </ds:X509Data>      </ds:KeyInfo>    </md:KeyDescriptor>    <md:KeyDescriptor use=\"encryption\">      <ds:KeyInfo xmlns:ds=\"http://www.w3.org/2000/09/xmldsig#\">        <ds:X509Data>          <ds:X509Certificate>MIIEBjCCAu4CCQCVmuLFmm49zjANBgkqhkiG9w0BAQsFADCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwHhcNMTcwNDEzMTgwMTIwWhcNNDQwODI4MTgwMTIwWjCBxDELMAkGA1UEBhMCVVMxFDASBgNVBAgMC0Nvbm5lY3RpY3V0MRAwDgYDVQQHDAdXaW5kc29yMRwwGgYDVQQKDBNLb25pY2EgTWlub2x0YSBJbmMuMSUwIwYDVQQLDBxTb2x1dGlvbnMgRW5naW5lZXJpbmcgQ2VudGVyMR4wHAYDVQQDDBViaXpodWJtYXJrZXRwbGFjZS5jb20xKDAmBgkqhkiG9w0BCQEWGXNlY0BrbWJzLmtvbmljYW1pbm9sdGEudXMwggEiMA0GCSqGSIb3DQEBAQUAA4IBDwAwggEKAoIBAQC1qJTSlmOyTTKgJJ4IOgRwAdMIvIXWchwVtqLIkBX67vSawZpCxQhrUjRxw7yLm8kaUmS0O9NLhCEm7uwvmAWIkokjK0murRhD8slr4iJVHTQaPpkS0VouJ6jSMCdoJkvduCYq3vSrqZxPZCkM0WqpTbZwgU4jPgeAP/Pv2fUY7j/X3lc3wIui/BiegZd4rBvilq/1eJrQlNOImGQ2Vwo8HGi4/udChdSHv8BgB09qPZQtPHfnyppM59790aH0sR3ifsJHWuONjJiy+Ejj0FMGuLY7zdBnSRqNU/hoDaJAd+PpeUQ+cFA0DwCfdwSE/rNoKRI9Mhv6e5QNSYh5jbbxAgMBAAEwDQYJKoZIhvcNAQELBQADggEBAEbm2DGS37YcdU56AtAoQGzNmID8p2x/dHJUK99zVIqH2nk4rIRBIDpq0g1Mp5slFGG3ZgpXRyL7t4OLpkUd9vaL4cvXiRwXKlOaqaLOLu7W4G5YPGH6Oz0+iDuvQ5G1AZdtbgv11mygxZpry9dbOiuycdq7y0fq8S/OaiY3ESIYwIqEklBcLR1cseO8G8c2BJjH20X3XtQPKy8PJ7UumKOk/Hn8yaBFxdEYpKyz7PDXeCibUfr90BcyedCz4XES5X6lxkOh4LRRXXP+GoNNU8O6kJte/nPiWrZXMYf6i3mZbOlBfSe2NanVNUX+b2ov5Jsq+smi3gC8/heR1JIfc1M=</ds:X509Certificate>        </ds:X509Data>      </ds:KeyInfo>    </md:KeyDescriptor>    <md:SingleLogoutService Binding=\"urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect\" Location=\"https://bizhub.singles/idp/saml/logout.php\"/>    <md:NameIDFormat>urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified</md:NameIDFormat>    <md:SingleSignOnService Binding=\"urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect\" Location=\"https://bizhub.singles/idp/saml/login.php\"/>  </md:IDPSSODescriptor></md:EntityDescriptor>',
				},
			},
		]);
		mock.onGet('/api/user/profile').reply(200, {
			id: 'cbbe1db3-80e1-4c5b-b7d7-56ba88903d57',
			email: 'tcurran@kmbs.konicaminolta.us',
			status: 'ACCEPTED',
			firstName: 'Tobin',
			lastName: 'Curran',
			userName: 'konicaminoltamarketplace_a369432fa99cf7632d411b8100a71c146794b8bc',
			dateAdded: 1744748080776,
			hidden: false,
			licensed: false,
			badgeNumber: '123433333',
			roles: ['53c488e7-3843-40e2-af53-378c25b0f646', '859f92a6-a439-4c56-bf48-fcd75f74b875'],
			groupList: ['00000000-3333-3333-3333-000000000000'],
			nonMFPLicenseCapacity: 0,
			nonMFPLicenseQuantity: 0,
			pinNumber: '123456',
		});
		mock.onGet(`/api/bsn/devices/${selectedLicenseGroup.id}`).reply(200, [
			{
				Serial: 'DA61F011000003',
				SK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				Hidden: false,
				Policy: {
					id: 0,
					settings: {
						pwr: {
							nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
						fttxenab: {
							nameDesc: 'FTP TX',
							statusDesc: 'Disabled',
							pass: true,
							name: 'fttxenab',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
						ftsvenab: {
							nameDesc: 'FTP Server',
							statusDesc: 'Disabled',
							pass: true,
							name: 'ftsvenab',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
						mfpsharedfolder: {
							nameDesc: 'MFP Shared Folder Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'mfpsharedfolder',
							required_time: 1440,
							changed: false,
							requirementDesc: 'Required, 1 Day',
						},
						pw: {
							nameDesc: 'Admin Password',
							statusDesc: 'Configured',
							pass: true,
							name: 'pw',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
						iddel: {
							nameDesc: 'ID + Print Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'iddel',
							required_time: 1440,
							changed: false,
							requirementDesc: 'Required, 1 Day',
						},
						enc: {
							nameDesc: 'Storage Encryption',
							statusDesc: 'Disabled',
							pass: true,
							name: 'enc',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
						secdel: {
							nameDesc: 'Secure Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'secdel',
							required_time: 1440,
							changed: false,
							requirementDesc: 'Required, 1 Day',
						},
						temp: {
							nameDesc: 'Temporary Data Overwrite',
							statusDesc: 'N/A',
							pass: true,
							name: 'temp',
							multi_requirement: 'True',
							changed: false,
							requirementDesc: 'Not Required',
						},
						docdel: {
							nameDesc: 'Auto Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'docdel',
							required_time: 1440,
							changed: false,
							requirementDesc: 'Required, 1 Day',
						},
						ssltls: {
							nameDesc: 'TLS Security',
							statusDesc: 'TLSv1.0 -> TLSv1.3',
							pass: false,
							name: 'ssltls',
							multi_requirement: 'tlsv1213',
							changed: false,
							requirementDesc: 'TLSv1.2 -> TLSv1.3',
						},
						encryptpdf: {
							nameDesc: 'Encrypted PDF Function',
							statusDesc: 'Disabled',
							pass: true,
							name: 'encryptpdf',
							required_time: 1440,
							changed: false,
							requirementDesc: 'Required, 1 Day',
						},
						slp: {
							nameDesc: 'SLP (Service Location Protocol)',
							statusDesc: 'Disabled',
							pass: true,
							name: 'slp',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
						uauth: {
							nameDesc: 'User Authentication',
							statusDesc: 'Disabled',
							pass: false,
							name: 'uauth',
							requirement: true,
							changed: false,
							requirementDesc: 'Required',
						},
						pauth: {
							nameDesc: 'Public User Authentication',
							statusDesc: 'Disabled (No Authentication)',
							pass: false,
							name: 'pauth',
							multi_requirement: 'onwi',
							changed: false,
							requirementDesc: 'Required, On w/ Login',
						},
						hdpw: {
							nameDesc: 'Storage Lock Password',
							statusDesc: 'Disabled',
							pass: true,
							name: 'hdpw',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
					},
				},
				PK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				// PolicyGroupId: '00000000-7777-7777-7777-000000000000',
				UserIds: [],
				Emails: ['svivier@kmbs.konicaminolta.us'],
			},
			{
				Serial: 'A61F011000005',
				SK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				Hidden: false,
				Policy: {
					id: 0,
					settings: {
						setting2: {
							nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
					},
				},
				PK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				PolicyGroupId: '3426aa8c-9950-4b21-a094-9760f3859c09',
				UserIds: [],
			},
			{
				Serial: 'A61F011000007',
				SK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				Hidden: false,
				Policy: {
					id: 0,
					settings: {
						setting2: {
							nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
					},
				},
				PK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				PolicyGroupId: '3426aa8c-9950-4b21-a094-9760f3859c09',
				UserIds: [],
			},
			{
				Serial: 'A61F011000009',
				SK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				Hidden: false,
				Policy: {
					id: 0,
					settings: {
						setting2: {
							nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
					},
				},
				PK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				PolicyGroupId: '3426aa8c-9950-4b21-a094-9760f3859c09',
				UserIds: [],
			},
			{
				Serial: 'A61F011000011',
				SK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				Hidden: false,
				Policy: {
					id: 0,
					settings: {
						setting2: {
							nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							requirementDesc: 'Not Required',
						},
					},
				},
				PK: 'D:461222f5dd690a20651c3d19848015cb0369db3f8e937571ffb775de70750847',
				PolicyGroupId: '3426aa8c-9950-4b21-a094-9760f3859c09',
				UserIds: [],
			},
		]);
		mock.onGet(`/api/bsn/policy-groups/${selectedLicenseGroup.id}`).reply(200, [
			{
				Id: '3426aa8c-9950-4b21-a094-9760f3859c09',
				SK: 'PG:3426aa8c-9950-4b21-a094-9760f3859c09',
				Hidden: false,
				Policy: {
					settings: {
						pwr: {
							// nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						fttxenab: {
							// nameDesc: 'FTP TX',
							statusDesc: 'Disabled',
							pass: true,
							name: 'fttxenab',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						ftsvenab: {
							// nameDesc: 'FTP Server',
							statusDesc: 'Disabled',
							pass: true,
							name: 'ftsvenab',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						mfpsharedfolder: {
							// nameDesc: 'MFP Shared Folder Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'mfpsharedfolder',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						pw: {
							// nameDesc: 'Admin Password',
							statusDesc: 'Configured',
							pass: true,
							name: 'pw',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						iddel: {
							// nameDesc: 'ID + Print Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'iddel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						enc: {
							// nameDesc: 'Storage Encryption',
							statusDesc: 'Disabled',
							pass: true,
							name: 'enc',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						secdel: {
							// nameDesc: 'Secure Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'secdel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						temp: {
							// nameDesc: 'Temporary Data Overwrite',
							statusDesc: 'N/A',
							pass: true,
							name: 'temp',
							multi_requirement: 'True',
							changed: false,
							// requirementDesc: 'Not Required',
						},
						docdel: {
							// nameDesc: 'Auto Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'docdel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						ssltls: {
							// nameDesc: 'TLS Security',
							statusDesc: 'TLSv1.0 -> TLSv1.3',
							pass: false,
							name: 'ssltls',
							multi_requirement: 'tlsv1213',
							changed: false,
							// requirementDesc: 'TLSv1.2 -> TLSv1.3',
						},
						encryptpdf: {
							// nameDesc: 'Encrypted PDF Function',
							statusDesc: 'Disabled',
							pass: true,
							name: 'encryptpdf',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						slp: {
							// nameDesc: 'SLP (Service Location Protocol)',
							statusDesc: 'Disabled',
							pass: true,
							name: 'slp',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						uauth: {
							// nameDesc: 'User Authentication',
							statusDesc: 'Disabled',
							pass: false,
							name: 'uauth',
							requirement: true,
							changed: false,
							// requirementDesc: 'Required',
						},
						pauth: {
							// nameDesc: 'Public User Authentication',
							statusDesc: 'Disabled (No Authentication)',
							pass: false,
							name: 'pauth',
							multi_requirement: 'onwi',
							changed: false,
							// requirementDesc: 'Required, On w/ Login',
						},
						hdpw: {
							// nameDesc: 'Storage Lock Password',
							statusDesc: 'Disabled',
							pass: true,
							name: 'hdpw',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
					},
					id: 0,
				},
				PK: 'T:11111111-1111-1111-1111-111111111111#PolicyGroup',
				Name: 'dev-test 1st floor',
				UserIds: [],
				Emails: ['svivier@kmbs.konicaminolta.us'],
			},
			{
				Id: '3426aa8c-9950-4b21-a094-9760f3859c11',
				SK: 'PG:3426aa8c-9950-4b21-a094-9760f3859c09',
				Hidden: false,
				groupOrder: 1,
				Policy: {
					settings: {
						pwr: {
							// nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						fttxenab: {
							// nameDesc: 'FTP TX',
							statusDesc: 'Disabled',
							pass: true,
							name: 'fttxenab',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						ftsvenab: {
							// nameDesc: 'FTP Server',
							statusDesc: 'Disabled',
							pass: true,
							name: 'ftsvenab',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						mfpsharedfolder: {
							// nameDesc: 'MFP Shared Folder Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'mfpsharedfolder',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						pw: {
							// nameDesc: 'Admin Password',
							statusDesc: 'Configured',
							pass: true,
							name: 'pw',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						iddel: {
							// nameDesc: 'ID + Print Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'iddel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						enc: {
							// nameDesc: 'Storage Encryption',
							statusDesc: 'Disabled',
							pass: true,
							name: 'enc',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						secdel: {
							// nameDesc: 'Secure Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'secdel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						temp: {
							// nameDesc: 'Temporary Data Overwrite',
							statusDesc: 'N/A',
							pass: true,
							name: 'temp',
							multi_requirement: 'True',
							changed: false,
							// requirementDesc: 'Not Required',
						},
						docdel: {
							// nameDesc: 'Auto Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'docdel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						ssltls: {
							// nameDesc: 'TLS Security',
							statusDesc: 'TLSv1.0 -> TLSv1.3',
							pass: false,
							name: 'ssltls',
							multi_requirement: 'tlsv1213',
							changed: false,
							// requirementDesc: 'TLSv1.2 -> TLSv1.3',
						},
						encryptpdf: {
							// nameDesc: 'Encrypted PDF Function',
							statusDesc: 'Disabled',
							pass: true,
							name: 'encryptpdf',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						slp: {
							// nameDesc: 'SLP (Service Location Protocol)',
							statusDesc: 'Disabled',
							pass: true,
							name: 'slp',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						uauth: {
							// nameDesc: 'User Authentication',
							statusDesc: 'Disabled',
							pass: false,
							name: 'uauth',
							requirement: true,
							changed: false,
							// requirementDesc: 'Required',
						},
						pauth: {
							// nameDesc: 'Public User Authentication',
							statusDesc: 'Disabled (No Authentication)',
							pass: false,
							name: 'pauth',
							multi_requirement: 'onwi',
							changed: false,
							// requirementDesc: 'Required, On w/ Login',
						},
						hdpw: {
							// nameDesc: 'Storage Lock Password',
							statusDesc: 'Disabled',
							pass: true,
							name: 'hdpw',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
					},
					id: 0,
				},
				PK: 'T:11111111-1111-1111-1111-111111111111#PolicyGroup',
				Name: 'dev-test 2nd floor',
				UserIds: [],
				Emails: ['svivier@kmbs.konicaminolta.us'],
				EmailEnabled: false,
			},
			{
				Id: '3426aa8c-9950-4b21-a094-9760f3859c10',
				SK: 'PG:3426aa8c-9950-4b21-a094-9760f3859c09',
				groupOrder: 2,
				Hidden: false,
				Policy: {
					settings: {
						pwr: {
							// nameDesc: 'Password Rules',
							statusDesc: 'Disabled',
							pass: true,
							name: 'pwr',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						fttxenab: {
							// nameDesc: 'FTP TX',
							statusDesc: 'Disabled',
							pass: true,
							name: 'fttxenab',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						ftsvenab: {
							// nameDesc: 'FTP Server',
							statusDesc: 'Disabled',
							pass: true,
							name: 'ftsvenab',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						mfpsharedfolder: {
							// nameDesc: 'MFP Shared Folder Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'mfpsharedfolder',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						pw: {
							// nameDesc: 'Admin Password',
							statusDesc: 'Configured',
							pass: true,
							name: 'pw',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						iddel: {
							// nameDesc: 'ID + Print Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'iddel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						enc: {
							// nameDesc: 'Storage Encryption',
							statusDesc: 'Disabled',
							pass: true,
							name: 'enc',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						secdel: {
							// nameDesc: 'Secure Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'secdel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						temp: {
							// nameDesc: 'Temporary Data Overwrite',
							statusDesc: 'N/A',
							pass: true,
							name: 'temp',
							multi_requirement: 'True',
							changed: false,
							// requirementDesc: 'Not Required',
						},
						docdel: {
							// nameDesc: 'Auto Document Deletion',
							statusDesc: 'Disabled',
							pass: true,
							name: 'docdel',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						ssltls: {
							// nameDesc: 'TLS Security',
							statusDesc: 'TLSv1.0 -> TLSv1.3',
							pass: false,
							name: 'ssltls',
							multi_requirement: 'tlsv1213',
							changed: false,
							// requirementDesc: 'TLSv1.2 -> TLSv1.3',
						},
						encryptpdf: {
							// nameDesc: 'Encrypted PDF Function',
							statusDesc: 'Disabled',
							pass: true,
							name: 'encryptpdf',
							required_time: 1440,
							changed: false,
							// requirementDesc: 'Required, 1 Day',
						},
						slp: {
							// nameDesc: 'SLP (Service Location Protocol)',
							statusDesc: 'Disabled',
							pass: true,
							name: 'slp',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
						uauth: {
							// nameDesc: 'User Authentication',
							statusDesc: 'Disabled',
							pass: false,
							name: 'uauth',
							requirement: true,
							changed: false,
							// requirementDesc: 'Required',
						},
						pauth: {
							// nameDesc: 'Public User Authentication',
							statusDesc: 'Disabled (No Authentication)',
							pass: false,
							name: 'pauth',
							multi_requirement: 'onwi',
							changed: false,
							// requirementDesc: 'Required, On w/ Login',
						},
						hdpw: {
							// nameDesc: 'Storage Lock Password',
							statusDesc: 'Disabled',
							pass: true,
							name: 'hdpw',
							requirement: false,
							changed: false,
							// requirementDesc: 'Not Required',
						},
					},
					id: 0,
				},
				PK: 'T:11111111-1111-1111-1111-111111111111#PolicyGroup',
				Name: 'dev-test 3rd floor',
				UserIds: [],
				Emails: ['svivier@kmbs.konicaminolta.us'],
			},
		]);
		mock.onGet(`/api/bsn/available-devices/${selectedLicenseGroup.id}`).reply(
			200,
			_.keyBy(
				[
					{
						id: 'A61F011000621',
						serial: 'A61F011000621',
						BSNApp: true,
						licensed: true,
						BSNTenant: '123123-000-1111',
					},
				],
				'id',
			),
		);

		mock.onGet(`/api/tenants/${selectedLicenseGroup.id}`).reply(200, {
			details: selectedLicenseGroup,
			users: _.keyBy(users, 'id'),
			pendingUsers: _.keyBy(pendingUsers, 'id'),
			userGroups: _.keyBy(userGroups, 'id'),
			devices: _.keyBy(devices, 'id'),
			deviceGroups: _.keyBy(deviceGroups, 'id'),
			appGroups: _.keyBy(appGroups, 'id'),
			printGroups: _.keyBy(printGroups, 'id'),
			inputNodes: _.keyBy(inputNodes, 'id'),
			forms: _.keyBy(forms, 'id'),
			formGroups: _.keyBy(formGroups, 'id'),
			roles: _.keyBy(roles, 'id'),
			// workflows: _.keyBy(workflows, 'id'),
			// workflowGroups: _.keyBy(workflowGroups, 'id')
			// logs: _.keyBy(logs, 'id')
		});

		mock.onGet(`/api/tenants/logs/${selectedLicenseGroup.id}`).reply(200, _.keyBy(logs, 'SK'));
		mock.onGet(`/api/device/user-devices`).reply(200, _.keyBy(mpDevices, 'id'));

		mock.onGet(`/api/user/user-tenants`).reply(200, adminedLicenseGroups);

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/preferences`).reply(() => {
			if (licenseGroupPlanDesignCheck('stratus')) {
				return [
					200,
					{
						...defaultUserPreferencesEnterprise,
						quickLinks: ['invite-users', 'create-workflow'],
					},
				];
			}
			return [204];
		});

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/app-data`).reply(200, {
			lastAcknowledgedNotificationDate: new Date('10/13/2023 10:00:30 am').getTime(),
			acknowledgedIds: notifications.map((notification) => `${notification.id}-${notification.dateUpdated}`),
		});

		mock.onGet(`/api/user/profile-signature`).reply(200, {
			signatureType: 'uploadImageSign',
			signatureActive: false,
			signatureImage: '',
			signatureText: '',
		});

		const univPrintApiUrl = getUnivPrintUrl('us-east-1');
		mock.onGet(`${univPrintApiUrl}/api/print-users/get`).reply(200, {
			userId: 'f9d77d65-3b29-41a3-adee-2411a7c57d1e',
			profileName: 'Jeff Albanese',
			profileEmail: 'jalbanese@seckmbs.onmicrosoft.com',
			profileRawId: 'fb1ff539-f0c4-46bf-ae3b-b03b12ac5f93',
		});

		mock.onPost(`${univPrintApiUrl}/api/print-devices/printer-login`).reply(200, {
			userCode: 'BY9F7Z9FH',
			deviceCode:
				'BAQABIQEAAABlMNzVhAPUTrARzfQjWPtKRXZvU3RzQXJ0aWZhY3RzAQAAAAAAtP2RzdPFdpnCsYHvnPzZTFO-ar2a5CUVkFtriympOO2RO9IhynU9f4qsuGwlAmE-yYj5UEftYXRclwXJgWQL8aPPcnETmZeZ4YHTXb3OEyLnmWN1VJx3zCPw_R5C1oO51eImHeuluYGNmF8QLC3Xm9ehj14wb5pAVp4qKYUR61ogAA',
			verificationUri: 'https://microsoft.com/devicelogin',
			message:
				'To sign in, use a web browser to open the page https://microsoft.com/devicelogin and enter the code BY9F7Z9FH to authenticate.',
		});

		mock.onGet(
			`${univPrintApiUrl}/api/print-devices/poll-tokens/BAQABIQEAAABlMNzVhAPUTrARzfQjWPtKRXZvU3RzQXJ0aWZhY3RzAQAAAAAAtP2RzdPFdpnCsYHvnPzZTFO-ar2a5CUVkFtriympOO2RO9IhynU9f4qsuGwlAmE-yYj5UEftYXRclwXJgWQL8aPPcnETmZeZ4YHTXb3OEyLnmWN1VJx3zCPw_R5C1oO51eImHeuluYGNmF8QLC3Xm9ehj14wb5pAVp4qKYUR61ogAA`,
		).reply((config) => {
			const number = Math.random();
			return [
				200,
				{
					success: number > 0.8,
					number,
				},
			];
		});

		mock.onGet(`/api/user/profile`).reply(200, userRecord);

		const logApiUrl = getLogApiUrl('us-east-1');

		mock.onGet(`${logApiUrl}/log/${selectedLicenseGroup.id}/message`).reply(200, logs);

		const workflowLogsUrl = new RegExp(
			`${logApiUrl.replace(
				/[.*+?^${}()|[\]\\]/g,
				'\\$&',
			)}/log/.*/message\\?limit=.*&from=.*&to=.*&type=workflow&id=.*`,
		);
		mock.onGet(workflowLogsUrl).reply((config) => {
			const { id: workflowId } = qs.parse(new URL(config.url!).search, { ignoreQueryPrefix: true });
			return [
				200,
				{
					Items: [
						{
							DateAdded: 1683578664753,
							SK: '2387330135247-2jcd5RiuzQ8jTKoLEBxgJL',
							Action: 'checkin',
							Type: 'workflow',
							Info: {
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxCheckIn',
							MessageTranslated: 'wfxCheckIn',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683578640081,
							SK: '2387330159919-4LUvtVdQRXPitBFgcGH3ki',
							Action: 'checkout',
							Type: 'workflow',
							Info: {
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxCheckOut',
							MessageTranslated: 'wfxCheckOut',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683578636217,
							SK: '2387330163784-ML9d9Bx3wyaDhSoBLfzoXV',
							Action: 'stop',
							Type: 'workflow',
							Info: {
								status: 'stopped',
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxUpdateStatus',
							MessageTranslated: 'wfxUpdateStatus',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683578557703,
							SK: '2387330242297-3zE2sE6yBCYRhFJuUhJgm1',
							Action: 'stop',
							Type: 'instance',
							Info: {
								fileName: '20230508T164059813690.pdf',
								filePath: 'root/STS_April23/May 23/05-08',
								nodeName: 'Google Drive Connector',
							},
							MsgKey: 'fileUploadSuccess',
							MessageTranslated: 'fileUploadSuccess',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683578553448,
							SK: '2387330246552-3vp8YJnJb13gUsHAEPhQnh',
							Action: 'start',
							Type: 'instance',
							Info: {
								fileName: '20230508T164059813690.pdf',
								filePath: 'root/STS_April23/May 23/05-08',
								nodeName: 'Google Drive Connector',
							},
							MsgKey: 'fileUploadStart',
							MessageTranslated: 'fileUploadStart',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683578551601,
							SK: '2387330248400-XaYni11s6PFDVys3PWXm1h',
							Action: 'error',
							Type: 'instance',
							Info: {
								fileName: '20230508T164059813690.pdf',
								filePath: 'TC_Test/05-03',
								nodeName: 'WebDAV Connector',
							},
							MsgKey: 'fileUploadFailOnInsufficientStorage',
							MessageTranslated: 'fileUploadFailOnInsufficientStorage',
							LogLevel: 'error',
						},
						{
							DateAdded: 1683578551448,
							SK: '2387330248553-MwsXgc2Q5P42C4ozsbqsH2',
							Action: 'start',
							Type: 'instance',
							Info: {
								fileName: '20230508T164059813690.pdf',
								filePath: 'root/TC_Test/05-03',
								nodeName: 'WebDAV Connector',
							},
							MsgKey: 'fileUploadStart',
							MessageTranslated: 'fileUploadStart',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683578520743,
							SK: '2387330279257-EdGogaB5grLwcn6GbSRFWW',
							Action: 'added',
							Type: 'instance',
							Info: {
								arn: 'c65d03e6-4546-4f33-9a2d-fe09e820c0b5',
								filename: '20230508T164059813690.pdf',
								size: 2725,
								workflowName: 'Issue_78',
							},
							MsgKey: 'fileAdded',
							MessageTranslated: 'fileAdded',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683577752000,
							SK: '2387331048000-1ipseq7EpZdjPSbyiAnM7b',
							Action: 'start',
							Type: 'workflow',
							Info: {
								status: 'running',
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'running',
								},
							},
							MsgKey: 'wfxUpdateStatus',
							MessageTranslated: 'wfxUpdateStatus',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683577746991,
							SK: '2387331053009-W3ph2WNWnFYR9K1uUBsK18',
							Action: 'checkin',
							Type: 'workflow',
							Info: {
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxCheckIn',
							MessageTranslated: 'wfxCheckIn',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683577628303,
							SK: '2387331171697-KzuCwVcrXCuWNJnZSHsk84',
							Action: 'checkout',
							Type: 'workflow',
							Info: {
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxCheckOut',
							MessageTranslated: 'wfxCheckOut',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683577618111,
							SK: '2387331181889-1WbUraJiW8WMjTMdnusU3a',
							Action: 'stop',
							Type: 'workflow',
							Info: {
								status: 'stopped',
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxUpdateStatus',
							MessageTranslated: 'wfxUpdateStatus',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576985830,
							SK: '2387331814170-4qoxg2aWxKDtHydcodCBCx',
							Action: 'error',
							Type: 'instance',
							Info: {
								fileName: '20230508T161502928775.pdf',
								filePath: 'Skaur',
								nodeName: 'WebDAV Connector',
							},
							MsgKey: 'fileUploadFailOnInsufficientStorage',
							MessageTranslated: 'fileUploadFailOnInsufficientStorage',
							LogLevel: 'error',
						},
						{
							DateAdded: 1683576985662,
							SK: '2387331814338-2djLmAe7Hhd773JA3CKGwJ',
							Action: 'start',
							Type: 'instance',
							Info: {
								fileName: '20230508T161502928775.pdf',
								filePath: 'root/Skaur',
								nodeName: 'WebDAV Connector',
							},
							MsgKey: 'fileUploadStart',
							MessageTranslated: 'fileUploadStart',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576983794,
							SK: '2387331816206-YCfS6XqKeMzyn9a9Qwu9Hi',
							Action: 'error',
							Type: 'instance',
							Info: {
								fileName: '20230508T161502928775.pdf',
								filePath: 'TC_Test/05-03',
								nodeName: 'WebDAV Connector',
							},
							MsgKey: 'fileUploadFailOnInsufficientStorage',
							MessageTranslated: 'fileUploadFailOnInsufficientStorage',
							LogLevel: 'error',
						},
						{
							DateAdded: 1683576983623,
							SK: '2387331816378-84E19kP9FTknZ6x7qWVc4k',
							Action: 'start',
							Type: 'instance',
							Info: {
								fileName: '20230508T161502928775.pdf',
								filePath: 'root/TC_Test/05-03',
								nodeName: 'WebDAV Connector',
							},
							MsgKey: 'fileUploadStart',
							MessageTranslated: 'fileUploadStart',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576963629,
							SK: '2387331836371-SD1CGNQRDcUVhNsMgCMqes',
							Action: 'added',
							Type: 'instance',
							Info: {
								arn: '1b0e0290-0084-45a8-b210-929a8303210c',
								filename: '20230508T161502928775.pdf',
								size: 2613,
								workflowName: 'Issue_78',
							},
							MsgKey: 'fileAdded',
							MessageTranslated: 'fileAdded',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576915637,
							SK: '2387331884363-8zFerDFpyfUn5a4EdmVvc7',
							Action: 'start',
							Type: 'workflow',
							Info: {
								status: 'running',
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'running',
								},
							},
							MsgKey: 'wfxUpdateStatus',
							MessageTranslated: 'wfxUpdateStatus',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576911617,
							SK: '2387331888383-Er5iAzCz2zkbPZzTtxnF8Q',
							Action: 'checkin',
							Type: 'workflow',
							Info: {
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxCheckIn',
							MessageTranslated: 'wfxCheckIn',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576885401,
							SK: '2387331914599-TpgXowSXt8JioL8CcKocv6',
							Action: 'checkout',
							Type: 'workflow',
							Info: {
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxCheckOut',
							MessageTranslated: 'wfxCheckOut',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576882887,
							SK: '2387331917113-LQst81pesyswvzSiWpwTnD',
							Action: 'stop',
							Type: 'workflow',
							Info: {
								status: 'stopped',
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxUpdateStatus',
							MessageTranslated: 'wfxUpdateStatus',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576870562,
							SK: '2387331929438-SRuusgvFsEcbc7LffXxWrm',
							Action: 'start',
							Type: 'workflow',
							Info: {
								status: 'running',
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'running',
								},
							},
							MsgKey: 'wfxUpdateStatus',
							MessageTranslated: 'wfxUpdateStatus',
							LogLevel: 'info',
						},
						{
							DateAdded: 1683576857909,
							SK: '2387331942092-31o6qMvUs7ZYzWQwje3Uua',
							Action: 'checkin',
							Type: 'workflow',
							Info: {
								user: {
									name: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
									email: 'qa@kmbs.konicaminolta.us',
									id: '6f436e4e-564a-4227-a5a1-fedaccc8f075',
								},
								workflow: {
									id: workflowId,
									name: 'Issue_78',
									status: 'stopped',
								},
							},
							MsgKey: 'wfxCheckIn',
							MessageTranslated: 'wfxCheckIn',
							LogLevel: 'info',
						},
					],
					Count: 23,
					qs: [null],
				},
			];
		});

		const metadataApiUrl = getMetadataUrl(user.awsRegion);
		mock.onPut(`${metadataApiUrl}/api/v1/user/${user.id}`).reply(200);

		// return a custom array on a get to metadata api, like: `GET 	https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/drop/all/b84813c780154de0b3a7070c25458637`
		// mock.onGet(new RegExp(`${metadataApiUrl}/api/v1/drop/all/.*`)).reply(200, {
		// 	files: [
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/06c25fc2-c633-4f88-9be1-adb2673fde40?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=d25bb49bfddf404744a0624ec36068e17d1f26ed814f1db826fa4c7b5f91f451&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/2f84bde8-3248-4e26-8ce1-773ecef06eb8?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=4c7192619982f2006a5a3c6b7330062421ebb28db4bb16315b2d420707c9af4f&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/470bb307-635e-439e-8816-c57dcff89259?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=955f3febcf7e99ef2ae03f3d17867df6abc490eeacc692d7975f31088ded8725&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/509ca932-fc0d-4a0a-9ec8-05027c347539?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=591b72fc86b463e00cee4dfda889f07fde633f1f4f6690a4bda0abe8cbc941fe&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/51dbf494-0049-478f-ab17-71286baee5ba?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=e6c70674d4984d4720231c10f9b5f492eea3e81f1d44a52e4ac8e56a35af4d02&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/b1db055a-4f5c-4b05-abbe-dcbecc8bf1bf?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=ca663a8fcd1c4578c2b1946ae1cf54f1bedb528a4b9b59339057bf4cdf30499e&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/b5643f4a-b392-4a15-ad87-62e6921694eb?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=4eb2f637eeaba2465e0199f193bfa7bec295d414cd7b11be2006f08aa58220e1&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/cdb88d53-c26a-4245-be37-093992f54103?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEY7SQWGP%2F20240202%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240202T190414Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=5a1f105e9a43a4837c041d00cac0999870eef3ff21b34b7709b467c834948616&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEJv%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIDeLMqdSsZj%2FAh523K8N%2BMKqcnNOIEMJK4UUCu9kKUPZAiEA61FyK3Xp%2FTIrDtnrURkwI%2FxAiaSQn68I54vGgkD4uykqiwQIZBAEGgwwODIwMjY1NzYwNjYiDGKR3FmZJmx85OOmcCroAxfQUvkdvuQvAaWDYAmdKFhOyXMaCYAj5DfrroWM1tVqila1fNP7%2BqPJ%2BGNIUoGyPoVyvIE%2BVkhYYNK1lVi%2Fv0dFHcbiBUAEWI%2Bu7HqF5fMoJbsoMaAZ7%2F8tliTCsAtqwXg8TcsILiHbc2aonQvpo8n8PZOxVHGEo38DETFtcrOpQGBkLdxcEoGtFeUsohojIuBslSh5Be345L5xoUqhU%2BnlSqjrOozt34MlXewjkc%2FcbT4NpFnGKaE1EXldrp6V4s19nqqLd0eeuMf0OIBkIl0WUBLD8E3KaR%2FWrBTwpuLY5EqGHPsS5duVyd6v%2BaGbZOcXfcnkgFMEytFcFJH62j5oMPvjvV8tKZvYVzwivvvlbhmtHhq%2FOHZLGa1Cx8blfaumgSpayzaNgQtzqLx7R9%2Fk2X1p%2BxEzXH1tWlrMnoAwwm7OsSveYD5LSo95X6%2BjPl93bh9FdLNsh4wXClzW8%2FZEM94WBQQ8eVGfUNvLor1pIfsKsDpt36Kbav1BFTahOjEZfT65urcGFkF2rtxtKEjo0qoA7ypnpYXkK8stKr%2FSl7WJ4S0R1tjw48VRq5LOuueWOqrtjdsmeMG5B1kYqgzQ%2FCbkNJL7Xcyjgu8oVWfmJ6ILgecgSLHFFDEB%2BO0R0alAA56siATCMMz39K0GOqUBhbpUc71gEmAOpQX1XtVZL%2FLdKZBsxoCtjRAkhHA4IRM84CB3WamptqToi62Mf8ZYInKfBPxVSzYCI3Yzz7s%2Bn%2F2ZYJLU6uV2EDvm5XmEh4bvcsiOUIo8qdTtLOFMZg8t%2FUypusoWeCSvN8xUstMm4MzeIRSrd6F1nvS2UDedjwP1yscwrt7cbDEmJnOBcmW1oHRSU5puTMF2701mCeJMQxzzsKBl',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			}
		// 		}
		// 	]
		// });

		// const mockSelectedJob = {
		// 	id: '526745a9-0f55-4bf7-a9c4-5b0535efbae8',
		// 	active: true,
		// 	name: '470bb307-635e-439e-8816-c57dcff89259|2024-02-01|18:36',
		// 	owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 	queue: '',
		// 	workflow: {
		// 		id: '2f5201ae94614c51b69b4eea1ebb939a',
		// 		name: 'DBI1'
		// 	},
		// 	accepted: true,
		// 	status: 'action',
		// 	token:
		// 		'AQCAAAAAKgAAAAMAAAAAAAAAASuRkJIq7S/DZ+2TxaTXxDv7Dj/GUUO4SYw7ceq6yGpMOCvcvs61n1EV+nSQ/KYcaW2Bqi4eWJTIY+iRTTQwcTszCB544QHrso+WiO5ka9E=V76uuuH2kARAPJqD1VGthqtYW5IEtVWRj9bmIckyoWxTMIJ2uvje1QVa16lFEPZ/TNj5xNOZ8Z7b8+B4ypwdW4I8hiUCUm3Po2Gdim+hEcn/LxQ7IkTxH+Dc36Nqs2/rR0UllOB6rQYrjVTAYaE5NdpHpBWJq8zqipRx9bMT7cFne891Kq8CNqF9sRagynMG0M4P/NHvmH2342OdDKt0ckK/pv+Gt9eeJ/tlE9Z64Qwa2sd7o5xx8U+za7BbeyDXP4n1dJgMlJaCgKBY+Acj6dl/RTk3WgkJVve32lDPpUFI3gIIfoI0g8w07VeifyJzlFBW2FU1Ae804JFFhqZtGWypEjPEAmbkfR4BhiLgaLWBz8VuVoOGTM4ZBFJ2qR97Avr1CoqujPxafoR4o/iYbkvJFXPGZ1QiN4bUbW1tpNsYaF/tk1SOgtzqw5P7q0Sokm3It/oqP81I0l0Gu2zl1txnUDyEf3t85R+sSJdzZ5+ANRyjsrZp8H0c8m3JaGq/bouxV8hq2tuvL+TPGOsy',
		// 	nodeID: 'c1dc0cea85254d18b593bdf75693c37b',
		// 	nodeNamespace: 'PeopleUser',
		// 	returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 	createdAt: 1706812570955,
		// 	updatedAt: 1706812595574,
		// 	nodeConfig: {
		// 		allowReassignment: false,
		// 		sendEmailNotification: false,
		// 		user: {
		// 			id: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			firstName: 'Pat',
		// 			lastName: 'White',
		// 			groupList: [
		// 				'00000000-1111-1111-1111-000000000000',
		// 				'00000000-2222-2222-2222-000000000000',
		// 				'60cb476d-a849-4879-81a6-d65c5a5dbecd',
		// 				'76c6c636-d773-4046-8ed8-d452aa3a9e5e'
		// 			]
		// 		},
		// 		groups: [],
		// 		nextStep: true,
		// 		workflowEngine: {
		// 			nextStepName: 'Pat White'
		// 		}
		// 	},
		// 	history: [
		// 		{
		// 			id: '1b1d345f-9943-401d-89bb-a94440009adc',
		// 			event: {
		// 				type: 'accept'
		// 			},
		// 			source: {
		// 				type: 'user',
		// 				id: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 			},
		// 			createdAt: 1706812595959
		// 		},
		// 		{
		// 			id: 'aaca76a8-cb38-42c1-ba52-d3227b51b39c',
		// 			event: {
		// 				type: 'received'
		// 			},
		// 			source: {
		// 				type: 'user',
		// 				id: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 			},
		// 			createdAt: 1706812571799
		// 		},
		// 		{
		// 			id: 'd5a70cd8-f0ad-4183-a0a4-42d3913ed7a3',
		// 			event: {
		// 				type: 'created'
		// 			},
		// 			source: {
		// 				type: 'system',
		// 				id: 'system'
		// 			},
		// 			createdAt: 1706812571694
		// 		}
		// 	],
		// 	notes: [],
		// 	attachments: [
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/470bb307-635e-439e-8816-c57dcff89259?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183559Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=402626712550e827de601d77b95c741eaedb83ae8927050e31da04f78d3343a5&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.jpg',
		// 				name: 'anastasia',
		// 				fullname: 'anastasia.jpg',
		// 				size: 437034,
		// 				dropboxin: {
		// 					ext: '.jpg',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/anastasia.jpg',
		// 					name: 'anastasia',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAABYg',
		// 					fullname: 'anastasia.jpg',
		// 					filesize: 437034
		// 				},
		// 				uuid: '470bb307-635e-439e-8816-c57dcff89259',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/51dbf494-0049-478f-ab17-71286baee5ba?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183601Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=be284ad7b60c7f4adfe47b0ecf509af7731372a9ec98b72b5f09bed2c5ee840b&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.png',
		// 				name: 'color_fullcolor',
		// 				fullname: 'color_fullcolor.png',
		// 				size: 632,
		// 				dropboxin: {
		// 					ext: '.png',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/color_fullcolor.png',
		// 					name: 'color_fullcolor',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAACag',
		// 					fullname: 'color_fullcolor.png',
		// 					filesize: 632
		// 				},
		// 				uuid: '51dbf494-0049-478f-ab17-71286baee5ba',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/b5643f4a-b392-4a15-ad87-62e6921694eb?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183602Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=f94c5d5baac70ded703512edde43ea35526a7ecb7e221a011d29e4748220c6de&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.png',
		// 				name: 'conn-logo-dropbox',
		// 				fullname: 'conn-logo-dropbox.png',
		// 				size: 3729,
		// 				dropboxin: {
		// 					ext: '.png',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/conn-logo-dropbox.png',
		// 					name: 'conn-logo-dropbox',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAACaQ',
		// 					fullname: 'conn-logo-dropbox.png',
		// 					filesize: 3729
		// 				},
		// 				uuid: 'b5643f4a-b392-4a15-ad87-62e6921694eb',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/06c25fc2-c633-4f88-9be1-adb2673fde40?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183603Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=4f38898e49522956cdbdd14824e15f8fb5736e6d008c651e206237fc7455e7d0&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.png',
		// 				name: 'conn-logo-googledrive',
		// 				fullname: 'conn-logo-googledrive.png',
		// 				size: 4767,
		// 				dropboxin: {
		// 					ext: '.png',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/conn-logo-googledrive.png',
		// 					name: 'conn-logo-googledrive',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAACaw',
		// 					fullname: 'conn-logo-googledrive.png',
		// 					filesize: 4767
		// 				},
		// 				uuid: '06c25fc2-c633-4f88-9be1-adb2673fde40',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/509ca932-fc0d-4a0a-9ec8-05027c347539?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183603Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=27dcd8267531c1204d08cc3d8ede7ca46ea7800fac8aec24906ee2956e9fb035&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.png',
		// 				name: 'download',
		// 				fullname: 'download.png',
		// 				size: 73923,
		// 				dropboxin: {
		// 					ext: '.png',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/download.png',
		// 					name: 'download',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAABKQ',
		// 					fullname: 'download.png',
		// 					filesize: 73923
		// 				},
		// 				uuid: '509ca932-fc0d-4a0a-9ec8-05027c347539',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/2f84bde8-3248-4e26-8ce1-773ecef06eb8?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183605Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=a46d937e320fb817854bd9a6f0e0e891d3e267a2b60003652a3d39bef3ae6c3f&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.jpg',
		// 				name: 'PXL_20231014_154640785',
		// 				fullname: 'PXL_20231014_154640785.jpg',
		// 				size: 1138738,
		// 				dropboxin: {
		// 					ext: '.jpg',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/PXL_20231014_154640785.jpg',
		// 					name: 'PXL_20231014_154640785',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAABYw',
		// 					fullname: 'PXL_20231014_154640785.jpg',
		// 					filesize: 1138738
		// 				},
		// 				uuid: '2f84bde8-3248-4e26-8ce1-773ecef06eb8',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/b1db055a-4f5c-4b05-abbe-dcbecc8bf1bf?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183606Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=d2bfddfa4fa5970c3a1d8f3fccf742ad5afaaaadff47df9c71247ac7ff1b1612&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.jpg',
		// 				name: 'настя',
		// 				fullname: 'настя.jpg',
		// 				size: 1184579,
		// 				dropboxin: {
		// 					ext: '.jpg',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/настя.jpg',
		// 					name: 'настя',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAACXA',
		// 					fullname: 'настя.jpg',
		// 					filesize: 1184579
		// 				},
		// 				uuid: 'b1db055a-4f5c-4b05-abbe-dcbecc8bf1bf',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		},
		// 		{
		// 			url:
		// 				'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/cdb88d53-c26a-4245-be37-093992f54103?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183607Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=38062127a997527a55180ddd054116e59fdb4bb860ad9a6d74786499e4a2d55f&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 			headers: {
		// 				'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 				'x-amz-server-side-encryption-customer-key': 'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 				'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 			},
		// 			metadata: {
		// 				ext: '.jpg',
		// 				name: 'настя1',
		// 				fullname: 'настя1.jpg',
		// 				size: 1116084,
		// 				dropboxin: {
		// 					ext: '.jpg',
		// 					date: '2024-02-01',
		// 					folder: '/2',
		// 					fullpath: '/2/настя1.jpg',
		// 					name: 'настя1',
		// 					count: 1,
		// 					id: 'id:tjzgzDIHGKsAAAAAAAACXQ',
		// 					fullname: 'настя1.jpg',
		// 					filesize: 1116084
		// 				},
		// 				uuid: 'cdb88d53-c26a-4245-be37-093992f54103',
		// 				date: 1706812569499,
		// 				uploadedBy: 'system'
		// 			}
		// 		}
		// 	],
		// 	nextSteps: [
		// 		{
		// 			node: {
		// 				id: '8e042dc9f3094c6c95c32809ee9d20ad',
		// 				name: 'Dropbox Connector',
		// 				namespace: 'Dropbox',
		// 				typename: 'DropboxNode.Models.Dropbox,DropboxNode',
		// 				version: '1',
		// 				enabled: true,
		// 				config: {
		// 					account:
		// 						'dbid:AACG-z9K6jW5hpEYmQb8sbRX1-NMLM93v1w-konicaminoltamarketplace_4b7314fc12378d7e138b3b3126a87b339643b271',
		// 					site: '',
		// 					timeout: 100,
		// 					url: 'id:tjzgzDIHGKsAAAAAAAAAeA',
		// 					customUrl: 'root/3',
		// 					checkInMessage: '',
		// 					overwrite: false,
		// 					keywords: {},
		// 					showFiles: false,
		// 					mode: 'GRID',
		// 					expanded: ['/', 'id:tjzgzDIHGKsAAAAAAAAAeA'],
		// 					breadcrumb: [
		// 						{
		// 							id: '/',
		// 							name: 'root',
		// 							type: 'folder',
		// 							childCount: 1,
		// 							children: []
		// 						},
		// 						{
		// 							id: 'id:tjzgzDIHGKsAAAAAAAAAeA',
		// 							name: '3',
		// 							path: '/3',
		// 							type: 'folder',
		// 							nextToken: '',
		// 							childCount: 1,
		// 							children: []
		// 						}
		// 					]
		// 				},
		// 				status: {
		// 					isValid: true,
		// 					errors: {
		// 						'en-us': []
		// 					}
		// 				},
		// 				options: {}
		// 			},
		// 			name: 'Dropbox Connector',
		// 			transition: 'Normal'
		// 		}
		// 	],
		// 	drop: {
		// 		password: 'sUb9RwBE8xfcHdpQ6ydHit5Ae6Fw4sfl',
		// 		id: 'b84813c780154de0b3a7070c25458637',
		// 		username: '2WL8fCAEL3TZVTIK'
		// 	}
		// };

		// const mockJobs = {
		// 	jobs: [
		// 		{
		// 			id: '02a93c13-f3cf-4b09-917a-6b0fcac96b12',
		// 			active: true,
		// 			name: '20240129T120209080061.pdf|2024-01-29|17:04',
		// 			owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			queue: '',
		// 			workflow: {
		// 				id: '9bbd9aa6fc834ee9912064a28679b72e',
		// 				name: 'Pat user node wf'
		// 			},
		// 			accepted: true,
		// 			status: 'action',
		// 			token:
		// 				'AQCAAAAAKgAAAAMAAAAAAAAAAbuv7X+Cr2MnSAy+0xWRTBYav+MIyj1jAAv/rqFN6KnlfaR4qiGfqoQx0k1UpDf7pTaawPSmXsB9m2EkUWDG8Ac/7kPY6UbbuvqlUumfXTc=eiSFoKBXhSNxWgjx++mDVO/HUv4rKHRpRP1gmAfVs02/w8qhVCFT8bMFWD/Q8WPUSQw/+x5UCdaGHDjI02IEm+OESaqcVHD9XWC5ZsxcHm02bIcLppQ8ua5xthtFy8khEFIeD5EImGVR5Yh9xwshGF4DzSmKHFp3Ep9pzRE7iF4+je3XA26kfmAOSz8d/3U2iqII0JJ3L8NNV/cuCpKq/nnzjSiLKmv4ZWTUN1btYve5NoFRikd2Fcd/WX/GupurT02mgMUwLeddHPQenfvsDR38LDwClkphTpkvqX6ZzD31UvCtivkwNdgvOAJbHljZtR7oisribvrjoOLiYXeANZbW/S8Gag8XmjIsCiRWdS8r3bw4et6lwS+XJGkGkFskjAzfAXN/cw5a9U/+gIsRCe8NnfMX7Q6/wd9eqRY2xKy0CeEmoKGnsEFMPy0l/vvX81/DC88t99a4uv3zfmpvVpS23Dc5QgHjJLlURg3Z89ZvCjtAzj4q5WRBbElcADMRZR1HHgvz2vbX97xfkVOt',
		// 			nodeID: '8fed5478b92948c9855098140ee3d226',
		// 			nodeNamespace: 'PeopleUser',
		// 			returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 			createdAt: 1706547860478,
		// 			updatedAt: 1706548755528,
		// 			history: [],
		// 			notes: [],
		// 			attachments: [
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0be2148a2a2248a6ab271e45abe7862f/20240129T120209080061.pdf?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBNA2JCC7M%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T170413Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=299590947b72ea9364d969ba75200758158500123b1f7f26eb574033974c1ac9&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEDkaCXVzLWVhc3QtMSJIMEYCIQCDx0brWCocVX4EBhg3ZuE6yf3K9jMkGX%2BsoQ%2BymbL5NAIhAIMykgty1LfuzwwrR4uWqYCWAvZPOi8xMbFpImKDME5OKpQECPL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMDgyMDI2NTc2MDY2IgzjDgPpQ1xcmaPH0I8q6ANhVJmcnnh0UXee5VAyPHsJO3%2BRbt0sjf8nZtlo241dKYdJOjNuBpa3nCjYXWt%2FI7d0sKcp6AgenbGabWso48ZCDCdnKYDV2PkjySthF3oGctGWMb%2BhGfoqi%2F8KXcepCJqNXTlW7CnvQaVpCRLjjcnJBCwiOivfz0ZZLbzMX8O2CycmiPTpix%2BQSh%2BML%2FGGoPNiYpxvnhsNtdx8d0uek0Mfo5uJHK5rIA5b8osn6oLs1mMEEAGlIbOaGwWygfk73YQikkxmFRqswAL%2Bk6OwwbG%2B75dM4C%2Bl%2Fx4SIWybS9Sbr9WWYYCZYmSistecHVryEVrP7xGWh4cvYKJxwZCxUIE0PcXrD1DlBmzv1xiqwxZapAe9pBMqQaNgW5VSGA6BUY%2Bo89d9u9WRJbp%2BaPdBxTb55WMx%2F3GVfRO%2FJHq32mv4XWoM8lvgQZFcvfa0Q%2BdWUfxtH%2FjdqAt1cz5qmY0R%2Fpx%2FeCEeBvt6KNddvMObZOUFF2cTPrgNWRNfbCrHG2jlezX88Twow9DU0H3ye1cbYRcvIj531fzOwiFKDSzp%2BBdYn9Lfxfq0jCpl4dhyMxxQvXrx2JvpPl%2BwVKs5X%2FsaUDNkoP15rfRuYjqeSW5O%2Bu%2B6YMDjiEDA6Yie6Lu7zx%2BS%2BAbcR%2F8JMfm5zTC2st%2BtBjqkAch%2BuZuooeWIo11YLuwAwsMoh6sWRf0Yw%2F7C2J7RYLRuOzbwCDdgFPhxYQTpE10RbhJIb15Z%2F%2BPqD9bBkI19zBnJqlNO8xCwqOtKTGLgVZrdwVKgYYfKGJS0ckU10CNU5edIKT7uPdxL92BYjAA7bKc1vLW5YGWS%2FauPGwoqNb%2FuKw12PYyZxieC%2Bv6SEQ0hb9mF54cC5%2Ff0cRymCNnn%2BRQqv9dh',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'Q0J4djY3QWJma2l6RWdzZFdsRkd2S2w1dlpBdlVSek0=',
		// 						'x-amz-server-side-encryption-customer-key-md5': '9W1mNAo3Yie4WeOX0fRIMg==',
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256'
		// 					},
		// 					metadata: {
		// 						date: 1706547860477,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0be2148a2a2248a6ab271e45abe7862f/toastman.jpg?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBJ274LVXB%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T171914Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=1a5a14d1ebba1e511c9eaf26c6edde9065589f6eb569e86a2e3f25bc36b09759&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEDoaCXVzLWVhc3QtMSJHMEUCIQD%2FNDLgLIcy7HXXZ37crlzb8Kg5%2FU%2FKVmCUEhTeEpXHtwIgDXTX7EKq5xEWbWOr4YHIVfjkLecJ92rN2fAvWdyhJYAqlAQI8v%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FARAEGgwwODIwMjY1NzYwNjYiDNy%2FU2pneiXWwbVJWCroA0cA%2BJ%2Bm%2BFBJygQGJeVnSinaOHkHOXiVuWYQb3brBn0%2Fz05NDYWrkkp21Ux6ENEPU7q2lHjCnnkpZx3sNFg%2FxnpnzHqtKjO%2BTSqamqwWrqOBBmOGCWa7lWGNk5JGa31x1iXV8UELxTqHsfslu0x5FWgrHpeTfPz2axmQeTVfOE6k4zWhHl5hQm9OXBSSChPBPmUF%2B8op1xeLELr44VCrWHwq6%2FQ7bXn%2FWkEL%2FCaR0gnoUVbimsqH7R5YacW2ONJPcKkkcG9lfYUfyQF6NGPabWyWz7UZwUt3YDROh7wQnhndmpG7%2FE4mzg2wrRqzlS5%2BjGpe6AHzUfXf%2FD3R54SiiCXBFEKY2jObWfWVoGG85SU5ZGkuba7WLFx54mkZA9Fc7W9zRX2TkNKyyveehCSo738WaZYfWXxSYTPzODhBU1fuOUwCULYAxG6K2LKXkQNrxrrvp3TAFSZ3HPyGD522IB%2FQBVXtbdZxpG7nVe%2Fgsec2jxLXQ6elUB%2BGbMQ6Ul%2FdqAQBIU0pi8wh5gzBEkNSv2O4%2BE2qNXSCc2UNABMJlIQvj9JVd0bM%2BO0Q%2BbZS9gSWut3PEV1IIE5pxeDOSyMTrHc%2F6uoPl1L3O5G7SV63AkHoqHC6Z3rxAoyDnv1nq9DTW9gzhN85I1DTMJq5360GOqUBvBaNodrnZVgjiktYEdhYMK800zVpmc%2Fe2ZedIsPc6R4ijn6p6urHoM%2BFm0mkg1fZmol%2BNYsojQR4DnAmfvKB4ceI4fxRx26kxgMkefmwh5wT39onEULkqZbQ9FOPBqGCYgMw9chrNYfOHaqWcELcbFnUP%2FgJKlG%2B0ra2rATCVO21%2BXU0gS%2BPVfVN1RHATvcgYx1WNOT%2Fzvu21AOOw7ny9miUfhSB',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'Q0J4djY3QWJma2l6RWdzZFdsRkd2S2w1dlpBdlVSek0=',
		// 						'x-amz-server-side-encryption-customer-key-md5': '9W1mNAo3Yie4WeOX0fRIMg=='
		// 					},
		// 					metadata: {
		// 						date: 1706548755096,
		// 						uploadedBy: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 					}
		// 				}
		// 			],
		// 			drop: {
		// 				password: 'CBxv67AbfkizEgsdWlFGvKl5vZAvURzM',
		// 				username: 'MVFTNeXSz1k0wPhK',
		// 				id: '0be2148a2a2248a6ab271e45abe7862f'
		// 			}
		// 		},
		// 		{
		// 			id: '073e94a0-9d33-4282-948e-c1fa67222dc3',
		// 			active: true,
		// 			name: '20240129T182733991067.pdf|2024-01-29|23:29',
		// 			owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			queue: '',
		// 			workflow: {
		// 				id: '9bbd9aa6fc834ee9912064a28679b72e',
		// 				name: 'Pat user node wf'
		// 			},
		// 			accepted: true,
		// 			status: 'action',
		// 			token:
		// 				'AQCAAAAAKgAAAAMAAAAAAAAAAdxnmS6v5KKzSt4K6wudlGLZ4Tt1qzOl0pVsTpcSiUpREC9AlRzG5GfMfhD+1XDeDtMVJNeY9KwvF820S3PRkr93ySljnJXwnDNLAkfEHFg=7O7IFVQsbOQSUg6tKQCqAJoddCY4Bk9wn3KYPS8rfZGMDsvN0jYqXLrCOF28/Ks7Cq0mDWA6W3bFYs296dePjDeijaMue/PMaH3P/3QpHvRpnMXXM5dMM/Sedltn66+p5k7JsRvhyzrH0tAKZ2mDW3e0HVQEXV5PvSA2nx8cnomorEdSKHQJN0H2k/oIu3LzEqio2m4BNLkqlIQALNfiO/CkUDnpTd2WqVGrFRPkqRgbGIKdr3Snbpr5Ho8mLSNcJbwcNGepqy+sI7YtyiFKIcrjh1kuvxtsSUyJWO5CPAyIFhzIZEA890XN5Wa58mTD8dSaZCa2FSUhCC05gH/Y4csAKjVuYr5e8IRoMZ1aa3LtSLZXlI0no9O8ppKGvaSfof8ZvnTPH9jtYz2gekZ6tooVw1NxkI0EM8kEMgeIAjRRqc21CEpT6JPU5DSbVmHIC5htFkRKQT60YlY9YR7XGFUztM+X4KugfyHGlKHWDmn1oiU31pFZ7rUbKiZkyzVocSMOvsdDcS3NgYCW6tHi',
		// 			nodeID: '8fed5478b92948c9855098140ee3d226',
		// 			nodeNamespace: 'PeopleUser',
		// 			returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 			createdAt: 1706570992975,
		// 			updatedAt: 1706573373430,
		// 			history: [],
		// 			notes: [],
		// 			attachments: [
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/9d82e869f5e0467bb94fbc06d5e3be7d/20240129T182733991067.pdf?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBNLFGIBPF%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T232938Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=b91a46ccfe2823b6766db9d7d400687945cc649cf58fd966736f0b51a70188d6&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEAaCXVzLWVhc3QtMSJGMEQCIDP0nDAULK6X7rL7AJMqiFpSgF51%2BpPJwS4hpo3DX7JvAiBpLi7frdk2OoLSB%2BBN%2FQL3RIWFfubFqPx9BHmAV%2BALJSqUBAj4%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F8BEAQaDDA4MjAyNjU3NjA2NiIMCBtFa8icwtsHrwujKugDyvUAzmvy%2BQkhq8bXvgSg6z3z9BmaWrngT7kqbeIYRkrPKGjfS52g2zVP3jYI42kOU%2F3M9BzjJkGVOVb%2FJAms3hhyxN45cI%2BDwOPvQ3efMWYYXuITWylp4W0xshm2j5wbPAl3GJjgUJxZPuNBTpsZ5V42BBCDyWKEEIrUQu%2FrMQDL209RBcBHu7%2BVfs6liNfrpZot7lMGp59dN2iaAgt4hJSpCD7q8gAfLKP%2BWKh4qU2SBKDB1Ky2NAuKAjbOj5nf6kgs1lUVL1Ad511ssEtucePfbJVWusUNQamUxWmK7vL6H09XR%2BhVWyusxo3y6TZwv7jjFwLT9pss2oZW2rZu%2F44kVkfkkbmzczb6n8SzvRwr8QozOrI7lO7%2F7TxBQCmFhVrRGfXRBs35Wh6ByxvL4NEyoM9Gk7zrF%2F8AKyc6Ftmsc8i6IcplMAknKzB%2B1HqGMbaW81R4nABebtP2ZRGEVFI%2FiDyZlg0Aw4S8RehtSE2U3hP7nBowspocHgE7H%2BqpKfCuRn1%2B5hqj3%2F1NG0QWaHO2wRKZgiFMFoEBy9jHvL%2BL0tmMazCgU62IZq8PWRHNwrznBEURjodMYKznYYkX5gemAz3EyToykmsMOPs7ocIAPA7UA9ndiEYtBhfcHY8WS0CibAXtVWUwruHgrQY6pgHKAMVsBrfNssMU0bogFkmsWfpC9Om6M23gNeXmBy1Co5cIzllzdjQdXs%2FBNNZVo7XERDMuLh8%2FsvpKDpN3fZJ0ryrHw45RSsQNkr7bBwO2F9kJyUjyGUHpoCwHzomeVRWWXwG05xSIcXBqG0TxlWogx3uvC6xgN8fyGO92Okl69tN93WXYcjxbCxgvq1d6yftP7cd%2FqrW6RJV9bTdF1B4ivp1%2FP8%2Fv',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-key-md5': 'DN1CdN4H+czX7yMoD3gaWA==',
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'U3kwdU5MZU1YVDA3UUJXcVpMRGowNHcxTnc0Z0tXQUg='
		// 					},
		// 					metadata: {
		// 						date: 1706570992611,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/9d82e869f5e0467bb94fbc06d5e3be7d/couch.svg?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBFPTK6QPC%2F20240130%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240130T000927Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=d011c90de5ce72e5e46fbc3511e00b2676030d30c40e489d9ebf11a762be7572&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEAaCXVzLWVhc3QtMSJHMEUCIQCi0atkiz2j2etfm0qLA%2BYFjuDu00W6SfDL45%2FDufy4LwIgY6icItIdyKzNtO69BiPQLdniDw82Vsdqu5zYYTZFHgwqlAQI%2Bf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FARAEGgwwODIwMjY1NzYwNjYiDOps5Er3kt8HDgRDDCroA4IluNMeHp0M0QEvhExwcVMR%2FzLzVNv%2BieOgN3gao2WbywPicytIsT0AShyQsN%2FyHFa48%2B2fhJ1YwnnoklUol4KEAwqbVhVPqKyT8AjFYhNS8VaOJO3ZURlSXFAJHtr9%2FijeMttaqtxRB%2B4AGwyjlCRr2iNYK2DH6tw9D0QkotI6os6XP%2BZHeUUWM9IOZChnWn6sCnS0pXV%2F0UokKEoCpfaJFOnX8u5EYL01iAMLBIpNDcKxHiQJfA%2BQAIlXZ9d3g8JsN0o5GkBFjLiaCgYOx7FNv%2FRhGK4rpHCAki8XuLcV5%2BnlH9MYeWmebQUqXCKKc2YRXRf9e%2FKpDjn%2FNCjSv2wiYIjPSkHn%2FPc0GJQTU7UwhKb4WZjc4hhdtYvi3cKxCUodWwlkh5hMUm3qUgLeUwNXaxYmvSeThcz%2Blaa5ZrcHd%2FSjaqZdK3YvajiPvROeN80LH4nwPWHHKl6xKBWTlGCUcngPgZ8e9FQybe81riYOfJo%2BIPJBHZD4UPNTCOuYtUvP6iQjeUFhXqaQVCN9vgYLkPYBOojrC0nEt81qy2GqMJd%2B1UmbsWfGF%2FCUNcCrsZwZA46g72r1O1Gw6lGr4S%2BCpDZiTpdOe927aqypsV34wviFhiT%2FZqhzs%2F7PHhlMMy5F7e%2F9GLAhMMH04K0GOqUBUAHPldHN9GKfBhHE5ErrHoHOzn%2BxvW9xCYZCmIchQHW0XWdKToYBSi9ct5GHo0yBrqCDXGwwEPLLXGSTCDI3kRK4Kdd8yW0YUq2PJwOrmMONcE7cqOtwOoShIj8p9OeMsV16Dyq36x9Qp4Bhpgehcvs3Q%2Fzpu1CPzaYnHzh3pixvhyB0tY9Gxk0hqpyQfbqvXtBAx6m5qPdr4fAya8obVA7q9Z8j',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'U3kwdU5MZU1YVDA3UUJXcVpMRGowNHcxTnc0Z0tXQUg=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'DN1CdN4H+czX7yMoD3gaWA=='
		// 					},
		// 					metadata: {
		// 						date: 1706573369248,
		// 						uploadedBy: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 					}
		// 				}
		// 			],
		// 			drop: {
		// 				password: 'Sy0uNLeMXT07QBWqZLDj04w1Nw4gKWAH',
		// 				username: 'cwTInBjuFydCpeIc',
		// 				id: '9d82e869f5e0467bb94fbc06d5e3be7d'
		// 			}
		// 		},
		// 		{
		// 			id: '09809032-a150-48f6-b519-a57ccd4b8ba8',
		// 			active: true,
		// 			name: '101a6ef9-9040-40d4-8472-f274f77545d5|2024-02-01|22:46',
		// 			owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			queue: '',
		// 			workflow: {
		// 				id: '2f5201ae94614c51b69b4eea1ebb939a',
		// 				name: 'DBI1'
		// 			},
		// 			accepted: true,
		// 			status: 'action',
		// 			token:
		// 				'AQCAAAAAKgAAAAMAAAAAAAAAARIcQ9frBtJ5KhtlHWCk0YmYsM/J5/RYgYtoz0xADuwQaYSG0lSA72ms7YGR+gEAoA46bfARAwfNLIe9/AAKu6UR4anB9CBd4Ulb6AtO/xc=twCpk7FoLuuK5MwwfeQMbdGaCI9ZKs59jpcS+engUvWpi2z1yLPQkC1zJtSFGaZQh+/z8lUG+FaDCcl/4juM+QNbILKG6ZCIj9H9RVeBYW3L2ofSjJeu71iEJq4+6Mc0zn9pKW1EDsI/2MWi65sIoO+pqHTFEV+mKwqWXOEUE1a8w3BSZO0+Jm9ozqcY3rATitQzULIKhpAUnp7VP1zveqOofjlMyzTCs5hVBbXIuOsGZyOSmTGcVZnO1RZjHXhCfmKoCWTuEUcAnov2dCPxzVvfvul515gGzAxR5b+zcPrITQwtI1Yl3pjZx9CsVc/gF7gOhhGSm3VPUtiADRaM/ckyYLce2xlLyUIwucw7clrCnn60o2w3r3XRKlLTjKlQPxjIinR9Rz1166bKdl/llOaVDkwZtPZIIs7BJ2YTPsFiLfQz2xHs2Y2uKe27YDROOBeGlSpyfLbAQB0szccxk55oVAowbDH9ZPSJ8ZZQWX32BBXBNsBlZnCzLVI08E3wMv87OPuaMRuVcupiZg50',
		// 			nodeID: 'c1dc0cea85254d18b593bdf75693c37b',
		// 			nodeNamespace: 'PeopleUser',
		// 			returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 			createdAt: 1706827563796,
		// 			updatedAt: 1706827580919,
		// 			history: [],
		// 			notes: [],
		// 			attachments: [
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/101a6ef9-9040-40d4-8472-f274f77545d5?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224553Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=a22a34cd107fd65d835faf66decb372b41c029a89de4d61b4c1de4bed4a608e1&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/eb1869a8-fbe0-4180-93fc-98a149787d7f?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224554Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=e9a2bea4a58e63faf8fa4c9c25fcafaffe30b49df574f986334f7e4836e3e465&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/47def441-003d-44cf-85ea-8d726b2078c1?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224555Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=692c9ec9f05caef232d509c664e9dc9008d98ec817ec1bdc28746e39771aaeb0&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/1a35962e-05dc-42a3-b00e-6905ccfd080b?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224556Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=e099756249964356f9a0782987bd5b7eee42614211f5fc692445ab32c724d9c1&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/2bbd0b67-313e-4999-aa28-3695fe3ca9f0?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224557Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=1ecce715546cc716e6309928721062ac9bef4a4fa97d46dc66ffdf023cf5168b&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/85fe3891-2eec-45ca-99c9-ce4f6fc3d798?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224558Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=b82aa76e5f4fbe31f4828b2b03a7920d4539fa1d261bf8ce70bea180279b9467&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/5bea4b13-f700-4c43-ba72-ddb2f6bf1e27?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224559Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=f5b0792c3162af0098a03a0fc525aa68d8817ed366e84c89dca01c5284be6a09&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/4da11f6dfcd444668326e59b15b6531f/6a11e0d7-4f77-4a13-a2ed-c2417981e114?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5NV65XO%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T224600Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=ba3a506a3ccb700b28660dbb972c410127a226858dea9194d243cdd22ec330fa&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIAdYS3KqkIA1CaEGPqaDHWN5%2FUgQt38sfOWcuQma%2B6NmAiEA9qiFpttjtmdpE3bz5GfCX3s%2BFWN3EVsow8QxqJ3vl7wqiwQIUBAEGgwwODIwMjY1NzYwNjYiDFJMrz2UWA1GN1HwtSroA4tnHTytar0VkdA8K7X6XYax9VtZ%2BXukb5bxGhl7Rw8enuIaokJTEqr2%2FznzoJ9Xyo6om56aH7DQ9sjTaONH18emBlL1BKsuCdxONyU2UymDrXQxeiWK5DNc%2B%2FV59zxhzzSNCRrlK4p11gJaTOq6zi%2BSeTrcmY15E7dArva1%2Bs0mP3iOjwsoBdeTF5SKbeOjgdeq8z606zf7nkcf2da20%2BExGnTE1JS4O5ZH3AkeGdczbejT9vtcNKxvcK9yhVpSeIWvFmQEh50M6XV5HXGy3bbXquALb1E5K1VWjv2tIEIE7zCJx0FJhwm4wSz0aIVbyesJsyVG8weHn8SraU31xRSAi1YURXwRW5L84BBrVfyN8S0ZsbQr%2Fql8cKy143dShL2DlRYTdzkF%2F2kbENzDU%2BWGLSSrrb9BLz2SAuCx3mUTvlIiDzNKbmYYHrDdR4fFD0iJrqmp0%2FZyjSZ3qPW8%2FmhHCQnYJ5r93f19l%2F8aRIWAh2kezqUi3Jc8S6%2BOD8ibT2cALC4zzKVmxTMWA8JYZqIav6hrwEGmUp8J8ZhfegJ7SeKYGh6GAl6JwgtBygEH4rR8fXVYtyUbiRGI%2BYcV0pUG90AAWWYWT%2B3Yixk4gcRsf8a194jgjYWz3zVgReLL%2BGVIVxBDGwHLMNq78K0GOqUBCtInHZHRcDV1%2Fw2nA%2BeGedy6uR8EhiHcI4OxkJtx2vfymmc4aPQkazVpUagMnp5UsKMULbwDvSNFHxFgSDwfuLQ2uV8FGSdIn2m2KF%2FqDljkwkPvvydxcitil1A4vmJjAlofu1l%2FLe6F1QHYBIACIOmq9jBrzKzCC9KM%2BBo6Lv0OJ0Tp4fBHPtSstlKedD8smI2fXbEZxYWCnZDeETNXGNDS9h7W',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'RHgxYVJ4aWw2U1FlbUY3Tkk4OXpQQW01UlgxTnhud1Q=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'CjEvdEOHOoXNpR9Vt2E+7g=='
		// 					},
		// 					metadata: {
		// 						date: 1706827562798,
		// 						uploadedBy: 'system'
		// 					}
		// 				}
		// 			],
		// 			drop: {
		// 				password: 'Dx1aRxil6SQemF7NI89zPAm5RX1NxnwT',
		// 				id: '4da11f6dfcd444668326e59b15b6531f',
		// 				username: 'wohGHNcGrhT9uG88'
		// 			}
		// 		},
		// 		{
		// 			id: '13322948-8a55-40c9-9dbe-29487e997dc4',
		// 			active: true,
		// 			name: 'fac0cecd-5143-4ef3-8858-ecd66db81ed1|2024-01-29|20:28',
		// 			owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			queue: '',
		// 			workflow: {
		// 				id: 'cdc495a5a0c84b4bbb6b5905408adc10',
		// 				name: "Josh's User Workflow"
		// 			},
		// 			accepted: true,
		// 			status: 'action',
		// 			token:
		// 				'AQCAAAAAKgAAAAMAAAAAAAAAAQ7Nn7cxrsKRymJfZJi6A0E6zbVF1OZulf3aDi4lIaQukqci3H+24rjIN/6DdkiSkktJZVCzbTjcDQdBkKbC1SptNLTk27JlJ6a+nLJwITs=d2mtDb3I8ClRKV8S8RoNZKzfPKYVaGnZLuO/EZlNphAyD7KIM1/RZQB9y8pb3PCi1xh9bcPN4I1dkrFOQdF6iQID6ES568eKJgTaoGOh2GKw/ldbUMQEftqOsPORu+KcedB85D71vlZH/wmt0zfEZVQAhnTfjVbrdP9b+757zZlJdIdx9U49xuTqywer26b956lWyHcLY22uRA/cyhb+FlLNVhAmZzgXXSKlXWYzv+5FClzGNQQKkX+HuAaRa7LhXsx74LBMXotSufnrnTeuUsUcg8ol8x6fD7Db3r/9cEjw2Iai1+eOxfyoPO0Zf+L/RUuU0XgOWhK+E1B7uR/diFLFaqKHmnJonV0JnABY6R+YMWxYTOoyZ8kyXTeBtZexpiV79LM+FrQQnyYv01C3gI7CnY87VZrfp4BJ1EIJv4mvrDBZ/8rV7G0sijGh8Hm5MiMwOyYIRdmqjfvStPMn0BkEhcolPNOTOa25dk9Wlxvh1KE1zZFly8K7QFRoCo4Yjv6R7vOUotXUSItzGiRo',
		// 			nodeID: 'a6b31abf2d684a4cb3ca4099b89bfa00',
		// 			nodeNamespace: 'PeopleUser',
		// 			returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 			createdAt: 1706560080575,
		// 			updatedAt: 1706570711997,
		// 			history: [],
		// 			notes: [],
		// 			attachments: [
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a07d39c039d84717860aef3da917d8ee/fac0cecd-5143-4ef3-8858-ecd66db81ed1?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBOP6BTE6B%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T202746Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=2e1cf18165f9c0374b7095867274cf6639f229e63ea586397e4aa585ebd5cb1c&X-Amz-Security-Token=IQoJb3JpZ2luX2VjED0aCXVzLWVhc3QtMSJGMEQCIHs9Ji35GScaTNyXRoBjVhhQQIMn98LiGc5J6aWk0FWDAiB%2Bv5lDHGY4%2FWKb0cPAltW4MTLSTzyJezerIztJ%2Bsnz6yqUBAj1%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F8BEAQaDDA4MjAyNjU3NjA2NiIMqFmYUUMOWGbUM6L%2BKugD7r8olVD3ZiCSEyOzS1YKGzCTmdEKAyuFd%2BmSTFqmkoiOCvmz1HDE8jK9iIiPpTes%2Fu2dS4b2DvZU%2BKXj%2BJfSeAG1l0E5XQqLrla7ZtFW%2F3cm6545e7Z%2BFQ6DqQm5kOk7xbbA4djcp1bd1sJbDAqi83Zw87kZfpzhJ6evPxiDs4hi2n0KEFrsdgwJw%2F1raBNlcSFlEMVgx9iKA2164vEtAf64b923xOJLAgnjywCg9SYIZpcDWpP5pXNoiRjv%2B7jeM57RuaSh4ngVXSdaYRAhcdaxkgerkUWUu2kRMLD09Tam5yEPq5MkpJ2ZXtlmMGt6dCYedClh4vHt2DUh4pgVYdz22iQDPGtGb0j9ceWJCTZMTGC7WgsvAyFzVSt3InPFYUJSfLKIQTjAzFOhly238ubsUvESwnVAnNAzbhSNbNmbs%2BHMJAKFfwryKtAiVQlcz0M7Qc792yBWMylYMHv1kWPuDEKu7%2FfDakf%2BsAYXq6SUAv3IpGYlx7eIh413rThAxnczAuUdIw1tAStBS9bQ3XYP00k04x%2Bg1C4JRA2W63tUdZ9ASFcRrMznCMbh7dTx%2FzzYvelSmevgQfzEYt%2FXoJc%2BtkM3qwo88uq97mTCrm4XgX5kSFeY3B7Nk4UXSsSA%2BodFhvuDwrYwqYvgrQY6pgFOIlnnKgWvIRD%2Fb%2BLeFGg1oExLRtVuqSYzAbbkKDEyExeC%2BsXgqqbVUME%2BifEa80BmIBPQI0Cez0QLzMEtfyEQp%2Bj3762IjWqmThUwP4Lzs%2F5iuCTwEdZYmU4llM8dneFnENDRnD4pFsJI2YgIlQh1bAHoqTdJJc2SmjX6cZHpolrLnobhBbH2NoFCND0O5WnEz2%2FXXgx5ZvNt2FuxdUVpK6d9hB%2B5',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'aURDSE9BUnRpMUVsTG81UGRoVzJIMzNvaGNMNlB5aXc=',
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'gsslJm5OKKxyFr6Chgwcow=='
		// 					},
		// 					metadata: {
		// 						date: 1706560080558,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a07d39c039d84717860aef3da917d8ee/toastman.jpg?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBOHCWDAOD%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T211951Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=22e608a923033e818e461cb1e97003002dbf66f8ae42f504410b4a35ad782fb4&X-Amz-Security-Token=IQoJb3JpZ2luX2VjED0aCXVzLWVhc3QtMSJHMEUCIQC3UzEUCG9barAtgaZ%2F%2B2Gb2EOV5lwUE6iVzeWfphJI5gIgAPFvPFQSnmuvjQpdp4g3ndgswAjLTPLsqx2HUmBCS74qlAQI9v%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FARAEGgwwODIwMjY1NzYwNjYiDEwcxD6HHszcQigvqSroA5WyA3vdHvt2lGgz4%2FW7CfEWgeMgFVM1zpKBZtNlDKQaeQW9iAaqRDgLB57sp3z3hExNKHx7NCWUlxIKxSG1apMGOjAGtnT%2FYJzeZfE%2Bca8HrAWfhixH9%2FraG4C3SKd9WY8oirOEKORxlPB0O1eg8va37jE4jiQ61EHLu3skbNENmE476iP7gCqqFPMkRoRVPUHw2A6YV3PCs1wg%2BJWSnsv4KrQcXdY48yBRIkBae2oZKyJ4YLlFufgwK%2BlvjkDCtpgE%2BJKKau3s%2FlJAh2TvrWif32wIfS8bjYB9XaVQLJEFIXAnrIiMGjPLn6WEVL9KQwpI%2FOM1GTYNO%2F2s1q7qhKD7g3%2BvD6qq6zsPLYyh4YzTQ9j8zrdjOWXyFp%2Fb9r%2FKL4uv0qXMQJA0HlwCIR9kvm%2B526IOFI0GAR%2FLZv%2FVKP2SrCO%2BCOfn8kLDl60uyEUK0Ky5kEzjOyweiSiBI4s%2BLXLikViLBTRryVrTtwrWd%2Bj1XhAFFOIkFDEHXMvLNiO5ungw1ZVUqO8ZH5oXR2xj5ykttiUBGXz6PIwvAgw638oTYOU1okoNua8b%2BaW4BBwVoU1zk91veOj1J0xPuVn8j175oyi7APE1tzj6bqu48GIqCmKnXZ9boteew8IHeOR0i%2FgRi5hE6XTPMMWk4K0GOqUB3rJ9uo%2Bm4wViigQwxMjDinrLwzx3HCbEqpbeIm99BMd7cEZtMXmf56wrYZjp2e1hoYExaGDrFUoA%2BikY1TBko70OyFU52imU3PZbKpbzWNl1n1wZOpk2y2%2BdyLGPdaAERTIZwcdvjHwoEjg8wJfaHp%2BzRwwHZHlKlA79k6KLktMQtW9OqRyy4PH%2BL2EpJ2YZ4Er8zGlF%2FQ%2BQ%2FkhnPs%2BWL8VaaWx6',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'aURDSE9BUnRpMUVsTG81UGRoVzJIMzNvaGNMNlB5aXc=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'gsslJm5OKKxyFr6Chgwcow=='
		// 					},
		// 					metadata: {
		// 						date: 1706563192049,
		// 						uploadedBy: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a07d39c039d84717860aef3da917d8ee/toastman.jpg?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBAI4PZJXW%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T212939Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=0f0352007355d7420756893837d24dd2c070961d2260611a002b4f7756ecf21a&X-Amz-Security-Token=IQoJb3JpZ2luX2VjED4aCXVzLWVhc3QtMSJIMEYCIQD7JDborm151eWFiEHFFknaTFs3K5Weo9Y27N9H6diTFgIhANS2kGHMSvnRhY8AFxac2wR9RbFseqSueUiBBqkJcdp%2BKpQECPf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMDgyMDI2NTc2MDY2Igwx8UHsaHLRnUKF86Mq6AOVXfXeg3cAIl58BqANObkpAVo2Rp%2FX74PxgIO36zalGXL8EYnf%2BiEvShl081eriYxYDkghaBGged5rBaXGx6bnRBjxM6fIt0KQ8qVMVlIgyzFkQATzTFBMC3%2BXdmKzhQjvhSfTOVB9WRE7LFQtQf0lDEuSOaDM4xzhmLfNZaej7zHdJxWy9vXBYohMTAASHmmVDU5%2BipraGT5CRNm62%2Bb7tHmJEvs%2FdiEUxrcSzu8aa8PCygNrF%2FstyGa1Yj427X54rJQYvAx7i2lkz92IzJrGkTnvrPWhM8vFcPFiFZOwjkuC28StSjSIRuCdMmfeOBVxxnU%2BF5r%2BoWNlx26oCkWZn767k6LDaryHg2algdtvtJmHxQFrGX4m9lF5q5J8FTgBscrY4xIWE7LeuC3B5aSKHq4JKesxQ5SLk17jJFYwViwZugLMWIOLrtZISwdake4n1bFgVw7Z83tVpcCJJemnu%2F8sTRmDnvo2%2BOT07hdbYzicDCxgBfkHA9gC8Ix09BWPRLJ64cef1IOCkbp%2Bk1ypSZXRGB%2BSe2AMezBR86ihPogO4cPNtTXfTtWtdnjtwkEAgDDJJUIrYgHj4AtO4mXtiiEXUf86tbHdz5ISHlRsPAoUrIMasttY%2FNtFfx0mR7OEi1Gdckud1TCHsOCtBjqkAZl%2FNEn3i6S4pFgGG4BkdqyBcixi0X14hwevrdMjOk4Fg1azF38DM98tfY46B8Wx5Dq9oK%2FZG%2Bw7ipipCcTozME9lOJiqQzwovd%2FV5m95ectNnkYyZay3p3o%2FU3y37%2BuHMugqoNK811%2FqBs%2FkoPDKbkeFCmoG%2BKuAdA9OzfDKiyQg7tF%2FHAQcl01SSvqqac6fQjeYaemPKkvTCHIAHmg24%2FDjThU',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'aURDSE9BUnRpMUVsTG81UGRoVzJIMzNvaGNMNlB5aXc=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'gsslJm5OKKxyFr6Chgwcow=='
		// 					},
		// 					metadata: {
		// 						date: 1706563780414,
		// 						uploadedBy: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a07d39c039d84717860aef3da917d8ee/toastman.jpg?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBAI4PZJXW%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T213104Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=12605aa85302cc6e514e39d8f601f5e889dc7bdbde77581706c6544e154491f3&X-Amz-Security-Token=IQoJb3JpZ2luX2VjED4aCXVzLWVhc3QtMSJIMEYCIQD7JDborm151eWFiEHFFknaTFs3K5Weo9Y27N9H6diTFgIhANS2kGHMSvnRhY8AFxac2wR9RbFseqSueUiBBqkJcdp%2BKpQECPf%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMDgyMDI2NTc2MDY2Igwx8UHsaHLRnUKF86Mq6AOVXfXeg3cAIl58BqANObkpAVo2Rp%2FX74PxgIO36zalGXL8EYnf%2BiEvShl081eriYxYDkghaBGged5rBaXGx6bnRBjxM6fIt0KQ8qVMVlIgyzFkQATzTFBMC3%2BXdmKzhQjvhSfTOVB9WRE7LFQtQf0lDEuSOaDM4xzhmLfNZaej7zHdJxWy9vXBYohMTAASHmmVDU5%2BipraGT5CRNm62%2Bb7tHmJEvs%2FdiEUxrcSzu8aa8PCygNrF%2FstyGa1Yj427X54rJQYvAx7i2lkz92IzJrGkTnvrPWhM8vFcPFiFZOwjkuC28StSjSIRuCdMmfeOBVxxnU%2BF5r%2BoWNlx26oCkWZn767k6LDaryHg2algdtvtJmHxQFrGX4m9lF5q5J8FTgBscrY4xIWE7LeuC3B5aSKHq4JKesxQ5SLk17jJFYwViwZugLMWIOLrtZISwdake4n1bFgVw7Z83tVpcCJJemnu%2F8sTRmDnvo2%2BOT07hdbYzicDCxgBfkHA9gC8Ix09BWPRLJ64cef1IOCkbp%2Bk1ypSZXRGB%2BSe2AMezBR86ihPogO4cPNtTXfTtWtdnjtwkEAgDDJJUIrYgHj4AtO4mXtiiEXUf86tbHdz5ISHlRsPAoUrIMasttY%2FNtFfx0mR7OEi1Gdckud1TCHsOCtBjqkAZl%2FNEn3i6S4pFgGG4BkdqyBcixi0X14hwevrdMjOk4Fg1azF38DM98tfY46B8Wx5Dq9oK%2FZG%2Bw7ipipCcTozME9lOJiqQzwovd%2FV5m95ectNnkYyZay3p3o%2FU3y37%2BuHMugqoNK811%2FqBs%2FkoPDKbkeFCmoG%2BKuAdA9OzfDKiyQg7tF%2FHAQcl01SSvqqac6fQjeYaemPKkvTCHIAHmg24%2FDjThU',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'aURDSE9BUnRpMUVsTG81UGRoVzJIMzNvaGNMNlB5aXc=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'gsslJm5OKKxyFr6Chgwcow=='
		// 					},
		// 					metadata: {
		// 						date: 1706563865584,
		// 						uploadedBy: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a07d39c039d84717860aef3da917d8ee/dstrat5.png?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBNLFGIBPF%2F20240129%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240129T232507Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=15c6087c156d1e81773631aab5e2c7bb01a008dd2b3d372092b92e7e12aeafda&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEAaCXVzLWVhc3QtMSJGMEQCIDP0nDAULK6X7rL7AJMqiFpSgF51%2BpPJwS4hpo3DX7JvAiBpLi7frdk2OoLSB%2BBN%2FQL3RIWFfubFqPx9BHmAV%2BALJSqUBAj4%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F8BEAQaDDA4MjAyNjU3NjA2NiIMCBtFa8icwtsHrwujKugDyvUAzmvy%2BQkhq8bXvgSg6z3z9BmaWrngT7kqbeIYRkrPKGjfS52g2zVP3jYI42kOU%2F3M9BzjJkGVOVb%2FJAms3hhyxN45cI%2BDwOPvQ3efMWYYXuITWylp4W0xshm2j5wbPAl3GJjgUJxZPuNBTpsZ5V42BBCDyWKEEIrUQu%2FrMQDL209RBcBHu7%2BVfs6liNfrpZot7lMGp59dN2iaAgt4hJSpCD7q8gAfLKP%2BWKh4qU2SBKDB1Ky2NAuKAjbOj5nf6kgs1lUVL1Ad511ssEtucePfbJVWusUNQamUxWmK7vL6H09XR%2BhVWyusxo3y6TZwv7jjFwLT9pss2oZW2rZu%2F44kVkfkkbmzczb6n8SzvRwr8QozOrI7lO7%2F7TxBQCmFhVrRGfXRBs35Wh6ByxvL4NEyoM9Gk7zrF%2F8AKyc6Ftmsc8i6IcplMAknKzB%2B1HqGMbaW81R4nABebtP2ZRGEVFI%2FiDyZlg0Aw4S8RehtSE2U3hP7nBowspocHgE7H%2BqpKfCuRn1%2B5hqj3%2F1NG0QWaHO2wRKZgiFMFoEBy9jHvL%2BL0tmMazCgU62IZq8PWRHNwrznBEURjodMYKznYYkX5gemAz3EyToykmsMOPs7ocIAPA7UA9ndiEYtBhfcHY8WS0CibAXtVWUwruHgrQY6pgHKAMVsBrfNssMU0bogFkmsWfpC9Om6M23gNeXmBy1Co5cIzllzdjQdXs%2FBNNZVo7XERDMuLh8%2FsvpKDpN3fZJ0ryrHw45RSsQNkr7bBwO2F9kJyUjyGUHpoCwHzomeVRWWXwG05xSIcXBqG0TxlWogx3uvC6xgN8fyGO92Okl69tN93WXYcjxbCxgvq1d6yftP7cd%2FqrW6RJV9bTdF1B4ivp1%2FP8%2Fv',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'aURDSE9BUnRpMUVsTG81UGRoVzJIMzNvaGNMNlB5aXc=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'gsslJm5OKKxyFr6Chgwcow=='
		// 					},
		// 					metadata: {
		// 						date: 1706570708913,
		// 						uploadedBy: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd'
		// 					}
		// 				}
		// 			],
		// 			drop: {
		// 				password: 'iDCHOARti1ElLo5PdhW2H33ohcL6Pyiw',
		// 				username: 'gdIkjnokJwThVI22',
		// 				id: 'a07d39c039d84717860aef3da917d8ee'
		// 			}
		// 		},
		// 		{
		// 			id: '2bd8f684-59e7-4582-9794-8b90c548ec36',
		// 			active: true,
		// 			name: '20240130T112512692366.pdf|2024-01-30|16:27',
		// 			owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			queue: '',
		// 			workflow: {
		// 				id: '9bbd9aa6fc834ee9912064a28679b72e',
		// 				name: 'Pat user node wf'
		// 			},
		// 			accepted: true,
		// 			status: 'action',
		// 			token:
		// 				'AQCAAAAAKgAAAAMAAAAAAAAAAZRXaJBB7gFHq1KzVigScio1nda9LIgso/OENmYe4LCrzbX/aMXrjCXg+hedSD2IRQd5UFdxLXynKczbzPONmtY3RVZz3AKIeHJhAD6pMpE=NGu+me/WJrPIYDyduK5pvN+NCpvmgMf52oQimrTw4uW23B0eMCw9e6CDJ3VGnQU2qWxD6SI+LctjgJs9cljiAe9X0m/J/sJZG84VktLnHVvv28VZGcG3HFnImf0wOmCIssj1V8onPiIR/2Sh1dLz8ZiyH5RI9sd3R/EqrnzWZcyR5HNXvb/WSyE3886SCIa5YJpxXsyN5ywt/sZP5rdkbNmQy8knQRG+C8CmObZ/LsUj5j3LRg6j61+tFiVAyjvQGu7UJ7ovu9D5E5ClNds22oZZlQFfNAD4ZSiW/cEmJwfZYahIyQnF4GzAwa9BhyUlAFg2pfXU/eaakMZzQWvLugxBThJwUmcA7LJeyTFaMO+thixSqkN2QBTLH8oOmJfILc8c/hbce8nhIhfsh/OLawfGrsCj0hjf+lphKEQwhFEbZoTxgMoXol4o3ekQh/2/WQP0Dqdvs8AF5ef5iZtMb/JdWMS1IqEW93dpstN1gHGhaBD0wsckmifRXkvq4s6YERYIZxZdtG30Am7zH9ak',
		// 			nodeID: '8fed5478b92948c9855098140ee3d226',
		// 			nodeNamespace: 'PeopleUser',
		// 			returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 			createdAt: 1706632051847,
		// 			updatedAt: 1706632717781,
		// 			history: [],
		// 			notes: [],
		// 			attachments: [
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/18e01f0c741447ec93209175d3a3d6ea/20240130T112512692366.pdf?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBNO22HO3G%2F20240130%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240130T162723Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=03910a4937460b2b21e4766b2b9dee00c18c7a82f726d310b120e8171f20c7ab&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEFEaCXVzLWVhc3QtMSJHMEUCIDrdZeaBT1kveVvUrhnSbbc7URS2bYNHhY8iZP9HPZ3XAiEAlxxG7%2FpBWDF83XsawKhh8fbVJexQyvXEPQtqjOHtcxMqiwQIGhAEGgwwODIwMjY1NzYwNjYiDDa10m7MpmZbnqpU0yroAw3A3Kk9QO0z5ZH9pek49XijXSWYnMMBCpCaT%2Fn%2FXxMxbn%2F5oSN%2BtDSXZVzMI%2BrJ5ZkzzObd3csyGJO68XlcDG2bS9vn9oP7z%2FA7DR3qUhOoU%2BOoK8GVULU95ud86XwjUH1L5c8R5FLHddlGIZCNO0oRY%2B0KB1ttxFC337mzmNOLVvNqNLffHcmmmYRHum6ojB5bRv6leDVSD5S7XnEzLxmwQGNR5k1At450Vw5T3B5sHqWvFl%2Bsdl%2B%2BEtjxMes2Dm4KS8ZrsNLyzK%2F7dqd0ddcgvxGTqXf0aVxAM8QrIRa1iLFwDKK7hf%2B4BibRnRZDjGuIME2lufqDsf55gAyaSShSxDTn8%2FxCBOXR0kxcnRTFI23fcoDJfYaYUfqmGai06n%2FSJ6BJ5jEDuAVOaYXIcp%2B%2F7esod0r8VWkbvWYyGmwo2kk6Wb1PlJ2%2B6vP1gayPvE0%2Fm9%2Fm54jNUKvwi2ckG7BdRcVCHNY9y7ABgIm1PSoAmyr%2FEBJeNNdAqlDTvEozCSbFcHoXVJMIZ1JKNH0rT%2FCSYX2lh8fCyRGXnt%2Fib9suppm6uoTFN9wflOz9%2Bw2Kimf9buC5z%2Fp6bOyE75IODoXIkufOkejuTj8K1jvtZf9fEqksd2v7fEhH2YKGvZL%2BMA%2BnrKUcsIseMPHE5K0GOqUBQxC%2BgyTaJ2QZG%2FRkZwSVra52C5r6cGwPq51tKCu85avOCJlUy4Dky4kGahqszccRPRMbr1ytFx85h2XkzfB8vgNnDz0djoPS%2BgFRwaJ6o242PV24mHDqXNxwENyk5%2B%2B0%2Fk%2BN80C7hGG5uXpZGmIgAjJAkU%2BGtxL6hLu1rbvt9HKTp%2FGMwRI0CZL3wUcgQZi9WmS7KskyAUTQmVJf6EBpeL05GNlm',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'rTtx4/YdECAVVTe0Tets3g==',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'S0Y4UGlUYmk2dE9MNkV0R25qd2tXenl1R0JJM1pZWmw='
		// 					},
		// 					metadata: {
		// 						date: 1706632051528,
		// 						uploadedBy: 'system'
		// 					}
		// 				}
		// 			],
		// 			drop: {
		// 				password: 'KF8PiTbi6tOL6EtGnjwkWzyuGBI3ZYZl',
		// 				id: '18e01f0c741447ec93209175d3a3d6ea',
		// 				username: 'Z7l3OdIkNSQ2TVXx'
		// 			}
		// 		},
		// 		{
		// 			id: '33105ae7-1471-4a8d-9d79-4f560d76470b',
		// 			active: true,
		// 			name: 'f9e49a20-4ba0-49f4-b545-71547b60f771|2024-02-01|17:51',
		// 			owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			queue: '',
		// 			workflow: {
		// 				id: '2f5201ae94614c51b69b4eea1ebb939a',
		// 				name: 'DBI1'
		// 			},
		// 			accepted: true,
		// 			status: 'action',
		// 			token:
		// 				'AQCAAAAAKgAAAAMAAAAAAAAAAe+SHa+jY8VtAPEcZSBUJYBYL0Ttkh6k46CSaU/z3QG0L09hF7Jr6tSs/4Uy3JKPvn1DxGa0SySPYH1WESsvhTFfDSxc0pElCHDdOPcMJG4=4qx5ldbBFYo41fLwAM4XyTUebfIHnFyr0Y5nyEUmq/5rCqiAovbfiNbVjZ2UOIEjBu/tW1nxDOZCL9FERsSQn7q00ZhcdMxPGy1df2vbjQ6AQIEpMuJkUyppAlPLgpGd6If6NQHxKCznGOE0UoQ954k35kMIvNHRCIxsO/xHXUDXCcpgGK4dRVleAsF4c3P5rFJ6XbOON+2M/h6u58LpK9S6VtmUNo+/ryoDzDuvleUr9Y7CVSNjCOqlaj8LoJe/A9gs5efhtHqVrWlmBUx3os0kthLwPh3lbn8nFz5ycOAODwSBnAK3GMsDF7TvhBmUH0QSlItLpN9zJONTFsINey9fdJUdBiady/dJFRfB+bhf80ArM0UZPPmexxcmkhX990dRcWoKvaTCObVZNS1kEdHg1MEce0exdVdIKGJIoBCVyWgU7B7BHG6mF0Ejua5my0khJWh+FzciUS58oijcQrS36AL9NTNzSQnEOXoSVMbWbQq/zF0YQapVdOMS/vTTJAWAYdHThY7GgAtRc7g7',
		// 			nodeID: 'c1dc0cea85254d18b593bdf75693c37b',
		// 			nodeNamespace: 'PeopleUser',
		// 			returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 			createdAt: 1706809897386,
		// 			updatedAt: 1706810163244,
		// 			history: [],
		// 			notes: [],
		// 			attachments: [
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0c312e75a9cc45a3bca91ee9f398b63c/f66bd49e-81a2-4279-99e2-69650c51300c?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEGHSHIE3%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T175125Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=e47b3389df68a961062229df99af333fec41df9eee63b2b305aeccccb97c987b&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDycCqOyWZetHrGTUdF2%2FBBh2i13d6AQMU%2FL4cv62uPrgIhAIHEuUaKKEiY%2Bjv7Gy5rzzkmkcHswJEcaWDLyyr01Z0dKosECEsQBBoMMDgyMDI2NTc2MDY2IgykEjTJRrPnrTTJyDoq6AMr80yg6hW8iBVv8FeoV5fihhV03QUtsspqQgKMT1thDKWCqXArtTvGUjkjPprENB0D7A%2FgOKAXnyTxRE8dnUmnboafYnzfhaem2lddlNPAhSeEgJT9AyH1WQNABOosUwlpeyAVO3qBLuzAlfi0w2DUZKFtHc%2B4QRg%2Fe69q%2Fd85SMixebST1sIicPP7tWcRFtguS%2FfQH%2FFilyUL2fiadEmW2pwSQ2RA2ZpkRf0EQd3OoztK8IBA1cti%2BIX84bwf0p8QJlmAENVyrRBY9DLqOKAep27IpHu1Fzpq7LenbHYLfAlIHqPXxjL82XEoNaOQm%2Bw2OSegTPI%2BIp%2B6TQM%2FR3SB4tURLRk8ad4GHVaqI0x1TWCeoVlDEwqfWO%2BME%2FOjscqqX%2BqismoT59vvpacVIyFk%2F%2FTD9tXCNorAMRE%2FJXQ4e5qtuCcq8O%2FxZCQk2pdrHtBu5UCc6%2BrqE%2Bm8u7WPfES5eBkrXBXQiCN3zH9HXsbnG%2BTbcB7I4%2B4N%2F%2BCOdrMZ4zeQ89SycbbIZdwu1oDke2aJ96olm9jbIGCBDgK6UDFrMNxtw3GjiuQODH6OhI6L27Il58bU%2BvpLWz%2FbIHFKKEs0n2iwXfdOrTQ0Rq%2B31N9Ux2N3YUbFBNpbGqPjUeBw8nYVr3Cr4CwzJzChsu%2BtBjqkAc5x1GupznX4dumRywcsbEbR2k5Mg0C3Ogs71VRSzF%2F1MxQQ8CHwR%2F7wNu48mRPm5F8oi5mZCL9Rjs%2FVoypIZwAoadcZcFttDV3YKrUng08I%2BEk0A0nCSfxxHlexdMopbsEihQ9b5NQOesFzCgwuU1i4NxPvTg8ilVc64jRZ76ZsWf4BTwf%2FW%2FYYHwb4Y%2F2excjDB8N2Ikf9sMZkfzbJldRyNaKT',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'N2NSbU51bjVtUjE2OVoyanpBd3hZSUZxM3NIZTh6dzY=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'D1r+5Dnm5RDLg6IXUuBdsA=='
		// 					},
		// 					metadata: {
		// 						date: 1706809896410,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0c312e75a9cc45a3bca91ee9f398b63c/c0cab34b-c15c-4472-ae63-7f9650c01e75?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEGHSHIE3%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T175126Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=d1a236b02c240228978d2f388442809c35eec9b3d6a042a071fb5d2413906289&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDycCqOyWZetHrGTUdF2%2FBBh2i13d6AQMU%2FL4cv62uPrgIhAIHEuUaKKEiY%2Bjv7Gy5rzzkmkcHswJEcaWDLyyr01Z0dKosECEsQBBoMMDgyMDI2NTc2MDY2IgykEjTJRrPnrTTJyDoq6AMr80yg6hW8iBVv8FeoV5fihhV03QUtsspqQgKMT1thDKWCqXArtTvGUjkjPprENB0D7A%2FgOKAXnyTxRE8dnUmnboafYnzfhaem2lddlNPAhSeEgJT9AyH1WQNABOosUwlpeyAVO3qBLuzAlfi0w2DUZKFtHc%2B4QRg%2Fe69q%2Fd85SMixebST1sIicPP7tWcRFtguS%2FfQH%2FFilyUL2fiadEmW2pwSQ2RA2ZpkRf0EQd3OoztK8IBA1cti%2BIX84bwf0p8QJlmAENVyrRBY9DLqOKAep27IpHu1Fzpq7LenbHYLfAlIHqPXxjL82XEoNaOQm%2Bw2OSegTPI%2BIp%2B6TQM%2FR3SB4tURLRk8ad4GHVaqI0x1TWCeoVlDEwqfWO%2BME%2FOjscqqX%2BqismoT59vvpacVIyFk%2F%2FTD9tXCNorAMRE%2FJXQ4e5qtuCcq8O%2FxZCQk2pdrHtBu5UCc6%2BrqE%2Bm8u7WPfES5eBkrXBXQiCN3zH9HXsbnG%2BTbcB7I4%2B4N%2F%2BCOdrMZ4zeQ89SycbbIZdwu1oDke2aJ96olm9jbIGCBDgK6UDFrMNxtw3GjiuQODH6OhI6L27Il58bU%2BvpLWz%2FbIHFKKEs0n2iwXfdOrTQ0Rq%2B31N9Ux2N3YUbFBNpbGqPjUeBw8nYVr3Cr4CwzJzChsu%2BtBjqkAc5x1GupznX4dumRywcsbEbR2k5Mg0C3Ogs71VRSzF%2F1MxQQ8CHwR%2F7wNu48mRPm5F8oi5mZCL9Rjs%2FVoypIZwAoadcZcFttDV3YKrUng08I%2BEk0A0nCSfxxHlexdMopbsEihQ9b5NQOesFzCgwuU1i4NxPvTg8ilVc64jRZ76ZsWf4BTwf%2FW%2FYYHwb4Y%2F2excjDB8N2Ikf9sMZkfzbJldRyNaKT',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'N2NSbU51bjVtUjE2OVoyanpBd3hZSUZxM3NIZTh6dzY=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'D1r+5Dnm5RDLg6IXUuBdsA=='
		// 					},
		// 					metadata: {
		// 						date: 1706809896410,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0c312e75a9cc45a3bca91ee9f398b63c/82214d9a-b6d5-4bc1-8369-f23a5b8010e1?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEGHSHIE3%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T175127Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=11188fb620420dfc19486e706026d0bf1735f7fa312b0cde5097742eeec46a00&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDycCqOyWZetHrGTUdF2%2FBBh2i13d6AQMU%2FL4cv62uPrgIhAIHEuUaKKEiY%2Bjv7Gy5rzzkmkcHswJEcaWDLyyr01Z0dKosECEsQBBoMMDgyMDI2NTc2MDY2IgykEjTJRrPnrTTJyDoq6AMr80yg6hW8iBVv8FeoV5fihhV03QUtsspqQgKMT1thDKWCqXArtTvGUjkjPprENB0D7A%2FgOKAXnyTxRE8dnUmnboafYnzfhaem2lddlNPAhSeEgJT9AyH1WQNABOosUwlpeyAVO3qBLuzAlfi0w2DUZKFtHc%2B4QRg%2Fe69q%2Fd85SMixebST1sIicPP7tWcRFtguS%2FfQH%2FFilyUL2fiadEmW2pwSQ2RA2ZpkRf0EQd3OoztK8IBA1cti%2BIX84bwf0p8QJlmAENVyrRBY9DLqOKAep27IpHu1Fzpq7LenbHYLfAlIHqPXxjL82XEoNaOQm%2Bw2OSegTPI%2BIp%2B6TQM%2FR3SB4tURLRk8ad4GHVaqI0x1TWCeoVlDEwqfWO%2BME%2FOjscqqX%2BqismoT59vvpacVIyFk%2F%2FTD9tXCNorAMRE%2FJXQ4e5qtuCcq8O%2FxZCQk2pdrHtBu5UCc6%2BrqE%2Bm8u7WPfES5eBkrXBXQiCN3zH9HXsbnG%2BTbcB7I4%2B4N%2F%2BCOdrMZ4zeQ89SycbbIZdwu1oDke2aJ96olm9jbIGCBDgK6UDFrMNxtw3GjiuQODH6OhI6L27Il58bU%2BvpLWz%2FbIHFKKEs0n2iwXfdOrTQ0Rq%2B31N9Ux2N3YUbFBNpbGqPjUeBw8nYVr3Cr4CwzJzChsu%2BtBjqkAc5x1GupznX4dumRywcsbEbR2k5Mg0C3Ogs71VRSzF%2F1MxQQ8CHwR%2F7wNu48mRPm5F8oi5mZCL9Rjs%2FVoypIZwAoadcZcFttDV3YKrUng08I%2BEk0A0nCSfxxHlexdMopbsEihQ9b5NQOesFzCgwuU1i4NxPvTg8ilVc64jRZ76ZsWf4BTwf%2FW%2FYYHwb4Y%2F2excjDB8N2Ikf9sMZkfzbJldRyNaKT',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'N2NSbU51bjVtUjE2OVoyanpBd3hZSUZxM3NIZTh6dzY=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'D1r+5Dnm5RDLg6IXUuBdsA=='
		// 					},
		// 					metadata: {
		// 						date: 1706809896410,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0c312e75a9cc45a3bca91ee9f398b63c/19cae1a7-2446-450b-a9e6-8baab8ccf595?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEGHSHIE3%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T175128Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=cdafdf3116d91c24f6aeb759e2dea7b26b2c968e06fb96c32c66a4af865b3016&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDycCqOyWZetHrGTUdF2%2FBBh2i13d6AQMU%2FL4cv62uPrgIhAIHEuUaKKEiY%2Bjv7Gy5rzzkmkcHswJEcaWDLyyr01Z0dKosECEsQBBoMMDgyMDI2NTc2MDY2IgykEjTJRrPnrTTJyDoq6AMr80yg6hW8iBVv8FeoV5fihhV03QUtsspqQgKMT1thDKWCqXArtTvGUjkjPprENB0D7A%2FgOKAXnyTxRE8dnUmnboafYnzfhaem2lddlNPAhSeEgJT9AyH1WQNABOosUwlpeyAVO3qBLuzAlfi0w2DUZKFtHc%2B4QRg%2Fe69q%2Fd85SMixebST1sIicPP7tWcRFtguS%2FfQH%2FFilyUL2fiadEmW2pwSQ2RA2ZpkRf0EQd3OoztK8IBA1cti%2BIX84bwf0p8QJlmAENVyrRBY9DLqOKAep27IpHu1Fzpq7LenbHYLfAlIHqPXxjL82XEoNaOQm%2Bw2OSegTPI%2BIp%2B6TQM%2FR3SB4tURLRk8ad4GHVaqI0x1TWCeoVlDEwqfWO%2BME%2FOjscqqX%2BqismoT59vvpacVIyFk%2F%2FTD9tXCNorAMRE%2FJXQ4e5qtuCcq8O%2FxZCQk2pdrHtBu5UCc6%2BrqE%2Bm8u7WPfES5eBkrXBXQiCN3zH9HXsbnG%2BTbcB7I4%2B4N%2F%2BCOdrMZ4zeQ89SycbbIZdwu1oDke2aJ96olm9jbIGCBDgK6UDFrMNxtw3GjiuQODH6OhI6L27Il58bU%2BvpLWz%2FbIHFKKEs0n2iwXfdOrTQ0Rq%2B31N9Ux2N3YUbFBNpbGqPjUeBw8nYVr3Cr4CwzJzChsu%2BtBjqkAc5x1GupznX4dumRywcsbEbR2k5Mg0C3Ogs71VRSzF%2F1MxQQ8CHwR%2F7wNu48mRPm5F8oi5mZCL9Rjs%2FVoypIZwAoadcZcFttDV3YKrUng08I%2BEk0A0nCSfxxHlexdMopbsEihQ9b5NQOesFzCgwuU1i4NxPvTg8ilVc64jRZ76ZsWf4BTwf%2FW%2FYYHwb4Y%2F2excjDB8N2Ikf9sMZkfzbJldRyNaKT',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'N2NSbU51bjVtUjE2OVoyanpBd3hZSUZxM3NIZTh6dzY=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'D1r+5Dnm5RDLg6IXUuBdsA=='
		// 					},
		// 					metadata: {
		// 						date: 1706809896410,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0c312e75a9cc45a3bca91ee9f398b63c/2938cc41-06e5-4aa8-96a3-fbbd20a19050?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEGHSHIE3%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T175129Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=39c448ddfc6da6d0a943dbf1127098208b9478be02578a329cc8f8e112418701&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDycCqOyWZetHrGTUdF2%2FBBh2i13d6AQMU%2FL4cv62uPrgIhAIHEuUaKKEiY%2Bjv7Gy5rzzkmkcHswJEcaWDLyyr01Z0dKosECEsQBBoMMDgyMDI2NTc2MDY2IgykEjTJRrPnrTTJyDoq6AMr80yg6hW8iBVv8FeoV5fihhV03QUtsspqQgKMT1thDKWCqXArtTvGUjkjPprENB0D7A%2FgOKAXnyTxRE8dnUmnboafYnzfhaem2lddlNPAhSeEgJT9AyH1WQNABOosUwlpeyAVO3qBLuzAlfi0w2DUZKFtHc%2B4QRg%2Fe69q%2Fd85SMixebST1sIicPP7tWcRFtguS%2FfQH%2FFilyUL2fiadEmW2pwSQ2RA2ZpkRf0EQd3OoztK8IBA1cti%2BIX84bwf0p8QJlmAENVyrRBY9DLqOKAep27IpHu1Fzpq7LenbHYLfAlIHqPXxjL82XEoNaOQm%2Bw2OSegTPI%2BIp%2B6TQM%2FR3SB4tURLRk8ad4GHVaqI0x1TWCeoVlDEwqfWO%2BME%2FOjscqqX%2BqismoT59vvpacVIyFk%2F%2FTD9tXCNorAMRE%2FJXQ4e5qtuCcq8O%2FxZCQk2pdrHtBu5UCc6%2BrqE%2Bm8u7WPfES5eBkrXBXQiCN3zH9HXsbnG%2BTbcB7I4%2B4N%2F%2BCOdrMZ4zeQ89SycbbIZdwu1oDke2aJ96olm9jbIGCBDgK6UDFrMNxtw3GjiuQODH6OhI6L27Il58bU%2BvpLWz%2FbIHFKKEs0n2iwXfdOrTQ0Rq%2B31N9Ux2N3YUbFBNpbGqPjUeBw8nYVr3Cr4CwzJzChsu%2BtBjqkAc5x1GupznX4dumRywcsbEbR2k5Mg0C3Ogs71VRSzF%2F1MxQQ8CHwR%2F7wNu48mRPm5F8oi5mZCL9Rjs%2FVoypIZwAoadcZcFttDV3YKrUng08I%2BEk0A0nCSfxxHlexdMopbsEihQ9b5NQOesFzCgwuU1i4NxPvTg8ilVc64jRZ76ZsWf4BTwf%2FW%2FYYHwb4Y%2F2excjDB8N2Ikf9sMZkfzbJldRyNaKT',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'N2NSbU51bjVtUjE2OVoyanpBd3hZSUZxM3NIZTh6dzY=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'D1r+5Dnm5RDLg6IXUuBdsA=='
		// 					},
		// 					metadata: {
		// 						date: 1706809896410,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0c312e75a9cc45a3bca91ee9f398b63c/4a2071ac-7d3c-4dcb-a442-7f6c00158a48?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEGHSHIE3%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T175131Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=6324d3a009386527f5b0611864b5f721d94ecb36c60891362e0e108e9885951b&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDycCqOyWZetHrGTUdF2%2FBBh2i13d6AQMU%2FL4cv62uPrgIhAIHEuUaKKEiY%2Bjv7Gy5rzzkmkcHswJEcaWDLyyr01Z0dKosECEsQBBoMMDgyMDI2NTc2MDY2IgykEjTJRrPnrTTJyDoq6AMr80yg6hW8iBVv8FeoV5fihhV03QUtsspqQgKMT1thDKWCqXArtTvGUjkjPprENB0D7A%2FgOKAXnyTxRE8dnUmnboafYnzfhaem2lddlNPAhSeEgJT9AyH1WQNABOosUwlpeyAVO3qBLuzAlfi0w2DUZKFtHc%2B4QRg%2Fe69q%2Fd85SMixebST1sIicPP7tWcRFtguS%2FfQH%2FFilyUL2fiadEmW2pwSQ2RA2ZpkRf0EQd3OoztK8IBA1cti%2BIX84bwf0p8QJlmAENVyrRBY9DLqOKAep27IpHu1Fzpq7LenbHYLfAlIHqPXxjL82XEoNaOQm%2Bw2OSegTPI%2BIp%2B6TQM%2FR3SB4tURLRk8ad4GHVaqI0x1TWCeoVlDEwqfWO%2BME%2FOjscqqX%2BqismoT59vvpacVIyFk%2F%2FTD9tXCNorAMRE%2FJXQ4e5qtuCcq8O%2FxZCQk2pdrHtBu5UCc6%2BrqE%2Bm8u7WPfES5eBkrXBXQiCN3zH9HXsbnG%2BTbcB7I4%2B4N%2F%2BCOdrMZ4zeQ89SycbbIZdwu1oDke2aJ96olm9jbIGCBDgK6UDFrMNxtw3GjiuQODH6OhI6L27Il58bU%2BvpLWz%2FbIHFKKEs0n2iwXfdOrTQ0Rq%2B31N9Ux2N3YUbFBNpbGqPjUeBw8nYVr3Cr4CwzJzChsu%2BtBjqkAc5x1GupznX4dumRywcsbEbR2k5Mg0C3Ogs71VRSzF%2F1MxQQ8CHwR%2F7wNu48mRPm5F8oi5mZCL9Rjs%2FVoypIZwAoadcZcFttDV3YKrUng08I%2BEk0A0nCSfxxHlexdMopbsEihQ9b5NQOesFzCgwuU1i4NxPvTg8ilVc64jRZ76ZsWf4BTwf%2FW%2FYYHwb4Y%2F2excjDB8N2Ikf9sMZkfzbJldRyNaKT',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'N2NSbU51bjVtUjE2OVoyanpBd3hZSUZxM3NIZTh6dzY=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'D1r+5Dnm5RDLg6IXUuBdsA=='
		// 					},
		// 					metadata: {
		// 						date: 1706809896410,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/0c312e75a9cc45a3bca91ee9f398b63c/2b3c83af-0864-42c1-8e89-c526128bc515?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEGHSHIE3%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T175132Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=6bd37b5dc56edef3ccba9a6fab542942acaaf1f12442504602c4350e6cd7ba8c&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDycCqOyWZetHrGTUdF2%2FBBh2i13d6AQMU%2FL4cv62uPrgIhAIHEuUaKKEiY%2Bjv7Gy5rzzkmkcHswJEcaWDLyyr01Z0dKosECEsQBBoMMDgyMDI2NTc2MDY2IgykEjTJRrPnrTTJyDoq6AMr80yg6hW8iBVv8FeoV5fihhV03QUtsspqQgKMT1thDKWCqXArtTvGUjkjPprENB0D7A%2FgOKAXnyTxRE8dnUmnboafYnzfhaem2lddlNPAhSeEgJT9AyH1WQNABOosUwlpeyAVO3qBLuzAlfi0w2DUZKFtHc%2B4QRg%2Fe69q%2Fd85SMixebST1sIicPP7tWcRFtguS%2FfQH%2FFilyUL2fiadEmW2pwSQ2RA2ZpkRf0EQd3OoztK8IBA1cti%2BIX84bwf0p8QJlmAENVyrRBY9DLqOKAep27IpHu1Fzpq7LenbHYLfAlIHqPXxjL82XEoNaOQm%2Bw2OSegTPI%2BIp%2B6TQM%2FR3SB4tURLRk8ad4GHVaqI0x1TWCeoVlDEwqfWO%2BME%2FOjscqqX%2BqismoT59vvpacVIyFk%2F%2FTD9tXCNorAMRE%2FJXQ4e5qtuCcq8O%2FxZCQk2pdrHtBu5UCc6%2BrqE%2Bm8u7WPfES5eBkrXBXQiCN3zH9HXsbnG%2BTbcB7I4%2B4N%2F%2BCOdrMZ4zeQ89SycbbIZdwu1oDke2aJ96olm9jbIGCBDgK6UDFrMNxtw3GjiuQODH6OhI6L27Il58bU%2BvpLWz%2FbIHFKKEs0n2iwXfdOrTQ0Rq%2B31N9Ux2N3YUbFBNpbGqPjUeBw8nYVr3Cr4CwzJzChsu%2BtBjqkAc5x1GupznX4dumRywcsbEbR2k5Mg0C3Ogs71VRSzF%2F1MxQQ8CHwR%2F7wNu48mRPm5F8oi5mZCL9Rjs%2FVoypIZwAoadcZcFttDV3YKrUng08I%2BEk0A0nCSfxxHlexdMopbsEihQ9b5NQOesFzCgwuU1i4NxPvTg8ilVc64jRZ76ZsWf4BTwf%2FW%2FYYHwb4Y%2F2excjDB8N2Ikf9sMZkfzbJldRyNaKT',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'N2NSbU51bjVtUjE2OVoyanpBd3hZSUZxM3NIZTh6dzY=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'D1r+5Dnm5RDLg6IXUuBdsA=='
		// 					},
		// 					metadata: {
		// 						date: 1706809896410,
		// 						uploadedBy: 'system'
		// 					}
		// 				}
		// 			],
		// 			drop: {
		// 				password: '7cRmNun5mR169Z2jzAwxYIFq3sHe8zw6',
		// 				id: '0c312e75a9cc45a3bca91ee9f398b63c',
		// 				username: 'UTKMzQR7m5PVe2yy'
		// 			}
		// 		},
		// 		{
		// 			id: '526745a9-0f55-4bf7-a9c4-5b0535efbae8',
		// 			active: true,
		// 			name: '470bb307-635e-439e-8816-c57dcff89259|2024-02-01|18:36',
		// 			owner: 'be9bdc3d-4d67-4c23-970d-4c7a432649dd',
		// 			queue: '',
		// 			workflow: {
		// 				id: '2f5201ae94614c51b69b4eea1ebb939a',
		// 				name: 'DBI1'
		// 			},
		// 			accepted: true,
		// 			status: 'action',
		// 			token:
		// 				'AQCAAAAAKgAAAAMAAAAAAAAAASuRkJIq7S/DZ+2TxaTXxDv7Dj/GUUO4SYw7ceq6yGpMOCvcvs61n1EV+nSQ/KYcaW2Bqi4eWJTIY+iRTTQwcTszCB544QHrso+WiO5ka9E=V76uuuH2kARAPJqD1VGthqtYW5IEtVWRj9bmIckyoWxTMIJ2uvje1QVa16lFEPZ/TNj5xNOZ8Z7b8+B4ypwdW4I8hiUCUm3Po2Gdim+hEcn/LxQ7IkTxH+Dc36Nqs2/rR0UllOB6rQYrjVTAYaE5NdpHpBWJq8zqipRx9bMT7cFne891Kq8CNqF9sRagynMG0M4P/NHvmH2342OdDKt0ckK/pv+Gt9eeJ/tlE9Z64Qwa2sd7o5xx8U+za7BbeyDXP4n1dJgMlJaCgKBY+Acj6dl/RTk3WgkJVve32lDPpUFI3gIIfoI0g8w07VeifyJzlFBW2FU1Ae804JFFhqZtGWypEjPEAmbkfR4BhiLgaLWBz8VuVoOGTM4ZBFJ2qR97Avr1CoqujPxafoR4o/iYbkvJFXPGZ1QiN4bUbW1tpNsYaF/tk1SOgtzqw5P7q0Sokm3It/oqP81I0l0Gu2zl1txnUDyEf3t85R+sSJdzZ5+ANRyjsrZp8H0c8m3JaGq/bouxV8hq2tuvL+TPGOsy',
		// 			nodeID: 'c1dc0cea85254d18b593bdf75693c37b',
		// 			nodeNamespace: 'PeopleUser',
		// 			returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
		// 			createdAt: 1706812570955,
		// 			updatedAt: 1706812595574,
		// 			history: [],
		// 			notes: [],
		// 			attachments: [
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/470bb307-635e-439e-8816-c57dcff89259?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183559Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=402626712550e827de601d77b95c741eaedb83ae8927050e31da04f78d3343a5&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/51dbf494-0049-478f-ab17-71286baee5ba?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183601Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=be284ad7b60c7f4adfe47b0ecf509af7731372a9ec98b72b5f09bed2c5ee840b&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/b5643f4a-b392-4a15-ad87-62e6921694eb?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183602Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=f94c5d5baac70ded703512edde43ea35526a7ecb7e221a011d29e4748220c6de&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/06c25fc2-c633-4f88-9be1-adb2673fde40?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183603Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=4f38898e49522956cdbdd14824e15f8fb5736e6d008c651e206237fc7455e7d0&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/509ca932-fc0d-4a0a-9ec8-05027c347539?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183603Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=27dcd8267531c1204d08cc3d8ede7ca46ea7800fac8aec24906ee2956e9fb035&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/2f84bde8-3248-4e26-8ce1-773ecef06eb8?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183605Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=a46d937e320fb817854bd9a6f0e0e891d3e267a2b60003652a3d39bef3ae6c3f&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/b1db055a-4f5c-4b05-abbe-dcbecc8bf1bf?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183606Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=d2bfddfa4fa5970c3a1d8f3fccf742ad5afaaaadff47df9c71247ac7ff1b1612&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				},
		// 				{
		// 					url:
		// 						'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b84813c780154de0b3a7070c25458637/cdb88d53-c26a-4245-be37-093992f54103?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBB5I2ZUM6%2F20240201%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240201T183607Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=38062127a997527a55180ddd054116e59fdb4bb860ad9a6d74786499e4a2d55f&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEIP%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDISFEaoAXeWyRhRNk6PVqbfq1DAZTSuPhZbDzghJ7bLAIhAKBXM5nY8%2BPVAEksF%2F1EGulCOFCOvF3n8J2tXZ4RQGNqKosECEwQBBoMMDgyMDI2NTc2MDY2IgxT3irOHTSKLRgNpQMq6ANmVPRkjMqH6xy0ngkTbcoUSDDxT18zzXUurBLcalOuWCx5vyZO6jTevB9coLEDzx16SALL3lrupXJsLjc5bnhv0TA%2FlQch9TcEpLz8pr3DBRH6IhvwjQNP9Kfr2Hj6BVegYIVnKvaWBCFmOtjKdTOlnYTkCLb9Bduh2rsqheA3O45QLy9O%2F8G5eqUSGxBpjPW4eWrCTqQ%2BAigy9icBtyBcPZtG2H7OKuMnaJGXpJkUIZpSF%2B%2B1C%2FZfsKxtHUmH9B7BjPeGCV3U3HcStgNuIcp%2BcVNBNOO94XeGT2N%2BT4a0MkTq5dEKPmc3vraUYV0jCGY2Z0W4m9Vd6BNb%2Bm7sUVJ%2F07k9kElxtoh862Kid57DYkadq9V9rXmFZArnbLOGLeOkmiZwXjUvceEbYjfY0LP%2BSphbEEIhVnoporkrolS4nqwyKHKLpiNvcFFj%2F13Y0cJ6X3WpAbopeEBqH0Ra4iDXykHb3yTQQ1nuOVnYtuiKflqjUzsbrWovbQjFbUm9YDg4hiAJuwk6OCZXPjONE5YWfUM9NJq4f4%2FLtCPPXsgnuEFojVC50MaQaLmzDNtVyDTMv%2FMhODSFZalh650L7i8jkuu4Y1X7kAuBEqaJr9rnWCLCu6qJDMD4G8%2BVFPlEIJO0IAcX30L%2B5jDxx%2B%2BtBjqkAY7bQWkupB%2FhQVQ7OaLOGgytbhh%2FXREREits7euZL4gdFdeKawsYTtTiy9NBoVPGTfn2CxPm9%2FIQ47ASCoFJr5zJ1vPMmEjhvH495YeWePz2ipB%2FOHx54mECrmnGh555f7p7%2BqS5mSP4BsBWQzBOglrev4N8kq3%2BiGGs%2F%2FWQYIKrbDpNDe11ODof9E7lb6xV5lcRvz3E%2BdGLlBpc9I%2Bx5VBOF0su',
		// 					headers: {
		// 						'x-amz-server-side-encryption-customer-algorithm': 'AES256',
		// 						'x-amz-server-side-encryption-customer-key':
		// 							'c1ViOVJ3QkU4eGZjSGRwUTZ5ZEhpdDVBZTZGdzRzZmw=',
		// 						'x-amz-server-side-encryption-customer-key-md5': 'ilEHUFs1Gvvxx3BIn+BPRw=='
		// 					},
		// 					metadata: {
		// 						date: 1706812569499,
		// 						uploadedBy: 'system'
		// 					}
		// 				}
		// 			],
		// 			drop: {
		// 				password: 'sUb9RwBE8xfcHdpQ6ydHit5Ae6Fw4sfl',
		// 				id: 'b84813c780154de0b3a7070c25458637',
		// 				username: '2WL8fCAEL3TZVTIK'
		// 			}
		// 		}
		// 	],
		// 	count: 7
		// };

		// mock.onGet(/\/people\/job/).reply(config => {
		// 	// Check if jobID is provided
		// 	if (config.params && config.params.jobID) {
		// 		// Find the job with the matching jobID
		// 		const job = mockJobs.jobs.find(j => j.id === config.params.jobID);

		// 		if (job) {
		// 			return [200, { job }];
		// 		}
		// 		return [404, { message: 'Job not found' }];
		// 	}

		// 	return [400, { message: 'jobID is required' }];
		// });

		// mock.onGet(/\/people\/jobs\/user/).reply((config: AxiosRequestConfig<any>) => {
		// 	return [200, mockJobs];
		// });

		mock.onGet(`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.EnterpriseMonthly}`).reply(
			200,
			{
				total: 5 * 50,
				currencyCode: 'USD',
			},
		);

		mock.onGet(
			`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.EnterpriseMonthly}&quantity=50`,
		).reply(200, {
			total: 5 * 50,
			currencyCode: 'USD',
		});

		mock.onGet(
			`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.EnterpriseMonthly}&quantity=60`,
		).reply(200, {
			total: 5 * 60,
			currencyCode: 'USD',
		});

		mock.onGet(`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.BusinessMonthly}`).reply(
			200,
			{
				total: 4 * 50,
				currencyCode: 'USD',
			},
		);

		mock.onGet(
			`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.BusinessMonthly}&quantity=50`,
		).reply(200, {
			total: 4 * 50,
			currencyCode: 'USD',
		});

		mock.onGet(
			`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.BusinessMonthly}&quantity=60`,
		).reply(200, {
			total: 4 * 60,
			currencyCode: 'USD',
		});

		mock.onGet(`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.StarterMonthly}`).reply(
			200,
			{
				total: 3 * 50,
				currencyCode: 'USD',
			},
		);

		mock.onGet(
			`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.StarterMonthly}&quantity=50`,
		).reply(200, {
			total: 3 * 50,
			currencyCode: 'USD',
		});

		mock.onGet(
			`/api/mp/catalog/pricing/${selectedLicenseGroup.id}?publicId=${PublicId.StarterMonthly}&quantity=60`,
		).reply(200, {
			total: 3 * 60,
			currencyCode: 'USD',
		});

		mock.onPatch(/\/api\/tenants\/[^/]+\/configure/).reply(200);

		// People Api Mocks
		const peopleApiUrl = `${getNodeTestingAPI(user.awsRegion)}/people`;
		mock.onGet(new RegExp(`${peopleApiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/jobs/user(\\?.*)?$`)).reply(200, {
			jobs: [
				{
					id: 'j1j1j1j1',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'workflowName:2023-10-2:2f2f3',
					owner: user.id,
					status: 'action',
					workflow: { id: 'workflow-9', name: 'Workflow 9' },
				},
				{
					id: 'j2j2j2j2',
					active: true,
					createdAt: 1699999179,
					updatedAt: 1699999179,
					name: 'workflow2:2023-10-2:abcd',
					owner: user.id,
					status: 'action',
					workflow: { id: 'workflow-11', name: 'Workflow 11' },
				},
				{
					id: 'j4j4j4j4',
					active: true,
					createdAt: 1699999179,
					updatedAt: 1699999179,
					name: 'MyWorkflow2:2023-11-19:ss2',
					owner: user.id,
					status: 'action',
					workflow: { id: 'workflow-11', name: 'Workflow 11' },
				},
				{
					id: '753a60ee-1f4d-45e1-81c8-a34c71c05426',
					active: true,
					name: "Sara's People Workflow:2024-0-2:05426",
					owner: user.id,
					queue: '',
					workflow: '6abb4f3ccc174cfc86b352c9fc6c30e8',
					accepted: false,
					status: 'action',
					token: 'AQCAAAAAKgAAAAMAAAAAAAAAAaTl3uRTPt5w3WSbOpexIqhf/JlUsPZ+Tb7fs/bOGFfQTb61OSUm8tFRlJi8CVyshZNIZrhc9eLsHbDfU/AAD3fq2p/VEQduSiJP/ltri+M=yFrnWabwo2p3OZ+VS0eykyHueXDzmLf9tS6x3fPTq2faH89aW7X6lUjS/nP8if9N9ngzYZi7CaxYVUCkcNfyglTbGWccgGZ/O1zmWXs7I+zPxuc5eHOaTSWVJopP1nFfT25XTSMCDyaVxEcGP4VLfDokaGjFCxJ3MuM3CnnEqjiUyOB3ky6QMvWKzeQDEVUBt5hN5tFYdIAW+bl3nv/Sp5vqO9QJTCPlMKB7sdzJHOmOMz0RN8cwba5nOqAr1VconmGlZNrS6hD2UxUBqvbPYYfc2pEACMF9UR/CsIbohrZxydGH4fhhC0Mknv/CLc17c5AWpIPrQnem8EqOyQWzZjQdr8VtA1cgACbWviRDL/Snpz1Mw+sMUSTJkJE4JrV9FQleV0GP8eDNG/oAqGx+vDNR0IOS1s6OJf2tnGH4VCIUolj8QHpaYCPGnYmXHK+tAEVwb4nOkai4d2kymJXc6cjrWc9kw9xyjJhYGHAHGA+fdYmsok+gsFm2dJAIes5K7vNEFtA8RvWTrTghjEwv',
					nodeID: 'c398f1c0f9214a73ada32facc0d240b3',
					nodeNamespace: 'PeopleGroup',
					returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
					groupID: '60cb476d-a849-4879-81a6-d65c5a5dbecd',
					createdAt: 1704836165836,
					updatedAt: 1704836165836,
				},
			],
			count: 4,
		});

		mock.onGet(new RegExp(`${peopleApiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/archive/user(\\?.*)?$`)).reply(
			200,
			{
				archives: [
					{
						Job: {
							Status: 'action',
							Owner: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5#ACCEPTED',
							PeopleJob: true,
							WorkflowName: '11-19-test-jh-mock',
							CreatedAt: 1763580363114,
							Workflow: '46d5d97de78a46a7b891b1cb70aff385',
							ReturnURL: 'https://us-east-1.ntf.stratus.lol/report-end',
							Token: 'AQCAAAAAKgAAAAMAAAAAAAAAAQJPMYefr98RF+GI9unE2K1nsQUFwKseF5L1/UHQizJ2s7C8OlAjg80sSBZSjoUM8sHOYDt4uIw5Zle41wSKLr7yf6IvE8wB9oUKv8qa4oo=bFccm3NIo4yNCA1QkDvarNSX2AKH6h4qSIGq5d75nUKKkEYFyPGF2iTg0FKgHPRG+xKYfWE5G/RTztNs3WgdVGTY4S8c/ZlvP4jLvjiDr3tIyQ7m6Shq1ohqnhJmHJWcG/lremVXZQzxp9nCFNXb0Gw9sNTelHJ66cO63MhJSKF4Rb0UGh95ltqzRx/x5+ee3MX2lTOMNg8QEmAMdXP1Xe7q1EmlTF8e6anszobc+BQpucTL1zVZqRUsAC/8u4ctvhR0zTpVJS/tiVSnaH4UGlVoaYQCFiaKoUy2H1x1jwVMY9DcTgM+Nyc+AE2SjMHs/v5hFDwXuPOBjTF53KWnaG2j/4e9ZrThYmPSHfnTXbqgb8Fqs0juvPm2FEDXTHaqL7iM3SfiePyiTQSIF9XNoHNaYK6nzo54Wa38vWyOpJiUMd9jNGYfZCqqNWIGDuvzefZ+N4ylDdosmPpuybaaH+BCi54t6tx91HCj1yvF6c2vJEYA4gfr14gNq2ytVEfUltmsDLR2hEPlTC4LvRr6',
							OwnerData: {
								lastName: 'Hubert',
								nonMFPLicenseCapacity: 117,
								licensed: true,
								hidden: false,
								nonMFPLicenseQuantity: 10,
								roles: ['6197657e-0149-4f79-bbdd-6ff94503768e', 'f32744a5-61c6-4bde-bb08-5b1f476181b8'],
								pinNumber: '710877',
								groupList: [
									'00000000-1111-1111-1111-000000000000',
									'00000000-2222-2222-2222-000000000000',
									'00000000-3333-3333-3333-000000000000',
									'078853b0-0a3e-4934-bbed-7f2730479749',
									'76c6c636-d773-4046-8ed8-d452aa3a9e5e',
								],
								badgeNumber: '1614624906760818347',
								userName: 'konicaminoltamarketplace_9421e76ce24bc746ded650aec34fab916c64325f',
								dateAdded: 1696615859083,
								firstName: 'James',
								id: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								email: 'jhubert@kmbs.konicaminolta.us',
								ippNumber: '',
								status: 'ACCEPTED',
							},
							Participants: [],
							UpdatedAt: 1763744235404,
							NodeNamespace: 'PeopleUser',
							Drop: {
								password: 'l48uFxLXfHM24VaUvZ3bdi8AJcfVTTpb',
								id: '34fa4feb409f4c23886c2f00cc187409',
								username: '3qBnaTpFQ2wPMxQt',
							},
							Archived: true,
							Name: 'Internal Form|2025-11-19|14:26:02',
							Active: true,
							NodeID: '84d34db8125e46bd9fc2e30a7ec98215',
							RetentionSettings: {
								metadata: true,
								attachments: true,
							},
							PreviousNodeID: '5a143ab5e8334452b80b72585be72748',
							ExpiresAt: 1795553124,
							TrashedArchive: false,
						},
						ExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						MaxExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						Notes: [
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PRIVATE#780b80c0-f11a-4fd9-8c3b-c726ebc95316',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102585442,
								Name: 'private wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c91',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
						],
						Files: [
							{
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a8ba060d8b864d8585f8a45b5053ec5f/264242e0-df29-46f6-a8af-ca46712f047f?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBMGVSR5I5%2F20251121%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20251121T165711Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=7a2ac05fad405db34d52854afb74ac527346a61a0e08eca85ce7fedd12b6529a&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEkaCXVzLWVhc3QtMSJHMEUCIB3vsQUtbI0R8WVEaw0Nhu17H11VWuZlnO9vb16dLt0%2FAiEA3QRaZpJsd9f3q3BWJHCq9SNdhe9zXMcVet3mWil66isqiwQIEhAEGgwwODIwMjY1NzYwNjYiDN3xipb2baDUG7fYAiroAxrmMAFSXz%2FQkBWOvtjnH0uyviXttgRfZdBMO9hAwCjKbH9C1mF75%2F4VEa2BwYBVVoEBGe4zUzRxCrJ1J0ZPTXrcc%2FCeLFVu00aI8eUTqxshTp%2BhHOC5ZBH7C4LoSry%2BEns0ezpU08K1Yxd1jfdjtJJyvZvRiayP%2BxLRg3Gd38%2FhlO5hdxz5th2qvXAcYXuCujamCcP39%2Bze12F6U89Lq37rarK7W24qyJ8MMZzV%2BLbNcOqto465qGZr179%2F6WzbPzX4ZJNnhZYTBEhid9l8hMRW%2F757whHuOLiNp026L0R5%2FiW2iP9dxNmITsPHbSaA83TGIj9qVWFcI4%2FqI%2F4JFyytB519FLAF1%2FU0k5EGb1R3iXQt51hbsZJnrDU%2BbWKkUm8LB0yvSBr%2FTJDIKMX%2Fzprus4pGMLqurWc68B%2Bfu9t2zEzYYJT4MZP23KvJ4KH8pgDwEnOZfAYHXv6u0gL2Kqwy1MJrK4VWTPzlDUP72YQCaT%2Bk50atBz1zyH09yZGHNSseX50DbRx4JiyJ3o5lD%2FXimcEMR9WlIyKSQVzNcGgSOpwxkFBMzfojWCa8gbubU5%2FE0jYL32vqVAB2kLLO%2FsP4VkjPCfKs%2B27OhAm7pBVIs3ENS76xS28lwzy9Oz9ZyLp51FcOua29MKWugskGOqUB3Kjecnb9yL9lyDejl4KCjwtHB0NGCYo0tDZfmDDGjXmZXjtG1njbj6BCUk3nBL6LE%2B8RGXIrqy6EvfkgV6vN%2F%2BGC43E05kFmyz3HLnTTHH2fvgHcn7lzLWEb0WgaRhxK4WRGy79sv90aGRy89r%2FxN2vDXevn4a%2B6rb6vkD%2BmUWl5bZc8SGiJ92Fb3tatNYhgy9fAEDFFD0TihyS6Subrw25zo8lO',
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'WlpxUXJFTHpPZ2hvUVJTRHh5YlViTWdObmJBakk5N2Y=',
									'x-amz-server-side-encryption-customer-key-md5': 'uUAoyqQ+8eNdXM8MT+xBjQ==',
								},
								metadata: {
									uploadedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									date: 1763744235000,
									ext: '.pdf',
									fullname:
										'some ridiculously long name that is ridiculous and long and riduclous.pdf',
									name: 'sample-document',
									size: 245678,
									uuid: '264242e0-df29-46f6-a8af-ca46712f047f',
								},
							},
						],
						PK: 'T:a67d94ba-69fa-5348-9a2d-220e02019d2c',
						Metadata: {
							sk: '46d5d97de78a46a7b891b1cb70aff385',
							itemType: 'PROCESS',
							pk: 'adf1813a-a21b-494c-b165-68113a69fd0f',
							data: {
								formsubmission: {
									formId: 'c788b930782e415aa40732c924caf600',
									submittedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									submittedAt: 1763580356468,
									'.type': 'formsubmission',
								},
								form: [
									{
										dropdown1: 'jethridge@kmbs.konicaminolta.us',
										'.id': 'c788b930782e415aa40732c924caf600',
										number1:
											'123123123456789123456789123456789123456789123123123456789123456789123456789123456789',
										textarea1: '123123',
										text1: '123123',
										'.type': 'form',
										'tool!exclude': 'hello world',
										file1: '110011001',
										'date-ISO': 'Thursday',
									},
									{
										dropdown1: 'jethridge@kmbs.konicaminolta.us',
										'.id': 'c788b930782e415aa40732c924caf600',
										number1: '123123',
										textarea1: '123123',
										text1: '123123',
										'.type': 'form',
									},
								],
								internalform: {
									formId: 'c788b930782e415aa40732c924caf600',
									formUrl:
										'https://james-01-test.tenant.stratus.lol/admin/internalform/us-east-1/46d5d97de78a46a7b891b1cb70aff385-5a143ab5e8334452b80b72585be72748',
									formTitle: 'Josh email out test',
								},
								files: '264242e0-df29-46f6-a8af-ca46712f047f',
								formlayout: [
									{
										'.type': 'formlayout',
										id: 'c788b930782e415aa40732c924caf600',
										formDefinition: {
											config: {
												styling: {},
											},
										},
									},
								],
								formsavedstate: {
									formId: 'c788b930782e415aa40732c924caf600',
									onChange: '',
									'.type': 'formsavedstate',
									setValue: 'dropdown1,text1,textarea1,number1',
								},
								job: {
									name: 'Internal Form|2025-11-19|14:26:02',
									'.type': 'job',
								},
							},
							created: 1763580357,
							status: 'no status',
						},
						JobID: 'adf1813a-a21b-494c-b165-68113a69fd0f',
						SK: 'AR:adf1813a-a21b-494c-b165-68113a69fd0f',
						UserData: {
							'0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5': {
								firstName: 'James',
								email: 'jhubert@kmbs.konicaminolta.us',
							},
						},
						TrashedArchive: false,
						ACL: ['0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5'],
						RetentionSettings: {
							metadata: true,
							attachments: true,
						},
						History: [
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'received',
								CreatedAt: 1763580377046,
							},
							{
								Data: {},
								Source: 'system',
								Name: 'created',
								CreatedAt: 1763580361704,
							},
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'received',
								CreatedAt: 1763580363186,
							},
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'fileAttached',
								CreatedAt: 1763744235428,
							},
						],
					},
					{
						Job: {
							Status: 'action',
							Owner: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5#ACCEPTED',
							PeopleJob: true,
							WorkflowName: '11-19-test-jh-mock',
							CreatedAt: 1763580363114,
							Workflow: '46d5d97de78a46a7b891b1cb70aff385',
							ReturnURL: 'https://us-east-1.ntf.stratus.lol/report-end',
							Token: 'AQCAAAAAKgAAAAMAAAAAAAAAAQJPMYefr98RF+GI9unE2K1nsQUFwKseF5L1/UHQizJ2s7C8OlAjg80sSBZSjoUM8sHOYDt4uIw5Zle41wSKLr7yf6IvE8wB9oUKv8qa4oo=bFccm3NIo4yNCA1QkDvarNSX2AKH6h4qSIGq5d75nUKKkEYFyPGF2iTg0FKgHPRG+xKYfWE5G/RTztNs3WgdVGTY4S8c/ZlvP4jLvjiDr3tIyQ7m6Shq1ohqnhJmHJWcG/lremVXZQzxp9nCFNXb0Gw9sNTelHJ66cO63MhJSKF4Rb0UGh95ltqzRx/x5+ee3MX2lTOMNg8QEmAMdXP1Xe7q1EmlTF8e6anszobc+BQpucTL1zVZqRUsAC/8u4ctvhR0zTpVJS/tiVSnaH4UGlVoaYQCFiaKoUy2H1x1jwVMY9DcTgM+Nyc+AE2SjMHs/v5hFDwXuPOBjTF53KWnaG2j/4e9ZrThYmPSHfnTXbqgb8Fqs0juvPm2FEDXTHaqL7iM3SfiePyiTQSIF9XNoHNaYK6nzo54Wa38vWyOpJiUMd9jNGYfZCqqNWIGDuvzefZ+N4ylDdosmPpuybaaH+BCi54t6tx91HCj1yvF6c2vJEYA4gfr14gNq2ytVEfUltmsDLR2hEPlTC4LvRr6',
							OwnerData: {
								lastName: 'Hubert',
								nonMFPLicenseCapacity: 117,
								licensed: true,
								hidden: false,
								nonMFPLicenseQuantity: 10,
								roles: ['6197657e-0149-4f79-bbdd-6ff94503768e', 'f32744a5-61c6-4bde-bb08-5b1f476181b8'],
								pinNumber: '710877',
								groupList: [
									'00000000-1111-1111-1111-000000000000',
									'00000000-2222-2222-2222-000000000000',
									'00000000-3333-3333-3333-000000000000',
									'078853b0-0a3e-4934-bbed-7f2730479749',
									'76c6c636-d773-4046-8ed8-d452aa3a9e5e',
								],
								badgeNumber: '1614624906760818347',
								userName: 'konicaminoltamarketplace_9421e76ce24bc746ded650aec34fab916c64325f',
								dateAdded: 1696615859083,
								firstName: 'James',
								id: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								email: 'jhubert@kmbs.konicaminolta.us',
								ippNumber: '',
								status: 'ACCEPTED',
							},
							Participants: [],
							UpdatedAt: 1763744235404,
							NodeNamespace: 'PeopleUser',
							Drop: {
								password: 'l48uFxLXfHM24VaUvZ3bdi8AJcfVTTpb',
								id: '34fa4feb409f4c23886c2f00cc187409',
								username: '3qBnaTpFQ2wPMxQt',
							},
							Archived: true,
							Name: 'Internal Form|2025-11-20|14:26:03|long',
							Active: true,
							NodeID: '84d34db8125e46bd9fc2e30a7ec98215',
							RetentionSettings: {
								metadata: true,
								attachments: true,
							},
							PreviousNodeID: '5a143ab5e8334452b80b72585be72748',
							ExpiresAt: 1795553124,
							TrashedArchive: false,
						},
						ExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						MaxExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						Notes: [
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PRIVATE#780b80c0-f11a-4fd9-8c3b-c726ebc95316',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102585442,
								Name: 'private wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c15',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c16',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c17',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c18',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c19',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c20',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c21',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c22',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c23',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c24',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c25',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c26',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c27',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c28',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c29',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c30',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c31',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c32',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c33',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c34',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c35',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c35',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c36',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c37',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c38',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c39',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c40',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c41',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c42',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c43',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c44',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c45',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c46',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c47',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c48',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c49',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c50',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c51',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c52',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c53',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c54',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c55',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c56',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c57',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c58',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c59',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c60',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c61',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c62',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c63',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c64',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
						],
						Files: [
							{
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a8ba060d8b864d8585f8a45b5053ec5f/264242e0-df29-46f6-a8af-ca46712f047f?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBMGVSR5I5%2F20251121%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20251121T165711Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=7a2ac05fad405db34d52854afb74ac527346a61a0e08eca85ce7fedd12b6529a&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEkaCXVzLWVhc3QtMSJHMEUCIB3vsQUtbI0R8WVEaw0Nhu17H11VWuZlnO9vb16dLt0%2FAiEA3QRaZpJsd9f3q3BWJHCq9SNdhe9zXMcVet3mWil66isqiwQIEhAEGgwwODIwMjY1NzYwNjYiDN3xipb2baDUG7fYAiroAxrmMAFSXz%2FQkBWOvtjnH0uyviXttgRfZdBMO9hAwCjKbH9C1mF75%2F4VEa2BwYBVVoEBGe4zUzRxCrJ1J0ZPTXrcc%2FCeLFVu00aI8eUTqxshTp%2BhHOC5ZBH7C4LoSry%2BEns0ezpU08K1Yxd1jfdjtJJyvZvRiayP%2BxLRg3Gd38%2FhlO5hdxz5th2qvXAcYXuCujamCcP39%2Bze12F6U89Lq37rarK7W24qyJ8MMZzV%2BLbNcOqto465qGZr179%2F6WzbPzX4ZJNnhZYTBEhid9l8hMRW%2F757whHuOLiNp026L0R5%2FiW2iP9dxNmITsPHbSaA83TGIj9qVWFcI4%2FqI%2F4JFyytB519FLAF1%2FU0k5EGb1R3iXQt51hbsZJnrDU%2BbWKkUm8LB0yvSBr%2FTJDIKMX%2Fzprus4pGMLqurWc68B%2Bfu9t2zEzYYJT4MZP23KvJ4KH8pgDwEnOZfAYHXv6u0gL2Kqwy1MJrK4VWTPzlDUP72YQCaT%2Bk50atBz1zyH09yZGHNSseX50DbRx4JiyJ3o5lD%2FXimcEMR9WlIyKSQVzNcGgSOpwxkFBMzfojWCa8gbubU5%2FE0jYL32vqVAB2kLLO%2FsP4VkjPCfKs%2B27OhAm7pBVIs3ENS76xS28lwzy9Oz9ZyLp51FcOua29MKWugskGOqUB3Kjecnb9yL9lyDejl4KCjwtHB0NGCYo0tDZfmDDGjXmZXjtG1njbj6BCUk3nBL6LE%2B8RGXIrqy6EvfkgV6vN%2F%2BGC43E05kFmyz3HLnTTHH2fvgHcn7lzLWEb0WgaRhxK4WRGy79sv90aGRy89r%2FxN2vDXevn4a%2B6rb6vkD%2BmUWl5bZc8SGiJ92Fb3tatNYhgy9fAEDFFD0TihyS6Subrw25zo8lO',
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'WlpxUXJFTHpPZ2hvUVJTRHh5YlViTWdObmJBakk5N2Y=',
									'x-amz-server-side-encryption-customer-key-md5': 'uUAoyqQ+8eNdXM8MT+xBjQ==',
								},
								metadata: {
									uploadedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									date: 1763744235000,
									ext: '.pdf',
									fullname:
										'some ridiculously long name that is ridiculous and long and riduclous.pdf',
									name: 'sample-document',
									size: 245678,
									uuid: '264242e0-df29-46f6-a8af-ca46712f047f',
								},
							},
						],
						PK: 'T:a67d94ba-69fa-5348-9a2d-220e02019d2c',
						Metadata: {
							sk: '46d5d97de78a46a7b891b1cb70aff385',
							itemType: 'PROCESS',
							pk: 'adf1813a-a21b-494c-b165-68113a69fd02',
							data: {
								formsubmission: {
									formId: 'c788b930782e415aa40732c924caf600',
									submittedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									submittedAt: 1763580356468,
									'.type': 'formsubmission',
								},
								form: [
									{
										dropdown1: 'jethridge@kmbs.konicaminolta.us',
										'.id': 'c788b930782e415aa40732c924caf600',
										number1:
											'123123123456789123456789123456789123456789123123123456789123456789123456789123456789',
										textarea1: '123123',
										text1: '123123',
										'.type': 'form',
										'tool!exclude': 'hello world',
										file1: '110011001',
										'date-ISO': 'Thursday',
									},
									{
										dropdown1: 'jethridge@kmbs.konicaminolta.us',
										'.id': 'c788b930782e415aa40732c924caf600',
										number1: '123123',
										textarea1: '123123',
										text1: '123123',
										'.type': 'form',
									},
								],
								internalform: {
									formId: 'c788b930782e415aa40732c924caf600',
									formUrl:
										'https://james-01-test.tenant.stratus.lol/admin/internalform/us-east-1/46d5d97de78a46a7b891b1cb70aff385-5a143ab5e8334452b80b72585be72748',
									formTitle: 'Josh email out test',
								},
								files: '264242e0-df29-46f6-a8af-ca46712f047f',
								formlayout: [
									{
										'.type': 'formlayout',
										id: 'c788b930782e415aa40732c924caf600',
										formDefinition: {
											config: {
												styling: {},
											},
										},
									},
								],
								formsavedstate: {
									formId: 'c788b930782e415aa40732c924caf600',
									onChange: '',
									'.type': 'formsavedstate',
									setValue: 'dropdown1,text1,textarea1,number1',
								},
								job: {
									name: 'Internal Form|2025-11-20|14:26:02|long',
									'.type': 'job',
								},
							},
							created: 1763580357,
							status: 'no status',
						},
						JobID: 'adf1813a-a21b-494c-b165-68113a69fd02',
						SK: 'AR:adf1813a-a21b-494c-b165-68113a69fd02',
						UserData: {
							'0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5': {
								firstName: 'James',
								email: 'jhubert@kmbs.konicaminolta.us',
							},
						},
						TrashedArchive: false,
						ACL: ['0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5'],
						RetentionSettings: {
							metadata: true,
							attachments: true,
						},
						History: [
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'received',
								CreatedAt: 1763580377046,
							},
							{
								Data: {},
								Source: 'system',
								Name: 'created',
								CreatedAt: 1763580361704,
							},
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'received',
								CreatedAt: 1763580363186,
							},
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'fileAttached',
								CreatedAt: 1763744235428,
							},
						],
					},
					{
						Job: {
							Status: 'action',
							Owner: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5#ACCEPTED',
							PeopleJob: true,
							WorkflowName: '11-19-test-jh-mock',
							CreatedAt: 1763580363114,
							Workflow: '46d5d97de78a46a7b891b1cb70aff385',
							ReturnURL: 'https://us-east-1.ntf.stratus.lol/report-end',
							Token: 'AQCAAAAAKgAAAAMAAAAAAAAAAQJPMYefr98RF+GI9unE2K1nsQUFwKseF5L1/UHQizJ2s7C8OlAjg80sSBZSjoUM8sHOYDt4uIw5Zle41wSKLr7yf6IvE8wB9oUKv8qa4oo=bFccm3NIo4yNCA1QkDvarNSX2AKH6h4qSIGq5d75nUKKkEYFyPGF2iTg0FKgHPRG+xKYfWE5G/RTztNs3WgdVGTY4S8c/ZlvP4jLvjiDr3tIyQ7m6Shq1ohqnhJmHJWcG/lremVXZQzxp9nCFNXb0Gw9sNTelHJ66cO63MhJSKF4Rb0UGh95ltqzRx/x5+ee3MX2lTOMNg8QEmAMdXP1Xe7q1EmlTF8e6anszobc+BQpucTL1zVZqRUsAC/8u4ctvhR0zTpVJS/tiVSnaH4UGlVoaYQCFiaKoUy2H1x1jwVMY9DcTgM+Nyc+AE2SjMHs/v5hFDwXuPOBjTF53KWnaG2j/4e9ZrThYmPSHfnTXbqgb8Fqs0juvPm2FEDXTHaqL7iM3SfiePyiTQSIF9XNoHNaYK6nzo54Wa38vWyOpJiUMd9jNGYfZCqqNWIGDuvzefZ+N4ylDdosmPpuybaaH+BCi54t6tx91HCj1yvF6c2vJEYA4gfr14gNq2ytVEfUltmsDLR2hEPlTC4LvRr6',
							OwnerData: {
								lastName: 'Hubert',
								nonMFPLicenseCapacity: 117,
								licensed: true,
								hidden: false,
								nonMFPLicenseQuantity: 10,
								roles: ['6197657e-0149-4f79-bbdd-6ff94503768e', 'f32744a5-61c6-4bde-bb08-5b1f476181b8'],
								pinNumber: '710877',
								groupList: [
									'00000000-1111-1111-1111-000000000000',
									'00000000-2222-2222-2222-000000000000',
									'00000000-3333-3333-3333-000000000000',
									'078853b0-0a3e-4934-bbed-7f2730479749',
									'76c6c636-d773-4046-8ed8-d452aa3a9e5e',
								],
								badgeNumber: '1614624906760818347',
								userName: 'konicaminoltamarketplace_9421e76ce24bc746ded650aec34fab916c64325f',
								dateAdded: 1696615859083,
								firstName: 'James',
								id: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								email: 'jhubert@kmbs.konicaminolta.us',
								ippNumber: '',
								status: 'ACCEPTED',
							},
							Participants: [],
							UpdatedAt: 1763744235404,
							NodeNamespace: 'PeopleUser',
							Drop: {
								password: 'l48uFxLXfHM24VaUvZ3bdi8AJcfVTTpb',
								id: '34fa4feb409f4c23886c2f00cc187409',
								username: '3qBnaTpFQ2wPMxQt',
							},
							Archived: true,
							Name: 'Internal Form|2025-11-20|14:26:04|short',
							Active: true,
							NodeID: '84d34db8125e46bd9fc2e30a7ec98215',
							RetentionSettings: {
								metadata: true,
								attachments: true,
							},
							PreviousNodeID: '5a143ab5e8334452b80b72585be72748',
							ExpiresAt: 1795553124,
							TrashedArchive: false,
						},
						ExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						MaxExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						Notes: [],
						Files: [
							{
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a8ba060d8b864d8585f8a45b5053ec5f/264242e0-df29-46f6-a8af-ca46712f047f?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBMGVSR5I5%2F20251121%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20251121T165711Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=7a2ac05fad405db34d52854afb74ac527346a61a0e08eca85ce7fedd12b6529a&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEkaCXVzLWVhc3QtMSJHMEUCIB3vsQUtbI0R8WVEaw0Nhu17H11VWuZlnO9vb16dLt0%2FAiEA3QRaZpJsd9f3q3BWJHCq9SNdhe9zXMcVet3mWil66isqiwQIEhAEGgwwODIwMjY1NzYwNjYiDN3xipb2baDUG7fYAiroAxrmMAFSXz%2FQkBWOvtjnH0uyviXttgRfZdBMO9hAwCjKbH9C1mF75%2F4VEa2BwYBVVoEBGe4zUzRxCrJ1J0ZPTXrcc%2FCeLFVu00aI8eUTqxshTp%2BhHOC5ZBH7C4LoSry%2BEns0ezpU08K1Yxd1jfdjtJJyvZvRiayP%2BxLRg3Gd38%2FhlO5hdxz5th2qvXAcYXuCujamCcP39%2Bze12F6U89Lq37rarK7W24qyJ8MMZzV%2BLbNcOqto465qGZr179%2F6WzbPzX4ZJNnhZYTBEhid9l8hMRW%2F757whHuOLiNp026L0R5%2FiW2iP9dxNmITsPHbSaA83TGIj9qVWFcI4%2FqI%2F4JFyytB519FLAF1%2FU0k5EGb1R3iXQt51hbsZJnrDU%2BbWKkUm8LB0yvSBr%2FTJDIKMX%2Fzprus4pGMLqurWc68B%2Bfu9t2zEzYYJT4MZP23KvJ4KH8pgDwEnOZfAYHXv6u0gL2Kqwy1MJrK4VWTPzlDUP72YQCaT%2Bk50atBz1zyH09yZGHNSseX50DbRx4JiyJ3o5lD%2FXimcEMR9WlIyKSQVzNcGgSOpwxkFBMzfojWCa8gbubU5%2FE0jYL32vqVAB2kLLO%2FsP4VkjPCfKs%2B27OhAm7pBVIs3ENS76xS28lwzy9Oz9ZyLp51FcOua29MKWugskGOqUB3Kjecnb9yL9lyDejl4KCjwtHB0NGCYo0tDZfmDDGjXmZXjtG1njbj6BCUk3nBL6LE%2B8RGXIrqy6EvfkgV6vN%2F%2BGC43E05kFmyz3HLnTTHH2fvgHcn7lzLWEb0WgaRhxK4WRGy79sv90aGRy89r%2FxN2vDXevn4a%2B6rb6vkD%2BmUWl5bZc8SGiJ92Fb3tatNYhgy9fAEDFFD0TihyS6Subrw25zo8lO',
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'WlpxUXJFTHpPZ2hvUVJTRHh5YlViTWdObmJBakk5N2Y=',
									'x-amz-server-side-encryption-customer-key-md5': 'uUAoyqQ+8eNdXM8MT+xBjQ==',
								},
								metadata: {
									uploadedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									date: 1763744235000,
									ext: '.pdf',
									fullname:
										'some ridiculously long name that is ridiculous and long and riduclous.pdf',
									name: 'sample-document',
									size: 245678,
									uuid: '264242e0-df29-46f6-a8af-ca46712f047f',
								},
							},
						],
						PK: 'T:a67d94ba-69fa-5348-9a2d-220e02019d2c',
						Metadata: {
							sk: '46d5d97de78a46a7b891b1cb70aff385',
							itemType: 'PROCESS',
							pk: 'adf1813a-a21b-494c-b165-68113a69fd03',
							data: {
								formsubmission: {
									formId: 'c788b930782e415aa40732c924caf600',
									submittedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									submittedAt: 1763580356468,
									'.type': 'formsubmission',
								},
								form: [
									{
										dropdown1: 'jethridge@kmbs.konicaminolta.us',
										'.id': 'c788b930782e415aa40732c924caf600',
										number1:
											'123123123456789123456789123456789123456789123123123456789123456789123456789123456789',
										textarea1: '123123',
										text1: '123123',
										'.type': 'form',
										'tool!exclude': 'hello world',
										file1: '110011001',
										'date-ISO': 'Thursday',
									},
									{
										dropdown1: 'jethridge@kmbs.konicaminolta.us',
										'.id': 'c788b930782e415aa40732c924caf600',
										number1: '123123',
										textarea1: '123123',
										text1: '123123',
										'.type': 'form',
									},
								],
								internalform: {
									formId: 'c788b930782e415aa40732c924caf600',
									formUrl:
										'https://james-01-test.tenant.stratus.lol/admin/internalform/us-east-1/46d5d97de78a46a7b891b1cb70aff385-5a143ab5e8334452b80b72585be72748',
									formTitle: 'Josh email out test',
								},
								files: '264242e0-df29-46f6-a8af-ca46712f047f',
								formlayout: [
									{
										'.type': 'formlayout',
										id: 'c788b930782e415aa40732c924caf600',
										formDefinition: {
											config: {
												styling: {},
											},
										},
									},
								],
								formsavedstate: {
									formId: 'c788b930782e415aa40732c924caf600',
									onChange: '',
									'.type': 'formsavedstate',
									setValue: 'dropdown1,text1,textarea1,number1',
								},
								job: {
									name: 'Internal Form|2025-11-20|14:26:02|short',
									'.type': 'job',
								},
							},
							created: 1763580357,
							status: 'no status',
						},
						JobID: 'adf1813a-a21b-494c-b165-68113a69fd03',
						SK: 'AR:adf1813a-a21b-494c-b165-68113a69fd03',
						UserData: {
							'0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5': {
								firstName: 'James',
								email: 'jhubert@kmbs.konicaminolta.us',
							},
						},
						TrashedArchive: false,
						ACL: ['0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5'],
						RetentionSettings: {
							metadata: true,
							attachments: true,
						},
						History: [],
					},
					{
						Job: {
							Status: 'action',
							Owner: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5#ACCEPTED',
							PeopleJob: true,
							WorkflowName: '11-19-test-jh-mock-trashed',
							CreatedAt: 1763580363114,
							Workflow: '46d5d97de78a46a7b891b1cb70aff385',
							ReturnURL: 'https://us-east-1.ntf.stratus.lol/report-end',
							Token: 'AQCAAAAAKgAAAAMAAAAAAAAAAQJPMYefr98RF+GI9unE2K1nsQUFwKseF5L1/UHQizJ2s7C8OlAjg80sSBZSjoUM8sHOYDt4uIw5Zle41wSKLr7yf6IvE8wB9oUKv8qa4oo=bFccm3NIo4yNCA1QkDvarNSX2AKH6h4qSIGq5d75nUKKkEYFyPGF2iTg0FKgHPRG+xKYfWE5G/RTztNs3WgdVGTY4S8c/ZlvP4jLvjiDr3tIyQ7m6Shq1ohqnhJmHJWcG/lremVXZQzxp9nCFNXb0Gw9sNTelHJ66cO63MhJSKF4Rb0UGh95ltqzRx/x5+ee3MX2lTOMNg8QEmAMdXP1Xe7q1EmlTF8e6anszobc+BQpucTL1zVZqRUsAC/8u4ctvhR0zTpVJS/tiVSnaH4UGlVoaYQCFiaKoUy2H1x1jwVMY9DcTgM+Nyc+AE2SjMHs/v5hFDwXuPOBjTF53KWnaG2j/4e9ZrThYmPSHfnTXbqgb8Fqs0juvPm2FEDXTHaqL7iM3SfiePyiTQSIF9XNoHNaYK6nzo54Wa38vWyOpJiUMd9jNGYfZCqqNWIGDuvzefZ+N4ylDdosmPpuybaaH+BCi54t6tx91HCj1yvF6c2vJEYA4gfr14gNq2ytVEfUltmsDLR2hEPlTC4LvRr6',
							OwnerData: {
								lastName: 'Hubert',
								nonMFPLicenseCapacity: 117,
								licensed: true,
								hidden: false,
								nonMFPLicenseQuantity: 10,
								roles: ['6197657e-0149-4f79-bbdd-6ff94503768e', 'f32744a5-61c6-4bde-bb08-5b1f476181b8'],
								pinNumber: '710877',
								groupList: [
									'00000000-1111-1111-1111-000000000000',
									'00000000-2222-2222-2222-000000000000',
									'00000000-3333-3333-3333-000000000000',
									'078853b0-0a3e-4934-bbed-7f2730479749',
									'76c6c636-d773-4046-8ed8-d452aa3a9e5e',
								],
								badgeNumber: '1614624906760818347',
								userName: 'konicaminoltamarketplace_9421e76ce24bc746ded650aec34fab916c64325f',
								dateAdded: 1696615859083,
								firstName: 'James',
								id: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								email: 'jhubert@kmbs.konicaminolta.us',
								ippNumber: '',
								status: 'ACCEPTED',
							},
							Participants: [],
							UpdatedAt: 1763744235404,
							NodeNamespace: 'PeopleUser',
							Drop: {
								password: 'l48uFxLXfHM24VaUvZ3bdi8AJcfVTTpb',
								id: '34fa4feb409f4c23886c2f00cc187409-trashed',
								username: '3qBnaTpFQ2wPMxQt',
							},
							Archived: true,
							Name: 'Internal Form Trashed|2025-11-19|14:26:02',
							Active: true,
							NodeID: '84d34db8125e46bd9fc2e30a7ec98215-trashed',
							RetentionSettings: {
								metadata: true,
								attachments: true,
							},
							PreviousNodeID: '5a143ab5e8334452b80b72585be72748-trashed',
							ExpiresAt: 1795553124,
							TrashedArchive: true,
						},
						ExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						MaxExpiresAt: Math.floor(new Date().getTime() / 1000) + 100 * 24 * 60 * 60,
						Notes: [
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PRIVATE#780b80c0-f11a-4fd9-8c3b-c726ebc95316',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102585442,
								Name: 'private wow',
							},
							{
								PK: 'J:ae20a17c-6fea-4000-82c0-1b278540eb06',
								SK: 'N:PUBLIC#8e156ef9-2892-432e-9e90-e868017b8c91',
								Author: 'James Hubert',
								AuthorId: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								CreatedAt: 1764102580033,
								Name: 'public wow',
							},
						],
						Files: [
							{
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/a8ba060d8b864d8585f8a45b5053ec5f/264242e0-df29-46f6-a8af-ca46712f047f?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBMGVSR5I5%2F20251121%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20251121T165711Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=7a2ac05fad405db34d52854afb74ac527346a61a0e08eca85ce7fedd12b6529a&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEEkaCXVzLWVhc3QtMSJHMEUCIB3vsQUtbI0R8WVEaw0Nhu17H11VWuZlnO9vb16dLt0%2FAiEA3QRaZpJsd9f3q3BWJHCq9SNdhe9zXMcVet3mWil66isqiwQIEhAEGgwwODIwMjY1NzYwNjYiDN3xipb2baDUG7fYAiroAxrmMAFSXz%2FQkBWOvtjnH0uyviXttgRfZdBMO9hAwCjKbH9C1mF75%2F4VEa2BwYBVVoEBGe4zUzRxCrJ1J0ZPTXrcc%2FCeLFVu00aI8eUTqxshTp%2BhHOC5ZBH7C4LoSry%2BEns0ezpU08K1Yxd1jfdjtJJyvZvRiayP%2BxLRg3Gd38%2FhlO5hdxz5th2qvXAcYXuCujamCcP39%2Bze12F6U89Lq37rarK7W24qyJ8MMZzV%2BLbNcOqto465qGZr179%2F6WzbPzX4ZJNnhZYTBEhid9l8hMRW%2F757whHuOLiNp026L0R5%2FiW2iP9dxNmITsPHbSaA83TGIj9qVWFcI4%2FqI%2F4JFyytB519FLAF1%2FU0k5EGb1R3iXQt51hbsZJnrDU%2BbWKkUm8LB0yvSBr%2FTJDIKMX%2Fzprus4pGMLqurWc68B%2Bfu9t2zEzYYJT4MZP23KvJ4KH8pgDwEnOZfAYHXv6u0gL2Kqwy1MJrK4VWTPzlDUP72YQCaT%2Bk50atBz1zyH09yZGHNSseX50DbRx4JiyJ3o5lD%2FXimcEMR9WlIyKSQVzNcGgSOpwxkFBMzfojWCa8gbubU5%2FE0jYL32vqVAB2kLLO%2FsP4VkjPCfKs%2B27OhAm7pBVIs3ENS76xS28lwzy9Oz9ZyLp51FcOua29MKWugskGOqUB3Kjecnb9yL9lyDejl4KCjwtHB0NGCYo0tDZfmDDGjXmZXjtG1njbj6BCUk3nBL6LE%2B8RGXIrqy6EvfkgV6vN%2F%2BGC43E05kFmyz3HLnTTHH2fvgHcn7lzLWEb0WgaRhxK4WRGy79sv90aGRy89r%2FxN2vDXevn4a%2B6rb6vkD%2BmUWl5bZc8SGiJ92Fb3tatNYhgy9fAEDFFD0TihyS6Subrw25zo8lO',
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'WlpxUXJFTHpPZ2hvUVJTRHh5YlViTWdObmJBakk5N2Y=',
									'x-amz-server-side-encryption-customer-key-md5': 'uUAoyqQ+8eNdXM8MT+xBjQ==',
								},
								metadata: {
									uploadedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									date: 1763744235000,
									ext: '.pdf',
									fullname: 'sample-document.pdf',
									name: 'sample-document',
									size: 245678,
									uuid: '264242e0-df29-46f6-a8af-ca46712f047f',
								},
							},
						],
						PK: 'T:a67d94ba-69fa-5348-9a2d-220e02019d2c',
						Metadata: {
							sk: '46d5d97de78a46a7b891b1cb70aff385',
							itemType: 'PROCESS',
							pk: 'adf1813a-a21b-494c-b165-68113a69fd0f-trashed',
							data: {
								formsubmission: {
									formId: 'c788b930782e415aa40732c924caf600',
									submittedBy: '0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
									submittedAt: 1763580356468,
									'.type': 'formsubmission',
								},
								form: {
									dropdown1: 'jethridge@kmbs.konicaminolta.us',
									'.id': 'c788b930782e415aa40732c924caf600-trashed',
									number1: '123123',
									textarea1: '123123',
									text1: '123123',
									'.type': 'form',
								},
								internalform: {
									formId: 'c788b930782e415aa40732c924caf600',
									formUrl:
										'https://james-01-test.tenant.stratus.lol/admin/internalform/us-east-1/46d5d97de78a46a7b891b1cb70aff385-5a143ab5e8334452b80b72585be72748',
									formTitle: 'Josh email out test',
								},
								files: '264242e0-df29-46f6-a8af-ca46712f047f',
								formlayout: [
									{
										'.type': 'formlayout',
										id: 'c788b930782e415aa40732c924caf600-trashed',
										formDefinition: {
											config: {
												styling: {},
											},
										},
									},
								],
								formsavedstate: {
									formId: 'c788b930782e415aa40732c924caf600',
									onChange: '',
									'.type': 'formsavedstate',
									setValue: 'dropdown1,text1,textarea1,number1',
								},
								job: {
									name: 'Internal Form Trashed|2025-11-19|14:26:02',
									'.type': 'job',
								},
							},
							created: 1763580357,
							status: 'no status',
						},
						JobID: 'adf1813a-a21b-494c-b165-68113a69fd0f-trashed',
						SK: 'AR:adf1813a-a21b-494c-b165-68113a69fd0f-trashed',
						UserData: {
							'0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5': {
								firstName: 'James',
								email: 'jhubert@kmbs.konicaminolta.us',
							},
						},
						TrashedArchive: true,
						ACL: ['0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5'],
						RetentionSettings: {
							metadata: true,
							attachments: true,
						},
						History: [
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'received',
								CreatedAt: 1763580377046,
							},
							{
								Data: {},
								Source: 'system',
								Name: 'created',
								CreatedAt: 1763580361704,
							},
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'received',
								CreatedAt: 1763580363186,
							},
							{
								Data: {},
								Source: 'U:0f8ccdb4-22a7-4d4b-9ea9-38c84f4f04d5',
								Name: 'fileAttached',
								CreatedAt: 1763744235428,
							},
						],
					},
					{
						PK: 'T:a67d94ba-69fa-5348-9a2d-220e02019d2c',
						Acl: {
							groups: [],
							users: [
								{
									name: 'bcaf366c-d7d1-4a57-bd10-a8abb72d05a8',
									role: 'owner',
								},
							],
						},
						SK: 'AR:96a13eb0-750d-4e6e-a6e9-7031a5115fd2',
						History: [
							{
								Data: {},
								Source: 'U:bcaf366c-d7d1-4a57-bd10-a8abb72d05a8',
								Name: 'received',
								CreatedAt: 1767971559775,
							},
							{
								Data: {},
								Source: 'system',
								Name: 'created',
								CreatedAt: 1767971558726,
							},
						],
						UserData: {
							'bcaf366c-d7d1-4a57-bd10-a8abb72d05a8': {
								firstName: 'Oleksii',
								email: 'omusinov@kmbs.konicaminolta.us',
							},
						},
						ExpiresAt: 1799507672,
						Notes: [],
						MaxExpiresAt: 1799507672,
						Metadata: {
							sk: '57ba7c0be7274860999e3b2db8185fe0',
							itemType: 'PROCESS',
							pk: '96a13eb0-750d-4e6e-a6e9-7031a5115fd2',
							data: {
								formsubmission: [
									{
										formId: 'c73d258afd114e9296ae4dcd4189cd1e',
										submittedBy: 'bcaf366c-d7d1-4a57-bd10-a8abb72d05a8',
										submittedAt: 1767971555746,
										authType: 'stratus-user',
										'.type': 'formsubmission',
									},
									{
										formId: 'e92ea1efc8414f09b542f05430c14f5a',
										submittedBy: 'bcaf366c-d7d1-4a57-bd10-a8abb72d05a8',
										submittedAt: 1767971645503,
										authType: {
											signature1:
												'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADUAAAATCAYAAAAwE0VbAAADqUlEQVR4AeyWW4iNaxjH/++3rIN9mHZbezfGbLvd7MhZyaHkghxzQUIiKW4oU7hScki5csGFInE1Y3AjIUVIpJQIhRDCmjUjN6LMWmvWrM/v+Zb5WouZVd/EiKye/3oO7/M+h/f93vf7PEX8ZXK6B3JgZ8SpkdzbOzSDHGnwNpPXyiiTIzcVJfi38o3cVF1So0ASbPuaRdcO1CVy1IOauoSaouSK3FSU4N/KN2yqLaclPL+3QBb4oLM1p7utea0pLw57xZlC3wlymawuw8+BDmDz38HPEHdk9/yyc5I2udtuHN8gTlteF0y3cWwVZ4och7D5xs2nHNitrrd2/oKmENb60kGcxjjpIbwF3EUe4XztobDt6NXJaRIO08F95h2X03vk+cRtSWc1DLnfyAsS+tog6XfQMjipcTzHK8AE57QfW4rCVrXnNQW5GiUYPGrziLGMxbAGH2MbGXNaDe838ih8IdmGsrrPir52IYfUVdQ+7M8x1Pm+ZsN7Jfwq5tPYfXbrFBPoT7Pg/UYeGRvIlvKdXtan9Ag5pEB3sqaSNDU0HOhJwC/wrxy7g9oB/s50ahq8X8jOVIxMbJiK8D4TTaerTHZeQfEq4190yJrqIiI1yWTEvhGrUv/pTM6iNcKTqU4/pnefjlfoTr+g96kGuxdIYmeaEJLHY/cEKStf/3GdTkQOyZyx/4shR9Ev4L0Sq1L72pddNqGPk+w6T2HIDE7oBrxETvEup19LSumfY2CLMqCkVf+nlt/KPcgznD5qum0exZxEsYL/QV6PHFLM03pW25pqJ9D5cKBnoaEzr83dQ3ZbUugC9CK4CFRwSlNABwtVQ7zRZjO0ZTWPPFNNrgpPbyQV8B1WvoDEWkzMQYwF5AWH22mvFDwey3kn3eFFdgTcpMl12AugqTah6/BqZNu/iRejvYRPFIs6TfIGOV2JJ7TbJloubNeQzXcLOc625vBzasZmu5yH90pel84y+AqMZwFvUOsxYJfRUmzWMEylc8S31QG0RhI+AMORl4NRyA/Y1kbePVvRq5OvqzhcoYnJ8IXwGLwpHteiv5zC80SDjditCVuseU6ai/6Uld4Bz4Feyb4HGdwM7oH/qW8pqCfXAXbrNraAwoNJ4c1DkhoLTwEHUqYPSehw4PnxD3uPH7TOU4GxOWAg8MCfYFV5QxbCdLODP4DlicMn1qW0F17D2ZtpftYA+mcftNiawRgwAFieQWzKRpuHHnz8hk1ZoB8FP5v6XnbyAwAAAP//b6EmBwAAAAZJREFUAwAvNEwMOCvVaAAAAABJRU5ErkJggg==',
										},
										'.type': 'formsubmission',
									},
								],
								externalform: {
									formId: 'c73d258afd114e9296ae4dcd4189cd1e',
									formTitle: 'Assign User test form',
									authType: 'stratus-user',
									formUrl:
										'https://forms.stratus.lol/externalform/us-east-1/james-01-test/57ba7c0be7274860999e3b2db8185fe0-d8f9d6bd1343451fb00235ca314f5564',
								},
								form: [
									{
										'.id': 'c73d258afd114e9296ae4dcd4189cd1e',
										text1: 'oiyu',
										'.type': 'form',
										text2: 'oiyoyoiu',
									},
									{
										'.id': 'e92ea1efc8414f09b542f05430c14f5a',
										'.type': 'form',
									},
								],
								formlayout: [
									{
										'.type': 'formlayout',
										id: 'c73d258afd114e9296ae4dcd4189cd1e',
										formDefinition: {
											config: {
												styling: {
													width: {
														unit: 'px',
														num: 1440,
													},
													height: {
														unit: 'px',
														num: 960,
													},
												},
											},
										},
									},
									{
										'.type': 'formlayout',
										id: 'e92ea1efc8414f09b542f05430c14f5a',
										formDefinition: {
											config: {
												styling: {
													width: {
														unit: 'px',
														num: 430,
													},
													height: {
														unit: 'px',
														num: 932,
													},
												},
											},
										},
									},
								],
								linearform: {
									formId: 'e92ea1efc8414f09b542f05430c14f5a',
									formUrl: '',
									formTitle: '386 test',
								},
								formsavedstate: [
									{
										formId: 'c73d258afd114e9296ae4dcd4189cd1e',
										onChange: '',
										'.type': 'formsavedstate',
										setValue: 'text1,text2',
									},
									{
										formId: 'e92ea1efc8414f09b542f05430c14f5a',
										onChange: '',
										'.type': 'formsavedstate',
										setValue: 'signature1',
									},
								],
								job: {
									name: 'External Form|2026-01-09|10:12:39',
									'.type': 'job',
								},
							},
							created: 1767971555,
							status: 'no status',
						},
						TrashedArchive: false,
						Forms: [
							{
								link: {
									headers: {
										'x-amz-server-side-encryption-customer-algorithm': 'AES256',
										'x-amz-server-side-encryption-customer-key':
											'TXpYTGpxVHJycmdpSjAzNXZCa0d1Q1pweUx2dHE1cU8=',
										'x-amz-server-side-encryption-customer-key-md5': '/iJCiZrb5qM4b+C/8rQb+g==',
									},
									url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/18ceda36e98c4454b5bca65406313fc1/c73d258afd114e9296ae4dcd4189cd1e.png?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBHTUWRTGZ%2F20260109%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260109T151431Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=7f87dbc7bd5fc661de0003dc6c2cfd48818fb840111796527a2ec255291114f8&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEN3%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDT7gOKhEw9xavjfUnNywSgKnpZKSsPPFW1p68Aa4ZPqQIhAIFl5z7HbzRejzjv7imTYRvveULZj6TzldyK%2Fvi1ON9TKpQECKX%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMDgyMDI2NTc2MDY2Igwa9R0MtUKNRiWbCQQq6APfg7Y94xO5TJGfNdmaxMaH1XH0FrE0pH3lpEsKK1Tnjp7H9SNH2xS%2FnlJTRWHeXyA70etXIAT9K7dli%2Fm0HdAYdTROIFV%2Bg38A6oxML6iCKl7HeJU1wkk9bx7SQ4J8%2BO2xOwSxXW6LiKVlgR6OLhhoiLM3MRjMpiB%2BezcFS170dOcukqKIlt5V98BiYzTi6o2btAXGMz5agBMCYO0cPWDAoAQRH3jlvMiR85yAQecNLK8hUk7F7dMNvKskbddPzu%2B6D%2B6B0Ctm3PW%2B%2B%2FlmJ4XQ8sn%2Fu2%2BiZ5j%2BOY8J32NUxceim%2FtC24UmHXYtsrNQbQl%2FiF8ya1khxH%2Fx5cRB3%2BN2JUjXyIfKZ2H202DnpxXG50KOe6e1iC7AV%2B%2B6TxqfawJiMtbQPv1lkoCT9xiNKOY2chp8r2JpZhCZsa8RAJ3G96DL7H9ercJ%2F8fVF4JrHMdEIs1u%2BxxulT%2FaQpSf2Qg1RFT0vhbz6dztLwysKcu8JnfcKR9qTISrXFOotBlxrtn65aJboErHKaXKdjKAV2YoDPK8UyfN0k7P9EO5k6wQo0LKfjnopWLu9vTc%2BArUvNM8yuG6eZSZ5GZJQSmA7x288w2AIOOiMGikltA1B14B52rdiP2VaO6wbqP%2FDv66GoC7TU2za5Q7KpzC95IPLBjqkAebYWw9oIVk4036qKYxBXVwAJOHhS1hN%2FQ%2Fy8pM2Q4CzqczKkWhFwjANz6lrw%2FDkqb9hp0RT6N%2BKlRUBDDadkBPs8E6cOwn9%2FRNbCmd2%2B%2BOedW92jqX%2BcqpivbnWdfxTWlRh1erWIamrjOZV5xevcLAlxYFGGlAnEBn3e50%2BKYmwZqdW3Uq99JOkqs%2BWhCdI4amFP6dFHTWpdCpn4gJIqkKtw80%2B',
								},
								data: {
									'.id': 'c73d258afd114e9296ae4dcd4189cd1e',
									id: 'c73d258afd114e9296ae4dcd4189cd1e',
									text1: 'oiyu',
									'.type': 'form',
									text2: 'oiyoyoiu',
								},
								id: 'c73d258afd114e9296ae4dcd4189cd1e',
							},
							{
								link: {
									headers: {
										'x-amz-server-side-encryption-customer-algorithm': 'AES256',
										'x-amz-server-side-encryption-customer-key':
											'TXpYTGpxVHJycmdpSjAzNXZCa0d1Q1pweUx2dHE1cU8=',
										'x-amz-server-side-encryption-customer-key-md5': '/iJCiZrb5qM4b+C/8rQb+g==',
									},
									url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/18ceda36e98c4454b5bca65406313fc1/e92ea1efc8414f09b542f05430c14f5a.png?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBHTUWRTGZ%2F20260109%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260109T151431Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=73b6262d7dab7d14c10884a69e01f9c12972f8cca3425456899711e1a9bd369f&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEN3%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDT7gOKhEw9xavjfUnNywSgKnpZKSsPPFW1p68Aa4ZPqQIhAIFl5z7HbzRejzjv7imTYRvveULZj6TzldyK%2Fvi1ON9TKpQECKX%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMDgyMDI2NTc2MDY2Igwa9R0MtUKNRiWbCQQq6APfg7Y94xO5TJGfNdmaxMaH1XH0FrE0pH3lpEsKK1Tnjp7H9SNH2xS%2FnlJTRWHeXyA70etXIAT9K7dli%2Fm0HdAYdTROIFV%2Bg38A6oxML6iCKl7HeJU1wkk9bx7SQ4J8%2BO2xOwSxXW6LiKVlgR6OLhhoiLM3MRjMpiB%2BezcFS170dOcukqKIlt5V98BiYzTi6o2btAXGMz5agBMCYO0cPWDAoAQRH3jlvMiR85yAQecNLK8hUk7F7dMNvKskbddPzu%2B6D%2B6B0Ctm3PW%2B%2B%2FlmJ4XQ8sn%2Fu2%2BiZ5j%2BOY8J32NUxceim%2FtC24UmHXYtsrNQbQl%2FiF8ya1khxH%2Fx5cRB3%2BN2JUjXyIfKZ2H202DnpxXG50KOe6e1iC7AV%2B%2B6TxqfawJiMtbQPv1lkoCT9xiNKOY2chp8r2JpZhCZsa8RAJ3G96DL7H9ercJ%2F8fVF4JrHMdEIs1u%2BxxulT%2FaQpSf2Qg1RFT0vhbz6dztLwysKcu8JnfcKR9qTISrXFOotBlxrtn65aJboErHKaXKdjKAV2YoDPK8UyfN0k7P9EO5k6wQo0LKfjnopWLu9vTc%2BArUvNM8yuG6eZSZ5GZJQSmA7x288w2AIOOiMGikltA1B14B52rdiP2VaO6wbqP%2FDv66GoC7TU2za5Q7KpzC95IPLBjqkAebYWw9oIVk4036qKYxBXVwAJOHhS1hN%2FQ%2Fy8pM2Q4CzqczKkWhFwjANz6lrw%2FDkqb9hp0RT6N%2BKlRUBDDadkBPs8E6cOwn9%2FRNbCmd2%2B%2BOedW92jqX%2BcqpivbnWdfxTWlRh1erWIamrjOZV5xevcLAlxYFGGlAnEBn3e50%2BKYmwZqdW3Uq99JOkqs%2BWhCdI4amFP6dFHTWpdCpn4gJIqkKtw80%2B',
								},
								data: {
									'.id': 'e92ea1efc8414f09b542f05430c14f5a',
									'.type': 'form',
									id: 'e92ea1efc8414f09b542f05430c14f5a',
								},
								id: 'e92ea1efc8414f09b542f05430c14f5a',
							},
						],
						Job: {
							Status: 'processing',
							Owner: 'U:bcaf366c-d7d1-4a57-bd10-a8abb72d05a8#ACCEPTED',
							WorkflowName: 'om-archive-form-selector',
							CreatedAt: 1767971559765,
							Workflow: '57ba7c0be7274860999e3b2db8185fe0',
							ReturnURL: 'n/a',
							Token: 'n/a',
							Participants: [],
							UpdatedAt: 1767971652695,
							NodeNamespace: 'ArchiveOut',
							Drop: {
								password: 'xiRCySomGHHki2LTAh7rE7mYimXXBeFw',
								id: 'ae4ae4bf5be54f858812f202670abc8b',
								username: 'SQ00ZGWWtbhCHAy0',
							},
							Name: 'External Form|2026-01-09|10:12:39',
							Active: true,
							NodeID: 'd8f9d6bd1343451fb00235ca314f5564',
							PreviousNodeID: 'e9eb954429fc48118e4b2b569bfd75d3',
							Files: [],
							Queue: 'Q:',
						},
						RetentionSettings: {
							metadata: true,
							attachments: true,
						},
						JobID: '96a13eb0-750d-4e6e-a6e9-7031a5115fd2',
						Files: [
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'TXpYTGpxVHJycmdpSjAzNXZCa0d1Q1pweUx2dHE1cU8=',
									'x-amz-server-side-encryption-customer-key-md5': '/iJCiZrb5qM4b+C/8rQb+g==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/18ceda36e98c4454b5bca65406313fc1/c73d258afd114e9296ae4dcd4189cd1e.png?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBHTUWRTGZ%2F20260109%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260109T151431Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=7f87dbc7bd5fc661de0003dc6c2cfd48818fb840111796527a2ec255291114f8&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEN3%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDT7gOKhEw9xavjfUnNywSgKnpZKSsPPFW1p68Aa4ZPqQIhAIFl5z7HbzRejzjv7imTYRvveULZj6TzldyK%2Fvi1ON9TKpQECKX%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMDgyMDI2NTc2MDY2Igwa9R0MtUKNRiWbCQQq6APfg7Y94xO5TJGfNdmaxMaH1XH0FrE0pH3lpEsKK1Tnjp7H9SNH2xS%2FnlJTRWHeXyA70etXIAT9K7dli%2Fm0HdAYdTROIFV%2Bg38A6oxML6iCKl7HeJU1wkk9bx7SQ4J8%2BO2xOwSxXW6LiKVlgR6OLhhoiLM3MRjMpiB%2BezcFS170dOcukqKIlt5V98BiYzTi6o2btAXGMz5agBMCYO0cPWDAoAQRH3jlvMiR85yAQecNLK8hUk7F7dMNvKskbddPzu%2B6D%2B6B0Ctm3PW%2B%2B%2FlmJ4XQ8sn%2Fu2%2BiZ5j%2BOY8J32NUxceim%2FtC24UmHXYtsrNQbQl%2FiF8ya1khxH%2Fx5cRB3%2BN2JUjXyIfKZ2H202DnpxXG50KOe6e1iC7AV%2B%2B6TxqfawJiMtbQPv1lkoCT9xiNKOY2chp8r2JpZhCZsa8RAJ3G96DL7H9ercJ%2F8fVF4JrHMdEIs1u%2BxxulT%2FaQpSf2Qg1RFT0vhbz6dztLwysKcu8JnfcKR9qTISrXFOotBlxrtn65aJboErHKaXKdjKAV2YoDPK8UyfN0k7P9EO5k6wQo0LKfjnopWLu9vTc%2BArUvNM8yuG6eZSZ5GZJQSmA7x288w2AIOOiMGikltA1B14B52rdiP2VaO6wbqP%2FDv66GoC7TU2za5Q7KpzC95IPLBjqkAebYWw9oIVk4036qKYxBXVwAJOHhS1hN%2FQ%2Fy8pM2Q4CzqczKkWhFwjANz6lrw%2FDkqb9hp0RT6N%2BKlRUBDDadkBPs8E6cOwn9%2FRNbCmd2%2B%2BOedW92jqX%2BcqpivbnWdfxTWlRh1erWIamrjOZV5xevcLAlxYFGGlAnEBn3e50%2BKYmwZqdW3Uq99JOkqs%2BWhCdI4amFP6dFHTWpdCpn4gJIqkKtw80%2B',
								metadata: {
									date: 1767971671963,
									uploadedBy: 'system',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'TXpYTGpxVHJycmdpSjAzNXZCa0d1Q1pweUx2dHE1cU8=',
									'x-amz-server-side-encryption-customer-key-md5': '/iJCiZrb5qM4b+C/8rQb+g==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/18ceda36e98c4454b5bca65406313fc1/e92ea1efc8414f09b542f05430c14f5a.png?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBHTUWRTGZ%2F20260109%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260109T151431Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=73b6262d7dab7d14c10884a69e01f9c12972f8cca3425456899711e1a9bd369f&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEN3%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQDT7gOKhEw9xavjfUnNywSgKnpZKSsPPFW1p68Aa4ZPqQIhAIFl5z7HbzRejzjv7imTYRvveULZj6TzldyK%2Fvi1ON9TKpQECKX%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQBBoMMDgyMDI2NTc2MDY2Igwa9R0MtUKNRiWbCQQq6APfg7Y94xO5TJGfNdmaxMaH1XH0FrE0pH3lpEsKK1Tnjp7H9SNH2xS%2FnlJTRWHeXyA70etXIAT9K7dli%2Fm0HdAYdTROIFV%2Bg38A6oxML6iCKl7HeJU1wkk9bx7SQ4J8%2BO2xOwSxXW6LiKVlgR6OLhhoiLM3MRjMpiB%2BezcFS170dOcukqKIlt5V98BiYzTi6o2btAXGMz5agBMCYO0cPWDAoAQRH3jlvMiR85yAQecNLK8hUk7F7dMNvKskbddPzu%2B6D%2B6B0Ctm3PW%2B%2B%2FlmJ4XQ8sn%2Fu2%2BiZ5j%2BOY8J32NUxceim%2FtC24UmHXYtsrNQbQl%2FiF8ya1khxH%2Fx5cRB3%2BN2JUjXyIfKZ2H202DnpxXG50KOe6e1iC7AV%2B%2B6TxqfawJiMtbQPv1lkoCT9xiNKOY2chp8r2JpZhCZsa8RAJ3G96DL7H9ercJ%2F8fVF4JrHMdEIs1u%2BxxulT%2FaQpSf2Qg1RFT0vhbz6dztLwysKcu8JnfcKR9qTISrXFOotBlxrtn65aJboErHKaXKdjKAV2YoDPK8UyfN0k7P9EO5k6wQo0LKfjnopWLu9vTc%2BArUvNM8yuG6eZSZ5GZJQSmA7x288w2AIOOiMGikltA1B14B52rdiP2VaO6wbqP%2FDv66GoC7TU2za5Q7KpzC95IPLBjqkAebYWw9oIVk4036qKYxBXVwAJOHhS1hN%2FQ%2Fy8pM2Q4CzqczKkWhFwjANz6lrw%2FDkqb9hp0RT6N%2BKlRUBDDadkBPs8E6cOwn9%2FRNbCmd2%2B%2BOedW92jqX%2BcqpivbnWdfxTWlRh1erWIamrjOZV5xevcLAlxYFGGlAnEBn3e50%2BKYmwZqdW3Uq99JOkqs%2BWhCdI4amFP6dFHTWpdCpn4gJIqkKtw80%2B',
								metadata: {
									date: 1767971672000,
									uploadedBy: 'system',
								},
							},
						],
						FormDefinitions: [
							{
								valid: true,
								formGroup: {
									id: '00000',
								},
								editor: 'konicaminoltamarketplace_04e7cbed17cbf23a3f2d0f467c6b8426d1e3d9c8',
								metadataKeys: 'text1,text2',
								creator: 'konicaminoltamarketplace_04e7cbed17cbf23a3f2d0f467c6b8426d1e3d9c8',
								formDefinition: {
									targetMfpId: 'large-tablet',
									pages: [
										{
											config: {
												styling: {
													backgroundColor: '#ffffffff',
													backgroundImage: {
														fitType: 'fit',
														image: null,
														opacity: 100,
														enabled: false,
														url: null,
													},
												},
											},
											id: 'HrjxLS_rpbpzHYqL10AuZ',
										},
									],
									fields: [
										{
											type: 'pagebreak',
											config: {
												helpText: '',
												coordinates: {
													x: 0,
													y: 0,
												},
												customClassName: '',
												title: '',
												pageId: '',
												hideBack: false,
												hideNext: false,
												styling: {
													rotate: '0deg',
													backgroundColor: '#ffffff00',
													borderColor: '#c4c4c4ff',
													color: '#000000ff',
													textAlign: 'left',
													textDecorationLine: 'none',
													fontStyle: 'normal',
													fontFamily: 'Sans-serif',
													borderRadius: 4,
													labelPosition: 'within-top',
													borderWidth: 0,
													width: 1,
													fontSize: 16,
													borderStyle: 'solid',
													fontWeight: 'normal',
													height: 1,
												},
												showHelp: false,
												hideReturn: false,
												variable: 'page1',
												hideScan: false,
												hidePageNumbers: false,
												locked: false,
												hideHome: false,
											},
											id: 'zWR62BlqKkPcpGKuq-3TH',
										},
										{
											type: 'text',
											config: {
												helpText: '',
												defaultValue: '',
												minLength: '0',
												coordinates: {
													x: 329,
													y: 127,
												},
												customClassName: '',
												title: 'Name',
												pageId: 'HrjxLS_rpbpzHYqL10AuZ',
												excludeFromMetadata: false,
												required: false,
												readonly: false,
												styling: {
													rotate: '0deg',
													backgroundColor: '#ffffff00',
													borderColor: '#c4c4c4ff',
													color: '#000000ff',
													textAlign: 'left',
													textDecorationLine: 'none',
													fontStyle: 'normal',
													fontFamily: 'Sans-serif',
													borderRadius: 4,
													labelPosition: 'within-top',
													borderWidth: 0,
													width: 360,
													fontSize: 16,
													borderStyle: 'solid',
													fontWeight: 'normal',
													height: 40,
												},
												showHelp: false,
												variable: 'text1',
												locked: false,
												maxLength: '1000',
												mask: false,
											},
											id: 'kSgvkehOAj3OMgHXEc8dn',
										},
										{
											type: 'text',
											config: {
												helpText: '',
												defaultValue: '',
												minLength: '0',
												coordinates: {
													x: 325,
													y: 237,
												},
												customClassName: '',
												title: 'Email',
												pageId: 'HrjxLS_rpbpzHYqL10AuZ',
												excludeFromMetadata: false,
												required: false,
												readonly: false,
												styling: {
													rotate: '0deg',
													backgroundColor: '#ffffff00',
													borderColor: '#c4c4c4ff',
													color: '#000000ff',
													textAlign: 'left',
													textDecorationLine: 'none',
													fontStyle: 'normal',
													fontFamily: 'Sans-serif',
													borderRadius: 4,
													labelPosition: 'within-top',
													borderWidth: 0,
													width: 360,
													fontSize: 16,
													borderStyle: 'solid',
													fontWeight: 'normal',
													height: 40,
												},
												showHelp: false,
												variable: 'text2',
												locked: false,
												maxLength: '1000',
												mask: false,
											},
											id: 'jty_RUNhZr5ayzWbDDIBg',
										},
									],
									config: {
										gridSize: 20,
										styling: {
											width: {
												unit: 'px',
												num: 1440,
											},
											height: {
												unit: 'px',
												num: 960,
											},
										},
										showGrid: false,
										snapToGrid: false,
										showCrosshairLines: false,
										customCSS: '',
										pageZoom: 100,
									},
								},
								advanced: true,
								created: 1720800204612,
								formStatus: 'published',
								id: 'c73d258afd114e9296ae4dcd4189cd1e',
								title: 'Assign User test form',
								updated: 1720800204612,
							},
							{
								valid: true,
								editor: 'konicaminoltamarketplace_4b7314fc12378d7e138b3b3126a87b339643b271',
								metadataKeys: 'signature1',
								formDefinition: {
									targetMfpId: 'large-mobile',
									pages: [
										{
											config: {
												styling: {
													backgroundColor: '#ffffffff',
													backgroundImage: {
														fitType: 'fit',
														image: null,
														opacity: 100,
														enabled: false,
														url: null,
													},
												},
											},
											id: '9Jv8hjrE9mgGqVzesyEjM',
										},
									],
									fields: [
										{
											type: 'pagebreak',
											config: {
												helpText: '',
												coordinates: {
													x: 0,
													y: 0,
												},
												customClassName: '',
												title: '',
												pageId: '',
												hideBack: false,
												hideNext: false,
												styling: {
													rotate: '0deg',
													backgroundColor: '#ffffff00',
													borderColor: '#c4c4c4ff',
													color: '#000000ff',
													textAlign: 'left',
													textDecorationLine: 'none',
													fontStyle: 'normal',
													fontFamily: 'Sans-serif',
													borderRadius: 4,
													labelPosition: 'within-top',
													borderWidth: 0,
													width: 1,
													fontSize: 16,
													borderStyle: 'solid',
													fontWeight: 'normal',
													height: 1,
												},
												showHelp: false,
												hideReturn: false,
												variable: 'page1',
												hideScan: false,
												hidePageNumbers: false,
												locked: false,
												hideHome: false,
											},
											id: 'eoE6oC924Kr8M10lNmu1I',
										},
										{
											type: 'signature',
											config: {
												helpText: '',
												defaultValue: '',
												minLength: '0',
												coordinates: {
													x: 74,
													y: 183,
												},
												customClassName: '',
												title: 'Signature',
												pageId: '9Jv8hjrE9mgGqVzesyEjM',
												excludeFromMetadata: false,
												required: false,
												readonly: false,
												styling: {
													rotate: '0deg',
													backgroundColor: 'rgba(189, 16, 224, 1)',
													borderColor: 'rgba(126, 211, 33, 1)',
													color: 'rgba(230, 249, 0, 1)',
													textAlign: 'left',
													textDecorationLine: 'none',
													fontStyle: 'normal',
													fontFamily: 'Sans-serif',
													borderRadius: 4,
													labelPosition: 'within-top',
													borderWidth: 8,
													width: 334,
													fontSize: 16,
													borderStyle: 'solid',
													fontWeight: 'normal',
													height: 59,
												},
												showHelp: false,
												drawSign: false,
												variable: 'signature1',
												typeSign: true,
												uploadImageSign: false,
												locked: false,
												maxLength: '1000',
												mask: false,
											},
											id: 'Q4GRbj4Qn-vSierKLXa7I',
										},
									],
									config: {
										gridSize: 20,
										styling: {
											width: {
												unit: 'px',
												num: 430,
											},
											height: {
												unit: 'px',
												num: 932,
											},
										},
										showGrid: false,
										snapToGrid: false,
										showCrosshairLines: false,
										customCSS: '',
										pageZoom: 100,
									},
								},
								advanced: true,
								formStatus: 'published',
								id: 'e92ea1efc8414f09b542f05430c14f5a',
								title: '386 test',
								updated: 1720811479909,
							},
						],
					},
					{
						PK: 'T:3d616e28-c7ee-5acf-b79e-fbe25b0a769d',
						UpdatedAt: 1769624028,
						Job: {
							Status: 'processing',
							Owner: 'U:927b5179-3df5-419e-a904-140719603a40#ACCEPTED',
							WorkflowName: '4095',
							CreatedAt: 1769624017755,
							Workflow: '50df9875d12948af821dcdbf606c84ce',
							ReturnURL: 'n/a',
							Token: 'n/a',
							Participants: [],
							UpdatedAt: 1769624019517,
							NodeNamespace: 'ArchiveOut',
							Drop: {
								password: 'UmBAxABnWaeJ8KdW4PTcGpDrHcM6jbrl',
								id: '3d00e9a10866406daea11d7e2bb96643',
								username: 'LTAU0AM6Y7hz7XQF',
							},
							Name: '4095 01/28/2026;,~!@.+-_ 01-13-37',
							Active: true,
							NodeID: '39f1050bd30140dc95c63f8f31253d15',
							PreviousNodeID: '39f1050bd30140dc95c63f8f31253d15',
							Files: [],
							Queue: 'Q:',
						},
						JobID: '726380ed-edf3-4da6-aba3-6e45d81f5f62',
						TrashedArchive: false,
						RetentionSettings: {
							metadata: true,
							attachments: true,
						},
						History: [
							{
								Data: {},
								Source: 'system',
								Name: 'created',
								CreatedAt: 1769624017063,
							},
							{
								Data: {},
								Source: 'U:927b5179-3df5-419e-a904-140719603a40',
								Name: 'received',
								CreatedAt: 1769624017771,
							},
						],
						SK: 'AR:726380ed-edf3-4da6-aba3-6e45d81f5f62',
						UserData: {
							'927b5179-3df5-419e-a904-140719603a40': {
								firstName: 'Meghaa',
								email: 'mdalsania@kmbs.konicaminolta.us',
							},
						},
						Metadata: {
							sk: '50df9875d12948af821dcdbf606c84ce',
							itemType: 'PROCESS',
							pk: '726380ed-edf3-4da6-aba3-6e45d81f5f62',
							data: {
								job: {
									name: '4095 01/28/2026;,~!@.+-_ 01-13-37',
									'.type': 'job',
								},
							},
							created: 1769624017,
							status: 'started',
						},
						Acl: {
							groups: [],
							users: [
								{
									name: '927b5179-3df5-419e-a904-140719603a40',
									role: 'owner',
								},
							],
						},
						ExpiresAt: 1801160028,
						CreatedAt: 1769624028,
						Notes: [],
						MaxExpiresAt: 1801160028,
						Files: [
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/0baaf07e-d254-4c93-8f63-05b92aa30225?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181342Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=801b4e4b4528c41681c7a633f24b2faba1e71a08021e45e0a1966fce1d4639d5&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769623998917,
									ext: '.tif',
									size: 4651444,
									name: '4.43 MB tif',
									fullname: '4.43 MB tif.tif',
									dropboxin: {
										ext: '.tif',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/4.43 MB tif.tif',
										name: '4.43 MB tif',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACbA',
										fullname: '4.43 MB tif.tif',
										filesize: 4651444,
									},
									uploadedBy: 'system',
									uuid: '0baaf07e-d254-4c93-8f63-05b92aa30225',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/0dcc7656-8ce2-406e-a60f-a05f5faa231c?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181342Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=abbb571d6b1896b3b80d17c31d59c61cbd012d27811cca2c85b4704d86e81917&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624012855,
									ext: '.xlsx',
									size: 8370,
									name: 'Test excel file',
									fullname: 'Test excel file.xlsx',
									dropboxin: {
										ext: '.xlsx',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/Test excel file.xlsx',
										name: 'Test excel file',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACZA',
										fullname: 'Test excel file.xlsx',
										filesize: 8370,
									},
									uploadedBy: 'system',
									uuid: '0dcc7656-8ce2-406e-a60f-a05f5faa231c',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/85cfba23-065a-4e36-b826-cbe4a4083a04?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=6394868aae2b1db590d9bac6ae46cfe0fd58f810bd69d873084c15718884fee6&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624014090,
									ext: '.pdf',
									size: 9822,
									name: 'test small',
									fullname: 'test small.pdf',
									dropboxin: {
										ext: '.pdf',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/test small.pdf',
										name: 'test small',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAAAEw',
										fullname: 'test small.pdf',
										filesize: 9822,
									},
									uploadedBy: 'system',
									uuid: '85cfba23-065a-4e36-b826-cbe4a4083a04',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/a67ae4cc-09d4-41ca-91e6-7f81da47586a?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=296bd58ec1977b46acbc30740fc1e054dbfa5e2a01eb249155cd409331af40f4&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624010443,
									ext: '.pptx',
									size: 4726910,
									name: 'Presentation 1',
									fullname: 'Presentation 1.pptx',
									dropboxin: {
										ext: '.pptx',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/Presentation 1.pptx',
										name: 'Presentation 1',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACyQ',
										fullname: 'Presentation 1.pptx',
										filesize: 4726910,
									},
									uploadedBy: 'system',
									uuid: 'a67ae4cc-09d4-41ca-91e6-7f81da47586a',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/b03a0e34-e99b-4dfc-92ea-65cb618f8832?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=d1181677c4fb530bf58fae8071d3cbf977e5ec2dfae11379e4d5ed5a2d6f5332&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624011726,
									ext: '.svg',
									size: 1057490,
									name: 'SVG-1mb',
									fullname: 'SVG-1mb.svg',
									dropboxin: {
										ext: '.svg',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/SVG-1mb.svg',
										name: 'SVG-1mb',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACag',
										fullname: 'SVG-1mb.svg',
										filesize: 1057490,
									},
									uploadedBy: 'system',
									uuid: 'b03a0e34-e99b-4dfc-92ea-65cb618f8832',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/b2727f88-7cfc-4c2e-916a-ee8d26a6ee54?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=b0492feffdc227e78a5ef9daa280cb473925973ab9d98e519d070f5f7299f4fb&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769623995414,
									ext: '.svg',
									size: 3147834,
									name: '3mb-SVG',
									fullname: '3mb-SVG.svg',
									dropboxin: {
										ext: '.svg',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/3mb-SVG.svg',
										name: '3mb-SVG',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACyg',
										fullname: '3mb-SVG.svg',
										filesize: 3147834,
									},
									uploadedBy: 'system',
									uuid: 'b2727f88-7cfc-4c2e-916a-ee8d26a6ee54',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/b9250442-3eb3-46dd-b49c-2663e4cc34e9?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=d65139833c79fec507e3eb1d20ba4d74fea2cc5b7b52236e9a01f0d87b621768&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769623990741,
									ext: '.bmp',
									size: 1177656,
									name: '1.1-MB',
									fullname: '1.1-MB.bmp',
									dropboxin: {
										ext: '.bmp',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/1.1-MB.bmp',
										name: '1.1-MB',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACYQ',
										fullname: '1.1-MB.bmp',
										filesize: 1177656,
									},
									uploadedBy: 'system',
									uuid: 'b9250442-3eb3-46dd-b49c-2663e4cc34e9',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/cdb3314e-8172-478a-abd0-a83870258e71?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181344Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=bde8189d461a7679541dbe9d61acf6b2a6c4c7d201b3fbc2c8cf1ecb52cb097c&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624001976,
									ext: '.jpeg',
									size: 91707,
									name: '89.5 KB JPEG',
									fullname: '89.5 KB JPEG.jpeg',
									dropboxin: {
										ext: '.jpeg',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/89.5 KB JPEG.jpeg',
										name: '89.5 KB JPEG',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACxA',
										fullname: '89.5 KB JPEG.jpeg',
										filesize: 91707,
									},
									uploadedBy: 'system',
									uuid: 'cdb3314e-8172-478a-abd0-a83870258e71',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/d75aea42-644b-48db-ac0e-41b8c346aa58?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181344Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=1cba42af33e8a8cf2dcfb1c0c9741d3ae65c21a756715e559bb029031f9385c3&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624006382,
									ext: '.csv',
									size: 162,
									name: 'invite_template',
									fullname: 'invite_template.csv',
									dropboxin: {
										ext: '.csv',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/invite_template.csv',
										name: 'invite_template',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACxg',
										fullname: 'invite_template.csv',
										filesize: 162,
									},
									uploadedBy: 'system',
									uuid: 'd75aea42-644b-48db-ac0e-41b8c346aa58',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/e06cce5e-86bd-409a-96d5-f2b9888631c5?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181344Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=edb7b2635e6798f0e9fae244ac406cc68d4fc985acb71e79e0339b7b4dd23f43&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624007527,
									ext: '.pdf',
									size: 186684,
									name: 'Multiple files test',
									fullname: 'Multiple files test.pdf',
									dropboxin: {
										ext: '.pdf',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/Multiple files test.pdf',
										name: 'Multiple files test',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACdA',
										fullname: 'Multiple files test.pdf',
										filesize: 186684,
									},
									uploadedBy: 'system',
									uuid: 'e06cce5e-86bd-409a-96d5-f2b9888631c5',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/ee808ca8-bbd1-4e0d-8669-5c2f2b01c7ee?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181344Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=9d8d7adf781c32d4272066e7a93097a954764265a14f63250992429e6ebc96a3&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624008641,
									ext: '.jpg',
									size: 20556,
									name: 'nature_2',
									fullname: 'nature_2.jpg',
									dropboxin: {
										ext: '.jpg',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/nature_2.jpg',
										name: 'nature_2',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACZg',
										fullname: 'nature_2.jpg',
										filesize: 20556,
									},
									uploadedBy: 'system',
									uuid: 'ee808ca8-bbd1-4e0d-8669-5c2f2b01c7ee',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/1bad38d4-341a-4a27-a412-87749dd4ad19?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181342Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=46a364eab395f3134541b9159daf75b57a02e949bee2f1c8e6c23d57a0a97087&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769623992005,
									ext: '.gif',
									size: 1527183,
									name: '1.4 MB GIF',
									fullname: '1.4 MB GIF.gif',
									dropboxin: {
										ext: '.gif',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/1.4 MB GIF.gif',
										name: '1.4 MB GIF',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACZQ',
										fullname: '1.4 MB GIF.gif',
										filesize: 1527183,
									},
									uploadedBy: 'system',
									uuid: '1bad38d4-341a-4a27-a412-87749dd4ad19',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/243a9df8-d53a-4a6d-9c69-bc1c9cce4972?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=a639648db16560838bd708be48da0fe4afb7eda5120754bb9166e117ebd28f97&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624003086,
									ext: '.jpg',
									size: 195702,
									name: '93.6 KB Elogated JPG',
									fullname: '93.6 KB Elogated JPG.jpg',
									dropboxin: {
										ext: '.jpg',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/93.6 KB Elogated JPG.jpg',
										name: '93.6 KB Elogated JPG',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACxQ',
										fullname: '93.6 KB Elogated JPG.jpg',
										filesize: 195702,
									},
									uploadedBy: 'system',
									uuid: '243a9df8-d53a-4a6d-9c69-bc1c9cce4972',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/3cc8d2d8-914c-4999-a281-44a63b92b6ac?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=fadf72fc6676e0d6363d5a9ab57d6af6a950faf75241d904c3241af0a6d73adf&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624015709,
									ext: '.png',
									size: 3879127,
									name: 'transparent-mountainous-landforms-natural-landscape-nature-hig-5dce9cb8cf63a9.6310274715738216248495',
									fullname:
										'transparent-mountainous-landforms-natural-landscape-nature-hig-5dce9cb8cf63a9.6310274715738216248495.png',
									dropboxin: {
										ext: '.png',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath:
											'/Megha Dropbox In/regular test/transparent-mountainous-landforms-natural-landscape-nature-hig-5dce9cb8cf63a9.6310274715738216248495.png',
										name: 'transparent-mountainous-landforms-natural-landscape-nature-hig-5dce9cb8cf63a9.6310274715738216248495',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACcw',
										fullname:
											'transparent-mountainous-landforms-natural-landscape-nature-hig-5dce9cb8cf63a9.6310274715738216248495.png',
										filesize: 3879127,
									},
									uploadedBy: 'system',
									uuid: '3cc8d2d8-914c-4999-a281-44a63b92b6ac',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/4d52d5e6-cdd3-4097-9c88-84a55d5759ba?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=7aa19a42ae25454ae5185173acdb71f7876de1867213e9b91704703d6480ec68&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624000792,
									ext: '.png',
									size: 4294304,
									name: '4mb',
									fullname: '4mb.png',
									dropboxin: {
										ext: '.png',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/4mb.png',
										name: '4mb',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACxw',
										fullname: '4mb.png',
										filesize: 4294304,
									},
									uploadedBy: 'system',
									uuid: '4d52d5e6-cdd3-4097-9c88-84a55d5759ba',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/5ac34a95-7ed7-428e-8b7e-065be801764c?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=e77e3ece0395ae778ab550842b6ea6f78b5c94c104cf66248eab68f18d8a5148&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769623996939,
									ext: '.pdf',
									size: 4409479,
									name: '4.20 MB',
									fullname: '4.20 MB.pdf',
									dropboxin: {
										ext: '.pdf',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/4.20 MB.pdf',
										name: '4.20 MB',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACyA',
										fullname: '4.20 MB.pdf',
										filesize: 4409479,
									},
									uploadedBy: 'system',
									uuid: '5ac34a95-7ed7-428e-8b7e-065be801764c',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/65981343-2d08-4258-b746-5ee24594b7ea?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=f2a0c4612db7d3ebe51c6bfe76e2e01e4ed3541821e3dd69018b2706202af604&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624004192,
									ext: '.png',
									size: 2142282,
									name: 'ezgif-5-7fed8034b9',
									fullname: 'ezgif-5-7fed8034b9.png',
									dropboxin: {
										ext: '.png',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/ezgif-5-7fed8034b9.png',
										name: 'ezgif-5-7fed8034b9',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACaA',
										fullname: 'ezgif-5-7fed8034b9.png',
										filesize: 2142282,
									},
									uploadedBy: 'system',
									uuid: '65981343-2d08-4258-b746-5ee24594b7ea',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/68c31054-1b83-4147-92b7-d8ff07319a99?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=702f6045a794c3389f7f71f8ccc9dfb7e26bb6d652bf62dc0234578dc1822c61&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769623993744,
									ext: '.docx',
									size: 3909414,
									name: '3.72',
									fullname: '3.72.docx',
									dropboxin: {
										ext: '.docx',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/3.72.docx',
										name: '3.72',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACYw',
										fullname: '3.72.docx',
										filesize: 3909414,
									},
									uploadedBy: 'system',
									uuid: '68c31054-1b83-4147-92b7-d8ff07319a99',
								},
							},
							{
								headers: {
									'x-amz-server-side-encryption-customer-algorithm': 'AES256',
									'x-amz-server-side-encryption-customer-key':
										'ZURqdWR0VldEMDhxM1pNeGhUb2FZYjlNNndNRWx0ZUk=',
									'x-amz-server-side-encryption-customer-key-md5': '5GjvdMhSPGF3gm9itf8TyA==',
								},
								url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/cc464c5b44964c4eb58bc947385719f6/794f78a8-f3ea-48bc-b642-40c0a4906371?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBII2WMTFP%2F20260128%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260128T181343Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=f3734f7a4d79d96e91eee8aef315a4038d341ded3caeea8ca6b3426bf287e867&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEKj%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJHMEUCIBLFtb3z9SwUg8cya%2B%2FIL7vxG9FgXlqzXZrcPScMpfGWAiEA9Gr7ZZJO1Dr16UNCA6dhU%2FE4bKEZna%2FF0ulGaK8KT5MqiwQIcRAEGgwwODIwMjY1NzYwNjYiDGzfd8W5%2F6YosI3MxCroAzjh8%2BtwhzVyEZsfdK7LJhq5cw8ykPxrdiIBc7eqN3q7fJX0b%2FJUwOQu9goaIzEAMlsZ0XG4KWkYp637N%2BGhlmuyR8V%2FI9M8DE78dfQhpPtBPTThJ0XsDQQDXG8Bq5J%2B53u%2B%2FWZ%2FeiCJrVx5esd2XY73DH7GI%2FcU%2FyavSwbuLfh24lcDc%2F4ZIX38pqrP7m5rGKpzzmc1OFAaNdx3qA0H99Fh%2BMfRrsJkFkOb5L0hRc%2BTSVXS79rjqYyxT7MWmVvz7H38Vi2DQNSITN7Kyh%2B2m%2BlMqwPs05FXVODVFCANc22vLLVldwOZOUVPRHX73r8UBXR1Ae8EFK0YUYYdIi%2FA8FfiDWEJpfvVwvxlKmtMOsojs5CMslKCXO1w%2BboU35x12fblhZvBV546w6wq48oN8lIIwN7lTgONNZWKxHnvRneCTVnZhmKTqjJIZZjqjiMDZF0Dyc6IfrxHjWN71oJ3THbEbmNHhuAnWweorFRc422bLJU4W1CHQm0o7Wx5Uzv1FwW6eLkVByPE8syN%2F%2F9LNoCm%2F%2B0nZZvIdpbRsL796alSX9xD7I%2BtSbGbVoVzMMAxxtUYO9IkRl%2Fd%2BBeFtmyo5AB5LX1lMjYvv8CEem2CoHub%2F0S5IL0CI22TC92xF%2BdyellMEPNZ7hGZMI%2Fh6MsGOqUBTYVkrnM4OMhi%2F5XwY0O9M130oCVmjxBYo9FmyArQh8n4dVpST8ISWgSXF3yUwDF%2BS46tbdkLPhI%2F5047lxOYfE%2BsKe1ylyhglKIRdVptBjz148ttNbYu6ncyvmUuuBbnfqG%2BlKr1bZfyV2ALP4crRLBcPMHOGLajIi%2FyT60lb8qncFdal3yFDGusIjFt4Pt1pDte5c4ssXlfbbZIwzSkB1tdZhL7',
								metadata: {
									date: 1769624005362,
									ext: '.ppt',
									size: 1049088,
									name: 'Free_Test_Data_1MB_PPT',
									fullname: 'Free_Test_Data_1MB_PPT.ppt',
									dropboxin: {
										ext: '.ppt',
										date: '2026-01-28',
										folder: '/Megha Dropbox In/regular test',
										fullpath: '/Megha Dropbox In/regular test/Free_Test_Data_1MB_PPT.ppt',
										name: 'Free_Test_Data_1MB_PPT',
										count: 1,
										id: 'id:D2_zmI539C4AAAAAAAACaQ',
										fullname: 'Free_Test_Data_1MB_PPT.ppt',
										filesize: 1049088,
									},
									uploadedBy: 'system',
									uuid: '794f78a8-f3ea-48bc-b642-40c0a4906371',
								},
							},
						],
						FormDefinitions: [],
					},
				],
			},
		);

		mock.onGet(`${peopleApiUrl}/queues/user`).reply(200, {
			queues: [
				{
					id: 'q1q1q1q1',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'Queue 1',
					members: [user.id, users[2].id],
				},
				{
					id: 'q2q2q2q2',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'Queue 2',
					members: [user.id, users[1].id],
				},
				{
					id: 'q3q3q3q3',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'Queue 3',
					members: [user.id],
				},
			],
			count: 3,
		});
		mock.onGet(`${peopleApiUrl}/queues`).reply(200, {
			queues: [
				{
					id: 'q1q1q1q1',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'queue 1',
					groupID: 'userGroup-1',
					members: [user.id, users[2].id],
				},
				{
					id: 'q2q2q2q2',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'Queue 2',
					members: [user.id, users[1].id],
				},
				{
					id: 'q3q3q3q3',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'queue 3',
					members: [user.id],
				},
				{
					id: 'q4q4q4q4',
					active: false,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'Queue 4',
					members: [users[1].id, users[2].id],
				},
				{
					id: 'q5q5q5q5',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'Queue 5',
					members: [users[2].id, users[3].id],
				},
				{
					id: 'q6q6q6q6',
					active: false,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'Queue 6',
					members: [users[4].id, users[5].id, users[6].id],
				},
			],
			count: 6,
		});

		mock.onGet(`${peopleApiUrl}/jobs/queue`).reply((config) => {
			switch (config.params.queueID) {
				case 'q1q1q1q1':
					return [
						200,
						{
							jobs: [
								{
									id: '9a654bc1-6226-49fb-b7fe-da693a639166',
									active: true,
									name: 'cristian queue test:2024-0-2:39166',
									owner: '02f8a272-6701-4298-a327-6b6afaa52b97',
									queue: 'q1q1q1q1',
									workflow: 'ae18bc2d74974cf1bede66f0726076be',
									accepted: true,
									status: 'action',
									token: 'AQCAAAAAKgAAAAMAAAAAAAAAAWErCSzM7XnKyRivOuRJFyXibLJFWVW3mRU5HCUHZTOG5PsvtEsPneH5YMzNjIqvk8za1t+4ZQfCxUY+zGrGCr9j6WPHhPeiPkcFu7XbdaE=cSKvC36jZaq18fKmH4Mc8bpp+zCBof7VPv68wSRyGdEbjI7A7OrIqpPQKlL9QJRC1+7eygm2EcB+9JiNicjRBQ0CBD4d8Jlok2Nx+4WfxGMamxcMuLMD2vr+WkGrlam6zCFatrvlrEn62QyDkmo49uG/dS0tci1A+VYGHUybcuk8B7m5suamKskg8ysWeHt0hoPVO3LWWC9olbQJ9MjYBgTs2xhNVZ+/6u9jneQtihM7rpYQ/P9BK2g66jR7GBWyBewuVAY53W0FkI+lGuiJh3KXxMKUBS8IOW7ZeSlmpXitTxyozTF4bGDKbXHIWB0hHa0/CXwJMVz0Ou8usD5pK8Y+yR629H/wjt5OowHje/Kd2hpDL8/O86WBq4zQSs06ZEduNlK8hubdSxOfOuTalQGTN8bTuavZukuUtYl8lsript6bFYFwZaYo4sHlxX30GWjVn1owlgh1aj7uOTrokq7QIbRCnQwXLrUS/R486vDZw5LjKExyIWw1jKLeiu48tejzzxjD5hzV+Dn86h4b',
									nodeID: '8051a394b73a4a2d8f61910fc11e6095',
									nodeNamespace: 'PeopleQueue',
									returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
									createdAt: 1704829398770,
									updatedAt: 1704832022011,
								},
								{
									id: 'j1j1j1j1',
									active: true,
									createdAt: 1699989179,
									updatedAt: 1699989179,
									name: 'workflowName:2023-10-2:2f2f3',
									owner: user.id,
									queue: 'q1q1q1q1',
									status: 'action',
									workflow: 'workflow-9',
								},
								{
									id: 'j2j2j2j2',
									active: true,
									createdAt: 1699999179,
									updatedAt: 1699999179,
									name: 'workflow2:2023-10-2:abcd',
									owner: '',
									queue: 'q1q1q1q1',
									status: 'action',
									workflow: 'workflow-11',
								},
								{
									id: 'j3j3j3j3',
									active: true,
									createdAt: 1699999179,
									updatedAt: 1699999179,
									name: 'MyWorkflow:2023-12-19:ss1',
									owner: users[2].id,
									queue: 'q1q1q1q1',
									status: 'action',
									workflow: 'workflow-13',
								},
							],
							count: 2,
						},
					];
				case 'q2q2q2q2':
					return [
						200,
						{
							jobs: [
								{
									id: 'q2j1j1j1j1',
									active: true,
									createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
									updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
									name: 'Queue 2 Job 1',
									queue: 'q2q2q2q2',
									status: 'action',
									workflow: 'workflow-11',
								},
								{
									id: 'q2j2j2j2j2',
									active: true,
									createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
									updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
									name: 'Queue 2 Job 2',
									queue: 'q2q2q2q2',
									status: 'verification',
									workflow: 'workflow-11',
								},
							],
							count: 2,
						},
					];
				default:
					return [200, { jobs: [], count: 0 }];
			}
		});

		mock.onGet(`${peopleApiUrl}/job`).reply((config) => {
			switch (config.params.jobID) {
				case 'j1j1j1j1':
					return [
						200,
						{
							job: {
								id: 'j1j1j1j1',
								active: true,
								createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
								updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
								accepted: false,
								name: 'workflowName:2023-10-2:2f2f3',
								owner: user.id,
								status: 'action',
								workflow: 'workflow-9',
								nextSteps: [
									{
										node: {},
										name: 'Cristian Test',
										transition: 'Named_2b4099b408b648efbe632662006e691e',
									},
									{
										node: {},
										name: 'test queue',
										transition: 'Named_5e93117311f943ce9362e5d50bc39cdc',
									},
								],
								nodeConfig: {
									queue: 'd1a44ab3-5bbd-40bd-a627-abff19aff834',
									group: '60cb476d-a849-4879-81a6-d65c5a5dbecd',
									workflowEngine: {
										nextStepName: "cristian's queue",
									},
								},
								history: [
									{
										id: 'e1e1e1e1',
										createdAt: new Date('10/12/2023 10:34:21 AM').getTime(),
										event: { type: 'completed' },
										source: { type: 'system', id: 'System' },
									},
									{
										id: 'e2e2e2e2',
										createdAt: new Date('10/12/2023 10:34:03 AM').getTime(),
										event: { type: 'accepted' },
										source: { type: 'user', id: users[1].id },
									},
									{
										id: 'e3e3e3e3',
										createdAt: new Date('10/10/2023 5:14:33 PM').getTime(),
										event: { type: 'reassigned', to: { type: 'user', id: users[1].id } },
										source: { type: 'system', id: 'System' },
									},
									{
										id: 'e4e4e4e4',
										createdAt: new Date('10/10/2023 2:56:14 PM').getTime(),
										event: { type: 'fileAttached' },
										source: { type: 'user', id: user.id },
									},
									{
										id: 'e5e5e5e5',
										createdAt: new Date('10/09/2023 8:38:06 AM').getTime(),
										event: { type: 'accepted' },
										source: { type: 'system', id: 'System' },
									},
								],
								notes: [
									{
										id: 'n1n1n1n1',
										createdAt: new Date('10/06/2023 12:34:15 pm').getTime(),
										note: 'some note',
										author: user.id,
										private: false,
									},
									{
										id: 'n2n2n2n2',
										createdAt: new Date('10/03/2023 4:22:56 pm').getTime(),
										note: 'Pellentesque aliquam est vitae lorem gravida lacinia. Nullam at ex eget lorem mattis tincidunt. Aenean vitae risus eget augue faucibus ullamcorper at ac magna. Donec tempus augue a justo scelerisque scelerisque. Vestibulum suscipit maximus vestibulum. Integer sed placerat felis. Phasellus turpis ipsum, dapibus aliquam aliquet vel, tristique ac risus. Ut lectus sem, rutrum et mattis vel, eleifend sit amet odio. Sed vel malesuada lacus. Quisque rutrum justo elit, sit amet cursus justo facilisis non. Quisque vitae tortor vel nibh bibendum feugiat.',
										author: user.id,
										private: true,
									},
									{
										id: 'n3n3n3n3',
										createdAt: new Date('10/02/2023 11:54:08 am').getTime(),
										note: 'Pellentesque eget augue neque. Sed erat urna, cursus et imperdiet id, condimentum et sem. Phasellus efficitur malesuada tempus. Ut id magna pharetra erat convallis finibus. Nam quis enim quis dui aliquet sodales vel et ex. Vivamus sem orci, maximus eu commodo eu, fermentum ut metus. In molestie magna sed lacus imperdiet pretium. Nam ut iaculis tellus. Cras id risus dictum, venenatis est quis, elementum lorem. Etiam id leo scelerisque, efficitur leo et, scelerisque diam. Proin a tellus velit. Nam a arcu cursus justo varius pulvinar. Quisque sagittis, nibh ut volutpat sodales, erat nisi efficitur massa, in pellentesque enim enim rutrum eros. Vivamus sit amet nisl non tellus imperdiet faucibus et vel dui.',
										author: user.id,
										private: false,
									},
								],
								attachments: [
									{
										url: 'lo-and-behold.PNG',
										metadata: {
											ext: '.png',
											fullname: 'lo-and-behold.PNG',
											name: 'lo-and-behold',
											size: '10 KB',
											date: new Date('10/02/2023 11:54:08 am').getTime(),
											uploadedBy: user.id,
										},
									},
									{
										url: 'Bla-bla-bla.PDF',
										metadata: {
											ext: '.pdf',
											fullname: 'Bla-bla-bla.PDF',
											name: 'Bla-bla-bla',
											size: '15 KB',
											date: new Date('10/01/2023 10:32:54 am').getTime(),
											uploadedBy: user.id,
										},
									},
								],
							},
						},
					];
				case 'j2j2j2j2':
					return [
						200,
						{
							job: {
								id: 'j2j2j2j2',
								active: true,
								createdAt: 1699989179,
								updatedAt: 1699989179,
								accepted: false,
								name: 'workflow2:2023-10-2:abcd',
								owner: user.id,
								status: 'action',
								workflow: 'workflow-11',
								nextSteps: [
									{
										node: {},
										name: 'Cristian Test',
										transition: 'Named_2b4099b408b648efbe632662006e691e',
									},
									{
										node: {},
										name: 'test queue',
										transition: 'Named_5e93117311f943ce9362e5d50bc39cdc',
									},
								],
								nodeConfig: {
									queue: 'd1a44ab3-5bbd-40bd-a627-abff19aff834',
									group: '60cb476d-a849-4879-81a6-d65c5a5dbecd',
									workflowEngine: {
										nextStepName: "cristian's queue",
									},
								},
								history: [
									{
										id: 'e1e1e1e1',
										createdAt: new Date('10/12/2023 10:34:21 AM').getTime(),
										event: { type: 'completed' },
										source: { type: 'system', id: 'System' },
									},
									{
										id: 'e2e2e2e2',
										createdAt: new Date('10/12/2023 10:34:03 AM').getTime(),
										event: { type: 'accepted' },
										source: { type: 'user', id: users[1].id },
									},
									{
										id: 'e3e3e3e3',
										createdAt: new Date('10/10/2023 5:14:33 PM').getTime(),
										event: { type: 'reassigned', to: users[1].id },
										source: { type: 'system', id: 'System' },
									},
									{
										id: 'e4e4e4e4',
										createdAt: new Date('10/10/2023 2:56:14 PM').getTime(),
										event: { type: 'fileAttached' },
										source: { type: 'user', id: user.id },
									},
									{
										id: 'e5e5e5e5',
										createdAt: new Date('10/09/2023 8:38:06 AM').getTime(),
										event: { type: 'accepted' },
										source: { type: 'system', id: 'System' },
									},
								],
								notes: [
									{
										id: 'n1n1n1n1',
										createdAt: new Date('10/06/2023 12:34:15 pm').getTime(),
										note: 'some note',
										author: user.id,
										private: false,
									},
									{
										id: 'n2n2n2n2',
										createdAt: new Date('10/03/2023 4:22:56 pm').getTime(),
										note: 'Pellentesque aliquam est vitae lorem gravida lacinia. Nullam at ex eget lorem mattis tincidunt. Aenean vitae risus eget augue faucibus ullamcorper at ac magna. Donec tempus augue a justo scelerisque scelerisque. Vestibulum suscipit maximus vestibulum. Integer sed placerat felis. Phasellus turpis ipsum, dapibus aliquam aliquet vel, tristique ac risus. Ut lectus sem, rutrum et mattis vel, eleifend sit amet odio. Sed vel malesuada lacus. Quisque rutrum justo elit, sit amet cursus justo facilisis non. Quisque vitae tortor vel nibh bibendum feugiat.',
										author: user.id,
										private: true,
									},
									{
										id: 'n3n3n3n3',
										createdAt: new Date('10/02/2023 11:54:08 am').getTime(),
										note: 'Pellentesque eget augue neque. Sed erat urna, cursus et imperdiet id, condimentum et sem. Phasellus efficitur malesuada tempus. Ut id magna pharetra erat convallis finibus. Nam quis enim quis dui aliquet sodales vel et ex. Vivamus sem orci, maximus eu commodo eu, fermentum ut metus. In molestie magna sed lacus imperdiet pretium. Nam ut iaculis tellus. Cras id risus dictum, venenatis est quis, elementum lorem. Etiam id leo scelerisque, efficitur leo et, scelerisque diam. Proin a tellus velit. Nam a arcu cursus justo varius pulvinar. Quisque sagittis, nibh ut volutpat sodales, erat nisi efficitur massa, in pellentesque enim enim rutrum eros. Vivamus sit amet nisl non tellus imperdiet faucibus et vel dui.',
										author: user.id,
										private: false,
									},
								],
								attachments: [
									{
										url: 'lo-and-behold.PNG',
										metadata: {
											ext: '.png',
											fullname: 'lo-and-behold.PNG',
											name: 'lo-and-behold',
											size: '10 KB',
											date: new Date('10/02/2023 11:54:08 am').getTime(),
											uploadedBy: user.id,
										},
									},
									{
										url: 'Bla-bla-bla.PDF',
										metadata: {
											ext: '.pdf',
											fullname: 'Bla-bla-bla.PDF',
											name: 'Bla-bla-bla',
											size: '15 KB',
											date: new Date('10/01/2023 10:32:54 am').getTime(),
											uploadedBy: user.id,
										},
									},
								],
							},
						},
					];
				case 'j3j3j3j3':
					return [
						200,
						{
							job: {
								id: 'j3j3j3j3',
								active: true,
								createdAt: 1699989179,
								updatedAt: 1699989179,
								accepted: false,
								name: 'MyWorkflow:2023-12-19:ss1',
								owner: users[2].id,
								status: 'action',
								workflow: 'workflow-13',
								history: [],
								notes: [],
								attachments: [],
								nextSteps: [
									{
										node: {},
										name: 'Cristian Test',
										transition: 'Named_2b4099b408b648efbe632662006e691e',
									},
									{
										node: {},
										name: 'test queue',
										transition: 'Named_5e93117311f943ce9362e5d50bc39cdc',
									},
								],
								nodeConfig: {
									queue: 'd1a44ab3-5bbd-40bd-a627-abff19aff834',
									group: '60cb476d-a849-4879-81a6-d65c5a5dbecd',
									workflowEngine: {
										nextStepName: "cristian's queue",
									},
								},
							},
						},
					];
				case 'j4j4j4j4':
					return [
						200,
						{
							job: {
								id: 'j4j4j4j4',
								active: true,
								createdAt: 1699989179,
								updatedAt: 1699989179,
								accepted: false,
								name: 'MyWorkflow2:2023-11-19:ss2',
								owner: user.id,
								status: 'action',
								workflow: 'workflow-13',
								nextSteps: [
									{
										node: {},
										name: 'Cristian Test',
										transition: 'Named_2b4099b408b648efbe632662006e691e',
									},
									{
										node: {},
										name: 'test queue',
										transition: 'Named_5e93117311f943ce9362e5d50bc39cdc',
									},
								],
								nodeConfig: {
									queue: 'd1a44ab3-5bbd-40bd-a627-abff19aff834',
									group: '60cb476d-a849-4879-81a6-d65c5a5dbecd',
									workflowEngine: {
										nextStepName: "cristian's queue",
									},
								},
								history: [],
								notes: [],
								attachments: [],
							},
						},
					];
				case '753a60ee-1f4d-45e1-81c8-a34c71c05426':
					return [
						200,
						{
							job: {
								id: '753a60ee-1f4d-45e1-81c8-a34c71c05426',
								active: true,
								name: "Sara's People Workflow:2024-0-2:05426",
								owner: user.id,
								queue: '',
								workflow: '6abb4f3ccc174cfc86b352c9fc6c30e8',
								accepted: false,
								status: 'action',
								token: 'AQCAAAAAKgAAAAMAAAAAAAAAAaTl3uRTPt5w3WSbOpexIqhf/JlUsPZ+Tb7fs/bOGFfQTb61OSUm8tFRlJi8CVyshZNIZrhc9eLsHbDfU/AAD3fq2p/VEQduSiJP/ltri+M=yFrnWabwo2p3OZ+VS0eykyHueXDzmLf9tS6x3fPTq2faH89aW7X6lUjS/nP8if9N9ngzYZi7CaxYVUCkcNfyglTbGWccgGZ/O1zmWXs7I+zPxuc5eHOaTSWVJopP1nFfT25XTSMCDyaVxEcGP4VLfDokaGjFCxJ3MuM3CnnEqjiUyOB3ky6QMvWKzeQDEVUBt5hN5tFYdIAW+bl3nv/Sp5vqO9QJTCPlMKB7sdzJHOmOMz0RN8cwba5nOqAr1VconmGlZNrS6hD2UxUBqvbPYYfc2pEACMF9UR/CsIbohrZxydGH4fhhC0Mknv/CLc17c5AWpIPrQnem8EqOyQWzZjQdr8VtA1cgACbWviRDL/Snpz1Mw+sMUSTJkJE4JrV9FQleV0GP8eDNG/oAqGx+vDNR0IOS1s6OJf2tnGH4VCIUolj8QHpaYCPGnYmXHK+tAEVwb4nOkai4d2kymJXc6cjrWc9kw9xyjJhYGHAHGA+fdYmsok+gsFm2dJAIes5K7vNEFtA8RvWTrTghjEwv',
								nodeID: 'c398f1c0f9214a73ada32facc0d240b3',
								nodeNamespace: 'PeopleGroup',
								returnURL: 'https://2rkjc4p901.execute-api.us-east-1.amazonaws.com/dev/report-end',
								groupID: 'userGroup-1',
								createdAt: 1704836165836,
								updatedAt: 1704836165836,
								nextSteps: [
									{
										node: {},
										name: 'Email Out',
										transition: 'Normal',
									},
									{
										node: {},
										name: 'End',
										transition: 'Normal',
									},
									{
										node: {},
										name: 'End',
										transition: 'Negative',
									},
								],
								nodeConfig: {
									allowReassignment: true,
									group: '60cb476d-a849-4879-81a6-d65c5a5dbecd',
									sendEmailNotification: false,
									nextStep: true,
									workflowEngine: {
										nextStepName: 'people testing',
									},
									allowCollaboration: true,
								},
								history: [
									{
										id: '91b8e04f-155c-4a16-868c-169199fe2cc2',
										event: {
											type: 'created',
										},
										source: {
											type: 'system',
											id: 'system',
										},
										createdAt: 1704836165905,
									},
									{
										id: 'e7755772-0a32-49d4-b51b-e23c309cadba',
										event: {
											type: 'received',
										},
										source: {
											type: 'user',
											id: '02f8a272-6701-4298-a327-6b6afaa52b97',
										},
										createdAt: 1704836166001,
									},
								],
								notes: [],
								attachments: [
									{
										url: 'https://s3.us-east-1.amazonaws.com/lol.stratus.us-east-1.s3-adapter/b06fb58120614c2495638f2aec2ea022/20240109T171734293076.pdf?x-id=GetObject&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=ASIARGGJKHDBEZNONBVR%2F20240109%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20240109T213558Z&X-Amz-Expires=1800&X-Amz-SignedHeaders=host%3Bx-amz-server-side-encryption-customer-algorithm%3Bx-amz-server-side-encryption-customer-key%3Bx-amz-server-side-encryption-customer-key-md5&X-Amz-Signature=c1a698b4a907a08dc3396c36e7de329a20734eb53278026e2d2a89eae5899f68&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEF4aCXVzLWVhc3QtMSJGMEQCIQCgFZnrq3VoK90OwtaTPE2aUGPkDAhWsTi%2FOlFSN7ZYDAIfU8cnLlVXaqGjLEnt53WFaYE3KdMoCJHZ1XmR7LMWoyqUBAj2%2F%2F%2F%2F%2F%2F%2F%2F%2F%2F8BEAQaDDA4MjAyNjU3NjA2NiIMS8tEqka7eQn1VwM5KugDqavI5%2FRN%2BgeDMCTMn8OcE%2Bml6im5QFNK8ygROV8Efo1f95Uv%2FCiOAGmJbL%2BGX1wVuDUYh50XMr9a73o3zZOdx%2FjJCv4QzJFVmDr%2B%2Bxa14Py0Z4gx9ulIJAa9AzL5Yy73FBVjeIqKIbetSZt0dYjeTNRAV6Q%2FzQoA4OY68aMX%2F3tYg1ylNRvPQi5XXwuWHNGB1r0IesCxjbrZLaIWG952LH8YvgIsX1WEwWQSxzawAGaCxji%2F%2FxiIT7Zfu4GI37FkolLSiwSkII2R5rjSB%2B3DJRoFOYQRGaekUAU7vhoIGLhnaC2IwB%2FbXfEgF4KksSIBJBjnz%2FkV9un3w6HnGq6e1Bf9Al1zUUA%2Bq6HkBWYtFW1%2Frw2EcTP7qKn60Ovos1m34hn51nprGCmvXCIWPhjUT9JZIO3ham99CXDI6bEohU4AzNw0MvvwmnXv0AWJqWmk3XDovBkbK2ItDUeIGfLQ7q1Y6N34L%2BdXE86JhkzrhID%2FLARnJzHePgjyDUK8PZQ3A94AwY9R4RWgGFz%2BaTGuLdM6uHh8veziIeSPNDzvF2RMwF9Dl9dWCWMoFR5SQzguLqTaGibLD8w5ugw2vdEiTQOnZOYyTtI1BPEN%2BxSMKonyspNVMVziPf616Wk%2BDvxJfjlN%2FvMGMrowve32rAY6pgEJfr%2FMm5K2Bpxo9rU2IJNsmUNkB2FqAsjPw2ZgdssxFiROnCbKoa3kkN%2FyzmO0GU%2B3uX3OhUVrggycXrPTs%2FvBeSMHnHQKIt0mwlmIH%2BFSG7i0JeWnNCMBjcXEHw8k8hBASfKMHVMg0c6%2BGLsjfxlZGW491NXX6BQk6thok8TKrN5B9BMkJSmP3TEFR9pSNHWGU6IttcE5i3Fzy55ZkJF4J6Pcl75J',
										headers: {
											'x-amz-server-side-encryption-customer-key-md5': '/4NdloddRs2b0Ktze2qolw==',
											'x-amz-server-side-encryption-customer-key':
												'SURIMGl2em96ZDZxWTd6dFJPblZ2UTdVaGRhdU5lNUM=',
											'x-amz-server-side-encryption-customer-algorithm': 'AES256',
										},
										metadata: {
											ext: '.pdf',
											name: '20240109T171734293076',
											fullname: '20240109T171734293076.pdf',
											size: 957388,
											date: 1704836165835,
											uploadedBy: 'system',
										},
									},
								],
							},
						},
					];
				default:
					return [404, { job: {} }];
			}
		});

		mock.onGet(`${peopleApiUrl}/inbox`).reply(200, {
			inbox: [
				{
					id: 'nx0nx0nx0nx0',
					message:
						'There was an external error while processing this job. Please contact support with the following reference number should you need assistance: ADJ33D4',
					type: 'error',
					origin: {
						type: 'workflow',
						id: 'workflow-9',
						name: workflows.find(({ id }) => id === 'workflow-9')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/01/2023 11:53:08 am').getTime(),
				},
				{
					id: 'nx1nx1nx1nx1',
					message: 'New Job Assigned',
					type: 'job',
					status: 'action',
					job: 'j1j1j1j1',
					origin: {
						type: 'workflow',
						id: 'workflow-9',
						name: workflows.find(({ id }) => id === 'workflow-9')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/02/2023 11:53:08 am').getTime(),
				},
				{
					id: 'e3959cad-8837-477f-a769-db9adc8a5882',
					message: 'csv-create-process-fail',
					type: 'error',
					origin: {
						type: 'system',
						id: 'a67d94ba-69fa-5348-9a2d-220e02019d2c',
					},
					createdAt: 1753802857018,
					status: 'created',
				},
				{
					id: 'a4ee10de-54f5-4e54-8a57-07d57efd7dc7',
					message: 'csv-create-fail',
					type: 'error',
					origin: {
						type: 'system',
						id: 'a67d94ba-69fa-5348-9a2d-220e02019d2c',
						name: '[{"row":2,"email":"lohi5kmbs5@gmail.com","groups":"stgsbr","roles":"srthdrt","card id":"b","errors":["user-exists","card-id-exists","invalid-groups","invalid-roles"]},{"row":3,"email":"lohi6kmbs6@gmail.com","groups":"srthsrt","roles":"drthbdrt","card id":"a","errors":["user-exists","card-id-exists","invalid-groups","invalid-roles"]}]',
					},
					createdAt: 1753803334012,
					status: 'created',
				},
				{
					id: 'nx2nx2nx2nx2',
					message: 'Get this done ASAP',
					type: 'job',
					status: 'action',
					job: 'j2j2j2j2',
					origin: {
						type: 'workflow',
						id: 'workflow-11',
						name: workflows.find(({ id }) => id === 'workflow-11')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/03/2023 4:22:56 pm').getTime(),
				},
				{
					id: 'nx3nx3nx3nx3',
					message: 'This job does not exist',
					type: 'job',
					status: 'action',
					job: 'j5j5j5j5',
					origin: {
						type: 'workflow',
						id: 'workflow-9',
						name: workflows.find(({ id }) => id === 'workflow-9')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/02/2023 11:54:08 am').getTime(),
				},
				{
					id: 'nx4nx4nx4nx4',
					message: 'Workflows error message 1',
					type: 'error',
					// status: 'error',
					origin: {
						type: 'workflow',
						id: 'workflow-9',
						name: workflows.find(({ id }) => id === 'workflow-9')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/12/2023 11:54:08 am').getTime(),
				},
				{
					id: 'nx5nx5nx5nx5',
					message: 'Workflows error message 2',
					type: 'error',
					// status: 'error',
					origin: {
						type: 'workflow',
						id: 'workflow-11',
						name: workflows.find(({ id }) => id === 'workflow-11')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/13/2023 4:22:56 pm').getTime(),
				},
				{
					id: 'nx6nx6nx6nx6',
					message: 'Workflows error message 3',
					type: 'error',
					// status: 'error',
					origin: {
						type: 'workflow',
						id: 'workflow-9',
						name: workflows.find(({ id }) => id === 'workflow-9')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/14/2023 11:54:08 am').getTime(),
				},
				{
					id: 'nx7nx7nx7nx7',
					message: 'Workflows error message 4',
					type: 'error',
					// status: 'error',
					origin: {
						type: 'workflow',
						id: 'workflow-9',
						name: workflows.find(({ id }) => id === 'workflow-9')?.name ?? 'Unknown Workflow',
					},
					createdAt: new Date('10/15/2023 11:54:08 am').getTime(),
				},
				{
					id: 'nx8nx8nx8nx8',
					message: "Workflow status set to 'Running' by 'John Smith'",
					type: 'info',
					status: 'collaboration',
					origin: {
						type: 'system',
					},
					job: 'j2j2j2j2',
					data: {
						collab: {
							collabType: 'extension-request',
							userId: 'mock-user-id-19',
							jobName: '1111111111',
							message: 'aaaaaaaaaaaaaaa',
							expireDate: 1759828334000,
							status: 'pending',
							jobId: 'j2j2j2j2',
							accessDate: '1759828334',
						},
					},
					createdAt: new Date('10/15/2023 11:58:20 am').getTime(),
				},
				{
					id: 'nx9nx9nx9nx9',
					message: 'System info message 2',
					type: 'info',
					origin: {
						type: 'system',
					},
					createdAt: new Date('10/15/2023 11:59:20 am').getTime(),
				},
				{
					id: '82e98cf1-1f98-4ba2-9e82-f64e4cdaed5f',
					message: 'delegated-pin-update-success',
					type: 'info',
					status: 'action',
					origin: {
						type: 'system',
						id: 'a67d94ba-69fa-5348-9a2d-220e02019d2c',
					},
					createdAt: new Date(1757728611194).getTime(),
				},
				{
					createdAt: new Date(1769615770248).getTime(),
					id: '3b7fc202-d2ba-4cac-9b02-d4e2f0a20fdb',
					message: 'mfp-dash-empty-paper',
					origin: {
						id: 'a67d94ba-69fa-5348-9a2d-220e02019d2c',
						type: 'system',
						name: '{"deviceName":"C361i","fields":"Tray1, Tray2, Tray3","deviceSerial":"ADXJ013000003"}',
					},
					type: 'error',
				},
			],
			count: 2,
		});

		mock.onPost(`${peopleApiUrl}/job/notes`).reply(200);
		mock.onPost(`${peopleApiUrl}/job/attachments`).reply(200);
		mock.onPut(`${peopleApiUrl}/job/action`).reply(200);
		mock.onPost(`${peopleApiUrl}/tracker`).reply((config) => {
			const data = JSON.parse(config.data);
			return [200, { jobIDs: [...data.jobs] }];
		});
		mock.onGet(`${peopleApiUrl}/tracker`).reply(200, {
			jobs: [
				{
					id: 'j1j1j1j1',
					active: true,
					createdAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					updatedAt: new Date('10/09/2023 7:38:00 AM').getTime(),
					name: 'workflowName:2023-10-2:2f2f3',
					owner: user.id,
					status: 'action',
					workflow: 'workflow-9',
				},
				{
					id: 'j2j2j2j2',
					active: true,
					createdAt: 1699999179,
					updatedAt: 1699999179,
					name: 'workflow2:2023-10-2:abcd',
					owner: user.id,
					status: 'action',
					workflow: 'workflow-11',
				},
			],
		});

		mock.onPut(`${peopleApiUrl}/archive/grace-period`).reply((config) => {
			const data = JSON.parse(config.data);
			console.log('Mock: Setting grace period', data);
			return [
				200,
				{
					message: 'Grace period set successfully',
					updated: 5,
					errors: [],
					gracePeriodDays: data.gracePeriodDays,
				},
			];
		});

		mock.onGet(
			new RegExp(`${peopleApiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/archive/storage-metadata(\\?.*)?$`),
		).reply(200, {
			count: 12,
			totalFileSize: 3.125 * 1024 * 1024 * 1024,
			totalFileCount: 45,
		});

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/group-order/user`).reply(200, [
			'userGroup-1',
			'userGroup-3',
			'',
			'userGroup-2',
		]);

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/group-order/device`).reply(200, [
			'deviceGroup-8',
			'deviceGroup-1',
			'deviceGroup-3',
			'deviceGroup-4',
			'not-a-real-one',
			'fake-ronald',
			'deviceGroup-9',
			'deviceGroup-6',
			'deviceGroup-7',
			'deviceGroup-8',
			'00000000-4444-4444-4444-000000000000',
			'deviceGroup-5',
		]);

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/group-order/form`).reply(200, [
			'deviceGroup-1',
			'deviceGroup-3',
			'',
			'deviceGroup-2',
		]);

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/group-order/workflow`).reply(200, [
			'userGroup-1',
			'workflowGroup-2',
			'userGroup-3',
			'workflowGroup-1',
			'userGroup-2',
			'workflowGroup-3',
		]);

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/group-order/jobNameTemplate`).reply(200, []);

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/group-order/print`).reply(200, []);

		mock.onGet(`/api/user/${selectedLicenseGroup.id}/group-order/managedPrint`).reply(200, []);

		// APIs for the customer feedback
		// mock.onPost(`/api/feedback/file`).reply(200, {
		// 	status: 'success',
		// 	key: '9a61ebfec1117533c9243f7ced95aeb3/2023-03-21_15-05.png'
		// });

		// mock.onPost(`/api/feedback/email`).reply(200, {
		// 	status: 'success'
		// });

		// APIs for the contact us form
		// mock.onPost('/api/contact').reply(200, {
		// 	status: 'success'
		// });
		//
		// APIs for the upgrade license
		// mock.onPatch('/api/tenants/${selectedLicenseGroup.id}/upgrade').reply(200, {
		// 	success: true,
		// 	isMoreThanNewCapacity: true,
		// 	unassigned: true
		// });

		// Mock Tenant config onboarding page
		// mock.onGet(
		// 	`/api/confirmation/d79ad530-da0e-5a12-b60f-7876cb28a84c/tenant/d8bdff9e-5ff1-4306-9a72-747a8c05a98e`
		// ).reply(200, { valid: true, status: 'INVITED', orderNumber: 'JAMES100037', plan: 'stratus' });
		// mock.onGet(
		// 	`/api/tenants/d79ad530-da0e-5a12-b60f-7876cb28a84c/tenant/d8bdff9e-5ff1-4306-9a72-747a8c05a98e/config-status`
		// ).reply(200, {
		// 	status: 'INVITED'
		// });
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockDirectPrintAgents')) {
		mock.onGet(/\/api\/tenants\/[^/]+\/direct-print-agents/).reply(200, directPrintAgents);
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockWorkflows')) {
		const wfxApiUrl = getWfxUrl('us-east-1');

		mock.onGet(`${wfxApiUrl}/api/wfx`).reply(200, workflows);

		mock.onGet(`${wfxApiUrl}/api/samples`).reply(200, sampleWorkflows);

		mock.onGet(`${wfxApiUrl}/api/groups`).reply(200, workflowGroups);

		mock.onGet(`${wfxApiUrl}/api/zip?password=false&id=workflow-21`).reply(
			200,
			Buffer.from('arrayBufferOfWorkflowDataFake'),
		);

		mock.onGet(`${wfxApiUrl}/api/wfx/mock-workflow-id-1.json`).reply(200, {
			nodes: [
				{
					id: 'mock-node-id-1',
					name: 'Mock Internal Form Node 1',
					namespace: 'InternalForm',
					enabled: true,
				},
				{
					id: 'mock-node-id-2',
					name: 'Mock Internal Form Node 2',
					namespace: 'InternalForm',
					enabled: true,
				},
				{
					id: 'mock-node-id-3',
					name: 'Mock Rename Node 3',
					namespace: 'Rename',
					enabled: true,
				},
				{
					id: 'mock-node-id-4',
					name: 'Mock External Form Node 4',
					namespace: 'ExternalForm',
					enabled: true,
				},
			],
		});

		mock.onGet(`${wfxApiUrl}/api/wfx/mock-workflow-id-2.json`).reply(200, {
			nodes: [
				{
					id: 'mock-node-id-201',
					name: 'Mock Internal Form Node 201',
					namespace: 'InternalForm',
					enabled: true,
				},
				{
					id: 'mock-node-id-202',
					name: 'Mock Internal Form Node 202',
					namespace: 'InternalForm',
					enabled: true,
				},
				{
					id: 'mock-node-id-203',
					name: 'Mock Rename Node 203',
					namespace: 'Rename',
					enabled: true,
				},
			],
		});
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockForms')) {
		const formsApiUrl = getFormsUrl(user.awsRegion);
		mock.onGet(`${formsApiUrl}/api/groups`).reply(200, [
			{
				id: '4976d6b2fccd40ce9b825c1eb12a5434',
				title: 'form-group-2',
				sortOrder: 2,
			},
			{
				id: 'eabdc55208d842619bccb6397680930a',
				title: 'group-2',
				sortOrder: 3,
			},
		]);

		mock.onGet(`${formsApiUrl}/api/forms`).reply(200, [
			{
				id: '1a2b3c4d5e6f7g8h',
				title: 'Project X',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1696000000000,
				updated: 1696100000000,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '2b3c4d5e6f7g8h9i',
				title: 'Task Management 2',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: false,
				created: 1696100000000,
				updated: 1696200000000,
				formStatus: 'draft',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '3c4d5e6f7g8h9i0j',
				title: 'Product Launch 3',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1696200000000,
				updated: 1696300000000,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '4d5e6f7g8h9i0j1k',
				title: 'Meeting Notes 4',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1696300000000,
				updated: 1696400000000,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '5e6f7g8h9i0j1k2l',
				title: 'Expense Report 5',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1696400000000,
				updated: 1696500000000,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '6f7g8h9i0j1k2l3m',
				title: 'Project Y',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: false,
				created: 1696500000000,
				updated: 1696600000000,
				formStatus: 'draft',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '7g8h9i0j1k2l3m4n',
				title: 'Survey Results 7',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1696600000000,
				updated: 1696700000000,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '8h9i0j1k2l3m4n5o',
				title: 'Marketing Campaign 8',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1696700000000,
				updated: 1696800000000,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: '3c58f69b470f42178b77d9249e2f3989',
				title: 'nmassey',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1695312048179,
				updated: 1695312065423,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
			{
				id: 'b555c9e7f8fa41dfbc5a79683e22dd43',
				title: 'Test 1',
				creator: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				editor: 'KonicaMinoltaMarketPlace_f4c9b1c3db8f093fd72f4f67febf1f951c25d0c2',
				valid: true,
				created: 1694832174280,
				updated: 1694832778817,
				formStatus: 'published',
				formGroup: {
					id: '00000',
				},
			},
		]);
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockPricing')) {
		mock.onGet('/api/mp/catalog/pricing?region=US').reply(200, {
			region: 'US',
			currencyCode: 'USD',
			pricing: {
				'F34EB719-1E3E-4AA9-B31B-AE691B0AB090': {
					publicId: 'F34EB719-1E3E-4AA9-B31B-AE691B0AB090',
					name: 'Dispatcher ScanTrip Cloud 1 device license, 1 Year',
					showPrice: true,
					productionDuration: 1,
					priceRanges: [
						{
							sapId: 'AEMPA00',
							amount: 241.14,
							min: 1,
						},
						{
							sapId: 'AEMPA05',
							amount: 236.32,
							min: 10,
						},
						{
							sapId: 'AEMPA0A',
							amount: 231.5,
							min: 25,
						},
						{
							sapId: 'AEMPA0G',
							amount: 226.67,
							min: 50,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'04847520-17F3-4582-A1DA-827D7D4D2B52': {
					publicId: '04847520-17F3-4582-A1DA-827D7D4D2B52',
					name: 'Dispatcher ScanTrip Cloud 1 device license, 2 Year',
					showPrice: true,
					productionDuration: 2,
					priceRanges: [
						{
							sapId: 'AEMPA01',
							amount: 458.17,
							min: 1,
						},
						{
							sapId: 'AEMPA06',
							amount: 449.01,
							min: 10,
						},
						{
							sapId: 'AEMPA0C',
							amount: 439.85,
							min: 25,
						},
						{
							sapId: 'AEMPA0H',
							amount: 430.67,
							min: 50,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'2B4EEAA7-9576-43C8-820D-25C09DAC99B3': {
					publicId: '2B4EEAA7-9576-43C8-820D-25C09DAC99B3',
					name: 'Dispatcher ScanTrip Cloud 1 device license, 3 Year',
					showPrice: true,
					productionDuration: 3,
					priceRanges: [
						{
							sapId: 'AEMPA02',
							amount: 651.08,
							min: 1,
						},
						{
							sapId: 'AEMPA07',
							amount: 638.06,
							min: 10,
						},
						{
							sapId: 'AEMPA0D',
							amount: 625.05,
							min: 25,
						},
						{
							sapId: 'AEMPA0J',
							amount: 612.01,
							min: 50,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'3A14C409-D4CB-4C16-A346-B5D732570A17': {
					publicId: '3A14C409-D4CB-4C16-A346-B5D732570A17',
					name: 'Dispatcher ScanTrip Cloud 1 device license, 4 Year',
					showPrice: true,
					productionDuration: 4,
					priceRanges: [
						{
							sapId: 'AEMPA03',
							amount: 819.88,
							min: 1,
						},
						{
							sapId: 'AEMPA08',
							amount: 803.49,
							min: 10,
						},
						{
							sapId: 'AEMPA0E',
							amount: 787.1,
							min: 25,
						},
						{
							sapId: 'AEMPA0K',
							amount: 770.68,
							min: 50,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'A0C7C4C6-FF94-44D1-AD3F-16E5274385F5': {
					publicId: 'A0C7C4C6-FF94-44D1-AD3F-16E5274385F5',
					name: 'Dispatcher ScanTrip Cloud 1 device license, 5 Year',
					showPrice: true,
					productionDuration: 5,
					priceRanges: [
						{
							sapId: 'AEMPA04',
							amount: 964.56,
							min: 1,
						},
						{
							sapId: 'AEMPA09',
							amount: 945.28,
							min: 10,
						},
						{
							sapId: 'AEMPA0F',
							amount: 926,
							min: 25,
						},
						{
							sapId: 'AEMPA0M',
							amount: 906.68,
							min: 50,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'C3F39D09-6091-4302-8C31-C78A81F1ADAE': {
					publicId: 'C3F39D09-6091-4302-8C31-C78A81F1ADAE',
					name: 'Dispatcher ScanTrip Cloud BUS Bundle 1 device license, 1 Year',
					showPrice: true,
					productionDuration: 1,
					priceRanges: [
						{
							sapId: 'AEMPA0N',
							amount: 0.2,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'3348C5FD-7123-4332-ABD0-4D5C644CE3A7': {
					publicId: '3348C5FD-7123-4332-ABD0-4D5C644CE3A7',
					name: 'Dispatcher ScanTrip Cloud 1 device license, Monthy Subscription',
					showPrice: true,
					productionDuration: null,
					priceRanges: [
						{
							sapId: 'DPSAAA01',
							amount: 20.1,
							min: 1,
						},
						{
							sapId: 'DPSAAA10',
							amount: 19.69,
							min: 10,
						},
						{
							sapId: 'DPSAAA25',
							amount: 19.29,
							min: 25,
						},
						{
							sapId: 'DPSAAA50',
							amount: 18.89,
							min: 50,
						},
					],
					billing: {
						name: 'Monthly',
						uid: 'bmonth',
						duration: 1,
						months: true,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 100000,
						step: 1,
						lock: false,
					},
				},
				'1293F292-D870-4578-9ED0-50D151A46820': {
					publicId: '1293F292-D870-4578-9ED0-50D151A46820',
					name: 'Dispatcher ScanTrip Cloud BUS Bundle 1 device license, 2 Year',
					showPrice: true,
					productionDuration: 2,
					priceRanges: [
						{
							sapId: 'AEMPA0P',
							amount: 229.28,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'6852DB6D-DD15-4910-A68F-63CAEE12EC17': {
					publicId: '6852DB6D-DD15-4910-A68F-63CAEE12EC17',
					name: 'Dispatcher ScanTrip Cloud BUS Bundle 1 device license, 3 Year',
					showPrice: true,
					productionDuration: 3,
					priceRanges: [
						{
							sapId: 'AEMPA0R',
							amount: 434.26,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'ED4FA711-9159-4437-9398-1AEC3CA4B196': {
					publicId: 'ED4FA711-9159-4437-9398-1AEC3CA4B196',
					name: 'Dispatcher ScanTrip Cloud BUS Bundle 1 device license, 4 Year',
					showPrice: true,
					productionDuration: 4,
					priceRanges: [
						{
							sapId: 'AEMPA0T',
							amount: 615.11,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'0FA99FF0-16D2-4971-9CD5-157F99F2ED29': {
					publicId: '0FA99FF0-16D2-4971-9CD5-157F99F2ED29',
					name: 'Dispatcher ScanTrip Cloud BUS Bundle 1 device license, 5 Year',
					showPrice: true,
					productionDuration: 5,
					priceRanges: [
						{
							sapId: 'AEMPA0U',
							amount: 771.85,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'CB73A7BE-5C65-41D0-9312-D95DA2301E3B': {
					publicId: 'CB73A7BE-5C65-41D0-9312-D95DA2301E3B',
					name: 'Dispatcher ScanTrip Cloud BUS Bundle Dealer 1 device license, 3 Year',
					showPrice: true,
					productionDuration: 3,
					priceRanges: [
						{
							sapId: 'AEMPA0V',
							amount: 300,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'539FF6A8-86DF-4B3B-B9C3-556637F6DB85': {
					publicId: '539FF6A8-86DF-4B3B-B9C3-556637F6DB85',
					name: 'Dispatcher Stratus 1 device license, 1 Year',
					showPrice: true,
					productionDuration: 1,
					priceRanges: [
						{
							sapId: 'STRATUSTERM1MIN1',
							amount: 432,
							min: 1,
						},
						{
							sapId: 'STRATUSTERM1MIN10',
							amount: 410.4,
							min: 10,
						},
						{
							sapId: 'STRATUSTERM1MIN50',
							amount: 389.88,
							min: 50,
						},
						{
							sapId: 'STRATUSTERM1MIN200',
							amount: 370.39,
							min: 200,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'53FDDC16-E37E-4712-BE6D-34E4D08977D2': {
					publicId: '53FDDC16-E37E-4712-BE6D-34E4D08977D2',
					name: 'Dispatcher Stratus 1 device license, 2 Year',
					showPrice: true,
					productionDuration: 2,
					priceRanges: [
						{
							sapId: 'STRATUSTERM2MIN1',
							amount: 820.8,
							min: 1,
						},
						{
							sapId: 'STRATUSTERM2MIN10',
							amount: 779.76,
							min: 10,
						},
						{
							sapId: 'STRATUSTERM2MIN50',
							amount: 740.77,
							min: 50,
						},
						{
							sapId: 'STRATUSTERM2MIN200',
							amount: 703.73,
							min: 200,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'C1512F4C-42F1-4F7E-A477-D635C2B5AFC5': {
					publicId: 'C1512F4C-42F1-4F7E-A477-D635C2B5AFC5',
					name: 'Dispatcher Stratus 1 device license, 3 Year',
					showPrice: true,
					productionDuration: 3,
					priceRanges: [
						{
							sapId: 'STRATUSTERM3MIN1',
							amount: 1166.4,
							min: 1,
						},
						{
							sapId: 'STRATUSTERM3MIN10',
							amount: 1108.08,
							min: 10,
						},
						{
							sapId: 'STRATUSTERM3MIN50',
							amount: 1052.68,
							min: 50,
						},
						{
							sapId: 'STRATUSTERM3MIN200',
							amount: 1000.04,
							min: 200,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'EE08CEBF-2AAC-44E0-A899-7489547BEF8F': {
					publicId: 'EE08CEBF-2AAC-44E0-A899-7489547BEF8F',
					name: 'Dispatcher Stratus 1 device license, 4 Year',
					showPrice: true,
					productionDuration: 4,
					priceRanges: [
						{
							sapId: 'STRATUSTERM4MIN1',
							amount: 1468.8,
							min: 1,
						},
						{
							sapId: 'STRATUSTERM4MIN10',
							amount: 1395.36,
							min: 10,
						},
						{
							sapId: 'STRATUSTERM4MIN50',
							amount: 1325.59,
							min: 50,
						},
						{
							sapId: 'STRATUSTERM4MIN200',
							amount: 1259.31,
							min: 200,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'86EE6148-DBC6-41E7-9D75-2200A349511C': {
					publicId: '86EE6148-DBC6-41E7-9D75-2200A349511C',
					name: 'Dispatcher Stratus 1 device license, 5 Year',
					showPrice: true,
					productionDuration: 5,
					priceRanges: [
						{
							sapId: 'STRATUSTERM5MIN1',
							amount: 1728,
							min: 1,
						},
						{
							sapId: 'STRATUSTERM5MIN10',
							amount: 1641.6,
							min: 10,
						},
						{
							sapId: 'STRATUSTERM5MIN50',
							amount: 1559.52,
							min: 50,
						},
						{
							sapId: 'STRATUSTERM5MIN200',
							amount: 1481.54,
							min: 200,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'6D0F7639-1251-4032-8254-B0DA4872CE93': {
					publicId: '6D0F7639-1251-4032-8254-B0DA4872CE93',
					name: 'Dispatcher Stratus 1 people license, 1 Year',
					showPrice: true,
					productionDuration: 1,
					priceRanges: [
						{
							sapId: 'STRATUSPEOPLETERM1',
							amount: 432,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'5BAEFAE4-9469-449F-9043-28992F137480': {
					publicId: '5BAEFAE4-9469-449F-9043-28992F137480',
					name: 'Dispatcher Stratus 1 people license, 2 Year',
					showPrice: true,
					productionDuration: 2,
					priceRanges: [
						{
							sapId: 'STRATUSPEOPLETERM2',
							amount: 820.8,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'E3FC89C6-E56A-4DE9-B2DF-D13CDF38EB61': {
					publicId: 'E3FC89C6-E56A-4DE9-B2DF-D13CDF38EB61',
					name: 'Dispatcher Stratus 1 people license, 3 Year',
					showPrice: true,
					productionDuration: 3,
					priceRanges: [
						{
							sapId: 'STRATUSPEOPLETERM3',
							amount: 1166.4,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'A974ABCF-2D72-4DC7-A1DB-3D839335BA73': {
					publicId: 'A974ABCF-2D72-4DC7-A1DB-3D839335BA73',
					name: 'Dispatcher Stratus 1 people license, 4 Year',
					showPrice: true,
					productionDuration: 4,
					priceRanges: [
						{
							sapId: 'STRATUSPEOPLETERM4',
							amount: 1468.8,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'309201E3-6482-4BC0-BDBB-37DD3CF4F488': {
					publicId: '309201E3-6482-4BC0-BDBB-37DD3CF4F488',
					name: 'Dispatcher Stratus 1 people license, 5 Year',
					showPrice: true,
					productionDuration: 5,
					priceRanges: [
						{
							sapId: 'STRATUSPEOPLETERM5',
							amount: 1728,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 10000,
						step: 1,
						lock: false,
					},
				},
				'52AC9157-8FBF-4A2E-8950-4A1D9CE560C1': {
					publicId: '52AC9157-8FBF-4A2E-8950-4A1D9CE560C1',
					name: 'Dispatcher Stratus Service - Test Plan',
					showPrice: true,
					productionDuration: null,
					priceRanges: [
						{
							sapId: 'DPSAAA02',
							amount: 2,
							min: 1,
						},
					],
					billing: {
						name: 'Monthly',
						uid: 'bmonth',
						duration: 1,
						months: true,
						gracePeriod: 0,
					},
					counter: {
						min: 1,
						max: 100,
						step: 1,
						lock: false,
					},
				},
				'1E92B38B-EEA4-4B88-9DAB-CBDE4A9D472B': {
					publicId: '1E92B38B-EEA4-4B88-9DAB-CBDE4A9D472B',
					name: 'Dispatcher ScanTrip Cloud 1 device license, Monthy Subscription(1-time)',
					showPrice: true,
					productionDuration: null,
					priceRanges: [
						{
							sapId: 'DPSAAA03',
							amount: 0,
							min: 1,
						},
					],
					billing: {
						name: 'One Time Fee',
						uid: 'bonce',
						duration: 0,
						months: false,
						gracePeriod: 0,
					},
				},
			},
		});
	}

	// `mockEntitlements` example:
	// localStorage.setItem(
	// 	'mockEntitlements',
	// 	JSON.stringify({
	// 		features: {
	// 			r2m: false,
	// 		},
	// 		permissions: {
	// 			r2mTab: {
	// 				sharedQueueSection: {
	// 					deleteJobs: false,
	// 				},
	// 			},
	// 		},
	// 	}),
	// );
	const mockEntitlements = localStorage.getItem('mockEntitlements');
	if (localStorage.getItem('mock') || mockEntitlements) {
		const mockEntitlementsObject = (() => {
			try {
				const data = JSON.parse(mockEntitlements ?? '{}');
				if (_.isObject(data)) {
					return data;
				}
				return undefined;
			} catch (error) {
				return undefined;
			}
		})();

		const defaultResponse = {
			permissions,
			features: selectedLicenseGroup.plan === 'stc' ? stcPlanFeatures : stratusPlanFeatures,
		};
		const response = mockEntitlementsObject
			? _.merge({}, defaultResponse, mockEntitlementsObject)
			: defaultResponse;
		mock.onGet(`/api/user/entitlements`).reply(200, response);
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockApps')) {
		mock.onGet('/api/app').reply(200, apps);
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockPrints')) {
		const r2mApiUrl = getR2mUrl(user.awsRegion);
		mock.onGet(`${r2mApiUrl}/api/print-job/get/user`).reply(200, prints);
		mock.onGet(`${r2mApiUrl}/api/print-queue/get/user`).reply(200, printGroups);
		mock.onGet(`${r2mApiUrl}/api/print-preferences/tenant`).reply(200, { preferences: tenantPrintPreferences });
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockReports')) {
		const r2mApiUrl = getR2mUrl(user.awsRegion);
		mock.onGet(`${r2mApiUrl}/api/scheduled-reports/get/user`).reply(200, scheduledReports);
		reportTypeOptions.forEach((type) => {
			mock.onGet(new RegExp(`\\/api\\/reports\\/type\\/${type}\\/[^/]+\\?.+`)).reply(200, reportByType[type]);
		});
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockMfpAuth')) {
		mock.onGet(`/api/tenants/mfp-auth`).reply(200, {
			enablePublicUserAccess: {
				enabled: false,
				allowAdminEdit: true,
			},
			enableQuickCopyAccess: {
				enabled: false,
				allowAdminEdit: true,
			},
			openStratusOnUnlock: {
				enabled: false,
				allowAdminEdit: true,
			},
			allowUserCardLogin: {
				enabled: true,
				allowAdminEdit: true,
			},
			allowUserPinLogin: {
				enabled: true,
				allowAdminEdit: true,
			},
			allowReleaseOnly: {
				enabled: true,
				allowAdminEdit: true,
			},
		});
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockNotifications')) {
		mock.onGet('/api/notification').reply(200, notifications);
	}

	if (localStorage.getItem('mock') || localStorage.getItem('mockRegion')) {
		const mockRegion = localStorage.getItem('mockRegion') ?? 'US';
		mock.onGet('https://static.getpersonalize.com/geolocation').reply(200, mockRegion);
	}

	if (localStorage.getItem('mockUser')) {
		mock.onGet('/api/sso/session').reply(200, {
			user,
		});
		// mock.onGet('/api/sso/session').reply(401, null);
	}
};

export default mocking;
